import React, {
  useState, useEffect, useRef, useCallback, useMemo, lazy, Suspense,
} from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { auth, api, apiUrl, authHeaders, IS_NATIVE_APP, type PushSettings, type PushType, InviteRequiredError, POPUP_WINDOW_NAME, POPUP_RESULT_KEY, FashionCollection, FashionProfile, QidUser, Product, WardrobeItem, WardrobeItemLike, ReturnBag, WardrobeTransaction, Drop, PublicDrop, SwapProposal, PurchaseOffer, PublicSellerProfile, AdminStats, AdminIssue, BlockedUser, AdminAction, AgentTask, SupportTicket, IdentityStatus, Conversation, ChatMessage, ItemReturn, ItemReturnStatus, GeoArea, SellerArea, ApiToken, UserReputation, UserReview, PendingReview, DealKind, FeaturePack, FortnoxAccountPlan, FortnoxCategory, FortnoxPreview, FortnoxStatus, FortnoxVoucherRow, ShippingOption, Shipment, CrmLead, CrmLeadDetail, CrmNote, CrmStage, CrmImportRow, CRM_STAGES, CRM_PLATFORMS, type OutfitSuggestion, TaxInfoStatus } from './api';
import { t, tn, vl, locale, getLang, setLang, LANGS, type MessageKey } from './i18n';
import { TermsCheckbox, TermsUpdateModal, useTermsVersion, LEGAL_PATHS } from './Legal';
import { INVITE_KEY } from './attribution';
import { AdminMarketingView } from './AdminMarketing';
import { OutfitCards, OutfitsView } from './outfits/Outfits';
import type { PickedArea } from './maps';
import { initPush, syncPush, enablePush, pushPermission, pushPromptDismissed, dismissPushPrompt, unregisterPush } from './lib/push';
import { useBackgroundRemoval, type BackgroundRemovalState } from './bgRemoval/useBackgroundRemoval';
import { BackgroundRemovalTimeout, type RemovalProgress } from './bgRemoval/client';
import { canCutout, chooseCutout, chooseOriginal, findVariant, pruneVariants, uploadPath, type ImageVariant, type ListingImages } from './bgRemoval/variants';
import { QButton } from './q/QButton';

// ─────────────────────────────────────────────────────────────────
// GAMMELROSA THEME
// ─────────────────────────────────────────────────────────────────
const T = {
  bg: '#ffffff',
  surface: '#ffffff',
  surfaceHover: '#f7f7f7',
  border: '#e8e6e7',
  borderHover: '#111111',
  text: '#111111',
  textSub: '#4a4a4a',
  textMuted: '#8f8a8c',
  rose: 'hsl(var(--primary))',
  roseLight: 'hsl(var(--primary) / 0.7)',
  roseDim: 'hsl(var(--primary) / 0.1)',
  danger: 'hsl(var(--destructive))',
  success: '#2e7d32',
  forest: '#4a4a4a',
  accent1: 'hsl(var(--primary) / 0.6)',
  accent2: 'hsl(var(--primary) / 0.3)',
  accent3: 'hsl(var(--primary))',
  overlay: 'rgba(0,0,0,0.55)',
  navH: 72,
  amber: 'hsl(var(--primary))',
  amberLight: 'hsl(var(--primary) / 0.7)',
  amberDim: 'hsl(var(--primary) / 0.15)',
};

// ─────────────────────────────────────────────────────────────────
// STATIC DATA
// ─────────────────────────────────────────────────────────────────

const STYLE_TAGS = ['Alla', 'Klassisk', 'Modern', 'Streetwear', 'Formell', 'Casual', 'Bohemisk'];
const CATEGORY_TAGS = ['Alla', 'Kvinna', 'Man', 'Barn', 'Unisex'];
const SIZES = ['XS', 'S', 'M', 'L', 'XL', '2XL', '3XL', '4XL'];
const STYLE_PREFS = ['Minimalistisk', 'Maximalistisk', 'Vintage', 'Bohemisk', 'Preppy', 'Athleisure', 'Business casual', 'Avant-garde'];

// ─────────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────────

// Plausible-mål (se index.html). Syns under Goals i Plausible när de lagts
// till där med samma namn. No-op om skriptet blockerats.
function track(event: string, props?: Record<string, string>) {
  try { (window as any).plausible?.(event, props ? { props } : undefined); } catch { /* ignorera */ }
}

function fmt(n: number) {
  return Math.round(n).toLocaleString(locale());
}

// Som fmt men med ören när beloppet inte är jämnt — för köparskydd och
// totalbelopp, så att det som visas stämmer exakt med det Stripe drar.
function fmtKr(n: number) {
  const r = Math.round(n * 100) / 100;
  return r.toLocaleString(locale(), { minimumFractionDigits: Number.isInteger(r) ? 0 : 2, maximumFractionDigits: 2 });
}

// Köparskydd vid köp: 5 % av priset + 7,50 kr, inkl. moms (samma nivå som
// Vinted). Servern räknar alltid om — se api/src/payments/fees.ts.
function buyerProtectionFee(price: number) {
  return Math.round((price * 0.05 + 7.5) * 100) / 100;
}

// There is no stored display name for arbitrary users (QidUser.name only exists for
// the currently-authenticated user via their own JWT) — so wherever we need to show
// *someone else's* name (e.g. the seller on a marketplace item), we derive one from
// the email local-part instead. Not a real name, just a readable stand-in.
// Reservnamn när servern inte gett något (sellerName m.fl. i api.ts). Andra
// användares id är publika ("u_…") och innehåller inget namn att visa.
function displayNameFromUserId(userId: string, name?: string | null): string {
  if (name?.trim()) return name.trim();
  if (!userId.includes('@')) return t('common.member');
  const first = (userId.split('@')[0] || '').split(/[._\-+\d]+/).find(Boolean) ?? '';
  return first ? first.charAt(0).toUpperCase() + first.slice(1).toLowerCase() : t('common.member');
}

// ─────────────────────────────────────────────────────────────────
// FAVORITES ("Spara"-hjärtat på produkt-/kollektionskort)
// Lokal, per-enhet (localStorage) — inget backend-stöd finns ännu för
// serversynkade favoriter.
// ─────────────────────────────────────────────────────────────────

const FAVORITES_KEY = 'qvestium_favorites';

function getFavoriteIds(): Set<string> {
  try { return new Set(JSON.parse(localStorage.getItem(FAVORITES_KEY) ?? '[]')); }
  catch { return new Set(); }
}

function toggleFavoriteId(id: string): boolean {
  const favs = getFavoriteIds();
  const willBeFavorited = !favs.has(id);
  if (willBeFavorited) favs.add(id); else favs.delete(id);
  localStorage.setItem(FAVORITES_KEY, JSON.stringify(Array.from(favs)));
  return willBeFavorited;
}

function FavoriteHeartButton({ id, hover }: { id: string; hover: boolean }) {
  const [favorited, setFavorited] = useState(() => getFavoriteIds().has(id));
  return (
    <button
      aria-label={favorited ? t('favoriteHeartButton.removeSaved') : t('favoriteHeartButton.save')}
      aria-pressed={favorited}
      onClick={e => { e.stopPropagation(); setFavorited(toggleFavoriteId(id)); }}
      style={{
        position: 'absolute', top: 10, right: 10, width: 30, height: 30, borderRadius: '50%',
        background: 'rgba(255,255,255,0.85)', border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center',
        cursor: 'pointer', opacity: hover || favorited ? 1 : 0.85, transition: 'opacity 0.2s',
      }}
    >
      <svg width="15" height="15" viewBox="0 0 24 24" fill={favorited ? T.rose : 'none'} stroke={favorited ? T.rose : T.text} strokeWidth="1.8">
        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
      </svg>
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────
// AI-BILD-MÄRKNING
// AI-genererade bilder sparas av backend som /uploads/images/ai-<key>-<uuid>.png
// (se routes/ai-images.ts) — det prefixet är det enda sättet att skilja dem
// från riktiga foton, eftersom vi inte lagrar någon separat "är AI"-kolumn.
// ─────────────────────────────────────────────────────────────────

function isAiImage(url?: string | null): boolean {
  return Boolean(url && /\/uploads\/images\/ai-/.test(url));
}

function AiImageBadge({ small, corner = 'top-left' }: { small?: boolean; corner?: 'top-left' | 'bottom-left' }) {
  const offset = small ? 4 : 8;
  return (
    <div style={{
      position: 'absolute',
      ...(corner === 'top-left' ? { top: offset } : { bottom: offset }),
      left: offset,
      background: 'rgba(0,0,0,0.75)', color: '#fff',
      fontSize: small ? 8 : 9, fontWeight: 700, letterSpacing: 0.5,
      padding: small ? '1px 4px' : '2px 6px', borderRadius: 3,
      textTransform: 'uppercase' as const, pointerEvents: 'none' as const,
    }}>
      {t('aiImageBadge.label')}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// WARDROBE ITEM LIKES ("gilla" ett plagg, Instagram-stil)
// ─────────────────────────────────────────────────────────────────

// Toggles the current user's like on a wardrobe item (server-backed, distinct
// from FavoriteHeartButton's client-only localStorage favorites for shop
// products). Hidden interaction for the item's own owner — see isOwner.
function WardrobeLikeButton({ item, user, onNeedLogin, onUpdated, size = 'sm' }: {
  item: WardrobeItem;
  user: QidUser | null;
  onNeedLogin?: () => void;
  onUpdated?: (updated: WardrobeItem) => void;
  size?: 'sm' | 'lg';
}) {
  const [busy, setBusy] = useState(false);
  const isOwner = Boolean(user && item.userId === user.email);
  if (isOwner) return null;

  async function toggle(e: React.MouseEvent) {
    e.stopPropagation();
    if (!user) { onNeedLogin?.(); return; }
    if (busy) return;
    setBusy(true);
    try {
      const updated = item.likedByMe ? await api.unlikeWardrobeItem(item.id) : await api.likeWardrobeItem(item.id);
      onUpdated?.(updated);
    } catch (err: any) { alert(err.message ?? t('common.somethingWentWrong')); }
    finally { setBusy(false); }
  }

  const iconSize = size === 'lg' ? 20 : 14;
  return (
    <button
      aria-label={item.likedByMe ? t('wardrobeLikeButton.unlike') : t('wardrobeLikeButton.like')}
      aria-pressed={item.likedByMe}
      onClick={toggle}
      disabled={busy}
      style={{ display: 'flex', alignItems: 'center', gap: 4, background: 'none', border: 'none', cursor: 'pointer', padding: 0, opacity: busy ? 0.6 : 1 }}
    >
      <svg width={iconSize} height={iconSize} viewBox="0 0 24 24" fill={item.likedByMe ? T.rose : 'none'} stroke={item.likedByMe ? T.rose : T.textMuted} strokeWidth="1.8">
        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
      </svg>
      {item.likeCount > 0 && <span style={{ fontSize: size === 'lg' ? 13 : 11, color: T.textMuted, fontWeight: 600 }}>{item.likeCount}</span>}
    </button>
  );
}

// Owner-facing: shows the like count on the owner's own item, clickable to open
// the list of who liked it (wardrobeItemLikers is owner-only server-side too).
function OwnerLikesBadge({ item }: { item: WardrobeItem }) {
  const [showLikers, setShowLikers] = useState(false);
  if (item.likeCount === 0) return null;
  return (
    <>
      <button
        onClick={e => { e.stopPropagation(); setShowLikers(true); }}
        style={{ display: 'flex', alignItems: 'center', gap: 4, background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: T.textMuted, fontSize: 11 }}
      >
        <svg width="13" height="13" viewBox="0 0 24 24" fill={T.rose} stroke={T.rose} strokeWidth="1.8">
          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
        </svg>
        {t('ownerLikesBadge.likes', { count: item.likeCount })}
      </button>
      {showLikers && <LikersModal itemId={item.id} onClose={() => setShowLikers(false)} />}
    </>
  );
}

function LikersModal({ itemId, onClose }: { itemId: string; onClose: () => void }) {
  const [likers, setLikers] = useState<WardrobeItemLike[] | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    api.wardrobeItemLikers(itemId)
      .then(setLikers)
      .catch((err: any) => setError(err.message ?? t('likersModal.loadError')));
  }, [itemId]);

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }} onClick={onClose}>
      <div style={{ background: '#fff', borderRadius: 8, padding: 20, maxWidth: 360, width: '100%', maxHeight: '70vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: T.text }}>{t('likersModal.title')}</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: T.textMuted, lineHeight: 1 }}>×</button>
        </div>
        {error && <div style={{ color: T.danger, fontSize: 13 }}>{error}</div>}
        {!likers && !error ? (
          <div style={{ color: T.textMuted, fontSize: 13 }}>{t('common.loading')}</div>
        ) : likers && likers.length === 0 ? (
          <div style={{ color: T.textMuted, fontSize: 13 }}>{t('likersModal.empty')}</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {likers?.map(l => (
              <div key={l.userId} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 32, height: 32, borderRadius: '50%', background: T.bg, overflow: 'hidden', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, color: T.textMuted }}>
                  {l.avatarUrl ? <img src={l.avatarUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : (l.displayName?.[0] ?? l.userId[0]).toUpperCase()}
                </div>
                <div style={{ fontSize: 13, color: T.text }}>{l.displayName || l.userId}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function spotsColor(available: number, total: number) {
  const pct = available / total;
  if (pct <= 0) return T.danger;
  if (pct <= 0.3) return '#e07020';
  return T.rose;
}

// Breakpoint under vilket toppmenyn/produktsidan går över i mobilt läge
const MOBILE_BREAKPOINT = 760;

function useViewportWidth() {
  const [width, setWidth] = useState(() => window.innerWidth);
  useEffect(() => {
    function onResize() { setWidth(window.innerWidth); }
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return width;
}

// ─────────────────────────────────────────────────────────────────
// CART TYPES
// ─────────────────────────────────────────────────────────────────

interface CartItem {
  id: string;
  name: string;
  price: number;         // månadsvis vid 12 mån
  price1?: number | null;  // månadsvis vid 1 mån (satt av admin, annars beräknas)
  price3?: number | null;  // månadsvis vid 3 mån (satt av admin, annars beräknas)
  purchasePrice: number; // fullt köppris
  priceUnit: string;
  imageUrl: string;
  qty: number;
  category: string;
  bindingMonths: 1 | 3 | 12 | 'buy';
  // Peer-to-peer garderobsplagg (skiljer sig från adminhanterade produkter):
  // vid utcheckning slutförs köpet först när Stripe bekräftar betalningen.
  kind?: 'product' | 'wardrobe';
  wardrobeItemId?: string;
  wardrobeType?: 'buy' | 'rent';
  // Hämta (pickup) vs frakt (shipping) — bara relevant för wardrobe-plagg.
  // weightTier används för att räkna fram fraktpriset i kassan (se
  // SHIPPING_COST_BY_WEIGHT); servern räknar alltid om det på riktigt.
  deliveryMethod?: 'pickup' | 'shipping';
  weightTier?: string;
  // Betalning av ett accepterat bud — servern tar pris/typ från budet.
  offerId?: string;
}

function calcMonthly(item: CartItem): number {
  if (item.bindingMonths === 'buy') return 0;
  if (item.bindingMonths === 1) return item.price1 ? Math.round(item.price1) : Math.round(item.price * 4);
  if (item.bindingMonths === 3) return item.price3 ? Math.round(item.price3) : Math.round(item.price * 3);
  return item.price; // 12 mån
}

// ─────────────────────────────────────────────────────────────────
// INLINE STYLES FACTORY
// ─────────────────────────────────────────────────────────────────

const S = {
  nav: {
    position: 'fixed' as const,
    top: 0, left: 0, right: 0,
    height: T.navH,
    background: '#ffffff',
    backdropFilter: 'none',
    borderBottom: `1px solid ${T.border}`,
    display: 'flex',
    alignItems: 'center',
    padding: '0 24px',
    zIndex: 1000,
    gap: 20,
    transition: 'background 0.3s, backdrop-filter 0.3s',
  },
  logo: {
    display: 'flex',
    alignItems: 'center',
    fontWeight: 700,
    fontSize: 18,
    color: T.text,
    letterSpacing: 2,
    textTransform: 'uppercase' as const,
    cursor: 'pointer',
    userSelect: 'none' as const,
    flexShrink: 0,
  },
  btn: (variant: 'primary' | 'ghost' | 'danger' | 'secondary' = 'primary') => ({
    padding: '10px 20px',
    borderRadius: 2,
    border: variant === 'ghost' ? `1px solid ${T.border}`
      : variant === 'secondary' ? `1px solid ${T.text}`
      : 'none',
    background: variant === 'primary' ? T.rose
      : variant === 'danger' ? T.danger
      : 'transparent',
    color: variant === 'primary' || variant === 'danger' ? '#fff' : T.text,
    cursor: 'pointer',
    fontSize: 12,
    fontWeight: 600,
    letterSpacing: 0.5,
    textTransform: 'uppercase' as const,
    transition: 'opacity 0.15s, background 0.15s',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    whiteSpace: 'nowrap' as const,
  }),
  card: {
    background: T.surface,
    border: 'none',
    borderRadius: 0,
    overflow: 'hidden',
    transition: 'opacity 0.2s',
    cursor: 'pointer',
  },
  modal: {
    position: 'fixed' as const,
    inset: 0,
    background: T.overlay,
    zIndex: 2000,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16,
  },
  input: {
    background: T.surface,
    border: `1px solid ${T.border}`,
    borderRadius: 2,
    color: T.text,
    padding: '8px 12px',
    fontSize: 14,
    width: '100%',
    outline: 'none',
  },
  tag: (active?: boolean) => ({
    padding: '6px 14px',
    borderRadius: 20,
    border: `1px solid ${active ? T.text : T.border}`,
    background: active ? T.text : 'transparent',
    color: active ? '#fff' : T.textSub,
    cursor: 'pointer',
    fontSize: 12,
    fontWeight: active ? 600 : 400,
    whiteSpace: 'nowrap' as const,
    transition: 'all 0.15s',
  }),
  badge: (color: string = T.rose) => ({
    display: 'inline-block',
    padding: '3px 8px',
    borderRadius: 0,
    background: color === T.rose ? T.rose : '#111111',
    border: 'none',
    color: '#ffffff',
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: 0.8,
    textTransform: 'uppercase' as const,
  }),
};

// ─────────────────────────────────────────────────────────────────
// LOGIN MODAL
// ─────────────────────────────────────────────────────────────────

function AppleLogo() {
  return (
    <svg width="16" height="18" viewBox="0 0 384 512" aria-hidden="true" fill="currentColor">
      <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z" />
    </svg>
  );
}

function GoogleLogo() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" style={{ flexShrink: 0 }}>
      <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.91c1.7-1.57 2.69-3.87 2.69-6.62z" />
      <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.91-2.26c-.81.54-1.84.86-3.05.86-2.34 0-4.33-1.58-5.04-3.71H.96v2.33A9 9 0 0 0 9 18z" />
      <path fill="#FBBC05" d="M3.96 10.71A5.4 5.4 0 0 1 3.68 9c0-.59.1-1.17.28-1.71V4.96H.96A9 9 0 0 0 0 9c0 1.45.35 2.83.96 4.04l3-2.33z" />
      <path fill="#EA4335" d="M9 3.58c1.32 0 2.51.45 3.44 1.35l2.58-2.58C13.46.89 11.43 0 9 0A9 9 0 0 0 .96 4.96l3 2.33C4.67 5.16 6.66 3.58 9 3.58z" />
    </svg>
  );
}

// Personlig inbjudningskod från en länk (?invite=QV…, CRM:et i admin) — sparas
// tills registreringen och fylls i automatiskt.
function storedInvite(): string {
  try { return sessionStorage.getItem(INVITE_KEY) ?? ''; } catch { return ''; }
}

function LoginModal({ onClose, onLogin }: { onClose: () => void; onLogin: (u: QidUser, isNewAccount: boolean) => void }) {
  const [mode, setMode] = useState<'login' | 'register'>(storedInvite() ? 'register' : 'login');
  // Användarvillkor (Legal.tsx): krävs vid nya konton när en version är satt.
  const termsVersion = useTermsVersion();
  const [acceptTerms, setAcceptTerms] = useState(false);
  // form → e-post/lösenord; code → engångskoden från mejlet; google-invite →
  // Google-kontot saknar konto hos oss och behöver inbjudningskod.
  // forgot → ange e-post för glömt lösenord; reset → kod + nytt lösenord.
  const [step, setStep] = useState<'form' | 'code' | 'google-invite' | 'forgot' | 'reset'>('form');
  const [email, setEmail] = useState('');
  const [pw, setPw] = useState('');
  const [pwConfirm, setPwConfirm] = useState('');
  const [inviteCode, setInviteCode] = useState(storedInvite());
  const [code, setCode] = useState('');
  const [codeEmail, setCodeEmail] = useState('');
  const [info, setInfo] = useState('');
  const [googleIdToken, setGoogleIdToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => { auth.prepareGoogleLogin(); }, []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    if (mode === 'register' && pw !== pwConfirm) { setError(t('loginModal.passwordMismatch')); return; }
    if (mode === 'register' && !inviteCode.trim()) { setError(t('loginModal.inviteCodeRequired')); return; }
    setLoading(true);
    try {
      if (mode === 'register' && termsVersion && !acceptTerms) { setError(t('legal.mustAccept')); setLoading(false); return; }
      const result = mode === 'login' ? await auth.directLogin(email, pw) : await auth.register(email, pw, inviteCode.trim(), termsVersion ?? undefined);
      if ('verificationRequired' in result) {
        setCodeEmail(result.email || email);
        setInfo(result.message);
        setCode('');
        setStep('code');
      } else {
        onLogin(result.user, mode === 'register');
      }
    } catch (err: any) {
      setError(err.message ?? t('common.somethingWentWrong'));
    } finally {
      setLoading(false);
    }
  }

  async function handleVerifyCode(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const user = await auth.verifyEmailCode(codeEmail, code);
      onLogin(user, mode === 'register');
    } catch (err: any) {
      setError(err.message ?? t('loginModal.wrongCode'));
    } finally {
      setLoading(false);
    }
  }

  async function handleResend() {
    setError('');
    setLoading(true);
    try { setInfo(await auth.resendEmailCode(codeEmail)); }
    catch (err: any) { setError(err.message ?? t('loginModal.resendFailed')); }
    finally { setLoading(false); }
  }

  async function handleForgot(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      setInfo(await auth.forgotPassword(email.trim()));
      setCodeEmail(email.trim());
      setCode(''); setPw(''); setPwConfirm('');
      setStep('reset');
    } catch (err: any) { setError(err.message ?? t('common.somethingWentWrong')); }
    finally { setLoading(false); }
  }

  async function handleReset(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    if (pw !== pwConfirm) { setError(t('loginModal.passwordMismatch')); return; }
    setLoading(true);
    try {
      const user = await auth.resetPassword(codeEmail, code, pw);
      onLogin(user, false);
    } catch (err: any) { setError(err.message ?? t('loginModal.wrongCode')); }
    finally { setLoading(false); }
  }

  async function handleGoogle() {
    setError('');
    setLoading(true);
    try {
      const { user, isNewUser } = await auth.googleLogin();
      onLogin(user, isNewUser);
    } catch (err: any) {
      if (err instanceof InviteRequiredError) { setGoogleIdToken(err.idToken); setInviteCode(''); setStep('google-invite'); }
      else if (err?.code === 'auth/popup-closed-by-user' || err?.code === 'auth/cancelled-popup-request') setError(t('loginModal.loginCancelled'));
      else if (err?.code === 'auth/popup-blocked') setError(t('loginModal.popupBlocked'));
      else setError(err.message ?? t('loginModal.googleLoginFailed'));
    } finally {
      setLoading(false);
    }
  }

  async function handleApple() {
    setError('');
    setLoading(true);
    try {
      onLogin(await auth.appleLogin(), false);
    } catch (err: any) {
      if (err?.code === 'auth/popup-closed-by-user') setError(t('loginModal.loginCancelled'));
      else setError(err.message ?? t('loginModal.appleLoginFailed'));
    } finally {
      setLoading(false);
    }
  }

  async function handleGoogleInvite(e: React.FormEvent) {
    e.preventDefault();
    if (!googleIdToken) return;
    setError('');
    setLoading(true);
    try {
      if (termsVersion && !acceptTerms) { setError(t('legal.mustAccept')); setLoading(false); return; }
      const { user } = await auth.googleLoginWithInvite(googleIdToken, inviteCode.trim(), termsVersion ?? undefined);
      onLogin(user, true);
    } catch (err: any) {
      setError(err instanceof InviteRequiredError ? t('loginModal.wrongInviteCode') : (err.message ?? t('loginModal.createAccountFailed')));
    } finally {
      setLoading(false);
    }
  }

  const back = () => { setStep('form'); setError(''); setInfo(''); };
  const label = (text: string) => <div style={{ fontSize: 12, color: T.textSub, marginBottom: 4 }}>{text}</div>;
  const errorBox = error && <div style={{ color: T.danger, fontSize: 13, padding: '8px 12px', background: `${T.danger}15`, borderRadius: 3 }}>{error}</div>;
  const title = step === 'forgot' || step === 'reset' ? t('loginModal.forgotTitle') : step === 'code' ? t('loginModal.confirmEmailTitle') : step === 'google-invite' ? t('common.createAccount') : mode === 'login' ? t('common.logIn') : t('common.createAccount');

  return (
    <div style={S.modal} onClick={onClose}>
      <div style={{ background: T.surface, borderRadius: 8, padding: 32, width: '100%', maxWidth: 400, border: `1px solid ${T.border}` }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <div style={{ fontSize: 20, fontWeight: 700, color: T.text }}>{title}</div>
          <button style={{ ...S.btn('ghost'), padding: '4px 8px' }} onClick={onClose}>✕</button>
        </div>

        {step === 'forgot' ? (
          <form onSubmit={handleForgot} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ fontSize: 14, color: T.textSub, lineHeight: 1.5 }}>{t('loginModal.forgotIntro')}</div>
            <div>
              {label(t('loginModal.emailLabel'))}
              <input style={S.input} type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder={t('loginModal.emailPlaceholder')} autoFocus required autoComplete="email" />
            </div>
            {errorBox}
            <button type="submit" style={{ ...S.btn('primary'), justifyContent: 'center', padding: '10px' }} disabled={loading || !email.trim()}>
              {loading ? '...' : t('loginModal.sendResetCode')}
            </button>
            <button type="button" onClick={back} style={{ background: 'none', border: 'none', color: T.textSub, cursor: 'pointer', padding: 0, fontSize: 13, textAlign: 'left' }}>← {t('common.back')}</button>
          </form>
        ) : step === 'reset' ? (
          <form onSubmit={handleReset} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ fontSize: 14, color: T.textSub, lineHeight: 1.5 }}>
              {info} {tn('loginModal.codeSentTo', { email: <strong style={{ color: T.text }}>{codeEmail}</strong> })}
            </div>
            <div>
              {label(t('loginModal.codeLabel'))}
              <input style={{ ...S.input, fontSize: 20, letterSpacing: 6, textAlign: 'center' }} value={code} autoFocus
                onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                inputMode="numeric" autoComplete="one-time-code" placeholder="123456" required />
            </div>
            <div>
              {label(t('loginModal.newPasswordLabel'))}
              <input style={S.input} type="password" value={pw} onChange={e => setPw(e.target.value)} placeholder="••••••••" required minLength={8} autoComplete="new-password" />
            </div>
            <div>
              {label(t('loginModal.confirmPasswordLabel'))}
              <input style={S.input} type="password" value={pwConfirm} onChange={e => setPwConfirm(e.target.value)} placeholder="••••••••" required autoComplete="new-password" />
            </div>
            {errorBox}
            <button type="submit" style={{ ...S.btn('primary'), justifyContent: 'center', padding: '10px' }} disabled={loading || code.length !== 6 || pw.length < 8}>
              {loading ? '...' : t('loginModal.saveNewPassword')}
            </button>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
              <button type="button" onClick={() => { setStep('forgot'); setError(''); }} style={{ background: 'none', border: 'none', color: T.textSub, cursor: 'pointer', padding: 0 }}>← {t('common.back')}</button>
              <button type="button" onClick={() => auth.forgotPassword(codeEmail).then(setInfo).catch(err => setError(err.message))} disabled={loading} style={{ background: 'none', border: 'none', color: T.text, textDecoration: 'underline', cursor: 'pointer', padding: 0 }}>{t('loginModal.resendCode')}</button>
            </div>
          </form>
        ) : step === 'code' ? (
          <form onSubmit={handleVerifyCode} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ fontSize: 14, color: T.textSub, lineHeight: 1.5 }}>
              {info || t('loginModal.codeSentDefault')} {tn('loginModal.codeSentTo', { email: <strong style={{ color: T.text }}>{codeEmail}</strong> })}
            </div>
            <div>
              {label(t('loginModal.codeLabel'))}
              <input style={{ ...S.input, fontSize: 20, letterSpacing: 6, textAlign: 'center' }} value={code} autoFocus
                onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                inputMode="numeric" autoComplete="one-time-code" placeholder="123456" required />
            </div>
            {errorBox}
            <button type="submit" style={{ ...S.btn('primary'), justifyContent: 'center', padding: '10px' }} disabled={loading || code.length !== 6}>
              {loading ? '...' : t('common.confirm')}
            </button>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
              <button type="button" onClick={back} style={{ background: 'none', border: 'none', color: T.textSub, cursor: 'pointer', padding: 0 }}>← {t('common.back')}</button>
              <button type="button" onClick={handleResend} disabled={loading} style={{ background: 'none', border: 'none', color: T.text, textDecoration: 'underline', cursor: 'pointer', padding: 0 }}>{t('loginModal.resendCode')}</button>
            </div>
          </form>
        ) : step === 'google-invite' ? (
          <form onSubmit={handleGoogleInvite} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ fontSize: 14, color: T.textSub, lineHeight: 1.5 }}>
              {t('loginModal.googleInviteIntro')}
            </div>
            <div>
              {label(t('loginModal.inviteCodeLabel'))}
              <input style={S.input} value={inviteCode} onChange={e => setInviteCode(e.target.value)} autoFocus required autoCapitalize="none" autoCorrect="off" />
            </div>
            {termsVersion && <TermsCheckbox checked={acceptTerms} onChange={setAcceptTerms} />}
            {errorBox}
            <button type="submit" style={{ ...S.btn('primary'), justifyContent: 'center', padding: '10px' }} disabled={loading || !inviteCode.trim()}>
              {loading ? '...' : t('common.createAccount')}
            </button>
            <button type="button" onClick={back} style={{ background: 'none', border: 'none', color: T.textSub, cursor: 'pointer', padding: 0, fontSize: 13, textAlign: 'left' }}>← {t('common.back')}</button>
          </form>
        ) : (
          <>
            <button
              type="button"
              onClick={handleGoogle}
              disabled={loading}
              style={{
                ...S.btn('ghost'), width: '100%', justifyContent: 'center', gap: 10,
                display: 'flex', alignItems: 'center', padding: '10px',
              }}
            >
              <GoogleLogo />
              {t('loginModal.continueWithGoogle')}
            </button>
            {/* Apples riktlinjer: svart knapp med Apple-loggan, lika stor som Google-knappen. */}
            <button
              type="button"
              onClick={handleApple}
              disabled={loading}
              style={{
                ...S.btn('ghost'), width: '100%', justifyContent: 'center', gap: 10,
                display: 'flex', alignItems: 'center', padding: '10px', marginTop: 10,
                background: '#000', color: '#fff', borderColor: '#000',
              }}
            >
              <AppleLogo />
              {t('loginModal.continueWithApple')}
            </button>

            <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '20px 0', color: T.textMuted, fontSize: 13 }}>
              <div style={{ flex: 1, height: 1, background: T.border }} />
              <span>{t('loginModal.orWithEmail')}</span>
              <div style={{ flex: 1, height: 1, background: T.border }} />
            </div>

            <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
              <button style={{ ...S.btn(mode === 'login' ? 'primary' : 'ghost'), flex: 1, justifyContent: 'center' }} onClick={() => { setMode('login'); setError(''); }}>{t('common.logIn')}</button>
              <button style={{ ...S.btn(mode === 'register' ? 'primary' : 'ghost'), flex: 1, justifyContent: 'center' }} onClick={() => { setMode('register'); setError(''); }}>{t('common.createAccount')}</button>
            </div>

            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div>
                {label(t('loginModal.emailLabel'))}
                <input style={S.input} type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder={t('loginModal.emailPlaceholder')} required />
              </div>
              <div>
                {label(t('loginModal.passwordLabel'))}
                <input style={S.input} type="password" value={pw} onChange={e => setPw(e.target.value)} placeholder="••••••••" required minLength={mode === 'register' ? 8 : undefined} />
                {mode === 'login' && (
                  <button type="button" onClick={() => { setStep('forgot'); setError(''); setInfo(''); }}
                    style={{ background: 'none', border: 'none', color: T.textSub, cursor: 'pointer', padding: 0, fontSize: 12, marginTop: 6, textDecoration: 'underline' }}>
                    {t('loginModal.forgotPassword')}
                  </button>
                )}
              </div>
              {mode === 'register' && (
                <>
                  <div>
                    {label(t('loginModal.confirmPasswordLabel'))}
                    <input style={S.input} type="password" value={pwConfirm} onChange={e => setPwConfirm(e.target.value)} placeholder="••••••••" required />
                  </div>
                  <div>
                    {label(t('loginModal.inviteCodeLabel'))}
                    <input style={S.input} value={inviteCode} onChange={e => setInviteCode(e.target.value)} required autoCapitalize="none" autoCorrect="off" />
                  </div>
                  {termsVersion && <TermsCheckbox checked={acceptTerms} onChange={setAcceptTerms} />}
                </>
              )}
              {errorBox}
              <button type="submit" style={{ ...S.btn('primary'), justifyContent: 'center', padding: '10px', marginTop: 4 }} disabled={loading}>
                {loading ? '...' : mode === 'login' ? t('common.logIn') : t('common.createAccount')}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// COLLECTION CARD
// ─────────────────────────────────────────────────────────────────

function CollectionCard({ col, onOpen, isAdmin, onEdit, onDelete }: {
  col: FashionCollection;
  onOpen: () => void;
  isAdmin: boolean;
  onEdit?: () => void;
  onDelete?: () => void;
}) {
  const [hover, setHover] = useState(false);
  const spotsLeft = col.spotsAvailable;

  return (
    <div
      style={{ ...S.card, opacity: 1 }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={onOpen}
    >
      {/* Image */}
      <div style={{ position: 'relative', paddingTop: '125%', overflow: 'hidden', background: '#f4f4f4' }}>
        <img
          src={col.imageUrl || 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=600&h=400&fit=crop'}
          alt={col.title}
          style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 0.4s ease', transform: hover ? 'scale(1.03)' : 'scale(1)' }}
          onError={e => { (e.target as HTMLImageElement).src = 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=600&h=400&fit=crop'; }}
        />
        {col.featured && (
          <div style={{ position: 'absolute', top: 10, left: 10, ...S.badge(T.rose) }}>{t('collectionCard.featured')}</div>
        )}
        <FavoriteHeartButton id={col.id} hover={hover} />
      </div>

      {/* Body */}
      <div style={{ padding: '12px 2px 0' }}>
        <div style={{ fontSize: 11, color: T.text, marginBottom: 3, textTransform: 'uppercase', letterSpacing: 0.8, fontWeight: 700 }}>
          {col.brand ?? 'Qvestium'} {col.season ? `· ${col.season}` : ''}
        </div>
        <div style={{ fontSize: 13, color: T.textSub, marginBottom: 4, lineHeight: 1.3 }}>{col.title}</div>
        {col.subtitle && <div style={{ fontSize: 12, color: T.textMuted, marginBottom: 6 }}>{col.subtitle}</div>}

        <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 8 }}>
          {col.location} · {vl('style', col.style)} · {t('collectionCard.spotsLeft', { count: spotsLeft })}
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 10 }}>
          <div>
            <span style={{ fontWeight: 700, fontSize: 15, color: T.text }}>{t('common.kr', { amount: fmt(col.price) })}</span>
            {col.monthlyPrice > 0 && <span style={{ fontSize: 12, color: T.textMuted, marginLeft: 6 }}>{t('collectionCard.orMonthly', { amount: fmt(col.monthlyPrice) })}</span>}
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <button style={{ ...S.btn('primary'), padding: '9px 0', fontSize: 11, width: isAdmin ? '60%' : '100%', justifyContent: 'center' }} onClick={e => { e.stopPropagation(); onOpen(); }}>{t('collectionCard.seeMore')}</button>
          {isAdmin && (
            <div style={{ display: 'flex', gap: 10, marginLeft: 10 }}>
              <button style={{ background: 'none', border: 'none', color: T.textMuted, fontSize: 11, textDecoration: 'underline', cursor: 'pointer' }} onClick={e => { e.stopPropagation(); onEdit?.(); }}>{t('common.edit')}</button>
              <button style={{ background: 'none', border: 'none', color: T.danger, fontSize: 11, textDecoration: 'underline', cursor: 'pointer' }} onClick={e => { e.stopPropagation(); onDelete?.(); }}>{t('common.delete')}</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// MARKETPLACE WARDROBE CARD — användarplagg i huvudsortimentet
// (Plagg/Accessoarer-flikarna visar nu bara riktiga garderobsplagg
// från användare, inte en separat adminhanterad produktkatalog.)
// ─────────────────────────────────────────────────────────────────

// Två annonser i bredd på mobil, annars så många 230px-kolumner som ryms.
const MARKET_GRID = (narrow: boolean): React.CSSProperties => narrow
  ? { display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '24px 12px' }
  : { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', gap: '32px 24px' };

// Importerade plagg har beskrivningen "Märke · storlek · skick", som redan
// visas på raden ovanför — då visas bara märket.
function cardDescription(item: WardrobeItem): string | null {
  const d = item.description?.trim();
  if (!d) return null;
  const parts = d.split(' · ').map(p => p.trim());
  if (parts.length >= 2 && item.size && parts.slice(1).includes(item.size.trim())) return parts[0] || null;
  return d;
}

// Märkning för utvalda (featured) annonser — nere till vänster på bilden.
function FeaturedBadge() {
  return (
    <div style={{ position: 'absolute', bottom: 10, left: 10, background: T.text, color: '#fff', borderRadius: 3, padding: '3px 8px',
      fontSize: 10, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase' }}>
      ★ {t('featured.badge')}
    </div>
  );
}

function MarketplaceWardrobeCard({ item, onOpen, user, onNeedLogin, onAddToCart, onUpdated }: {
  item: WardrobeItem;
  onOpen: () => void;
  user: QidUser | null;
  onNeedLogin: () => void;
  onAddToCart: (cartItem: CartItem) => void;
  onUpdated?: (updated: WardrobeItem) => void;
}) {
  const condInfo = item.condition ? (CONDITION_LABELS[item.condition] ?? CONDITION_LABELS[5]) : null;
  return (
    <div style={{ ...S.card }} onClick={onOpen}>
      <div style={{ position: 'relative', paddingTop: '125%', overflow: 'hidden', background: '#f4f4f4' }}>
        {item.imageUrl ? (
          <img src={item.imageUrl} alt={item.name} style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : (
          <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', fontSize: 40, color: T.textMuted }}>👗</div>
        )}
        {isAiImage(item.imageUrl) && <AiImageBadge small />}
        {item.isFeatured && <FeaturedBadge />}
        {!(user && item.userId === user.email) && (
          <div style={{ position: 'absolute', top: 10, right: 10, background: 'rgba(255,255,255,0.85)', borderRadius: 14, padding: '5px 9px' }}>
            <WardrobeLikeButton item={item} user={user} onNeedLogin={onNeedLogin} onUpdated={onUpdated} />
          </div>
        )}
      </div>
      <div style={{ padding: '12px 2px 0' }}>
        <div style={{ fontSize: 13, color: T.text, fontWeight: 600, marginBottom: 4 }}>{item.name}</div>
        <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 6 }}>
          {item.size && <span>{t('marketplaceWardrobeCard.sizeShort', { size: item.size })}</span>}
          {item.size && condInfo && <span> · </span>}
          {condInfo && <span>{condInfo.label}</span>}
        </div>
        {cardDescription(item) && <div style={{ fontSize: 12, color: T.textMuted, marginBottom: 8, lineHeight: 1.4, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' as any }}>{cardDescription(item)}</div>}
        <div style={{ marginBottom: 10 }}>
          {item.forRent && <span style={{ fontWeight: 700, fontSize: 15, color: T.text }}>{t('common.krPerMonth', { amount: fmt(item.rentPrice) })}</span>}
          {item.forSale && <span style={{ fontSize: 12, color: T.textMuted, marginLeft: item.forRent ? 6 : 0 }}>{item.forRent ? t('marketplaceWardrobeCard.orBuyPrice', { amount: fmt(item.salePrice) }) : t('marketplaceWardrobeCard.buyPrice', { amount: fmt(item.salePrice) })}</span>}
          {item.forSwap && <div style={{ fontSize: 12, color: T.rose, fontWeight: 600, marginTop: (item.forRent || item.forSale) ? 2 : 0 }}>{t('marketplaceWardrobeCard.openForSwap')}</div>}
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {item.forRent && (
            <button style={{ ...S.btn('primary'), padding: '9px 0', fontSize: 11, flex: 1, justifyContent: 'center' }}
              onClick={e => {
                e.stopPropagation();
                if (!user) { onNeedLogin(); return; }
                onAddToCart({
                  id: item.id, name: item.name, price: item.rentPrice, price1: item.rentPrice1, price3: item.rentPrice3,
                  purchasePrice: item.salePrice, priceUnit: 'kr/mån', imageUrl: item.imageUrl ?? '', qty: 1, category: item.category,
                  bindingMonths: 12, kind: 'wardrobe', wardrobeItemId: item.id, wardrobeType: 'rent', weightTier: item.weightTier,
                });
              }}>{t('marketplaceWardrobeCard.rent')}</button>
          )}
          {item.forSale && (
            <button style={{ ...S.btn('ghost'), padding: '9px 0', fontSize: 11, flex: 1, justifyContent: 'center' }}
              onClick={e => {
                e.stopPropagation();
                if (!user) { onNeedLogin(); return; }
                onAddToCart({
                  id: item.id, name: item.name, price: item.rentPrice, price1: item.rentPrice1, price3: item.rentPrice3,
                  purchasePrice: item.salePrice, priceUnit: 'kr', imageUrl: item.imageUrl ?? '', qty: 1, category: item.category,
                  bindingMonths: 'buy', kind: 'wardrobe', wardrobeItemId: item.id, wardrobeType: 'buy', weightTier: item.weightTier,
                });
              }}>{t('marketplaceWardrobeCard.buyFor', { amount: fmt(item.salePrice) })}</button>
          )}
        </div>
        {item.forSale && (
          <div style={{ fontSize: 10, color: T.textMuted, marginTop: 4, textAlign: 'center' }}>
            {t('marketplaceWardrobeCard.inclBuyerProtection', { amount: fmtKr(item.salePrice + buyerProtectionFee(item.salePrice)) })}
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// SHOP CARD
// ─────────────────────────────────────────────────────────────────

function ShopCard({ item, onOpen, onAddToCart, isAdmin, onEdit, onDelete }: {
  item: Product;
  onOpen: () => void;
  onAddToCart: (item: Product) => void;
  isAdmin?: boolean;
  onEdit?: () => void;
  onDelete?: () => void;
}) {
  const [hover, setHover] = useState(false);
  return (
    <div
      style={{ ...S.card, cursor: 'pointer' }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={onOpen}
    >
      <div style={{ position: 'relative', paddingTop: '125%', overflow: 'hidden', background: '#f4f4f4' }}>
        <img
          src={item.imageUrl || 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400&h=500&fit=crop'}
          alt={item.name}
          style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 0.4s ease', transform: hover ? 'scale(1.03)' : 'scale(1)' }}
          onError={e => { (e.target as HTMLImageElement).src = 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400&h=500&fit=crop'; }}
        />
        {!item.inStock && (
          <div style={{ position: 'absolute', top: 10, left: 10, ...S.badge('#111111') }}>{t('shopCard.soldOut')}</div>
        )}
        {isAdmin && item.status === 'draft' && (
          <div style={{ position: 'absolute', top: 10, left: item.inStock ? 10 : 90, ...S.badge(T.rose) }}>{t('shopCard.draft')}</div>
        )}
        <FavoriteHeartButton id={item.id} hover={hover} />
      </div>
      <div style={{ padding: '12px 2px 0' }}>
        <div style={{ fontSize: 11, color: T.text, marginBottom: 3, textTransform: 'uppercase', letterSpacing: 0.8, fontWeight: 700 }}>{vl('category', item.category)}</div>
        <div style={{ fontSize: 13, color: T.textSub, marginBottom: 4, lineHeight: 1.3 }}>{item.name}</div>
        {(item.size || item.condition) && (
          <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 6 }}>
            {item.size && <span>{t('shopCard.sizeShort', { size: item.size })}</span>}
            {item.size && item.condition && <span> · </span>}
            {item.condition && (() => { const info = CONDITION_LABELS[item.condition!]; return <span>{info.label}</span>; })()}
          </div>
        )}
        <div style={{ marginBottom: 10 }}>
          <span style={{ fontWeight: 700, fontSize: 15, color: T.text }}>{t('common.krPerMonth', { amount: fmt(item.monthlyPrice) })}</span>
          <span style={{ fontSize: 12, color: T.textMuted, marginLeft: 6 }}>{t('shopCard.orBuyPrice', { amount: fmt(item.price), unit: vl('priceUnit', item.priceUnit) })}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <button
            style={{ ...S.btn('primary'), padding: '9px 0', fontSize: 11, width: isAdmin ? '60%' : '100%', justifyContent: 'center', opacity: item.inStock ? 1 : 0.4 }}
            onClick={e => { e.stopPropagation(); onAddToCart(item); }}
            disabled={!item.inStock}
          >
            {item.inStock ? t('shopCard.rent') : t('shopCard.soldOut')}
          </button>
          {isAdmin && (
            <div style={{ display: 'flex', gap: 10, marginLeft: 10 }}>
              <button style={{ background: 'none', border: 'none', color: T.textMuted, fontSize: 11, textDecoration: 'underline', cursor: 'pointer' }} onClick={e => { e.stopPropagation(); onEdit?.(); }}>{t('common.edit')}</button>
              <button style={{ background: 'none', border: 'none', color: T.danger, fontSize: 11, textDecoration: 'underline', cursor: 'pointer' }} onClick={e => { e.stopPropagation(); onDelete?.(); }}>{t('common.delete')}</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// PRODUCT DETAIL MODAL
// ─────────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────────
// CONDITION BADGE
// ─────────────────────────────────────────────────────────────────
const CONDITION_LABELS: Record<number, { label: string; color: string }> = {
  1: { label: t('conditionLabels.worn'), color: '#e53935' },
  2: { label: t('conditionLabels.used'), color: '#f57c00' },
  3: { label: t('conditionLabels.good'), color: '#fdd835' },
  4: { label: t('conditionLabels.veryGood'), color: '#7cb342' },
  5: { label: t('conditionLabels.unused'), color: '#43a047' },
};

// Vikttariff för fraktprisberäkning — måste matcha apps/api/src/shipping/pricing.ts
// exakt (frontend-kopian används bara för prisvisning, servern räknar alltid om).
const SHIPPING_COST_BY_WEIGHT: Record<string, number> = { '0-1': 69, '1-2': 89, '2-3': 95 };
const WEIGHT_TIERS = ['0-1', '1-2', '2-3'];
/** Viktklass för flera plagg i ett paket — samma regel som combinedWeightTier i API:t. */
const combinedWeightTier = (tiers: string[]): string => {
  let i = Math.max(0, ...tiers.map(t => Math.max(0, WEIGHT_TIERS.indexOf(t))));
  if (tiers.length > 1) i = Math.min(WEIGHT_TIERS.length - 1, i + 1);
  return WEIGHT_TIERS[i];
};
/** Viktklasser med fraktpris — fast pris via Fraktjakt (fixed) gäller alla klasser. */
const weightTierOptions = (fixed: number | null): { value: string; label: string }[] => [
  { value: '0-1', label: t('weightTierOptions.light', { amount: fixed ?? SHIPPING_COST_BY_WEIGHT['0-1'] }) },
  { value: '1-2', label: t('weightTierOptions.medium', { amount: fixed ?? SHIPPING_COST_BY_WEIGHT['1-2'] }) },
  { value: '2-3', label: t('weightTierOptions.heavy', { amount: fixed ?? SHIPPING_COST_BY_WEIGHT['2-3'] }) },
];

function ConditionBadge({ condition }: { condition?: number | null }) {
  if (!condition) return null;
  const info = CONDITION_LABELS[condition] ?? CONDITION_LABELS[5];
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 9px', borderRadius: 10, background: `${info.color}22`, border: `1px solid ${info.color}55` }}>
      <div style={{ display: 'flex', gap: 2 }}>
        {[1,2,3,4,5].map(i => (
          <div key={i} style={{ width: 6, height: 6, borderRadius: 2, background: i <= condition ? info.color : `${info.color}33` }} />
        ))}
      </div>
      <span style={{ fontSize: 11, fontWeight: 600, color: info.color }}>{condition}/5 · {info.label}</span>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// MARKNADSPLATS-FILTER (Sortera efter / Kategori / Skick / Storlekar / Pris)
// ─────────────────────────────────────────────────────────────────

type MarketplaceSort = 'newest' | 'price_asc' | 'price_desc';

function marketplaceItemPrice(item: WardrobeItem): number {
  if (item.forRent) return item.rentPrice;
  if (item.forSale) return item.salePrice;
  return 0;
}

function useMarketplaceFilters(items: WardrobeItem[], searchQuery: string = '') {
  const [sortBy, setSortBy] = useState<MarketplaceSort>('newest');
  const [categories, setCategories] = useState<Set<string>>(new Set());
  const [conditions, setConditions] = useState<Set<number>>(new Set());
  const [sizes, setSizes] = useState<Set<string>>(new Set());
  const [priceMin, setPriceMin] = useState('');
  const [priceMax, setPriceMax] = useState('');

  const availableCategories = useMemo(() => Array.from(new Set(items.map(i => i.category))).sort(), [items]);
  const availableSizes = useMemo(() => Array.from(new Set(items.map(i => i.size).filter((s): s is string => Boolean(s)))).sort(), [items]);

  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    let result = items.filter(i => {
      if (categories.size > 0 && !categories.has(i.category)) return false;
      if (conditions.size > 0 && (i.condition == null || !conditions.has(i.condition))) return false;
      if (sizes.size > 0 && (!i.size || !sizes.has(i.size))) return false;
      const price = marketplaceItemPrice(i);
      if (priceMin && price < Number(priceMin)) return false;
      if (priceMax && price > Number(priceMax)) return false;
      if (q) {
        const haystack = `${i.name} ${i.description ?? ''} ${i.category} ${i.size ?? ''}`.toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
    if (sortBy === 'price_asc') result = [...result].sort((a, b) => marketplaceItemPrice(a) - marketplaceItemPrice(b));
    else if (sortBy === 'price_desc') result = [...result].sort((a, b) => marketplaceItemPrice(b) - marketplaceItemPrice(a));
    // Standardsortering: featured först (senast utvalda överst), sedan nyast.
    else result = [...result].sort((a, b) =>
      Number(Boolean(b.isFeatured)) - Number(Boolean(a.isFeatured))
      || (a.isFeatured && b.isFeatured ? new Date(b.featuredAt ?? 0).getTime() - new Date(a.featuredAt ?? 0).getTime() : 0)
      || new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    return result;
  }, [items, categories, conditions, sizes, priceMin, priceMax, sortBy, searchQuery]);

  const activeCount = (categories.size > 0 ? 1 : 0) + (conditions.size > 0 ? 1 : 0) + (sizes.size > 0 ? 1 : 0) + (priceMin || priceMax ? 1 : 0);

  function reset() {
    setCategories(new Set()); setConditions(new Set()); setSizes(new Set());
    setPriceMin(''); setPriceMax(''); setSortBy('newest');
  }

  return {
    filtered,
    state: { sortBy, categories, conditions, sizes, priceMin, priceMax, availableCategories, availableSizes, activeCount },
    actions: { setSortBy, setCategories, setConditions, setSizes, setPriceMin, setPriceMax, reset },
  };
}

function toggleInSet<T>(set: Set<T>, value: T, setter: (s: Set<T>) => void) {
  const next = new Set(set);
  if (next.has(value)) next.delete(value); else next.add(value);
  setter(next);
}

function FilterDropdown({ label, count, children }: { label: string; count?: number; children: (close: () => void) => React.ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          display: 'flex', alignItems: 'center', gap: 6, padding: '9px 14px',
          border: `1.5px solid ${count ? T.text : T.border}`, borderRadius: 4, background: '#fff',
          fontSize: 13, fontWeight: count ? 700 : 500, color: T.text, cursor: 'pointer', whiteSpace: 'nowrap',
        }}
      >
        {label}{count ? ` (${count})` : ''}
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="6 9 12 15 18 9" /></svg>
      </button>
      {open && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 999 }} onClick={() => setOpen(false)} />
          <div style={{ position: 'absolute', top: '100%', left: 0, marginTop: 6, background: '#fff', border: `1px solid ${T.border}`, borderRadius: 6, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', padding: 16, minWidth: 220, maxWidth: 260, zIndex: 1000 }}>
            {children(() => setOpen(false))}
          </div>
        </>
      )}
    </div>
  );
}

const SORT_OPTIONS: { key: MarketplaceSort; label: string }[] = [
  { key: 'newest', label: t('sortOptions.newest') },
  { key: 'price_asc', label: t('sortOptions.priceAsc') },
  { key: 'price_desc', label: t('sortOptions.priceDesc') },
];

function MarketplaceFilterBar({ filters }: { filters: ReturnType<typeof useMarketplaceFilters> }) {
  const { sortBy, categories, conditions, sizes, priceMin, priceMax, availableCategories, availableSizes, activeCount } = filters.state;
  const { setSortBy, setCategories, setConditions, setSizes, setPriceMin, setPriceMax, reset } = filters.actions;

  return (
    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: 24 }}>
      <FilterDropdown label={sortBy !== 'newest' ? t('marketplaceFilterBar.sortByValue', { option: SORT_OPTIONS.find(o => o.key === sortBy)?.label ?? '' }) : t('marketplaceFilterBar.sortBy')}>
        {close => (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {SORT_OPTIONS.map(o => (
              <label key={o.key} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
                <input type="radio" checked={sortBy === o.key} onChange={() => { setSortBy(o.key); close(); }} />
                {o.label}
              </label>
            ))}
          </div>
        )}
      </FilterDropdown>

      <FilterDropdown label={t('common.category')} count={categories.size || undefined}>
        {() => (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {availableCategories.map(c => (
              <label key={c} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
                <input type="checkbox" checked={categories.has(c)} onChange={() => toggleInSet(categories, c, setCategories)} />
                {vl('category', c)}
              </label>
            ))}
          </div>
        )}
      </FilterDropdown>

      <FilterDropdown label={t('marketplaceFilterBar.condition')} count={conditions.size || undefined}>
        {() => (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {[5, 4, 3, 2, 1].map(c => (
              <label key={c} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
                <input type="checkbox" checked={conditions.has(c)} onChange={() => toggleInSet(conditions, c, setConditions)} />
                {CONDITION_LABELS[c]?.label} ({c}/5)
              </label>
            ))}
          </div>
        )}
      </FilterDropdown>

      <FilterDropdown label={t('marketplaceFilterBar.sizes')} count={sizes.size || undefined}>
        {() => (
          availableSizes.length === 0 ? (
            <div style={{ fontSize: 13, color: T.textMuted }}>{t('marketplaceFilterBar.noSizes')}</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 240, overflowY: 'auto' }}>
              {availableSizes.map(s => (
                <label key={s} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
                  <input type="checkbox" checked={sizes.has(s)} onChange={() => toggleInSet(sizes, s, setSizes)} />
                  {s}
                </label>
              ))}
            </div>
          )
        )}
      </FilterDropdown>

      <FilterDropdown label={t('common.price')} count={(priceMin || priceMax) ? 1 : undefined}>
        {() => (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <label style={{ fontSize: 12, color: T.textSub }}>
              {t('marketplaceFilterBar.minPrice')}
              <input type="number" min={0} value={priceMin} onChange={e => setPriceMin(e.target.value)} style={{ ...S.input, marginTop: 4 }} />
            </label>
            <label style={{ fontSize: 12, color: T.textSub }}>
              {t('marketplaceFilterBar.maxPrice')}
              <input type="number" min={0} value={priceMax} onChange={e => setPriceMax(e.target.value)} style={{ ...S.input, marginTop: 4 }} />
            </label>
          </div>
        )}
      </FilterDropdown>

      {activeCount > 0 && (
        <button onClick={reset} style={{ background: 'none', border: 'none', color: T.rose, fontSize: 13, fontWeight: 600, cursor: 'pointer', textDecoration: 'underline', padding: 0 }}>
          {t('marketplaceFilterBar.clearFilters')}
        </button>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// TESTA PLAGG PÅ MIG
// Slår ihop plaggbilden med kundens egen helkroppsbild (från profilen)
// till en ny bild, med storleksanpassning utifrån plaggets storlek vs
// kundens egna mått/storlek.
// ─────────────────────────────────────────────────────────────────

function TryOnButton({ garmentImageUrl, garmentName, garmentCategory, garmentSize, user, profile, onNeedLogin, onGoToProfile }: {
  garmentImageUrl: string;
  garmentName?: string;
  garmentCategory?: string;
  garmentSize?: string;
  user: QidUser | null;
  profile: FashionProfile | null;
  onNeedLogin: () => void;
  onGoToProfile: () => void;
}) {
  const [loading, setLoading] = useState(false);
  const [resultUrl, setResultUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showPicker, setShowPicker] = useState(false);
  const bodyPhotos = profile?.bodyPhotos ?? [];

  async function runTryOn(modelPhotoUrl: string) {
    setLoading(true);
    setError(null);
    setShowPicker(false);
    setResultUrl(null);
    try {
      const result = await api.tryOnGarment({
        garmentImageUrl, modelPhotoUrl,
        garmentName, garmentCategory, garmentSize,
        profileSize: profile?.size, bustCm: profile?.bustCm, waistCm: profile?.waistCm, hipCm: profile?.hipCm,
      });
      setResultUrl(result.url);
    } catch (err: any) {
      setError(err.message ?? t('tryOnButton.generateFailed'));
    } finally {
      setLoading(false);
    }
  }

  function handleClick() {
    if (!user) { onNeedLogin(); return; }
    if (bodyPhotos.length === 0) { setError('NO_PHOTO'); return; }
    if (bodyPhotos.length === 1) { runTryOn(bodyPhotos[0]); return; }
    setShowPicker(true);
  }

  return (
    <>
      <button type="button" style={{ ...S.btn('ghost'), width: '100%', justifyContent: 'center', marginTop: 10 }} onClick={handleClick} disabled={loading}>
        {loading ? t('tryOnButton.generating') : t('tryOnButton.tryOn')}
      </button>

      {error === 'NO_PHOTO' && (
        <div style={{ fontSize: 12, color: T.textSub, marginTop: 6 }}>
          {tn('tryOnButton.noPhoto', { link: (
            <button type="button" onClick={onGoToProfile} style={{ background: 'none', border: 'none', color: T.rose, textDecoration: 'underline', cursor: 'pointer', padding: 0, fontSize: 12 }}>
              {t('tryOnButton.yourProfile')}
            </button>
          ) })}
        </div>
      )}
      {error && error !== 'NO_PHOTO' && <div style={{ fontSize: 12, color: T.danger, marginTop: 6 }}>{error}</div>}

      {showPicker && (
        <div style={{ marginTop: 10 }}>
          <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 6 }}>{t('tryOnButton.pickPhoto')}</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {bodyPhotos.map(url => (
              <img key={url} src={url} onClick={() => runTryOn(url)} alt=""
                style={{ width: 50, height: 66, objectFit: 'cover', objectPosition: 'center top', borderRadius: 4, cursor: 'pointer', border: `1px solid ${T.border}` }} />
            ))}
          </div>
        </div>
      )}

      {resultUrl && (
        <div style={S.modal} onClick={() => setResultUrl(null)}>
          <div style={{ background: '#fff', borderRadius: 8, padding: 16, maxWidth: 480, width: '100%' }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: T.text }}>{t('tryOnButton.resultTitle')}</div>
              <button onClick={() => setResultUrl(null)} style={{ background: 'none', border: 'none', color: T.textMuted, fontSize: 20, cursor: 'pointer' }}>×</button>
            </div>
            <img src={resultUrl} alt={t('tryOnButton.tryOn')} style={{ width: '100%', borderRadius: 4 }} />
            <div style={{ fontSize: 11, color: T.textMuted, marginTop: 8 }}>{t('tryOnButton.aiDisclaimer')}</div>
          </div>
        </div>
      )}
    </>
  );
}

function ProductDetailPage({ product, user, profile, onClose, onAddToCart, onNeedLogin, onGoToProfile }: {
  product: Product;
  user: QidUser | null;
  profile: FashionProfile | null;
  onClose: () => void;
  onAddToCart: (item: CartItem) => void;
  onNeedLogin: () => void;
  onGoToProfile: () => void;
}) {
  const [binding, setBinding] = useState<1 | 3 | 12 | 'buy'>(12);
  const viewportWidth = useViewportWidth();
  const isNarrow = viewportWidth < MOBILE_BREAKPOINT;

  // Build image list: imageUrl first, then gallery
  const allImages = [
    ...(product.imageUrl ? [product.imageUrl] : []),
    ...(product.galleryUrls ?? []).filter(u => u !== product.imageUrl),
  ];
  const [imgIdx, setImgIdx] = useState(0);
  const currentImg = allImages[imgIdx];

  const tempItem: CartItem = {
    id: product.id,
    name: product.name,
    price: product.monthlyPrice,
    price3: product.price3,
    price1: product.price1,
    purchasePrice: product.price,
    priceUnit: product.priceUnit,
    imageUrl: product.imageUrl ?? '',
    qty: 1,
    category: product.category,
    bindingMonths: binding,
  };

  const displayPrice = binding === 'buy'
    ? t('productDetailPage.priceOnce', { amount: fmt(product.price) })
    : t('common.krPerMonth', { amount: fmt(calcMonthly(tempItem)) });

  function handleAdd() {
    onAddToCart({ ...tempItem });
  }

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: '24px 16px 64px' }}>
      <button onClick={onClose} style={{ background: 'none', border: 'none', color: T.textSub, fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, marginBottom: 20, padding: 0 }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>
        {t('common.back')}
      </button>

      <div style={{ display: 'grid', gridTemplateColumns: allImages.length > 0 && !isNarrow ? '1.4fr 1fr' : '1fr', gap: isNarrow ? 28 : 48 }}>
        {/* Image gallery */}
        {allImages.length > 0 && (
          <div>
            <div style={{ position: 'relative', background: '#f4f4f4', overflow: 'hidden', marginBottom: 10 }}>
              <img
                src={currentImg}
                alt={product.name}
                style={{ width: '100%', aspectRatio: '4/5', objectFit: 'cover', display: 'block' }}
              />
              {allImages.length > 1 && (
                <>
                  <button onClick={() => setImgIdx(i => (i - 1 + allImages.length) % allImages.length)}
                    style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', background: 'rgba(255,255,255,0.85)', border: 'none', color: T.text, width: 34, height: 34, borderRadius: '50%', fontSize: 18, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>‹</button>
                  <button onClick={() => setImgIdx(i => (i + 1) % allImages.length)}
                    style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'rgba(255,255,255,0.85)', border: 'none', color: T.text, width: 34, height: 34, borderRadius: '50%', fontSize: 18, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>›</button>
                </>
              )}
            </div>
            {allImages.length > 1 && (
              <div style={{ display: 'flex', gap: 8 }}>
                {allImages.map((url, i) => (
                  <div key={i} style={{ position: 'relative', flexShrink: 0 }}>
                    <img src={url} alt="" onClick={() => setImgIdx(i)}
                      style={{ width: 64, height: 80, objectFit: 'cover', cursor: 'pointer', opacity: i === imgIdx ? 1 : 0.5, border: `1px solid ${i === imgIdx ? T.text : 'transparent'}` }} />
                    {isAiImage(url) && <AiImageBadge small />}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Info + buy box */}
        <div style={{ maxWidth: isNarrow ? '100%' : 420 }}>
          <div style={{ fontSize: 11, color: T.textMuted, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>{vl('category', product.category)}</div>
          <div style={{ fontFamily: 'Georgia, "Times New Roman", serif', fontSize: 26, color: T.text, marginBottom: 14 }}>{product.name}</div>

          {/* Size & Condition row */}
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
            {product.size && (
              <div style={{ fontSize: 12, color: T.text, border: `1px solid ${T.border}`, padding: '4px 10px' }}>
                {t('productDetailPage.size', { size: product.size })}
              </div>
            )}
            <ConditionBadge condition={product.condition} />
          </div>

          {product.description && (
            <div style={{ fontSize: 14, color: T.textSub, lineHeight: 1.7, marginBottom: 24 }}>{product.description}</div>
          )}

          {/* Binding selector */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: T.text, marginBottom: 10 }}>{t('productDetailPage.choosePayment')}</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 8 }}>
              {BINDING_OPTIONS.map(opt => {
                const previewItem = { ...tempItem, bindingMonths: opt.months };
                const price = opt.months === 'buy'
                  ? t('common.kr', { amount: fmt(product.price) })
                  : t('common.krPerMonth', { amount: fmt(calcMonthly(previewItem)) });
                const isActive = binding === opt.months;
                return (
                  <button
                    key={String(opt.months)}
                    onClick={() => setBinding(opt.months)}
                    style={{
                      padding: '10px 6px', borderRadius: 0,
                      border: `1px solid ${isActive ? T.text : T.border}`,
                      background: isActive ? T.text : 'transparent',
                      color: isActive ? '#fff' : T.textSub,
                      cursor: 'pointer', textAlign: 'center',
                    }}
                  >
                    <div style={{ fontWeight: 700, fontSize: 13 }}>{opt.label}</div>
                    <div style={{ fontSize: 10, marginTop: 2, opacity: 0.75 }}>{opt.sublabel}</div>
                    <div style={{ fontSize: 11, fontWeight: 600, marginTop: 4 }}>{price}</div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Summary */}
          <div style={{ background: '#fafafa', border: `1px solid ${T.border}`, padding: '14px 16px', marginBottom: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14 }}>
              <span style={{ color: T.textSub }}>{t('productDetailPage.toPay')}</span>
              <span style={{ fontWeight: 700, color: T.text, fontSize: 16 }}>{displayPrice}</span>
            </div>
            {binding !== 'buy' && (
              <div style={{ fontSize: 12, color: T.textMuted, marginTop: 4 }}>
                {t('productDetailPage.bindingInfo', { count: binding })}
              </div>
            )}
          </div>

          <button
            style={{ ...S.btn('primary'), width: '100%', justifyContent: 'center', padding: '14px', fontSize: 13 }}
            onClick={handleAdd}
          >
            {binding === 'buy' ? t('productDetailPage.buyFor', { amount: fmt(product.price) }) : t('productDetailPage.rentNow', { price: displayPrice })}
          </button>

          {product.imageUrl && uploadPath(product.imageUrl) && (
            <TryOnButton
              garmentImageUrl={product.imageUrl}
              garmentName={product.name}
              garmentCategory={product.category}
              garmentSize={product.size}
              user={user}
              profile={profile}
              onNeedLogin={onNeedLogin}
              onGoToProfile={onGoToProfile}
            />
          )}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// COLLECTION DETAIL MODAL
// ─────────────────────────────────────────────────────────────────

type BookingStep = 'form' | 'confirm' | 'done';

function CollectionDetailModal({ col, onClose, onAddToCart, user }: {
  col: FashionCollection;
  onClose: () => void;
  onAddToCart: (item: CartItem) => void;
  user: QidUser | null;
}) {
  const [bookingStep, setBookingStep] = useState<BookingStep | null>(null);
  const [formData, setFormData] = useState({
    name: user?.name ?? '',
    email: user?.email ?? '',
    phone: '',
    size: 'M',
    notes: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  function handleAddToCart() {
    onAddToCart({
      id: col.id,
      name: col.title,
      price: col.price,
      purchasePrice: col.price * 12,
      priceUnit: col.priceUnit,
      imageUrl: col.imageUrl ?? '',
      qty: 1,
      category: 'Kollektion',
      bindingMonths: 12,
    });
    onClose();
  }

  async function handleBooking() {
    // Bokningen kräver inloggning — API:t tar e-posten från kontot.
    if (!user) { setError(t('collectionDetailModal.logInToBook')); return; }
    if (bookingStep === null) { setBookingStep('form'); return; }
    if (bookingStep === 'form') { setBookingStep('confirm'); return; }
    if (bookingStep === 'confirm') {
      setLoading(true);
      setError('');
      try {
        await api.createFashionBooking({
          collectionId: col.id,
          customerName: formData.name,
          customerEmail: user.email,
          customerPhone: formData.phone || undefined,
          size: formData.size || undefined,
          notes: formData.notes || undefined,
        });
        setBookingStep('done');
      } catch (err: any) {
        setError(err.message ?? t('common.somethingWentWrong'));
      } finally {
        setLoading(false);
      }
    }
  }

  return (
    <div style={S.modal} onClick={onClose}>
      <div style={{
        background: T.surface,
        borderRadius: 8,
        width: '100%',
        maxWidth: 680,
        maxHeight: '90vh',
        overflow: 'auto',
        border: `1px solid ${T.border}`,
      }} onClick={e => e.stopPropagation()}>

        {/* Hero Image */}
        <div style={{ position: 'relative', paddingTop: '45%', overflow: 'hidden' }}>
          <img
            src={col.imageUrl || 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800&h=400&fit=crop'}
            alt={col.title}
            style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover' }}
            onError={e => { (e.target as HTMLImageElement).src = 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800&h=400&fit=crop'; }}
          />
          <button
            style={{ position: 'absolute', top: 12, right: 12, ...S.btn('ghost'), background: 'rgba(0,0,0,0.5)', padding: '6px 10px' }}
            onClick={onClose}
          >✕</button>
          {col.featured && (
            <div style={{ position: 'absolute', top: 12, left: 12, ...S.badge(T.amber), background: `${T.amber}cc`, color: '#fff', fontSize: 12 }}>{t('collectionDetailModal.featured')}</div>
          )}
        </div>

        <div style={{ padding: '24px 28px 28px' }}>
          {/* Header */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
              <span style={S.badge(T.rose)}>{vl('collectionCategory', col.category)}</span>
              <span style={S.badge(T.forest)}>{vl('style', col.style)}</span>
              {col.season && <span style={S.badge(T.textMuted)}>{col.season}</span>}
              {col.brand && <span style={S.badge(T.accent1)}>{col.brand}</span>}
            </div>
            <div style={{ fontSize: 24, fontWeight: 800, color: T.text, marginBottom: 4 }}>{col.title}</div>
            {col.subtitle && <div style={{ fontSize: 15, color: T.textSub, marginBottom: 8 }}>{col.subtitle}</div>}
            {col.designer && <div style={{ fontSize: 13, color: T.textMuted }}>{tn('collectionDetailModal.designBy', { designer: <span style={{ color: T.roseLight }}>{col.designer}</span> })}</div>}
            <div style={{ fontSize: 13, color: T.textMuted, marginTop: 4 }}>📍 {col.location} · {col.region}</div>
          </div>

          {/* Description */}
          {col.description && (
            <div style={{ fontSize: 14, color: T.textSub, lineHeight: 1.6, marginBottom: 20, padding: '12px 16px', background: T.roseDim, borderRadius: 4, borderLeft: `3px solid ${T.rose}` }}>
              {col.description}
            </div>
          )}

          {/* Highlights & Includes */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 20 }}>
            {col.highlights.length > 0 && (
              <div style={{ background: T.bg, borderRadius: 5, padding: 14, border: `1px solid ${T.border}` }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: T.rose, marginBottom: 8, textTransform: 'uppercase', letterSpacing: 1 }}>{t('collectionDetailModal.highlights')}</div>
                {col.highlights.map((h, i) => (
                  <div key={i} style={{ fontSize: 13, color: T.textSub, marginBottom: 4, display: 'flex', gap: 6 }}>
                    <span style={{ color: T.rose }}>✦</span> {h}
                  </div>
                ))}
              </div>
            )}
            {col.includes.length > 0 && (
              <div style={{ background: T.bg, borderRadius: 5, padding: 14, border: `1px solid ${T.border}` }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: T.rose, marginBottom: 8, textTransform: 'uppercase', letterSpacing: 1 }}>{t('collectionDetailModal.includes')}</div>
                {col.includes.map((inc, i) => (
                  <div key={i} style={{ fontSize: 13, color: T.textSub, marginBottom: 4, display: 'flex', gap: 6 }}>
                    <span style={{ color: T.success }}>✓</span> {inc}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Spots + Price */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', background: T.bg, borderRadius: 5, border: `1px solid ${T.border}`, marginBottom: 20 }}>
            <div>
              <div style={{ fontSize: 12, color: T.textMuted }}>{t('collectionDetailModal.availability')}</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: spotsColor(col.spotsAvailable, col.spotsTotal) }}>
                {t('collectionDetailModal.spotsLeft', { available: col.spotsAvailable, total: col.spotsTotal })}
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 12, color: T.textMuted }}>{t('common.buy')}</div>
              <div style={{ fontSize: 24, fontWeight: 800, color: T.rose }}>{fmt(col.price)} <span style={{ fontSize: 13, fontWeight: 400, color: T.textMuted }}>{vl('priceUnit', col.priceUnit)}</span></div>
              {col.monthlyPrice > 0 && <div style={{ fontSize: 13, color: T.textMuted, marginTop: 2 }}>{t('collectionDetailModal.orMonthly', { amount: fmt(col.monthlyPrice) })}</div>}
            </div>
          </div>

          {/* Booking form */}
          {bookingStep === 'form' && (
            <div style={{ border: `1px solid ${T.border}`, borderRadius: 5, padding: 16, marginBottom: 16, background: T.bg }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: T.text, marginBottom: 12 }}>{t('collectionDetailModal.yourDetails')}</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10 }}>
                <div>
                  <div style={{ fontSize: 12, color: T.textSub, marginBottom: 4 }}>{t('collectionDetailModal.nameLabel')}</div>
                  <input style={S.input} value={formData.name} onChange={e => setFormData(p => ({ ...p, name: e.target.value }))} placeholder={t('collectionDetailModal.namePlaceholder')} required />
                </div>
                <div>
                  <div style={{ fontSize: 12, color: T.textSub, marginBottom: 4 }}>{t('collectionDetailModal.emailLabel')}</div>
                  <input style={S.input} type="email" value={user?.email ?? formData.email} readOnly placeholder={t('collectionDetailModal.emailPlaceholder')} />
                </div>
                <div>
                  <div style={{ fontSize: 12, color: T.textSub, marginBottom: 4 }}>{t('collectionDetailModal.phoneLabel')}</div>
                  <input style={S.input} value={formData.phone} onChange={e => setFormData(p => ({ ...p, phone: e.target.value }))} placeholder={t('collectionDetailModal.phonePlaceholder')} />
                </div>
                <div>
                  <div style={{ fontSize: 12, color: T.textSub, marginBottom: 4 }}>{t('common.size')}</div>
                  <select style={S.input} value={formData.size} onChange={e => setFormData(p => ({ ...p, size: e.target.value }))}>
                    {SIZES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>
              <div style={{ marginTop: 10 }}>
                <div style={{ fontSize: 12, color: T.textSub, marginBottom: 4 }}>{t('collectionDetailModal.messageLabel')}</div>
                <textarea style={{ ...S.input, minHeight: 60, resize: 'vertical' }} value={formData.notes} onChange={e => setFormData(p => ({ ...p, notes: e.target.value }))} placeholder={t('collectionDetailModal.messagePlaceholder')} />
              </div>
            </div>
          )}

          {bookingStep === 'confirm' && (
            <div style={{ border: `1px solid ${T.rose}40`, borderRadius: 5, padding: 16, marginBottom: 16, background: T.roseDim }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: T.rose, marginBottom: 10 }}>{t('collectionDetailModal.confirmTitle')}</div>
              <div style={{ fontSize: 13, color: T.textSub, lineHeight: 1.8 }}>
                <div>{tn('collectionDetailModal.summaryCollection', { value: <strong style={{ color: T.text }}>{col.title}</strong> })}</div>
                <div>{tn('collectionDetailModal.summaryName', { value: <strong style={{ color: T.text }}>{formData.name}</strong> })}</div>
                <div>{tn('collectionDetailModal.summaryEmail', { value: <strong style={{ color: T.text }}>{formData.email}</strong> })}</div>
                {formData.size && <div>{tn('collectionDetailModal.summarySize', { value: <strong style={{ color: T.text }}>{formData.size}</strong> })}</div>}
                <div>{tn('collectionDetailModal.summaryPrice', { value: <strong style={{ color: T.rose }}>{fmt(col.price)} {vl('priceUnit', col.priceUnit)}</strong> })}</div>
                {col.monthlyPrice > 0 && <div>{tn('collectionDetailModal.summaryMonthly', { value: <strong style={{ color: T.rose }}>{t('common.krPerMonth', { amount: fmt(col.monthlyPrice) })}</strong> })}</div>}
              </div>
              {error && <div style={{ color: T.danger, fontSize: 13, marginTop: 8 }}>{error}</div>}
            </div>
          )}

          {bookingStep === 'done' && (
            <div style={{ border: `1px solid ${T.success}40`, borderRadius: 5, padding: 20, marginBottom: 16, background: `${T.success}15`, textAlign: 'center' }}>
              <div style={{ fontSize: 28, marginBottom: 8 }}>✓</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: T.text, marginBottom: 4 }}>{t('collectionDetailModal.orderSent')}</div>
              <div style={{ fontSize: 13, color: T.textSub }}>{t('collectionDetailModal.weWillReply', { email: formData.email })}</div>
            </div>
          )}

          {/* Action buttons */}
          {bookingStep !== 'done' && (
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <button style={{ ...S.btn('ghost'), flex: 1, justifyContent: 'center' }} onClick={handleAddToCart}>
                {t('collectionDetailModal.rentNow')}
              </button>
              <button
                style={{ ...S.btn('primary'), flex: 2, justifyContent: 'center', padding: '10px' }}
                onClick={handleBooking}
                disabled={loading || col.spotsAvailable === 0}
              >
                {loading ? t('common.sending') : bookingStep === null ? t('collectionDetailModal.orderNow') : bookingStep === 'form' ? t('collectionDetailModal.continue') : t('common.confirm')}
              </button>
            </div>
          )}
          {bookingStep === 'done' && (
            <button style={{ ...S.btn('primary'), width: '100%', justifyContent: 'center' }} onClick={onClose}>{t('common.close')}</button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// CART MODAL
// ─────────────────────────────────────────────────────────────────

const BINDING_OPTIONS: { months: 1 | 3 | 12 | 'buy'; label: string; sublabel?: string }[] = [
  { months: 1,    label: t('common.months', { count: 1 }) },
  { months: 3,    label: t('common.months', { count: 3 }),  sublabel: t('bindingOptions.flexible') },
  { months: 12,   label: t('common.months', { count: 12 }), sublabel: t('bindingOptions.popular') },
  { months: 'buy', label: t('common.buy'),   sublabel: t('bindingOptions.ownIt') },
];

// ─────────────────────────────────────────────────────────────────
// FRAKT VIA FRAKTJAKT (api/src/shipping/) — fraktsätt till verkligt pris,
// och sändningen med etikett och spårning när Qvestium bokat frakten.
// ─────────────────────────────────────────────────────────────────

let fraktjaktEnabledPromise: Promise<boolean> | null = null;
let shippingFixedPricePromise: Promise<number | null> | null = null;
/** Fast fraktpris om API:t anger ett (gör det inte sedan 2026-11-03), annars null — då gäller viktklassernas priser. */
function useShippingFixedPrice(): number | null {
  const [price, setPrice] = useState<number | null>(null);
  useEffect(() => {
    shippingFixedPricePromise ??= api.shippingFixedPrice();
    shippingFixedPricePromise.then(setPrice);
  }, []);
  return price;
}
function useFraktjaktEnabled(): boolean {
  const [on, setOn] = useState(false);
  useEffect(() => {
    fraktjaktEnabledPromise ??= api.fraktjaktEnabled();
    fraktjaktEnabledPromise.then(setOn);
  }, []);
  return on;
}

function ShippingOptionPicker({ load, loadKey, value, onChange }: {
  load: () => Promise<ShippingOption[]>;
  loadKey: string;
  value?: string | null;
  onChange: (opt: ShippingOption) => void;
}) {
  const [state, setState] = useState<ShippingOption[] | { error: string } | 'loading'>('loading');
  useEffect(() => {
    let alive = true;
    setState('loading');
    load()
      .then(opts => {
        if (!alive) return;
        setState(opts);
        if (opts.length > 0 && !opts.some(o => o.id === value)) onChange(opts[0]);
      })
      .catch(err => { if (alive) setState({ error: err.message ?? t('shipping.loadFailed') }); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadKey]);

  if (state === 'loading') return <div style={{ fontSize: 12, color: T.textMuted, marginTop: 8 }}>{t('shipping.loading')}</div>;
  if ('error' in state) return <div style={{ fontSize: 12, color: T.danger, marginTop: 8 }}>{state.error}</div>;
  if (state.length === 0) return <div style={{ fontSize: 12, color: T.danger, marginTop: 8 }}>{t('shipping.none')}</div>;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8 }}>
      {state.map(opt => {
        const active = opt.id === value;
        return (
          <button key={opt.id} type="button" onClick={() => onChange(opt)}
            style={{ display: 'flex', gap: 10, alignItems: 'center', textAlign: 'left', padding: '8px 10px', borderRadius: 4, cursor: 'pointer',
              border: `1.5px solid ${active ? T.rose : T.border}`, background: active ? T.roseDim : 'transparent', color: T.text }}>
            {opt.logoUrl
              ? <img src={opt.logoUrl} alt="" style={{ width: 36, height: 24, objectFit: 'contain', flexShrink: 0, background: '#fff', borderRadius: 2 }} />
              : <span style={{ width: 36, flexShrink: 0 }} />}
            <span style={{ flex: 1, minWidth: 0 }}>
              <span style={{ display: 'block', fontSize: 12, fontWeight: 700 }}>{[opt.carrier, opt.productName].filter(Boolean).join(' · ')}</span>
              <span style={{ display: 'block', fontSize: 11, color: T.textMuted }}>
                {[opt.toAgent ? t('shipping.toAgent') : t('shipping.toDoor'), opt.arrivalTime].filter(Boolean).join(' · ')}
              </span>
              {opt.agentInfo && <span style={{ display: 'block', fontSize: 11, color: T.textMuted }}>{t('shipping.nearestAgent', { agent: opt.agentInfo })}</span>}
            </span>
            <span style={{ fontSize: 13, fontWeight: 700, color: active ? T.rose : T.text, whiteSpace: 'nowrap' }}>{t('common.kr', { amount: fmt(opt.price) })}</span>
          </button>
        );
      })}
      <div style={{ fontSize: 10, color: T.textMuted }}>{t('shipping.inclVat')}</div>
    </div>
  );
}

/** Välj fraktsätt och gå vidare till betalning (returer och byten). */
function ShippingChoiceModal({ title, load, loadKey, onPay, onClose }: {
  title: string;
  load: () => Promise<ShippingOption[]>;
  loadKey: string;
  onPay: (quoteId: string) => Promise<void>;
  onClose: () => void;
}) {
  const [choice, setChoice] = useState<ShippingOption | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function pay() {
    if (!choice) return;
    setBusy(true); setError(null);
    try { await onPay(choice.id); }
    catch (e: any) { setError(e.message ?? t('common.somethingWentWrong')); setBusy(false); }
  }
  return (
    <div style={S.modal} onClick={onClose}>
      <div style={{ background: T.surface, borderRadius: 8, width: '100%', maxWidth: 460, maxHeight: '90vh', overflow: 'auto', border: `1px solid ${T.border}`, padding: 20 }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontSize: 17, fontWeight: 700, color: T.text }}>{title}</div>
          <button style={{ ...S.btn('ghost'), padding: '4px 8px' }} onClick={onClose}>✕</button>
        </div>
        <ShippingOptionPicker load={load} loadKey={loadKey} value={choice?.id} onChange={setChoice} />
        {error && <div style={{ fontSize: 12, color: T.danger, marginTop: 8 }}>{error}</div>}
        <button style={{ ...S.btn('primary'), width: '100%', justifyContent: 'center', marginTop: 14 }} disabled={!choice || busy} onClick={pay}>
          {busy ? '…' : choice ? t('shipping.payAmount', { amount: fmt(choice.price) }) : t('shipping.choose')}
        </button>
      </div>
    </div>
  );
}

/** Sändningen: etikett för den som skickar, status och spårning för båda. */
function ShipmentPanel({ shipment, sender }: { shipment?: Shipment | null; sender: boolean }) {
  const box: React.CSSProperties = { marginTop: 12, padding: 12, borderRadius: 6, border: `1px solid ${T.border}`, background: T.surfaceHover, fontSize: 12, color: T.textSub, lineHeight: 1.5 };
  if (!shipment || shipment.status === 'booking') return <div style={box}>{t('shipping.status.booking')}</div>;
  if (shipment.bookingFailed) return <div style={box}>{t('shipping.status.bookingFailed')}</div>;
  const STATUSES = ['booked', 'shipped', 'in_transit', 'delivered', 'exception', 'cancelled'] as const;
  const statusKey: typeof STATUSES[number] = (STATUSES as readonly string[]).includes(shipment.status) ? shipment.status as typeof STATUSES[number] : 'booked';
  return (
    <div style={box}>
      <div style={{ fontWeight: 700, color: T.text }}>
        {[shipment.carrier, t(`shipping.status.${statusKey}`)].filter(Boolean).join(' · ')}
      </div>
      {sender && shipment.status === 'booked' && (
        shipment.labelUrls.length > 0 ? (
          <>
            <div style={{ marginTop: 4 }}>{t('shipping.labelReadyInfo')}</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
              {shipment.labelUrls.map((url, i) => (
                <a key={url} href={url} target="_blank" rel="noreferrer" style={{ ...S.btn('primary'), padding: '6px 14px', fontSize: 12, textDecoration: 'none' }}>
                  {shipment.labelUrls.length > 1 ? t('shipping.openLabelN', { n: i + 1 }) : t('shipping.openLabel')}
                </a>
              ))}
            </div>
          </>
        ) : <div style={{ marginTop: 4 }}>{t('shipping.labelPending')}</div>
      )}
      {shipment.agentInfo && <div style={{ marginTop: 6 }}>{t(sender ? 'shipping.dropOffAt' : 'shipping.pickUpAt', { agent: shipment.agentInfo })}</div>}
      {shipment.trackingNumber && <div style={{ marginTop: 6 }}>{t('shipping.trackingNumber', { number: shipment.trackingNumber })}</div>}
      {shipment.trackingLink && (
        <a href={shipment.trackingLink} target="_blank" rel="noreferrer" style={{ display: 'inline-block', marginTop: 6, color: T.rose, fontWeight: 600 }}>{t('shipping.track')}</a>
      )}
    </div>
  );
}

function CartModal({ cart, onClose, onRemove, onUpdateQty, onUpdateBinding, onUpdateDeliveryMethod, userEmail, onCheckoutSuccess }: {
  cart: CartItem[];
  onClose: () => void;
  onRemove: (id: string) => void;
  onUpdateQty: (id: string, delta: number) => void;
  onUpdateBinding: (id: string, months: 1 | 3 | 12 | 'buy') => void;
  onUpdateDeliveryMethod: (id: string, method: 'pickup' | 'shipping') => void;
  userEmail?: string;
  onCheckoutSuccess?: () => void;
}) {
  const [step, setStep] = useState<'cart' | 'confirm' | 'processing'>('cart');
  const [checkoutError, setCheckoutError] = useState('');

  const totalMonthly = cart.reduce((s, i) => s + calcMonthly(i) * i.qty, 0);
  const totalBuy = cart.reduce((s, i) => i.bindingMonths === 'buy' ? s + i.purchasePrice * i.qty : s, 0);
  // Fraktjakt: köparen väljer fraktsätt till verkligt pris per plagg; annars fasta priser per vikt.
  const fraktOn = useFraktjaktEnabled();
  const [shipChoice, setShipChoice] = useState<Record<string, ShippingOption>>({});
  const ships = (i: CartItem) => i.kind === 'wardrobe' && (i.deliveryMethod ?? 'shipping') === 'shipping';
  const totalShipping = cart.reduce((s, i) => !ships(i) ? s
    : s + (fraktOn ? (shipChoice[i.id]?.price ?? 0) : (SHIPPING_COST_BY_WEIGHT[i.weightTier ?? '0-1'] ?? 0)), 0);
  const shippingMissing = fraktOn && cart.some(i => ships(i) && !shipChoice[i.id]);
  const totalProtection = cart.reduce((s, i) => i.kind === 'wardrobe' && i.wardrobeType === 'buy'
    ? s + buyerProtectionFee(i.purchasePrice) * i.qty : s, 0);
  // Hyresförsäkring (Omocom) per hyrt plagg: valfri, priset hämtas när man kryssar i.
  const [insuranceOn, setInsuranceOn] = useState(false);
  const [insured, setInsured] = useState<Record<string, boolean>>({});
  const [quotes, setQuotes] = useState<Record<string, { id: string; premium: number; insuredAmount: number } | { error: string } | 'loading'>>({});
  useEffect(() => { api.insuranceAvailable().then(setInsuranceOn); }, []);
  const quoteKey = (i: CartItem) => `${i.wardrobeItemId}:${i.bindingMonths}`;
  const insurable = (i: CartItem) => insuranceOn && i.kind === 'wardrobe' && i.wardrobeType === 'rent' && !!i.wardrobeItemId && i.bindingMonths !== 'buy';
  const quoteFor = (i: CartItem) => { const q = quotes[quoteKey(i)]; return q && q !== 'loading' && 'id' in q ? q : null; };
  function loadQuote(i: CartItem) {
    const key = quoteKey(i);
    if (quotes[key]) return;
    setQuotes(q => ({ ...q, [key]: 'loading' }));
    api.insuranceQuote(i.wardrobeItemId!, Number(i.bindingMonths))
      .then(res => setQuotes(q => ({ ...q, [key]: res })))
      .catch(err => setQuotes(q => ({ ...q, [key]: { error: err.message } })));
  }
  useEffect(() => { cart.forEach(i => { if (insurable(i) && insured[i.id]) loadQuote(i); }); }, [cart, insured, insuranceOn]);
  const totalInsurance = cart.reduce((s, i) => insurable(i) && insured[i.id] ? s + (quoteFor(i)?.premium ?? 0) : s, 0);
  const totalNow = totalMonthly + totalBuy + totalShipping + totalProtection + totalInsurance;
  const insuranceLoading = cart.some(i => insurable(i) && insured[i.id] && quotes[quoteKey(i)] === 'loading');

  async function handleCheckout() {
    setStep('processing');
    setCheckoutError('');
    try {
      const headers: Record<string, string> = { 'Content-Type': 'application/json', ...authHeaders() };
      const res = await fetch(apiUrl('/checkout/create-session'), {
        method: 'POST',
        headers,
        body: JSON.stringify({
          items: cart.map(item => ({
            productId: item.id,
            name: item.name,
            price: item.price,
            price1: item.price1,
            price3: item.price3,
            purchasePrice: item.purchasePrice,
            qty: item.qty,
            bindingMonths: item.bindingMonths,
            imageUrl: item.imageUrl,
            kind: item.kind,
            wardrobeItemId: item.wardrobeItemId,
            wardrobeType: item.wardrobeType,
            deliveryMethod: item.kind === 'wardrobe' ? (item.deliveryMethod ?? 'shipping') : undefined,
            shippingQuoteId: fraktOn && ships(item) ? shipChoice[item.id]?.id : undefined,
            offerId: item.offerId,
            insuranceQuoteId: insurable(item) && insured[item.id] ? quoteFor(item)?.id : undefined,
          })),
          customerEmail: userEmail,
        }),
      });
      const data = await res.json();
      if (data.sessionUrl) {
        window.location.href = data.sessionUrl;
      } else {
        setCheckoutError(data.error || t('common.somethingWentWrong'));
        setStep('confirm');
      }
    } catch (err: any) {
      setCheckoutError(t('cartModal.paymentServerUnreachable'));
      setStep('confirm');
    }
  }

  return (
    <div style={S.modal} onClick={onClose}>
      <div style={{ background: T.surface, borderRadius: 8, width: '100%', maxWidth: 520, maxHeight: '90vh', overflow: 'auto', border: `1px solid ${T.border}` }} onClick={e => e.stopPropagation()}>
        <div style={{ padding: '20px 24px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: T.text }}>
            {step === 'cart' ? t('cartModal.title', { count: cart.length }) : step === 'confirm' ? t('cartModal.confirmTitle') : t('cartModal.processing')}
          </div>
          <button style={{ ...S.btn('ghost'), padding: '4px 8px' }} onClick={onClose}>✕</button>
        </div>
        <div style={{ padding: '16px 24px 24px' }}>
          {cart.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '32px 0', color: T.textMuted }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>🛒</div>
              <div>{t('cartModal.empty')}</div>
            </div>
          ) : step === 'processing' ? (
            <div style={{ textAlign: 'center', padding: '48px 0' }}>
              <div style={{ fontSize: 36, marginBottom: 16, animation: 'spin 1s linear infinite' }}>⏳</div>
              <div style={{ color: T.text, fontSize: 15, fontWeight: 600 }}>{t('cartModal.creatingPayment')}</div>
              <div style={{ color: T.textMuted, fontSize: 12, marginTop: 8 }}>{t('cartModal.redirectingToStripe')}</div>
              <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
            </div>
          ) : step === 'confirm' ? (
            <>
              {/* Bekräftelsesteg */}
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 13, color: T.textSub, marginBottom: 12 }}>{t('cartModal.reviewOrder')}</div>
                {cart.map(item => {
                  const monthly = calcMonthly(item);
                  return (
                    <div key={item.id} style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '8px 0', borderBottom: `1px solid ${T.border}` }}>
                      <img src={item.imageUrl || 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=80&h=80&fit=crop'} alt={item.name} style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 3, flexShrink: 0 }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: T.text }}>{item.name} x{item.qty}</div>
                        <div style={{ fontSize: 11, color: T.textMuted }}>
                          {item.bindingMonths === 'buy' ? t('common.buy') : t('cartModal.rentMonths', { count: item.bindingMonths })}
                        </div>
                      </div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: T.rose, flexShrink: 0 }}>
                        {item.bindingMonths === 'buy' ? t('common.kr', { amount: fmt(item.purchasePrice * item.qty) }) : t('common.krPerMonth', { amount: fmt(monthly * item.qty) })}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div style={{ borderTop: `1px solid ${T.border}`, paddingTop: 12, marginBottom: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <div style={{ fontSize: 15, fontWeight: 700, color: T.text }}>{t('cartModal.payNow')}</div>
                  <div style={{ fontSize: 22, fontWeight: 800, color: T.rose }}>{t('common.kr', { amount: fmtKr(totalNow) })}</div>
                </div>
                {totalMonthly > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ fontSize: 12, color: T.textSub }}>{t('cartModal.monthlyAfter')}</div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: T.textSub }}>{t('common.krPerMonth', { amount: fmt(totalMonthly) })}</div>
                  </div>
                )}
              </div>

              {/* Stripe info */}
              <div style={{ background: 'rgba(200,121,138,0.08)', borderRadius: 5, padding: '12px 14px', marginBottom: 16, display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0, marginTop: 1 }}>
                  <rect width="24" height="24" rx="4" fill="#635BFF"/>
                  <path d="M13.3 9.5c0-.8.7-1.1 1.7-1.1 1.5 0 3.4.5 4.9 1.3V5.5c-1.6-.7-3.3-1-4.9-1C12 4.5 9.5 6.3 9.5 9.5c0 5 6.8 4.2 6.8 6.4 0 .9-.8 1.2-1.9 1.2-1.7 0-3.8-.7-5.5-1.6v4.3c1.9.8 3.7 1.2 5.5 1.2 3.2 0 5.4-1.6 5.4-4.8.1-5.4-6.5-4.5-6.5-6.7z" fill="white"/>
                </svg>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: T.text }}>{t('cartModal.secureStripe')}</div>
                  <div style={{ fontSize: 11, color: T.textMuted, marginTop: 2 }}>{t('cartModal.stripeInfo')}</div>
                </div>
              </div>

              {checkoutError && (
                <div style={{ background: 'rgba(192,57,43,0.15)', border: '1px solid rgba(192,57,43,0.3)', borderRadius: 4, padding: '10px 12px', marginBottom: 12, color: '#e74c3c', fontSize: 12 }}>
                  {checkoutError}
                </div>
              )}

              <div style={{ display: 'flex', gap: 10 }}>
                <button style={{ ...S.btn('ghost'), flex: 1, justifyContent: 'center', padding: '12px' }} onClick={() => setStep('cart')}>{t('common.back')}</button>
                <button style={{ ...S.btn('primary'), flex: 2, justifyContent: 'center', padding: '12px', fontSize: 15, fontWeight: 700 }} onClick={handleCheckout} disabled={shippingMissing}>
                  {t('cartModal.payAmount', { amount: fmtKr(totalNow) })}
                </button>
              </div>
            </>
          ) : (
            <>
              {cart.map(item => {
                const monthly = calcMonthly(item);
                return (
                  <div key={item.id} style={{ padding: '14px 0', borderBottom: `1px solid ${T.border}` }}>
                    {/* Plagg rad */}
                    <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                      <img src={item.imageUrl || 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=80&h=80&fit=crop'} alt={item.name} style={{ width: 52, height: 52, objectFit: 'cover', borderRadius: 4, flexShrink: 0 }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: T.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.name}</div>
                        <div style={{ fontSize: 11, color: T.textMuted }}>{vl('category', item.category)}</div>
                        {item.bindingMonths === 'buy'
                          ? <div style={{ fontSize: 15, fontWeight: 800, color: T.rose, marginTop: 2 }}>{t('common.kr', { amount: fmt(item.purchasePrice * item.qty) })}</div>
                          : <div style={{ fontSize: 15, fontWeight: 800, color: T.rose, marginTop: 2 }}>{t('common.krPerMonth', { amount: fmt(monthly * item.qty) })}</div>
                        }
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                        {item.kind === 'wardrobe' ? (
                          <span style={{ fontSize: 11, color: T.textMuted, border: `1px solid ${T.border}`, borderRadius: 4, padding: '3px 8px' }}>{t('cartModal.uniqueItem')}</span>
                        ) : (
                          <>
                            <button style={{ ...S.btn('ghost'), padding: '2px 7px', fontSize: 14 }} onClick={() => onUpdateQty(item.id, -1)}>−</button>
                            <span style={{ color: T.text, fontSize: 13, minWidth: 18, textAlign: 'center' }}>{item.qty}</span>
                            <button style={{ ...S.btn('ghost'), padding: '2px 7px', fontSize: 14 }} onClick={() => onUpdateQty(item.id, 1)}>+</button>
                          </>
                        )}
                        <button style={{ ...S.btn('danger'), padding: '2px 7px', fontSize: 12 }} onClick={() => onRemove(item.id)}>✕</button>
                      </div>
                    </div>
                    {/* Bindningstid-väljare */}
                    {item.kind === 'wardrobe' ? (
                      item.wardrobeType === 'buy' ? (
                        <div style={{ marginTop: 10, fontSize: 12, color: T.textMuted }}>{t('cartModal.buyNoBinding')}</div>
                      ) : (
                        <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
                          {BINDING_OPTIONS.filter(o => o.months !== 'buy').map(opt => {
                            const active = item.bindingMonths === opt.months;
                            const price = opt.months === 1 ? (item.price1 ?? Math.round(item.price * 4))
                              : opt.months === 3 ? (item.price3 ?? Math.round(item.price * 3))
                              : item.price;
                            return (
                              <button
                                key={opt.months}
                                onClick={() => onUpdateBinding(item.id, opt.months)}
                                style={{
                                  flex: 1, padding: '6px 4px', borderRadius: 4,
                                  border: `1.5px solid ${active ? T.rose : T.border}`,
                                  background: active ? T.roseDim : 'transparent',
                                  color: active ? T.rose : T.textMuted,
                                  cursor: 'pointer', fontSize: 11, fontWeight: active ? 700 : 500,
                                  textAlign: 'center', transition: 'all 0.15s',
                                }}
                              >
                                <div style={{ fontWeight: 700, fontSize: 12 }}>{opt.label}</div>
                                <div style={{ fontSize: 11, marginTop: 2, color: active ? T.rose : T.textSub }}>{t('common.krPerMonth', { amount: fmt(price) })}</div>
                              </button>
                            );
                          })}
                        </div>
                      )
                    ) : (
                      <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
                        {BINDING_OPTIONS.map(opt => {
                          const active = item.bindingMonths === opt.months;
                          return (
                            <button
                              key={opt.months}
                              onClick={() => onUpdateBinding(item.id, opt.months)}
                              style={{
                                flex: 1,
                                padding: '6px 4px',
                                borderRadius: 4,
                                border: `1.5px solid ${active ? T.rose : T.border}`,
                                background: active ? T.roseDim : 'transparent',
                                color: active ? T.rose : T.textMuted,
                                cursor: 'pointer',
                                fontSize: 11,
                                fontWeight: active ? 700 : 500,
                                textAlign: 'center',
                                transition: 'all 0.15s',
                              }}
                            >
                              <div style={{ fontWeight: 700, fontSize: 12 }}>{opt.label}</div>
                              <div style={{ fontSize: 10, marginTop: 1, color: active ? T.roseLight : T.textMuted }}>{opt.sublabel}</div>
                              <div style={{ fontSize: 11, marginTop: 2, color: active ? T.rose : T.textSub }}>
                                {opt.months === 'buy'
                                  ? t('common.kr', { amount: fmt(item.purchasePrice) })
                                  : t('common.krPerMonth', { amount: opt.months === 1 ? fmt(item.price1 ?? item.price * 4) : opt.months === 3 ? fmt(item.price3 ?? item.price * 3) : fmt(item.price) })}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    )}
                    {item.kind === 'wardrobe' && (
                      <div style={{ marginTop: 10, display: 'flex', gap: 6, alignItems: 'center' }}>
                        <span style={{ fontSize: 11, color: T.textMuted }}>{t('cartModal.delivery')}</span>
                        {(['pickup', 'shipping'] as const).map(method => {
                          const active = (item.deliveryMethod ?? 'shipping') === method;
                          const cost = method === 'shipping' ? (SHIPPING_COST_BY_WEIGHT[item.weightTier ?? '0-1'] ?? 0) : 0;
                          return (
                            <button
                              key={method}
                              onClick={() => onUpdateDeliveryMethod(item.id, method)}
                              style={{
                                padding: '5px 10px', borderRadius: 4,
                                border: `1.5px solid ${active ? T.rose : T.border}`,
                                background: active ? T.roseDim : 'transparent',
                                color: active ? T.rose : T.textMuted,
                                cursor: 'pointer', fontSize: 11, fontWeight: active ? 700 : 500,
                              }}
                            >
                              {method === 'pickup' ? t('cartModal.pickup') : fraktOn ? t('shipping.shipping') : t('cartModal.shippingCost', { amount: fmt(cost) })}
                            </button>
                          );
                        })}
                      </div>
                    )}
                    {fraktOn && ships(item) && item.wardrobeItemId && (
                      <ShippingOptionPicker
                        load={() => api.shippingOptions(item.wardrobeItemId!, item.offerId)}
                        loadKey={`${item.wardrobeItemId}:${item.offerId ?? ''}`}
                        value={shipChoice[item.id]?.id}
                        onChange={opt => setShipChoice(c => ({ ...c, [item.id]: opt }))}
                      />
                    )}
                    {insurable(item) && (() => {
                      const q = quotes[quoteKey(item)];
                      return (
                        <label style={{ marginTop: 10, display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 12, color: T.textSub, cursor: 'pointer' }}>
                          <input type="checkbox" checked={!!insured[item.id]} onChange={e => setInsured(s => ({ ...s, [item.id]: e.target.checked }))} style={{ marginTop: 2 }} />
                          <span>
                            {tn('cartModal.insuranceVia', { name: <strong style={{ color: T.text }}>{t('cartModal.insuranceName')}</strong> })}
                            {insured[item.id] && (q === 'loading' ? ` — ${t('cartModal.fetchingPrice')}`
                              : q && 'error' in q ? <span style={{ color: T.danger }}> — {q.error}</span>
                              : q && 'id' in q ? <> — {t('cartModal.insuranceQuote', { premium: fmtKr(q.premium), count: Number(item.bindingMonths), insured: fmt(q.insuredAmount) })}</> : null)}
                            <div style={{ fontSize: 11, color: T.textMuted, marginTop: 2 }}>{t('cartModal.insuranceInfo')}</div>
                          </span>
                        </label>
                      );
                    })()}
                  </div>
                );
              })}

              {/* Summering */}
              <div style={{ marginTop: 16, borderTop: `1px solid ${T.border}`, paddingTop: 14 }}>
                {totalBuy > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                    <div style={{ fontSize: 13, color: T.textSub }}>{t('cartModal.buyOneTime')}</div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: T.textSub }}>{t('common.kr', { amount: fmt(totalBuy) })}</div>
                  </div>
                )}
                {totalMonthly > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                    <div style={{ fontSize: 13, color: T.textSub }}>{t('cartModal.rentLabel')} <span style={{ fontSize: 11, color: T.textMuted }}>{t('cartModal.rentFirstMonth')}</span></div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: T.textSub }}>{t('common.kr', { amount: fmt(totalMonthly) })}</div>
                  </div>
                )}
                {totalShipping > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                    <div style={{ fontSize: 13, color: T.textSub }}>{t('cartModal.shipping')}</div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: T.textSub }}>{t('common.kr', { amount: fmt(totalShipping) })}</div>
                  </div>
                )}
                {totalInsurance > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                    <div style={{ fontSize: 13, color: T.textSub }}>{t('cartModal.insuranceName')} <span style={{ fontSize: 11, color: T.textMuted }}>{t('cartModal.insuranceNote')}</span></div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: T.textSub }}>{t('common.kr', { amount: fmtKr(totalInsurance) })}</div>
                  </div>
                )}
                {totalProtection > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                    <div style={{ fontSize: 13, color: T.textSub }}>
                      {t('cartModal.buyerProtection')} <span style={{ fontSize: 11, color: T.textMuted }}>{t('cartModal.buyerProtectionNote')}</span>
                    </div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: T.textSub }}>{t('common.kr', { amount: fmtKr(totalProtection) })}</div>
                  </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, borderTop: `1px solid ${T.border}`, paddingTop: 8, marginTop: 6 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: T.text }}>{t('cartModal.payNow')}</div>
                  <div style={{ fontSize: 20, fontWeight: 800, color: T.rose }}>{t('common.kr', { amount: fmtKr(totalNow) })}</div>
                </div>
                {totalMonthly > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                    <div style={{ fontSize: 13, color: T.textSub }}>{t('cartModal.monthlyAfter')}</div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: T.textSub }}>{t('common.krPerMonth', { amount: fmt(totalMonthly) })}</div>
                  </div>
                )}
                <div style={{ fontSize: 10, color: T.textMuted, marginBottom: 12, lineHeight: 1.5 }}>
                  {t('cartModal.termsNote')}
                </div>
                <button
                  style={{ ...S.btn('primary'), width: '100%', justifyContent: 'center', padding: '12px', fontSize: 15, fontWeight: 700 }}
                  onClick={() => setStep('confirm')}
                  disabled={insuranceLoading || shippingMissing}
                >
                  {insuranceLoading ? t('cartModal.fetchingInsurancePrice') : t('cartModal.goToCheckout')}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// PROFILE VIEW
// ─────────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────────
// AGENTER (MCP) — låt en AI-agent agera på Qvestium åt användaren via
// MCP-servern (api/src/mcp/server.ts) med ett personligt åtkomsttoken.
// ─────────────────────────────────────────────────────────────────

function CopyField({ value, multiline }: { value: string; multiline?: boolean }) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    try { await navigator.clipboard.writeText(value); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch { /* ignorera */ }
  }
  return (
    <div style={{ position: 'relative', marginTop: 6 }}>
      <pre style={{ margin: 0, padding: '10px 70px 10px 12px', background: '#f6f5f5', border: `1px solid ${T.border}`, borderRadius: 4, fontSize: 12,
        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', whiteSpace: multiline ? 'pre' : 'pre-wrap', wordBreak: 'break-all', overflowX: 'auto', color: T.text }}>{value}</pre>
      <button onClick={copy} style={{ ...S.btn('ghost'), position: 'absolute', top: 6, right: 6, padding: '4px 8px', fontSize: 10, background: '#fff' }}>{copied ? t('copyField.copied') : t('copyField.copy')}</button>
    </div>
  );
}

/** Godkänn att en MCP-klient (t.ex. Claude.ai) agerar som användaren — /?oauth=<id>. */
function OAuthConsentModal({ requestId, user, onNeedLogin, onClose }: { requestId: string; user: QidUser | null; onNeedLogin: () => void; onClose: () => void }) {
  const [info, setInfo] = useState<{ clientName: string; redirectHost: string } | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.oauthRequest(requestId).then(setInfo).catch(e => setError(e.message)); }, [requestId]);

  async function decide(approve: boolean) {
    setBusy(true); setError('');
    try { window.location.href = await api.oauthDecide(requestId, approve); }
    catch (e: any) { setError(e.message ?? t('common.somethingWentWrong')); setBusy(false); }
  }

  return (
    <div style={S.modal}>
      <div style={{ background: T.surface, borderRadius: 8, padding: 28, width: '100%', maxWidth: 440, margin: 16, border: `1px solid ${T.border}` }}>
        <div style={{ fontSize: 20, fontWeight: 700, color: T.text, marginBottom: 12 }}>{t('oAuthConsentModal.title')}</div>
        {error && !info ? (
          <>
            <div style={{ fontSize: 14, color: T.danger, marginBottom: 16 }}>{error}</div>
            <button style={S.btn('ghost')} onClick={onClose}>{t('common.close')}</button>
          </>
        ) : !info ? (
          <div style={{ fontSize: 13, color: T.textMuted }}>{t('common.loading')}</div>
        ) : (
          <>
            <div style={{ fontSize: 14, color: T.textSub, lineHeight: 1.6 }}>
              {tn('oAuthConsentModal.description', { client: <strong style={{ color: T.text }}>{info.clientName}</strong> })}
            </div>
            <div style={{ fontSize: 12, color: T.textMuted, marginTop: 10 }}>
              {tn('oAuthConsentModal.redirectNotice', { host: <strong>{info.redirectHost}</strong> })}
            </div>
            {error && <div style={{ fontSize: 13, color: T.danger, marginTop: 12 }}>{error}</div>}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20, flexWrap: 'wrap' }}>
              {user ? (
                <>
                  <button style={S.btn('ghost')} disabled={busy} onClick={() => decide(false)}>{t('oAuthConsentModal.deny')}</button>
                  <button style={S.btn('primary')} disabled={busy} onClick={() => decide(true)}>{busy ? '...' : t('oAuthConsentModal.allow')}</button>
                </>
              ) : (
                <>
                  <button style={S.btn('ghost')} onClick={() => decide(false)}>{t('common.cancel')}</button>
                  <button style={S.btn('primary')} onClick={onNeedLogin}>{t('oAuthConsentModal.logInToContinue')}</button>
                </>
              )}
            </div>
            {user && <div style={{ fontSize: 11, color: T.textMuted, marginTop: 12, textAlign: 'right' }}>{t('oAuthConsentModal.loggedInAs', { email: user.email })}</div>}
          </>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// PUSHNOTISER (iOS-appen, lib/push.ts)
// ─────────────────────────────────────────────────────────────────

// Erbjuds en gång när man är inloggad i appen — iOS egen fråga visas först
// när användaren tryckt "Slå på", inte vid första start.
function PushPrompt() {
  const [show, setShow] = useState(false);
  useEffect(() => {
    if (!IS_NATIVE_APP || pushPromptDismissed()) return;
    pushPermission().then(p => setShow(p === 'prompt'));
  }, []);
  if (!show) return null;
  return (
    <div style={{ maxWidth: 1200, margin: '12px auto 0', padding: '0 16px' }}>
      <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 8, padding: 16, display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ flex: '1 1 220px' }}>
          <div style={{ fontWeight: 600, color: T.text, marginBottom: 4 }}>{t('pushPrompt.title')}</div>
          <div style={{ fontSize: 13, color: T.textSub }}>{t('pushPrompt.body')}</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button style={S.btn('ghost')} onClick={() => { dismissPushPrompt(); setShow(false); }}>{t('pushPrompt.notNow')}</button>
          <button style={S.btn('primary')} onClick={() => { setShow(false); enablePush().catch(() => {}); }}>{t('pushPrompt.enable')}</button>
        </div>
      </div>
    </div>
  );
}

const PUSH_TYPE_LABELS: Record<PushType, MessageKey> = {
  messages: 'pushSettings.typeMessages',
  deals: 'pushSettings.typeDeals',
  rentals: 'pushSettings.typeRentals',
  support: 'pushSettings.typeSupport',
};

function PushSettingsCard() {
  const [settings, setSettings] = useState<PushSettings | null>(null);
  const [permission, setPermission] = useState<string>('unavailable');
  const [saved, setSaved] = useState(false);
  useEffect(() => {
    api.getPushSettings().then(setSettings).catch(() => setSettings({ disabledTypes: [], quietStart: null, quietEnd: null }));
    pushPermission().then(setPermission);
  }, []);
  if (!settings) return null;

  async function save(next: PushSettings) {
    setSettings(next);
    setSaved(false);
    try { setSettings(await api.savePushSettings(next)); setSaved(true); } catch { /* visas inte — försök igen */ }
  }
  const toggle = (type: PushType) => save({
    ...settings!,
    disabledTypes: settings!.disabledTypes.includes(type) ? settings!.disabledTypes.filter(x => x !== type) : [...settings!.disabledTypes, type],
  });
  const hours = Array.from({ length: 24 }, (_, h) => h);
  const quietOn = settings.quietStart !== null && settings.quietEnd !== null;

  return (
    <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 6, padding: 20 }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: T.rose, marginBottom: 12, textTransform: 'uppercase', letterSpacing: 1 }}>{t('pushSettings.title')}</div>
      {permission === 'unavailable' && <div style={{ fontSize: 13, color: T.textMuted, marginBottom: 12 }}>{t('pushSettings.appOnly')}</div>}
      {permission === 'denied' && <div style={{ fontSize: 13, color: T.textMuted, marginBottom: 12 }}>{t('pushSettings.deniedHint')}</div>}
      {permission === 'prompt' && (
        <button style={{ ...S.btn('primary'), marginBottom: 12 }} onClick={() => enablePush().then(ok => setPermission(ok ? 'granted' : 'denied')).catch(() => {})}>{t('pushPrompt.enable')}</button>
      )}
      {(Object.keys(PUSH_TYPE_LABELS) as PushType[]).map(type => (
        <label key={type} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', fontSize: 14, color: T.text, cursor: 'pointer' }}>
          <input type="checkbox" checked={!settings.disabledTypes.includes(type)} onChange={() => toggle(type)} style={{ width: 20, height: 20 }} />
          {t(PUSH_TYPE_LABELS[type])}
        </label>
      ))}
      <label style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', fontSize: 14, color: T.text, cursor: 'pointer' }}>
        <input type="checkbox" checked={quietOn} onChange={() => save({ ...settings, quietStart: quietOn ? null : 22, quietEnd: quietOn ? null : 7 })} style={{ width: 20, height: 20 }} />
        {t('pushSettings.quietHours')}
      </label>
      {quietOn && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontSize: 14, color: T.textSub, paddingLeft: 30 }}>
          <select style={{ ...S.input, width: 'auto', fontSize: 16 }} value={settings.quietStart!} onChange={e => save({ ...settings, quietStart: Number(e.target.value) })}>
            {hours.map(h => <option key={h} value={h}>{String(h).padStart(2, '0')}:00</option>)}
          </select>
          –
          <select style={{ ...S.input, width: 'auto', fontSize: 16 }} value={settings.quietEnd!} onChange={e => save({ ...settings, quietEnd: Number(e.target.value) })}>
            {hours.map(h => <option key={h} value={h}>{String(h).padStart(2, '0')}:00</option>)}
          </select>
        </div>
      )}
      <div style={{ fontSize: 12, color: T.textMuted, marginTop: 12 }}>{t('pushSettings.essentialNote')}</div>
      {saved && <div style={{ fontSize: 12, color: T.textMuted, marginTop: 6 }}>{t('pushSettings.saved')}</div>}
    </div>
  );
}

function AgentsMcpCard() {
  const [tokens, setTokens] = useState<ApiToken[] | null>(null);
  const [name, setName] = useState('Claude');
  const [newToken, setNewToken] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [client, setClient] = useState<'claude-ai' | 'claude-code' | 'claude-desktop' | 'other'>('claude-ai');
  const mcpUrl = apiUrl('/mcp').startsWith('http') ? apiUrl('/mcp') : `${window.location.origin}/mcp`;
  const tokenText = newToken ?? t('agentsMcpCard.tokenPlaceholder');

  useEffect(() => { api.myApiTokens().then(setTokens).catch(() => setTokens([])); }, []);

  async function create() {
    setBusy(true); setError('');
    try {
      const res = await api.createApiToken(name);
      setNewToken(res.token);
      setTokens(prev => [res.apiToken, ...(prev ?? [])]);
    } catch (e: any) { setError(e.message ?? t('agentsMcpCard.createFailed')); }
    finally { setBusy(false); }
  }
  async function revoke(tok: ApiToken) {
    if (!confirm(t('agentsMcpCard.revokeConfirm', { name: tok.name }))) return;
    try { await api.revokeApiToken(tok.id); setTokens(ts => (ts ?? []).filter(x => x.id !== tok.id)); }
    catch (e: any) { setError(e.message ?? t('agentsMcpCard.revokeFailed')); }
  }

  const snippets = {
    'claude-ai': mcpUrl,
    'claude-code': `claude mcp add --transport http qvestium ${mcpUrl} \\\n  --header "Authorization: Bearer ${tokenText}"`,
    'claude-desktop': JSON.stringify({ mcpServers: { qvestium: { command: 'npx', args: ['-y', 'mcp-remote', mcpUrl, '--header', `Authorization:Bearer ${tokenText}`] } } }, null, 2),
    other: JSON.stringify({ mcpServers: { qvestium: { type: 'http', url: mcpUrl, headers: { Authorization: `Bearer ${tokenText}` } } } }, null, 2),
  };
  const hints = {
    'claude-ai': t('agentsMcpCard.hints.claudeAi'),
    'claude-code': t('agentsMcpCard.hints.claudeCode'),
    'claude-desktop': t('agentsMcpCard.hints.claudeDesktop'),
    other: t('agentsMcpCard.hints.generic'),
  };

  return (
    <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 6, padding: 20 }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: T.rose, marginBottom: 12, textTransform: 'uppercase', letterSpacing: 1 }}>{t('agentsMcpCard.title')}</div>
      <div style={{ fontSize: 13, color: T.textSub, lineHeight: 1.6 }}>
        {tn('agentsMcpCard.intro', { mcp: <strong>MCP</strong> })}
      </div>

      <div style={{ fontSize: 12, color: T.textSub, marginTop: 12, lineHeight: 1.5 }}>
        {tn('agentsMcpCard.claudeAiNote', { claudeAi: <strong>Claude.ai</strong> })}
      </div>
      <div style={{ fontSize: 12, fontWeight: 600, color: T.text, marginTop: 16 }}>{t('agentsMcpCard.step1')}</div>
      {newToken ? (
        <div style={{ marginTop: 6 }}>
          <div style={{ fontSize: 12, color: T.danger }}>{t('agentsMcpCard.copyNow')}</div>
          <CopyField value={newToken} />
          <button onClick={() => setNewToken(null)} style={{ ...S.btn('ghost'), marginTop: 8, padding: '6px 12px' }}>{t('agentsMcpCard.done')}</button>
        </div>
      ) : (
        <div style={{ display: 'flex', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
          <input style={{ ...S.input, flex: '1 1 160px' }} value={name} onChange={e => setName(e.target.value)} placeholder={t('agentsMcpCard.namePlaceholder')} maxLength={60} />
          <button style={S.btn('primary')} onClick={create} disabled={busy || !name.trim()}>{busy ? '...' : t('agentsMcpCard.createToken')}</button>
        </div>
      )}
      {error && <div style={{ fontSize: 12, color: T.danger, marginTop: 6 }}>{error}</div>}

      <div style={{ fontSize: 12, fontWeight: 600, color: T.text, marginTop: 16 }}>{t('agentsMcpCard.step2')}</div>
      <div style={{ fontSize: 12, color: T.textSub, marginTop: 4 }}>{t('agentsMcpCard.serverAddress')}</div>
      <CopyField value={mcpUrl} />
      <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap' }}>
        {([['claude-ai', 'Claude.ai'], ['claude-code', 'Claude Code'], ['claude-desktop', 'Claude Desktop'], ['other', t('agentsMcpCard.otherClients')]] as const).map(([k, label]) => (
          <button key={k} onClick={() => setClient(k)} style={{ ...S.btn(client === k ? 'primary' : 'ghost'), padding: '6px 10px' }}>{label}</button>
        ))}
      </div>
      <CopyField value={snippets[client]} multiline />
      <div style={{ fontSize: 11, color: T.textMuted, marginTop: 6, lineHeight: 1.5 }}>{hints[client]}</div>

      <div style={{ fontSize: 12, fontWeight: 600, color: T.text, marginTop: 16 }}>{t('agentsMcpCard.activeTokens')}</div>
      {tokens === null ? <div style={{ fontSize: 12, color: T.textMuted, marginTop: 6 }}>{t('common.loading')}</div>
        : tokens.length === 0 ? <div style={{ fontSize: 12, color: T.textMuted, marginTop: 6 }}>{t('agentsMcpCard.noneYet')}</div>
        : tokens.map(tok => (
          <div key={tok.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: `1px solid ${T.border}`, fontSize: 12 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600, color: T.text }}>{tok.name} <span style={{ fontFamily: 'ui-monospace, Menlo, monospace', color: T.textMuted, fontWeight: 400 }}>{tok.prefix}…</span></div>
              <div style={{ color: T.textMuted }}>{tok.lastUsedAt
                ? t('agentsMcpCard.tokenMetaUsed', { created: new Date(tok.createdAt).toLocaleDateString(locale()), used: new Date(tok.lastUsedAt).toLocaleString(locale(), { dateStyle: 'short', timeStyle: 'short' }) })
                : t('agentsMcpCard.tokenMetaNeverUsed', { created: new Date(tok.createdAt).toLocaleDateString(locale()) })}</div>
            </div>
            <button onClick={() => revoke(tok)} style={{ ...S.btn('ghost'), padding: '4px 10px', fontSize: 11, color: T.danger }}>{t('agentsMcpCard.revoke')}</button>
          </div>
        ))}
    </div>
  );
}

/** Skatteuppgifter för rapportering till Skatteverket (DAC7, api/src/account/dac7.ts). */
function TaxInfoCard({ profile }: { profile: FashionProfile | null }) {
  const [status, setStatus] = useState<TaxInfoStatus | null>(null);
  const [editing, setEditing] = useState(false);
  const [kind, setKind] = useState<'individual' | 'entity'>(profile?.sellerType === 'business' ? 'entity' : 'individual');
  const [f, setF] = useState<Record<string, string>>({
    firstName: '', lastName: '', personnummer: '', legalName: profile?.businessName ?? '', orgNumber: profile?.businessOrgNumber ?? '',
    vatNumber: profile?.businessVatNumber ?? '', addressLine: profile?.addressStreet ?? '', postalCode: profile?.addressPostalCode ?? '', city: profile?.addressCity ?? '',
  });
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<{ text: string; error: boolean } | null>(null);
  useEffect(() => { api.taxInfoStatus().then(setStatus).catch(() => {}); }, []);

  async function save() {
    setBusy(true); setNotice(null);
    try {
      const body = kind === 'individual'
        ? { kind, firstName: f.firstName, lastName: f.lastName, personnummer: f.personnummer, addressLine: f.addressLine, postalCode: f.postalCode, city: f.city }
        : { kind, legalName: f.legalName, orgNumber: f.orgNumber, vatNumber: f.vatNumber, addressLine: f.addressLine, postalCode: f.postalCode, city: f.city };
      setStatus(await api.saveTaxInfo(body));
      setEditing(false); setF(x => ({ ...x, personnummer: '' }));
      setNotice({ text: t('taxInfo.saved'), error: false });
    } catch (e: any) { setNotice({ text: e.message, error: true }); }
    finally { setBusy(false); }
  }

  const input = (key: string, label: string, extra: React.InputHTMLAttributes<HTMLInputElement> = {}) => (
    <input style={S.input} value={f[key]} onChange={e => setF(x => ({ ...x, [key]: e.target.value }))} placeholder={label} {...extra} />
  );
  const info = status?.info;
  // Utan krypteringsnyckel kan uppgifterna inte sparas — visa inget (api/src/lib/fieldCrypto.ts).
  if (!status?.available) return null;
  const fmtDay = (iso: string) => new Date(iso).toLocaleDateString(locale(), { dateStyle: 'medium' });

  return (
    <div style={{ background: T.surface, border: `1px solid ${status?.required ? '#f0ad4e' : T.border}`, borderRadius: 6, padding: 20 }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: T.rose, marginBottom: 12, textTransform: 'uppercase', letterSpacing: 1 }}>{t('taxInfo.title')}</div>
      <div style={{ fontSize: 13, color: T.textSub, lineHeight: 1.6, marginBottom: 12 }}>{t('taxInfo.intro')}</div>
      {status?.held && <div style={{ fontSize: 12, color: T.danger, marginBottom: 10 }}>{t('taxInfo.heldNotice')}</div>}
      {status?.required && !status.held && status.deadline && <div style={{ fontSize: 12, color: '#7a5200', background: '#fff8e1', padding: '8px 10px', marginBottom: 10 }}>{t('taxInfo.requiredNotice', { date: fmtDay(status.deadline) })}</div>}
      {info && !editing && (
        <div style={{ fontSize: 13, color: T.text, marginBottom: 10 }}>
          {t('taxInfo.submitted', { date: fmtDay(info.submittedAt), name: info.kind === 'entity' ? info.legalName ?? '' : `${info.firstName ?? ''} ${info.lastName ?? ''}`.trim(), tin: info.tinMasked })}
        </div>
      )}
      {editing ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ display: 'flex', gap: 18 }}>
            {(['individual', 'entity'] as const).map(k => (
              <label key={k} style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13, cursor: 'pointer' }}>
                <input type="radio" checked={kind === k} onChange={() => setKind(k)} />{t(`taxInfo.${k}`)}
              </label>
            ))}
          </div>
          {kind === 'individual' ? (
            <>
              <div style={{ display: 'flex', gap: 8 }}>{input('firstName', t('taxInfo.firstName'))}{input('lastName', t('taxInfo.lastName'))}</div>
              {input('personnummer', t('taxInfo.personnummer'), { inputMode: 'numeric', autoComplete: 'off' })}
            </>
          ) : (
            <>
              {input('legalName', t('taxInfo.legalName'))}
              {input('orgNumber', t('taxInfo.orgNumber'), { inputMode: 'numeric' })}
              {input('vatNumber', t('taxInfo.vatNumber'))}
            </>
          )}
          {input('addressLine', t('taxInfo.address'))}
          <div style={{ display: 'flex', gap: 8 }}>{input('postalCode', t('taxInfo.postalCode'))}{input('city', t('taxInfo.city'))}</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button style={S.btn('primary')} disabled={busy} onClick={save}>{busy ? '...' : t('taxInfo.save')}</button>
            <button style={S.btn('ghost')} disabled={busy} onClick={() => setEditing(false)}>{t('common.cancel')}</button>
          </div>
        </div>
      ) : (
        <button style={S.btn(status?.required ? 'primary' : 'ghost')} onClick={() => { setEditing(true); setNotice(null); }}>{info ? t('taxInfo.update') : t('taxInfo.show')}</button>
      )}
      {notice && <div style={{ fontSize: 12, color: notice.error ? T.danger : T.success, marginTop: 8 }}>{notice.text}</div>}
    </div>
  );
}

/** Ladda ner sina uppgifter och radera kontot (api/src/account/dataRights.ts). */
function MyDataCard({ onDeleted }: { onDeleted: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [typed, setTyped] = useState('');

  async function download() {
    setBusy(true); setError('');
    try { await api.exportMyData(); } catch (e: any) { setError(e.message); } finally { setBusy(false); }
  }
  async function startDelete() {
    setBusy(true); setError('');
    try {
      const check = await api.accountDeletionCheck();
      if (!check.canDelete) setError(t('myData.cannotDelete', { reasons: check.blockers.join(', ') }));
      else { setConfirming(true); setTyped(''); }
    } catch (e: any) { setError(e.message); } finally { setBusy(false); }
  }
  async function confirmDelete() {
    setBusy(true); setError('');
    try { await api.deleteMyAccount(); alert(t('myData.deleted')); onDeleted(); }
    catch (e: any) { setError(e.message); setBusy(false); }
  }

  return (
    <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 6, padding: 20 }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: T.rose, marginBottom: 12, textTransform: 'uppercase', letterSpacing: 1 }}>{t('myData.title')}</div>
      <div style={{ fontSize: 13, color: T.textSub, lineHeight: 1.6, marginBottom: 12 }}>{t('myData.intro')}</div>
      {confirming ? (
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: T.text, marginBottom: 6 }}>{t('myData.deleteTitle')}</div>
          <div style={{ fontSize: 12, color: T.textSub, lineHeight: 1.6, marginBottom: 10 }}>{t('myData.deleteBody')}</div>
          <input style={{ ...S.input, marginBottom: 8 }} value={typed} onChange={e => setTyped(e.target.value)} placeholder={t('myData.typeToConfirm')} autoCapitalize="characters" />
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button style={S.btn('danger')} disabled={busy || typed.trim() !== 'RADERA'} onClick={confirmDelete}>{busy ? '...' : t('myData.confirmDelete')}</button>
            <button style={S.btn('ghost')} disabled={busy} onClick={() => setConfirming(false)}>{t('common.cancel')}</button>
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button style={S.btn('ghost')} disabled={busy} onClick={download}>{t('myData.download')}</button>
          <button style={{ ...S.btn('ghost'), color: T.danger }} disabled={busy} onClick={startDelete}>{t('myData.delete')}</button>
        </div>
      )}
      {error && <div style={{ fontSize: 12, color: T.danger, marginTop: 10 }}>{error}</div>}
    </div>
  );
}

/** Privatperson eller företag — krävs innan plagg läggs ut (api/src/account/sellerType.ts). */
function SellerTypeCard({ profile, onSaved }: { profile: FashionProfile | null; onSaved: (p: FashionProfile) => void }) {
  const [type, setType] = useState<'private' | 'business' | null>(profile?.sellerTypeConfirmed ? profile.sellerType ?? null : null);
  const [name, setName] = useState(profile?.businessName ?? '');
  const [org, setOrg] = useState(profile?.businessOrgNumber ?? '');
  const [vat, setVat] = useState(profile?.businessVatNumber ?? '');
  const [address, setAddress] = useState(profile?.businessAddress ?? '');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<{ text: string; error: boolean } | null>(null);

  async function save() {
    if (!type) return;
    setBusy(true); setNotice(null);
    try {
      const updated = await api.saveProfileStrict(type === 'private'
        ? { sellerType: 'private' }
        : { sellerType: 'business', businessName: name, businessOrgNumber: org, businessVatNumber: vat, businessAddress: address });
      onSaved(updated);
      setNotice({ text: t('sellerType.saved'), error: false });
    } catch (e: any) { setNotice({ text: e.message, error: true }); }
    finally { setBusy(false); }
  }

  const radio = (value: 'private' | 'business') => (
    <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13, color: T.text, cursor: 'pointer' }}>
      <input type="radio" checked={type === value} onChange={() => { setType(value); setNotice(null); }} />
      {t(`sellerType.${value}`)}
    </label>
  );

  return (
    <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 6, padding: 20 }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: T.rose, marginBottom: 12, textTransform: 'uppercase', letterSpacing: 1 }}>{t('sellerType.title')}</div>
      <div style={{ fontSize: 13, color: T.textSub, lineHeight: 1.6, marginBottom: 12 }}>{t('sellerType.intro')}</div>
      <div style={{ display: 'flex', gap: 18, marginBottom: 12 }}>{radio('private')}{radio('business')}</div>
      {type === 'business' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
          <input style={S.input} value={name} onChange={e => setName(e.target.value)} placeholder={t('sellerType.businessName')} />
          <input style={S.input} value={org} onChange={e => setOrg(e.target.value)} placeholder={t('sellerType.orgNumber')} inputMode="numeric" />
          <input style={S.input} value={vat} onChange={e => setVat(e.target.value)} placeholder={t('sellerType.vatNumber')} />
          <input style={S.input} value={address} onChange={e => setAddress(e.target.value)} placeholder={t('sellerType.address')} />
        </div>
      )}
      <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
        <button style={{ ...S.btn('primary'), padding: '8px 16px' }} disabled={busy || !type} onClick={save}>{busy ? '...' : t('sellerType.save')}</button>
        {notice && <span style={{ fontSize: 12, color: notice.error ? T.danger : T.success }}>{notice.text}</span>}
        {!notice && !profile?.sellerTypeConfirmed && <span style={{ fontSize: 12, color: T.textMuted }}>{t('sellerType.notSet')}</span>}
      </div>
    </div>
  );
}

function ProfileView({ user, profile, onProfileUpdate, onLogout }: {
  user: QidUser;
  profile: FashionProfile | null;
  onProfileUpdate: (p: FashionProfile) => void;
  onLogout: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    displayName: profile?.displayName ?? user.name ?? '',
    addressStreet: profile?.addressStreet ?? '',
    addressPostalCode: profile?.addressPostalCode ?? '',
    addressCity: profile?.addressCity ?? '',
    addressCountry: profile?.addressCountry ?? 'Sverige',
    shippingName: profile?.shippingName ?? '',
    phone: profile?.phone ?? '',
    bio: profile?.bio ?? '',
    size: profile?.size ?? 'M',
    bustCm: profile?.bustCm ?? '',
    waistCm: profile?.waistCm ?? '',
    hipCm: profile?.hipCm ?? '',
    stylePreferences: profile?.stylePreferences ?? [],
    favoriteBrands: profile?.favoriteBrands ?? [],
    notes: profile?.notes ?? '',
  });
  const [brandInput, setBrandInput] = useState('');
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const avatarFileRef = useRef<HTMLInputElement>(null);
  const [uploadingBodyPhotos, setUploadingBodyPhotos] = useState(false);
  const bodyPhotoFileRef = useRef<HTMLInputElement>(null);

  const [connectStatus, setConnectStatus] = useState<{ onboarded: boolean; chargesEnabled: boolean; payoutsEnabled: boolean } | null>(null);
  const [identity, setIdentity] = useState<IdentityStatus | null>(null);
  const [identityLoading, setIdentityLoading] = useState(false);
  const [identityNotice, setIdentityNotice] = useState<{ text: string; isError: boolean } | null>(null);
  useEffect(() => {
    api.identityStatus().then(setIdentity);
    let result: string | null = null;
    try { result = sessionStorage.getItem('qvestium_identity_result'); sessionStorage.removeItem('qvestium_identity_result'); } catch { /* ignorera */ }
    const messages: Record<string, { text: string; isError: boolean }> = {
      verified: { text: t('profileView.identity.verified'), isError: false },
      test: { text: t('profileView.identity.test'), isError: false },
      cancelled: { text: t('profileView.identity.cancelled'), isError: true },
      failed: { text: t('profileView.identity.failed'), isError: true },
      expired: { text: t('profileView.identity.expired'), isError: true },
    };
    if (result && messages[result]) setIdentityNotice(messages[result]);
  }, []);

  async function handleIdentityStart() {
    setIdentityLoading(true);
    setIdentityNotice(null);
    try {
      const r = await api.identityStart();
      if (r.url) window.location.href = r.url; // samma flik — se handleConnectOnboard
      else if (r.already_verified) setIdentity(await api.identityStatus());
    } catch (err: any) {
      setIdentityNotice({ text: err?.message ?? t('profileView.identity.startFailed'), isError: true });
    } finally {
      setIdentityLoading(false);
    }
  }
  const [connectLoading, setConnectLoading] = useState(false);

  useEffect(() => {
    api.connectStatus().then(setConnectStatus);
  }, []);

  async function handleConnectOnboard() {
    setConnectLoading(true);
    try {
      const result = await api.connectOnboard();
      // Navigerar i samma flik (inte window.open) — window.open() efter ett
      // await tappar kopplingen till användarens klick och blockeras tyst av
      // popup-blockeraren i de flesta webbläsare. Stripes onboarding-länk har
      // ändå en return_url som tar användaren tillbaka hit när de är klara.
      if (result?.url) window.location.href = result.url;
      else alert(t('profileView.connectStartFailed'));
    } catch {
      alert(t('profileView.connectStartFailed'));
    } finally {
      setConnectLoading(false);
    }
  }

  async function handleAvatarUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingAvatar(true);
    try {
      const result = await api.uploadFile(file);
      const updated = await api.updateProfile({ avatarUrl: result.url });
      if (updated) onProfileUpdate(updated);
    } catch {
      alert(t('profileView.avatarUploadFailed'));
    } finally {
      setUploadingAvatar(false);
      if (avatarFileRef.current) avatarFileRef.current.value = '';
    }
  }

  async function handleBodyPhotoUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? []);
    if (!files.length) return;
    setUploadingBodyPhotos(true);
    try {
      const urls: string[] = [];
      for (const file of files) {
        const result = await api.uploadFile(file);
        urls.push(result.url);
      }
      const updated = await api.updateProfile({ bodyPhotos: [...(profile?.bodyPhotos ?? []), ...urls] });
      if (updated) onProfileUpdate(updated);
    } catch {
      alert(t('profileView.bodyPhotosUploadFailed'));
    } finally {
      setUploadingBodyPhotos(false);
      if (bodyPhotoFileRef.current) bodyPhotoFileRef.current.value = '';
    }
  }

  async function handleRemoveBodyPhoto(url: string) {
    try {
      const updated = await api.updateProfile({ bodyPhotos: (profile?.bodyPhotos ?? []).filter(u => u !== url) });
      if (updated) onProfileUpdate(updated);
    } catch {
      alert(t('profileView.bodyPhotoRemoveFailed'));
    }
  }

  async function handleSave() {
    setSaving(true);
    try {
      const updated = await api.updateProfile({
        displayName: form.displayName.trim() || undefined,
        addressStreet: form.addressStreet.trim() || undefined,
        addressPostalCode: form.addressPostalCode.trim() || undefined,
        addressCity: form.addressCity.trim() || undefined,
        addressCountry: form.addressCountry.trim() || undefined,
        shippingName: form.shippingName.trim() || undefined,
        phone: form.phone.trim() || undefined,
        bio: form.bio || undefined,
        size: form.size,
        bustCm: form.bustCm ? Number(form.bustCm) : undefined,
        waistCm: form.waistCm ? Number(form.waistCm) : undefined,
        hipCm: form.hipCm ? Number(form.hipCm) : undefined,
        stylePreferences: form.stylePreferences,
        favoriteBrands: form.favoriteBrands,
        notes: form.notes || undefined,
      });
      if (updated) { onProfileUpdate(updated); setEditing(false); }
    } finally {
      setSaving(false);
    }
  }

  function toggleStyle(s: string) {
    setForm(p => ({
      ...p,
      stylePreferences: p.stylePreferences.includes(s) ? p.stylePreferences.filter(x => x !== s) : [...p.stylePreferences, s],
    }));
  }

  function addBrand() {
    const b = brandInput.trim();
    if (b && !form.favoriteBrands.includes(b)) {
      setForm(p => ({ ...p, favoriteBrands: [...p.favoriteBrands, b] }));
    }
    setBrandInput('');
  }

  return (
    <div style={{ maxWidth: 680, margin: '0 auto', padding: '24px 0' }}>
      {/* Bryter på smala skärmar: knapparna hamnar då under namnet i stället för utanför skärmen. */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 16, marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, minWidth: 0 }}>
          {/* Profilbild — rund, centrerad */}
          <div style={{ position: 'relative', width: 72, height: 72, flexShrink: 0 }}>
            <div style={{
              width: 72, height: 72, borderRadius: '50%', overflow: 'hidden',
              background: T.bg, border: `1px solid ${T.border}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              {profile?.avatarUrl ? (
                <img src={profile.avatarUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center' }} />
              ) : (
                <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke={T.textMuted} strokeWidth="1.8"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
              )}
            </div>
            <button
              type="button"
              onClick={() => avatarFileRef.current?.click()}
              disabled={uploadingAvatar}
              title={t('profileView.uploadAvatar')}
              style={{
                position: 'absolute', bottom: -2, right: -2, width: 24, height: 24, borderRadius: '50%',
                background: T.rose, border: `2px solid ${T.surface}`, color: '#fff', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, padding: 0,
              }}
            >
              {uploadingAvatar ? '...' : '+'}
            </button>
            <input ref={avatarFileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleAvatarUpload} />
          </div>
          <div style={{ minWidth: 0, overflowWrap: 'anywhere' }}>
            {editing ? (
              <input
                style={{ ...S.input, fontFamily: 'Georgia, "Times New Roman", serif', fontSize: 20, padding: '6px 10px', marginBottom: 4 }}
                value={form.displayName}
                onChange={e => setForm(p => ({ ...p, displayName: e.target.value }))}
                placeholder={t('profileView.displayNamePlaceholder')}
              />
            ) : (
              <div style={{ fontFamily: 'Georgia, "Times New Roman", serif', fontSize: 26, color: T.text }}>{profile?.displayName || user.name || user.email}</div>
            )}
            <div style={{ fontSize: 13, color: T.textMuted }}>{user.email}</div>
            {profile?.is_admin && <span style={{ ...S.badge(T.amber), marginTop: 4 }}>{t('profileView.adminBadge')}</span>}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button style={S.btn(editing ? 'ghost' : 'primary')} onClick={() => editing ? setEditing(false) : setEditing(true)}>
            {editing ? t('common.cancel') : t('profileView.editProfile')}
          </button>
          <button style={S.btn('ghost')} onClick={onLogout}>{t('profileView.logOut')}</button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16 }}>
        {/* Measurements */}
        <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 6, padding: 20 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: T.rose, marginBottom: 16, textTransform: 'uppercase', letterSpacing: 1 }}>{t('profileView.measurementsTitle')}</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 4 }}>{t('profileView.clothingSize')}</div>
              {editing ? (
                <select style={S.input} value={form.size} onChange={e => setForm(p => ({ ...p, size: e.target.value }))}>
                  {SIZES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              ) : (
                <div style={{ fontSize: 20, fontWeight: 700, color: T.text }}>{profile?.size ?? '—'}</div>
              )}
            </div>
            {(['bustCm', 'waistCm', 'hipCm'] as const).map((field, i) => {
              const labels = [t('profileView.bustCm'), t('profileView.waistCm'), t('profileView.hipCm')];
              return (
                <div key={field}>
                  <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 4 }}>{labels[i]}</div>
                  {editing ? (
                    <input style={S.input} type="number" value={form[field]} onChange={e => setForm(p => ({ ...p, [field]: e.target.value }))} placeholder="—" />
                  ) : (
                    <div style={{ fontSize: 16, fontWeight: 600, color: T.text }}>{(profile as any)?.[field] ? t('profileView.cmValue', { value: (profile as any)[field] }) : '—'}</div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Style Preferences */}
        <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 6, padding: 20 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: T.rose, marginBottom: 16, textTransform: 'uppercase', letterSpacing: 1 }}>{t('profileView.stylePrefsTitle')}</div>
          {editing ? (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {STYLE_PREFS.map(s => (
                <button key={s} style={S.tag(form.stylePreferences.includes(s))} onClick={() => toggleStyle(s)}>{vl('stylePref', s)}</button>
              ))}
            </div>
          ) : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {(profile?.stylePreferences ?? []).length > 0
                ? (profile?.stylePreferences ?? []).map(s => <span key={s} style={S.tag(true)}>{vl('stylePref', s)}</span>)
                : <span style={{ color: T.textMuted, fontSize: 13 }}>{t('profileView.noStylePrefs')}</span>}
            </div>
          )}
        </div>

        {/* Favorite Brands */}
        <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 6, padding: 20 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: T.rose, marginBottom: 16, textTransform: 'uppercase', letterSpacing: 1 }}>{t('profileView.favoriteBrandsTitle')}</div>
          {editing ? (
            <>
              <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                <input style={{ ...S.input, flex: 1 }} value={brandInput} onChange={e => setBrandInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && addBrand()} placeholder={t('profileView.addBrandPlaceholder')} />
                <button style={S.btn('primary')} onClick={addBrand}>+</button>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {form.favoriteBrands.map(b => (
                  <span key={b} style={{ ...S.tag(true), cursor: 'pointer' }} onClick={() => setForm(p => ({ ...p, favoriteBrands: p.favoriteBrands.filter(x => x !== b) }))}>
                    {b} ✕
                  </span>
                ))}
              </div>
            </>
          ) : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {(profile?.favoriteBrands ?? []).length > 0
                ? (profile?.favoriteBrands ?? []).map(b => <span key={b} style={S.tag(true)}>{b}</span>)
                : <span style={{ color: T.textMuted, fontSize: 13 }}>{t('profileView.noBrands')}</span>}
            </div>
          )}
        </div>

        {/* Bio */}
        <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 6, padding: 20 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: T.rose, marginBottom: 16, textTransform: 'uppercase', letterSpacing: 1 }}>{t('profileView.aboutMeTitle')}</div>
          {editing ? (
            <textarea style={{ ...S.input, minHeight: 80, resize: 'vertical' }} value={form.bio} onChange={e => setForm(p => ({ ...p, bio: e.target.value }))} placeholder={t('profileView.bioPlaceholder')} />
          ) : (
            <div style={{ fontSize: 13, color: T.textSub, lineHeight: 1.6 }}>{profile?.bio || <span style={{ color: T.textMuted }}>{t('profileView.noBio')}</span>}</div>
          )}
        </div>

        {/* Address */}
        <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 6, padding: 20 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: T.rose, marginBottom: 16, textTransform: 'uppercase', letterSpacing: 1 }}>{t('profileView.addressTitle')}</div>
          {editing ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <input
                style={S.input}
                value={form.shippingName}
                onChange={e => setForm(p => ({ ...p, shippingName: e.target.value }))}
                placeholder={t('profileView.shippingNamePlaceholder')}
                autoComplete="name"
              />
              <input
                style={S.input}
                value={form.addressStreet}
                onChange={e => setForm(p => ({ ...p, addressStreet: e.target.value }))}
                placeholder={t('profileView.streetPlaceholder')}
              />
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  style={{ ...S.input, flex: '0 0 110px' }}
                  value={form.addressPostalCode}
                  onChange={e => setForm(p => ({ ...p, addressPostalCode: e.target.value }))}
                  placeholder={t('profileView.postalCodePlaceholder')}
                />
                <input
                  style={{ ...S.input, flex: 1 }}
                  value={form.addressCity}
                  onChange={e => setForm(p => ({ ...p, addressCity: e.target.value }))}
                  placeholder={t('profileView.cityPlaceholder')}
                />
              </div>
              <input
                style={S.input}
                value={form.addressCountry}
                onChange={e => setForm(p => ({ ...p, addressCountry: e.target.value }))}
                placeholder={t('profileView.countryPlaceholder')}
              />
              <input
                style={S.input}
                type="tel"
                value={form.phone}
                onChange={e => setForm(p => ({ ...p, phone: e.target.value }))}
                placeholder={t('profileView.phonePlaceholder')}
                autoComplete="tel"
              />
              <div style={{ fontSize: 11, color: T.textMuted }}>{t('profileView.shippingDetailsHint')}</div>
            </div>
          ) : (
            <div style={{ fontSize: 13, color: T.textSub, lineHeight: 1.6 }}>
              {profile?.addressStreet || profile?.addressCity ? (
                <>
                  {profile?.shippingName && <div>{profile.shippingName}</div>}
                  {profile?.addressStreet && <div>{profile.addressStreet}</div>}
                  {(profile?.addressPostalCode || profile?.addressCity) && (
                    <div>{[profile?.addressPostalCode, profile?.addressCity].filter(Boolean).join(' ')}</div>
                  )}
                  {profile?.addressCountry && <div>{profile.addressCountry}</div>}
                  {profile?.phone && <div>{profile.phone}</div>}
                  {profile?.area ? (
                    <AreaMapCard area={profile.area} caption={t('profileView.areaCaption')} />
                  ) : (
                    <div style={{ fontSize: 11, color: T.textMuted, marginTop: 8 }}>{t('profileView.addressNotOnMap')}</div>
                  )}
                </>
              ) : (
                <span style={{ color: T.textMuted }}>{t('profileView.noAddress')}</span>
              )}
            </div>
          )}
        </div>

        <PushSettingsCard />

        <SellerTypeCard profile={profile} onSaved={onProfileUpdate} />

        <TaxInfoCard profile={profile} />

        <MyDataCard onDeleted={onLogout} />

        <AgentsMcpCard />

        {/* Identitet — BankID via Qid/Idura (krävs före första hyran, se docs/plan-skyddsmodell.md) */}
        <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 6, padding: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, gap: 12 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: T.rose, textTransform: 'uppercase', letterSpacing: 1 }}>{t('profileView.identityTitle')}</div>
            {/* Officiell logotyp från BankID (bankid_masterbrand_v2), oförändrad och på vit yta enligt deras varumärkesregler */}
            <img src="/bankid-logo.svg" alt="BankID" style={{ height: 36, width: 'auto', display: 'block', background: '#ffffff', padding: 4 }} />
          </div>
          {identityNotice && (
            <div style={{ fontSize: 13, marginBottom: 12, color: identityNotice.isError ? T.danger : T.success }}>{identityNotice.text}</div>
          )}
          {identity?.verified ? (
            <div style={{ fontSize: 13, color: '#5cb85c', fontWeight: 600 }}>
              {identity.name ? t('profileView.identity.verifiedWithName', { name: identity.name }) : t('profileView.identity.verifiedBadge')}
            </div>
          ) : (
            <>
              <div style={{ fontSize: 13, color: T.textSub, lineHeight: 1.6, marginBottom: 12 }}>
                {identity?.test
                  ? (identity.name ? t('profileView.identity.testVerifiedWithName', { name: identity.name }) : t('profileView.identity.testVerified'))
                  : t('profileView.identity.intro')}
              </div>
              <button style={S.btn('primary')} onClick={handleIdentityStart} disabled={identityLoading}>
                {identityLoading ? t('profileView.starting') : t('profileView.identity.verifyButton')}
              </button>
            </>
          )}
        </div>

        {/* Utbetalningskonto (Stripe Connect) */}
        <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 6, padding: 20 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: T.rose, marginBottom: 16, textTransform: 'uppercase', letterSpacing: 1 }}>{t('profileView.payoutTitle')}</div>
          {connectStatus?.payoutsEnabled ? (
            <div style={{ fontSize: 13, color: '#5cb85c', fontWeight: 600 }}>{t('profileView.payoutReady')}</div>
          ) : (
            <>
              <div style={{ fontSize: 13, color: T.textSub, lineHeight: 1.6, marginBottom: 12 }}>
                {connectStatus?.onboarded
                  ? t('profileView.payoutIncomplete')
                  : t('profileView.payoutIntro')}
              </div>
              <button style={S.btn('primary')} onClick={handleConnectOnboard} disabled={connectLoading}>
                {connectLoading ? t('profileView.starting') : connectStatus?.onboarded ? t('profileView.continueConnect') : t('profileView.connectPayout')}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Helkroppsbilder — används som modellbilder när AI genererar "på modell"-bilder för plagg */}
      <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 6, padding: 20, marginTop: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: T.rose, textTransform: 'uppercase', letterSpacing: 1 }}>{t('profileView.bodyPhotosTitle')}</div>
          <button type="button" style={{ ...S.btn('ghost'), padding: '5px 12px', fontSize: 12 }} onClick={() => bodyPhotoFileRef.current?.click()} disabled={uploadingBodyPhotos}>
            {uploadingBodyPhotos ? t('common.loading') : t('profileView.uploadBodyPhoto')}
          </button>
          <input ref={bodyPhotoFileRef} type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={handleBodyPhotoUpload} />
        </div>
        <div style={{ fontSize: 12, color: T.textMuted, marginBottom: 12 }}>
          {t('profileView.bodyPhotosIntro')}
        </div>
        {(profile?.bodyPhotos ?? []).length === 0 ? (
          <div style={{ fontSize: 13, color: T.textMuted }}>{t('profileView.noBodyPhotos')}</div>
        ) : (
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {(profile?.bodyPhotos ?? []).map(url => (
              <div key={url} style={{ position: 'relative' }}>
                <img src={url} alt="" style={{ width: 90, height: 120, objectFit: 'cover', objectPosition: 'center top', borderRadius: 4, border: `1px solid ${T.border}` }} />
                <button
                  type="button"
                  onClick={() => handleRemoveBodyPhoto(url)}
                  style={{ position: 'absolute', top: -6, right: -6, width: 20, height: 20, borderRadius: 5, background: T.danger, border: 'none', color: '#fff', fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                >×</button>
              </div>
            ))}
          </div>
        )}
      </div>

      {editing && (
        <div style={{ marginTop: 20, display: 'flex', gap: 10 }}>
          <button style={{ ...S.btn('ghost'), flex: 1, justifyContent: 'center' }} onClick={() => setEditing(false)}>{t('common.cancel')}</button>
          <button style={{ ...S.btn('primary'), flex: 2, justifyContent: 'center' }} onClick={handleSave} disabled={saving}>
            {saving ? t('common.saving') : t('profileView.saveProfile')}
          </button>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// ERBJUD PLAGG FORM (Sell your clothes)
// ─────────────────────────────────────────────────────────────────

function ErbjudPlaggView({ user, isAdmin, onCreated }: {
  user: QidUser | null;
  isAdmin: boolean;
  onCreated: () => void;
}) {
  const [form, setForm] = useState({
    title: '',
    subtitle: '',
    description: '',
    location: '',
    region: 'Skandinavien',
    brand: '',
    designer: '',
    category: 'Kvinna' as const,
    style: 'Klassisk' as const,
    season: '',
    price: '',
    priceUnit: 'kr/plagg',
    spotsTotal: '10',
    imageUrl: '',
    highlights: ['', '', ''],
    includes: ['', '', ''],
    featured: false,
    contactEmail: user?.email ?? '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  async function handleImageUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingImage(true);
    try {
      const result = await api.uploadFile(file);
      setForm(p => ({ ...p, imageUrl: result.url }));
    } catch { setError(t('erbjudPlaggView.imageUploadFailed')); }
    finally { setUploadingImage(false); }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!user) { setError(t('erbjudPlaggView.mustBeLoggedIn')); return; }
    if (!form.title || !form.location || !form.price) { setError(t('erbjudPlaggView.fillRequired')); return; }
    setSaving(true);
    setError('');
    try {
      await api.createFashionCollection({
        title: form.title,
        subtitle: form.subtitle || undefined,
        description: form.description || undefined,
        location: form.location,
        region: form.region,
        brand: form.brand || undefined,
        designer: form.designer || undefined,
        category: form.category,
        style: form.style,
        season: form.season || undefined,
        price: Number(form.price),
        priceUnit: form.priceUnit,
        spotsTotal: Number(form.spotsTotal) || 10,
        spotsAvailable: Number(form.spotsTotal) || 10,
        imageUrl: form.imageUrl || undefined,
        highlights: form.highlights.filter(Boolean),
        includes: form.includes.filter(Boolean),
        featured: form.featured,
        contactEmail: form.contactEmail || undefined,
        status: 'active',
      });
      setSuccess(true);
      setTimeout(() => { setSuccess(false); onCreated(); }, 2000);
    } catch (err: any) {
      setError(err.message ?? t('common.somethingWentWrong'));
    } finally {
      setSaving(false);
    }
  }

  if (!user) {
    return (
      <div style={{ textAlign: 'center', padding: '48px 24px', color: T.textMuted }}>
        <div style={{ fontSize: 36, marginBottom: 12 }}>🔒</div>
        <div style={{ fontSize: 16, fontWeight: 600, color: T.text, marginBottom: 8 }}>{t('erbjudPlaggView.loginTitle')}</div>
        <div style={{ fontSize: 13 }}>{t('erbjudPlaggView.loginSubtitle')}</div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div style={{ textAlign: 'center', padding: '48px 24px', color: T.textMuted }}>
        <div style={{ fontSize: 36, marginBottom: 12 }}>👗</div>
        <div style={{ fontSize: 16, fontWeight: 600, color: T.text, marginBottom: 8 }}>{t('erbjudPlaggView.interestedTitle')}</div>
        <div style={{ fontSize: 13, maxWidth: 360, margin: '0 auto', lineHeight: 1.6 }}>
          {tn('erbjudPlaggView.interestedBody', { email: <span style={{ color: T.rose }}>info@qvestium.com</span> })}
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} style={{ maxWidth: 680, margin: '0 auto', padding: '24px 0' }}>
      <div style={{ fontSize: 22, fontWeight: 800, color: T.text, marginBottom: 24 }}>{t('erbjudPlaggView.title')}</div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 14, marginBottom: 16 }}>
        <div style={{ gridColumn: '1/-1' }}>
          <div style={{ fontSize: 12, color: T.textSub, marginBottom: 4 }}>{t('erbjudPlaggView.fields.title')}</div>
          <input style={S.input} value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} placeholder={t('erbjudPlaggView.placeholders.title')} required />
        </div>
        <div style={{ gridColumn: '1/-1' }}>
          <div style={{ fontSize: 12, color: T.textSub, marginBottom: 4 }}>{t('erbjudPlaggView.fields.subtitle')}</div>
          <input style={S.input} value={form.subtitle} onChange={e => setForm(p => ({ ...p, subtitle: e.target.value }))} placeholder={t('erbjudPlaggView.placeholders.subtitle')} />
        </div>
        <div style={{ gridColumn: '1/-1' }}>
          <div style={{ fontSize: 12, color: T.textSub, marginBottom: 4 }}>{t('common.description')}</div>
          <textarea style={{ ...S.input, minHeight: 80, resize: 'vertical' }} value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} placeholder={t('erbjudPlaggView.placeholders.description')} />
        </div>
        <div>
          <div style={{ fontSize: 12, color: T.textSub, marginBottom: 4 }}>{t('erbjudPlaggView.fields.location')}</div>
          <input style={S.input} value={form.location} onChange={e => setForm(p => ({ ...p, location: e.target.value }))} placeholder={t('erbjudPlaggView.placeholders.location')} required />
        </div>
        <div>
          <div style={{ fontSize: 12, color: T.textSub, marginBottom: 4 }}>{t('erbjudPlaggView.fields.region')}</div>
          <input style={S.input} value={form.region} onChange={e => setForm(p => ({ ...p, region: e.target.value }))} placeholder={t('erbjudPlaggView.placeholders.region')} />
        </div>
        <div>
          <div style={{ fontSize: 12, color: T.textSub, marginBottom: 4 }}>{t('erbjudPlaggView.fields.brand')}</div>
          <input style={S.input} value={form.brand} onChange={e => setForm(p => ({ ...p, brand: e.target.value }))} placeholder={t('erbjudPlaggView.placeholders.brand')} />
        </div>
        <div>
          <div style={{ fontSize: 12, color: T.textSub, marginBottom: 4 }}>{t('erbjudPlaggView.fields.designer')}</div>
          <input style={S.input} value={form.designer} onChange={e => setForm(p => ({ ...p, designer: e.target.value }))} placeholder={t('erbjudPlaggView.placeholders.designer')} />
        </div>
        <div>
          <div style={{ fontSize: 12, color: T.textSub, marginBottom: 4 }}>{t('common.category')}</div>
          <select style={S.input} value={form.category} onChange={e => setForm(p => ({ ...p, category: e.target.value as any }))}>
            {['Kvinna', 'Man', 'Barn', 'Unisex'].map(c => <option key={c} value={c}>{vl('collectionCategory', c)}</option>)}
          </select>
        </div>
        <div>
          <div style={{ fontSize: 12, color: T.textSub, marginBottom: 4 }}>{t('erbjudPlaggView.fields.style')}</div>
          <select style={S.input} value={form.style} onChange={e => setForm(p => ({ ...p, style: e.target.value as any }))}>
            {['Klassisk', 'Modern', 'Streetwear', 'Formell', 'Casual', 'Bohemisk'].map(s => <option key={s} value={s}>{vl('style', s)}</option>)}
          </select>
        </div>
        <div>
          <div style={{ fontSize: 12, color: T.textSub, marginBottom: 4 }}>{t('erbjudPlaggView.fields.season')}</div>
          <input style={S.input} value={form.season} onChange={e => setForm(p => ({ ...p, season: e.target.value }))} placeholder={t('erbjudPlaggView.placeholders.season')} />
        </div>
        <div>
          <div style={{ fontSize: 12, color: T.textSub, marginBottom: 4 }}>{t('erbjudPlaggView.fields.price')}</div>
          <input style={S.input} type="number" value={form.price} onChange={e => setForm(p => ({ ...p, price: e.target.value }))} placeholder="0" required />
        </div>
        <div>
          <div style={{ fontSize: 12, color: T.textSub, marginBottom: 4 }}>{t('erbjudPlaggView.fields.priceUnit')}</div>
          <select style={S.input} value={form.priceUnit} onChange={e => setForm(p => ({ ...p, priceUnit: e.target.value }))}>
            {([['kr/plagg', t('erbjudPlaggView.priceUnits.perItem')], ['kr/set', t('erbjudPlaggView.priceUnits.perSet')], ['kr/klänning', t('erbjudPlaggView.priceUnits.perDress')], ['kr/kostym', t('erbjudPlaggView.priceUnits.perSuit')]] as const).map(([u, label]) => <option key={u} value={u}>{label}</option>)}
          </select>
        </div>
        <div>
          <div style={{ fontSize: 12, color: T.textSub, marginBottom: 4 }}>{t('erbjudPlaggView.fields.spotsTotal')}</div>
          <input style={S.input} type="number" value={form.spotsTotal} onChange={e => setForm(p => ({ ...p, spotsTotal: e.target.value }))} min="1" />
        </div>
        <div>
          <div style={{ fontSize: 12, color: T.textSub, marginBottom: 4 }}>{t('erbjudPlaggView.fields.contactEmail')}</div>
          <input style={S.input} type="email" value={form.contactEmail} onChange={e => setForm(p => ({ ...p, contactEmail: e.target.value }))} placeholder={t('erbjudPlaggView.placeholders.contactEmail')} />
        </div>
      </div>

      {/* Image upload */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 12, color: T.textSub, marginBottom: 4 }}>{t('erbjudPlaggView.fields.image')}</div>
        <div style={{ display: 'flex', gap: 8 }}>
          <input style={{ ...S.input, flex: 1 }} value={form.imageUrl} onChange={e => setForm(p => ({ ...p, imageUrl: e.target.value }))} placeholder="https://..." />
          <button type="button" style={S.btn('ghost')} onClick={() => fileRef.current?.click()} disabled={uploadingImage}>
            {uploadingImage ? '...' : t('common.upload')}
          </button>
        </div>
        <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleImageUpload} />
        {form.imageUrl && <img src={form.imageUrl} alt="" style={{ marginTop: 8, width: 120, height: 80, objectFit: 'cover', borderRadius: 3 }} />}
      </div>

      {/* Highlights */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 12, color: T.textSub, marginBottom: 4 }}>{t('erbjudPlaggView.fields.highlights')}</div>
        {form.highlights.map((h, i) => (
          <input key={i} style={{ ...S.input, marginBottom: 6 }} value={h} onChange={e => { const arr = [...form.highlights]; arr[i] = e.target.value; setForm(p => ({ ...p, highlights: arr })); }} placeholder={t('erbjudPlaggView.placeholders.highlight', { n: i + 1 })} />
        ))}
      </div>

      {/* Includes */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 12, color: T.textSub, marginBottom: 4 }}>{t('erbjudPlaggView.fields.includes')}</div>
        {form.includes.map((inc, i) => (
          <input key={i} style={{ ...S.input, marginBottom: 6 }} value={inc} onChange={e => { const arr = [...form.includes]; arr[i] = e.target.value; setForm(p => ({ ...p, includes: arr })); }} placeholder={t('erbjudPlaggView.placeholders.include', { n: i + 1 })} />
        ))}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20 }}>
        <input type="checkbox" checked={form.featured} onChange={e => setForm(p => ({ ...p, featured: e.target.checked }))} id="featured" />
        <label htmlFor="featured" style={{ fontSize: 13, color: T.textSub, cursor: 'pointer' }}>{t('erbjudPlaggView.featured')}</label>
      </div>

      {error && <div style={{ color: T.danger, fontSize: 13, padding: '8px 12px', background: `${T.danger}15`, borderRadius: 3, marginBottom: 12 }}>{error}</div>}
      {success && <div style={{ color: T.success, fontSize: 13, padding: '8px 12px', background: `${T.success}15`, borderRadius: 3, marginBottom: 12 }}>{t('erbjudPlaggView.published')}</div>}

      <button type="submit" style={{ ...S.btn('primary'), width: '100%', justifyContent: 'center', padding: '12px' }} disabled={saving}>
        {saving ? t('erbjudPlaggView.publishing') : t('erbjudPlaggView.title')}
      </button>
    </form>
  );
}

// ─────────────────────────────────────────────────────────────────
// ADMIN (kommersiell översikt, transaktioner, bokföring)
// ─────────────────────────────────────────────────────────────────

type AdminSubTab = 'dashboard' | 'drift' | 'arenden' | 'crm' | 'marknadsforing' | 'transaktioner' | 'bokforing' | 'erbjud';

function KpiCard({ label, value, highlight }: { label: string; value: string | number; highlight?: boolean }) {
  return (
    <div style={{ border: `1px solid ${T.border}`, borderRadius: 6, padding: '16px 18px', background: highlight ? T.roseDim : '#fff' }}>
      <div style={{ fontSize: 11, color: T.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color: T.text }}>{value}</div>
    </div>
  );
}

function downloadCsv(filename: string, header: string[], rows: (string | number)[][]) {
  const escape = (c: string | number) => `"${String(c).replace(/"/g, '""')}"`;
  const csv = [header, ...rows].map(r => r.map(escape).join(';')).join('\n');
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// ─────────────────────────────────────────────────────────────────
// BOKFÖRING I FORTNOX (admin → Bokföring)
// Kopplar Fortnox, visar dagens verifikationsförslag (en per dag och
// kategori) och bokför dem med en knapp. API: /api/admin/fortnox
// (apps/api/src/routes/fortnox.ts, bookkeeping/).
// ─────────────────────────────────────────────────────────────────

const FORTNOX_ACCOUNT_KEYS: (keyof FortnoxAccountPlan)[] = ['cash', 'sellerLiability', 'insuranceLiability', 'protectionFeeRevenue', 'commissionRevenue', 'featureRevenue', 'shippingRevenue', 'outputVat'];

function sumDebit(rows: FortnoxVoucherRow[]) {
  return rows.reduce((s, r) => s + r.debit, 0);
}

function FortnoxRows({ rows }: { rows: FortnoxVoucherRow[] }) {
  const cell: React.CSSProperties = { padding: '4px 8px', borderBottom: `1px solid ${T.border}`, fontSize: 12 };
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ borderCollapse: 'collapse', width: '100%', marginTop: 6 }}>
        <thead>
          <tr style={{ color: T.textMuted, textAlign: 'left' }}>
            <th style={cell}>{t('fortnox.account')}</th><th style={cell}>{t('fortnox.text')}</th>
            <th style={{ ...cell, textAlign: 'right' }}>{t('fortnox.debit')}</th><th style={{ ...cell, textAlign: 'right' }}>{t('fortnox.credit')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              <td style={cell}>{r.account}</td><td style={cell}>{r.description}</td>
              <td style={{ ...cell, textAlign: 'right' }}>{r.debit ? fmtKr(r.debit) : ''}</td>
              <td style={{ ...cell, textAlign: 'right' }}>{r.credit ? fmtKr(r.credit) : ''}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function FortnoxVoucherCard({ day, category, rows, right }: { day: string; category: FortnoxCategory; rows: FortnoxVoucherRow[]; right?: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ border: `1px solid ${T.border}`, borderRadius: 4, padding: '10px 12px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ fontSize: 13 }}>
          <strong>{day}</strong> · {t(`fortnox.category.${category}`)} · {t('common.kr', { amount: fmtKr(sumDebit(rows)) })}
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {right}
          <button type="button" style={{ ...S.btn('ghost'), padding: '4px 10px', fontSize: 11 }} onClick={() => setOpen(o => !o)}>
            {open ? t('fortnox.hideRows') : t('fortnox.showRows')}
          </button>
        </div>
      </div>
      {open && <FortnoxRows rows={rows} />}
    </div>
  );
}

function FortnoxPanel({ fromDate, toDate }: { fromDate: string; toDate: string }) {
  const today = new Date().toLocaleDateString('sv-SE');
  const from = fromDate || `${today.slice(0, 8)}01`;
  const to = toDate || today;
  const [status, setStatus] = useState<FortnoxStatus | null>(null);
  const [preview, setPreview] = useState<FortnoxPreview | null>(null);
  const [series, setSeries] = useState('');
  const [accounts, setAccounts] = useState<FortnoxAccountPlan | null>(null);
  const [showSettings, setShowSettings] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [problems, setProblems] = useState<string[] | null>(null);

  async function load() {
    setError('');
    try {
      const s = await api.fortnox.status();
      setStatus(s);
      setSeries(s.voucherSeries);
      setAccounts(s.accounts);
      setPreview(await api.fortnox.preview(from, to));
    } catch (err: any) { setError(err.message ?? t('fortnox.loadFailed')); }
  }

  useEffect(() => { load(); }, [from, to]);

  // Tillbaka från Fortnox-inloggningen (?fortnox=ok|denied|failed|invalid_state).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const result = params.get('fortnox');
    if (!result) return;
    const text = result === 'ok' ? t('fortnox.resultOk')
      : result === 'denied' ? t('fortnox.resultDenied')
      : result === 'invalid_state' ? t('fortnox.resultInvalidState')
      : t('fortnox.resultFailed');
    (result === 'ok' ? setMessage : setError)(text);
    params.delete('fortnox');
    window.history.replaceState(null, '', `${window.location.pathname}?${params}`);
  }, []);

  async function run(name: string, fn: () => Promise<void>) {
    setBusy(name); setError(''); setMessage('');
    try { await fn(); } catch (err: any) { setError(err.message ?? t('fortnox.loadFailed')); }
    finally { setBusy(null); }
  }

  const connect = () => run('connect', async () => { window.location.href = (await api.fortnox.connect()).url; });
  const disconnect = () => { if (window.confirm(t('fortnox.confirmDisconnect'))) run('disconnect', async () => { await api.fortnox.disconnect(); await load(); }); };
  const saveSettings = () => run('save', async () => { await api.fortnox.saveSettings(series, accounts!); setMessage(t('fortnox.saved')); await load(); });
  const check = () => run('check', async () => { setProblems((await api.fortnox.check()).problems); });
  const toggleAutoBook = (enabled: boolean) => run('autobook', async () => {
    await api.fortnox.saveSettings(status!.voucherSeries, status!.accounts, enabled);
    setMessage(enabled ? t('fortnox.autoBookOn') : t('fortnox.autoBookOff'));
    await load();
  });
  const backfill = () => run('backfill', async () => { const { created } = await api.fortnox.backfill(); setMessage(t('fortnox.backfillDone', { count: created })); await load(); });
  const book = () => {
    const count = preview?.drafts.length ?? 0;
    if (!count || !window.confirm(t('fortnox.confirmBook', { count }))) return;
    run('book', async () => {
      const { vouchers } = await api.fortnox.book(from, to);
      const ok = vouchers.filter(v => v.status === 'booked').length;
      const failed = vouchers.length - ok;
      setMessage([t('fortnox.bookedResult', { count: ok }), failed ? t('fortnox.errorsResult', { count: failed }) : ''].filter(Boolean).join(' '));
      await load();
    });
  };
  const retry = (id: string) => run(`retry-${id}`, async () => { await api.fortnox.retry(id); await load(); });

  const box: React.CSSProperties = { border: `1px solid ${T.border}`, borderRadius: 6, padding: 16, marginBottom: 24, background: '#fff' };
  if (!status) return <div style={box}>{error ? <span style={{ color: T.danger, fontSize: 13 }}>{error}</span> : <span style={{ fontSize: 13, color: T.textMuted }}>{t('common.loading')}</span>}</div>;

  return (
    <div style={box}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <div style={{ maxWidth: 640 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: T.text, marginBottom: 4 }}>{t('fortnox.title')}</div>
          <div style={{ fontSize: 12, color: T.textSub }}>{t('fortnox.intro')}</div>
        </div>
        {status.configured && (status.connected ? (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12, color: T.success }}>✓ {status.companyName ? t('fortnox.connected', { company: status.companyName }) : t('fortnox.connectedUnknown')}</span>
            <button type="button" style={{ ...S.btn('ghost'), padding: '6px 12px', fontSize: 11 }} onClick={disconnect} disabled={!!busy}>{t('fortnox.disconnect')}</button>
          </div>
        ) : (
          <button type="button" style={S.btn('primary')} onClick={connect} disabled={!!busy}>{busy === 'connect' ? t('fortnox.connecting') : t('fortnox.connect')}</button>
        ))}
      </div>

      {!status.configured && <div style={{ fontSize: 12, color: T.danger, marginTop: 10 }}>{t('fortnox.notConfigured')}</div>}
      {status.configured && !status.connected && <div style={{ fontSize: 11, color: T.textMuted, marginTop: 8 }}>{t('fortnox.connectHint')}</div>}
      {status.connected && (
        <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginTop: 12, fontSize: 13, color: T.text, cursor: 'pointer' }}>
          <input type="checkbox" checked={status.autoBookEnabled} disabled={!!busy} onChange={e => toggleAutoBook(e.target.checked)} style={{ marginTop: 3 }} />
          <span>
            {t('fortnox.autoBook')}
            <span style={{ display: 'block', fontSize: 11, color: T.textMuted }}>
              {status.autoBookLastDay ? t('fortnox.autoBookLast', { day: status.autoBookLastDay }) : t('fortnox.autoBookHint')}
            </span>
          </span>
        </label>
      )}
      {message && <div style={{ fontSize: 13, color: T.text, marginTop: 10 }}>{message}</div>}
      {error && <div style={{ fontSize: 13, color: T.danger, marginTop: 10 }}>{error}</div>}

      <div style={{ display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap' }}>
        <button type="button" style={{ ...S.btn('ghost'), padding: '6px 12px', fontSize: 11 }} onClick={() => setShowSettings(v => !v)}>{t('fortnox.settings')}</button>
        <button type="button" style={{ ...S.btn('ghost'), padding: '6px 12px', fontSize: 11 }} onClick={backfill} disabled={!!busy}>{t('fortnox.backfill')}</button>
      </div>

      {showSettings && accounts && (
        <div style={{ marginTop: 12, padding: 12, background: T.bg, borderRadius: 4 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 10 }}>
            <label style={{ fontSize: 12, color: T.textSub }}>
              {t('fortnox.series')}
              <input style={{ ...S.input, marginTop: 4 }} value={series} maxLength={3} onChange={e => setSeries(e.target.value.toUpperCase())} />
            </label>
            {FORTNOX_ACCOUNT_KEYS.map(key => (
              <label key={key} style={{ fontSize: 12, color: T.textSub }}>
                {t(`fortnox.accounts.${key}`)}
                <input style={{ ...S.input, marginTop: 4 }} inputMode="numeric" value={accounts[key]}
                  onChange={e => setAccounts(a => ({ ...a!, [key]: Number(e.target.value.replace(/\D/g, '').slice(0, 4)) || 0 }))} />
              </label>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
            <button type="button" style={{ ...S.btn('primary'), padding: '6px 14px', fontSize: 11 }} onClick={saveSettings} disabled={!!busy}>{t('fortnox.save')}</button>
            <button type="button" style={{ ...S.btn('ghost'), padding: '6px 14px', fontSize: 11 }} onClick={() => setAccounts(status.defaultAccounts)}>{t('fortnox.resetDefaults')}</button>
            {status.connected && <button type="button" style={{ ...S.btn('ghost'), padding: '6px 14px', fontSize: 11 }} onClick={check} disabled={!!busy}>{t('fortnox.check')}</button>}
          </div>
          {problems && (
            <div style={{ fontSize: 12, marginTop: 10, color: problems.length ? T.danger : T.text }}>
              {problems.length ? problems.map(p => <div key={p}>• {p}</div>) : t('fortnox.checkOk')}
            </div>
          )}
        </div>
      )}

      <div style={{ marginTop: 18 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 8 }}>
          <div style={{ fontSize: 14, fontWeight: 600 }}>{t('fortnox.toBook')} <span style={{ fontWeight: 400, fontSize: 12, color: T.textMuted }}>{t('fortnox.period', { from, to })}</span></div>
          {preview && preview.drafts.length > 0 && (
            <button type="button" style={S.btn('primary')} onClick={book} disabled={!!busy || !status.connected}>
              {busy === 'book' ? t('fortnox.booking') : t('fortnox.book', { count: preview.drafts.length })}
            </button>
          )}
        </div>
        {preview && preview.backfilledCount > 0 && <div style={{ fontSize: 12, color: T.textSub, marginBottom: 8 }}>{t('fortnox.backfillNote', { count: preview.backfilledCount })}</div>}
        {preview && preview.drafts.length === 0 && <div style={{ fontSize: 13, color: T.textMuted }}>{t('fortnox.nothingToBook')}</div>}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {preview?.drafts.map(d => <FortnoxVoucherCard key={`${d.day}-${d.category}`} day={d.day} category={d.category} rows={d.rows} />)}
        </div>
      </div>

      {preview && preview.vouchers.length > 0 && (
        <div style={{ marginTop: 18 }}>
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>{t('fortnox.booked')}</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {preview.vouchers.map(v => (
              <FortnoxVoucherCard key={v.id} day={v.day} category={v.category} rows={v.rows} right={
                <>
                  <span style={{ fontSize: 12, color: v.status === 'error' ? T.danger : T.textSub }}>
                    {v.status === 'booked' ? `${v.fortnoxSeries}${v.fortnoxNumber}` : t(`fortnox.status.${v.status}`)}
                    {v.error ? ` — ${v.error}` : ''}
                  </span>
                  {v.status === 'error' && !v.fortnoxNumber && (
                    <button type="button" style={{ ...S.btn('ghost'), padding: '4px 10px', fontSize: 11 }} onClick={() => retry(v.id)} disabled={!!busy}>{t('fortnox.retry')}</button>
                  )}
                </>
              } />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function AdminView({ user, isAdmin, onProductCreated, initialSubTab }: {
  user: QidUser | null;
  isAdmin: boolean;
  onProductCreated: () => void;
  initialSubTab?: string | null;
}) {
  const [subTab, setSubTab] = useState<AdminSubTab>(
    ['dashboard', 'drift', 'arenden', 'crm', 'marknadsforing', 'transaktioner', 'bokforing', 'erbjud'].includes(initialSubTab ?? '') ? initialSubTab as AdminSubTab : 'dashboard');
  const [openTickets, setOpenTickets] = useState<number | null>(null);
  useEffect(() => { api.adminOpenTicketCount().then(setOpenTickets).catch(() => {}); }, [subTab]);
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [statsError, setStatsError] = useState('');
  const [transactions, setTransactions] = useState<WardrobeTransaction[]>([]);
  const [txLoading, setTxLoading] = useState(false);
  const [txError, setTxError] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [checkingId, setCheckingId] = useState<string | null>(null);
  const [checkMessage, setCheckMessage] = useState<{ id: string; text: string } | null>(null);

  useEffect(() => {
    api.adminStats().then(setStats).catch((err: any) => setStatsError(err.message ?? t('adminView.statsLoadFailed')));
  }, []);

  // Manuell Bring-koll (för test/felsökning) — se routes/shipping.ts
  // check-now. Bypassar avstämningsjobbets 30-min-intervall och dess
  // "väntat till förväntad leverans"-spärr, så man slipper vänta ~5 dagar
  // vid manuell testning av en sändning.
  async function handleCheckNow(transactionId: string) {
    setCheckingId(transactionId);
    setCheckMessage(null);
    try {
      const res = await api.checkShipmentNow(transactionId);
      const text = !res.found
        ? t('adminView.check.noUpdate')
        : (res.result as any)?.deduped
        ? t('adminView.check.alreadyUpdated')
        : t('adminView.check.newStatus', { status: (res.result as any)?.normalizedStatus ?? t('adminView.check.unknown') });
      setCheckMessage({ id: transactionId, text });
      await loadTransactions();
    } catch (err: any) {
      setCheckMessage({ id: transactionId, text: err.message ?? t('adminView.check.failed') });
    } finally {
      setCheckingId(null);
    }
  }

  async function loadTransactions() {
    setTxLoading(true);
    setTxError('');
    try {
      const rows = await api.adminTransactions({
        from: fromDate ? new Date(fromDate).toISOString() : undefined,
        to: toDate ? new Date(toDate + 'T23:59:59').toISOString() : undefined,
        status: statusFilter || undefined,
      });
      setTransactions(rows);
    } catch (err: any) { setTxError(err.message ?? t('adminView.txLoadFailed')); }
    finally { setTxLoading(false); }
  }

  useEffect(() => {
    if (subTab === 'transaktioner' || subTab === 'bokforing') loadTransactions();
  }, [subTab]);

  function exportCsv() {
    downloadCsv(
      t('adminView.csv.filename', { from: fromDate || t('adminView.csv.all'), to: toDate || t('adminView.csv.now') }),
      [t('adminView.csv.date'), t('adminView.csv.type'), t('adminView.csv.status'), t('adminView.csv.item'), t('adminView.csv.seller'), t('adminView.csv.buyer'), t('adminView.csv.price'), t('adminView.csv.commission'), t('adminView.csv.protection'), t('adminView.csv.shipping'), t('adminView.csv.payout')],
      transactions.map(tx => [
        // Fast ÅÅÅÅ-MM-DD oavsett språk — filen importeras i bokföringen.
        new Date(tx.createdAt).toLocaleDateString('sv-SE'),
        tx.type === 'buy' ? t('adminView.typeBuy') : t('adminView.typeRental'),
        TRANSACTION_STATUS_LABELS[tx.status]?.label ?? tx.status,
        tx.itemName ?? '',
        tx.sellerUserId,
        tx.buyerUserId,
        tx.itemPrice.toFixed(2),
        tx.commissionAmount.toFixed(2),
        (tx.protectionFee ?? 0).toFixed(2),
        tx.shippingCost.toFixed(2),
        tx.sellerPayout.toFixed(2),
      ]),
    );
  }

  if (!isAdmin) return null;

  const subTabs: { key: AdminSubTab; label: string }[] = [
    { key: 'dashboard', label: t('adminView.tabs.dashboard') },
    { key: 'drift', label: t('adminView.tabs.operations') },
    { key: 'arenden', label: openTickets ? t('adminView.tabs.ticketsCount', { count: openTickets }) : t('adminView.tabs.tickets') },
    { key: 'crm', label: t('adminView.tabs.crm') },
    { key: 'marknadsforing', label: t('adminView.tabs.marketing') },
    { key: 'transaktioner', label: t('adminView.tabs.transactions') },
    { key: 'bokforing', label: t('adminView.tabs.accounting') },
    { key: 'erbjud', label: t('adminView.tabs.offerItems') },
  ];

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: '24px 16px 64px' }}>
      <div style={{ fontFamily: 'Georgia, "Times New Roman", serif', fontSize: 26, color: T.text, marginBottom: 4 }}>{t('adminView.title')}</div>
      <div style={{ fontSize: 14, color: T.textSub, marginBottom: 20 }}>{t('adminView.subtitle')}</div>

      <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', marginBottom: 24, borderBottom: `1px solid ${T.border}` }}>
        {subTabs.map(st => (
          <button
            key={st.key}
            style={{
              background: 'transparent', border: 'none', cursor: 'pointer',
              padding: '0 0 12px', fontSize: 13, fontWeight: subTab === st.key ? 700 : 400,
              textTransform: 'uppercase' as const, letterSpacing: 0.5,
              color: subTab === st.key ? T.text : T.textSub,
              borderBottom: subTab === st.key ? `2px solid ${T.text}` : '2px solid transparent',
              marginBottom: -1,
            }}
            onClick={() => setSubTab(st.key)}
          >
            {st.label}
          </button>
        ))}
      </div>

      {subTab === 'dashboard' && (
        statsError ? (
          <div style={{ color: T.danger, fontSize: 13 }}>{statsError}</div>
        ) : !stats ? (
          <div style={{ textAlign: 'center', padding: 40, color: T.textMuted }}>{t('adminView.loadingStats')}</div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 16 }}>
            <KpiCard label={t('adminView.kpi.users')} value={stats.totalUsers} />
            <KpiCard label={t('adminView.kpi.activeListings')} value={stats.totalListings} />
            <KpiCard label={t('adminView.kpi.activeRentals')} value={stats.activeRentals} />
            <KpiCard label={t('adminView.kpi.totalOrders')} value={stats.totalTransactions} />
            <KpiCard label={t('adminView.kpi.pendingShipments')} value={stats.pendingShipments} />
            <KpiCard label={t('adminView.kpi.gmv')} value={t('common.kr', { amount: fmt(stats.grossMerchandiseValue) })} highlight />
            <KpiCard label={t('adminView.kpi.revenue')} value={t('common.kr', { amount: fmt(stats.totalCommission + stats.totalProtectionFees) })} highlight />
            <KpiCard label={t('adminView.kpi.ofWhichProtection')} value={t('common.kr', { amount: fmt(stats.totalProtectionFees) })} />
            <KpiCard label={t('adminView.kpi.sellerPayouts')} value={t('common.kr', { amount: fmt(stats.totalSellerPayouts) })} />
          </div>
        )
      )}

      {(subTab === 'transaktioner' || subTab === 'bokforing') && (
        <div>
          {subTab === 'bokforing' && (
            <div style={{ fontSize: 13, color: T.textSub, marginBottom: 12 }}>
              {t('adminView.accountingIntro')}
            </div>
          )}
          {subTab === 'bokforing' && (
            <div style={{ border: `1px solid ${T.border}`, borderRadius: 6, padding: 14, marginBottom: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: T.text, marginBottom: 4 }}>{t('taxInfo.adminTitle')}</div>
              <div style={{ fontSize: 12, color: T.textSub, lineHeight: 1.5, marginBottom: 8 }}>{t('taxInfo.adminIntro')}</div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {[new Date().getFullYear() - 1, new Date().getFullYear()].map(y => (
                  <button key={y} style={{ ...S.btn('ghost'), padding: '6px 12px' }} onClick={() => api.downloadDac7Report(y).catch(e => alert(e.message))}>
                    {t('taxInfo.adminDownload', { year: y })}
                  </button>
                ))}
              </div>
            </div>
          )}
          <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
            {subTab === 'bokforing' && (
              <>
                <label style={{ fontSize: 12, color: T.textSub, display: 'flex', alignItems: 'center', gap: 6 }}>
                  {t('adminView.from')}
                  <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} style={{ padding: '6px 8px', border: `1px solid ${T.border}`, borderRadius: 4, fontSize: 12 }} />
                </label>
                <label style={{ fontSize: 12, color: T.textSub, display: 'flex', alignItems: 'center', gap: 6 }}>
                  {t('adminView.to')}
                  <input type="date" value={toDate} onChange={e => setToDate(e.target.value)} style={{ padding: '6px 8px', border: `1px solid ${T.border}`, borderRadius: 4, fontSize: 12 }} />
                </label>
              </>
            )}
            {subTab === 'transaktioner' && (
              <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={{ padding: '7px 10px', border: `1px solid ${T.border}`, borderRadius: 4, fontSize: 12 }}>
                <option value="">{t('adminView.allStatuses')}</option>
                {Object.entries(TRANSACTION_STATUS_LABELS).map(([key, v]) => (
                  <option key={key} value={key}>{v.label}</option>
                ))}
              </select>
            )}
            <button style={S.btn('ghost')} onClick={loadTransactions} disabled={txLoading}>{txLoading ? t('adminView.fetching') : t('adminView.refresh')}</button>
            {subTab === 'bokforing' && (
              <button style={S.btn('primary')} onClick={exportCsv} disabled={transactions.length === 0}>
                {t('adminView.exportCsv', { count: transactions.length })}
              </button>
            )}
          </div>

          {subTab === 'bokforing' && <FortnoxPanel fromDate={fromDate} toDate={toDate} />}

          {subTab === 'bokforing' && transactions.length > 0 && (
            <div style={{ display: 'flex', gap: 24, marginBottom: 20, flexWrap: 'wrap', fontSize: 13 }}>
              <div>{tn('adminView.totals.revenue', { amount: <strong>{t('common.kr', { amount: fmt(transactions.reduce((s, tx) => s + tx.itemPrice, 0)) })}</strong> })}</div>
              <div>{tn('adminView.totals.commission', { amount: <strong>{t('common.kr', { amount: fmt(transactions.reduce((s, tx) => s + tx.commissionAmount, 0)) })}</strong> })}</div>
              <div>{tn('adminView.totals.protection', { amount: <strong>{t('common.kr', { amount: fmtKr(transactions.reduce((s, tx) => s + (tx.protectionFee ?? 0), 0)) })}</strong> })}</div>
              <div>{tn('adminView.totals.payouts', { amount: <strong>{t('common.kr', { amount: fmt(transactions.reduce((s, tx) => s + tx.sellerPayout, 0)) })}</strong> })}</div>
            </div>
          )}

          {txError && <div style={{ color: T.danger, fontSize: 13, marginBottom: 16 }}>{txError}</div>}
          {!txLoading && transactions.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 32, color: T.textMuted }}>
              <div style={{ fontSize: 13 }}>{t('adminView.noTransactions')}</div>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr style={{ borderBottom: `1px solid ${T.border}`, textAlign: 'left', color: T.textMuted }}>
                    <th style={{ padding: '8px 6px', fontWeight: 600 }}>{t('adminView.table.date')}</th>
                    <th style={{ padding: '8px 6px', fontWeight: 600 }}>{t('adminView.table.item')}</th>
                    <th style={{ padding: '8px 6px', fontWeight: 600 }}>{t('adminView.table.type')}</th>
                    <th style={{ padding: '8px 6px', fontWeight: 600 }}>{t('adminView.table.status')}</th>
                    <th style={{ padding: '8px 6px', fontWeight: 600 }}>{t('common.seller')}</th>
                    <th style={{ padding: '8px 6px', fontWeight: 600 }}>{t('common.buyer')}</th>
                    <th style={{ padding: '8px 6px', fontWeight: 600 }}>{t('common.price')}</th>
                    <th style={{ padding: '8px 6px', fontWeight: 600 }}>{t('adminView.table.commission')}</th>
                    <th style={{ padding: '8px 6px', fontWeight: 600 }}>{t('adminView.table.payout')}</th>
                    <th style={{ padding: '8px 6px', fontWeight: 600 }}>Bring</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.map(tx => {
                    const st = TRANSACTION_STATUS_LABELS[tx.status] ?? { label: tx.status, color: T.textMuted };
                    return (
                      <tr key={tx.id} style={{ borderBottom: `1px solid ${T.border}` }}>
                        <td style={{ padding: '8px 6px', whiteSpace: 'nowrap' }}>{new Date(tx.createdAt).toLocaleDateString(locale())}</td>
                        <td style={{ padding: '8px 6px' }}>{tx.itemName ?? '—'}</td>
                        <td style={{ padding: '8px 6px' }}>{tx.type === 'buy' ? t('adminView.typeBuy') : t('adminView.typeRental')}</td>
                        <td style={{ padding: '8px 6px' }}><span style={S.badge(st.color)}>{st.label}</span></td>
                        <td style={{ padding: '8px 6px', fontSize: 11, color: T.textMuted }}>{tx.sellerUserId}</td>
                        <td style={{ padding: '8px 6px', fontSize: 11, color: T.textMuted }}>{tx.buyerUserId}</td>
                        <td style={{ padding: '8px 6px', whiteSpace: 'nowrap' }}>{t('common.kr', { amount: fmt(tx.itemPrice) })}</td>
                        <td style={{ padding: '8px 6px', whiteSpace: 'nowrap' }}>{t('common.kr', { amount: fmt(tx.commissionAmount) })}</td>
                        <td style={{ padding: '8px 6px', whiteSpace: 'nowrap' }}>{t('common.kr', { amount: fmt(tx.sellerPayout) })}</td>
                        <td style={{ padding: '8px 6px', whiteSpace: 'nowrap' }}>
                          {tx.status === 'shipped' ? (
                            <button
                              style={{ ...S.btn('ghost'), padding: '4px 8px', fontSize: 10 }}
                              disabled={checkingId === tx.id}
                              onClick={() => handleCheckNow(tx.id)}
                            >
                              {checkingId === tx.id ? t('adminView.checking') : t('adminView.checkNow')}
                            </button>
                          ) : '—'}
                          {checkMessage?.id === tx.id && (
                            <div style={{ fontSize: 10, color: T.textMuted, marginTop: 4 }}>{checkMessage.text}</div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {subTab === 'drift' && <AdminDriftView />}
      {subTab === 'crm' && <AdminCrmView />}
      {subTab === 'marknadsforing' && <AdminMarketingView />}
      {subTab === 'arenden' && <AdminTicketsView onChanged={() => api.adminOpenTicketCount().then(setOpenTickets).catch(() => {})} />}

      {subTab === 'erbjud' && (
        <ErbjudPlaggView user={user} isAdmin={isAdmin} onCreated={onProductCreated} />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// MEDDELANDEN — direktmeddelanden mellan användare (api/src/messages).
// En konversation per motpart. Motparten visas med visningsnamn, aldrig
// e-postadress. Meddelanden som verkar handla om betalning utanför
// plattformen får en varning hos mottagaren.
// ─────────────────────────────────────────────────────────────────

const OFF_PLATFORM_WARNING = t('offPlatformWarning.text');

/** Annonsen ett meddelande skickades från — klick öppnar annonsen. */
function ChatItemCard({ item, alignRight }: { item: { id: string; name: string; imageUrl?: string | null }; alignRight: boolean }) {
  return (
    <a href={`/?item=${encodeURIComponent(item.id)}`} onClick={e => { e.preventDefault(); openListing(item.id); }}
      style={{ display: 'flex', gap: 10, alignItems: 'center', padding: 8, marginBottom: 4, border: `1px solid ${T.border}`, borderRadius: 10,
        background: T.surface, textDecoration: 'none', color: T.text, marginLeft: alignRight ? 'auto' : 0, maxWidth: 280 }}>
      {item.imageUrl
        ? <img src={item.imageUrl} alt="" style={{ width: 44, height: 56, objectFit: 'cover', borderRadius: 4, flexShrink: 0 }} />
        : <div style={{ width: 44, height: 56, background: T.surfaceHover, borderRadius: 4, flexShrink: 0 }} />}
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.name}</div>
        <div style={{ fontSize: 11, color: T.textSub, textDecoration: 'underline', marginTop: 2 }}>{t('chatItemCard.viewListing')}</div>
      </div>
    </a>
  );
}

function MessagesView({ user, initialConversationId, chatTarget, onTargetConsumed, onUnreadChanged }: {
  user: QidUser;
  initialConversationId: string | null;
  chatTarget: ChatTarget | null;
  onTargetConsumed: () => void;
  onUnreadChanged: () => void;
}) {
  const [conversations, setConversations] = useState<Conversation[] | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(initialConversationId);
  const [draft, setDraft] = useState<ChatTarget | null>(null); // ny konversation som inte skapats än
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [reportMessage, setReportMessage] = useState<ChatMessage | null>(null);
  const [narrow, setNarrow] = useState(() => window.innerWidth < 760);
  // Översättningar av motpartens meddelanden till valt språk (per meddelande-id).
  const [translations, setTranslations] = useState<Record<string, { text?: string; loading?: boolean; showOriginal?: boolean; error?: string }>>({});
  const bottomRef = useRef<HTMLDivElement>(null);

  async function toggleTranslation(m: ChatMessage) {
    const cur = translations[m.id];
    if (cur?.loading) return;
    if (cur?.text) {
      setTranslations(p => ({ ...p, [m.id]: { ...cur, showOriginal: !cur.showOriginal } }));
      return;
    }
    setTranslations(p => ({ ...p, [m.id]: { loading: true } }));
    try {
      const text = await api.translateMessage(m.id);
      setTranslations(p => ({ ...p, [m.id]: { text } }));
    } catch (err: any) {
      setTranslations(p => ({ ...p, [m.id]: { error: err?.message ?? t('messagesView.translateFailed') } }));
    }
  }

  useEffect(() => {
    const onResize = () => setNarrow(window.innerWidth < 760);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  async function loadConversations() {
    try { setConversations(await api.myConversations()); } catch (err: any) { setError(err?.message ?? t('messagesView.loadConversationsFailed')); }
  }
  useEffect(() => {
    loadConversations();
    const t = setInterval(loadConversations, 20_000);
    return () => clearInterval(t);
  }, []);

  // Öppna en chatt med en viss person (från annons/säljarprofil).
  useEffect(() => {
    if (!chatTarget || !conversations) return;
    const existing = conversations.find(c => c.otherUser.id.toLowerCase() === chatTarget.userId.toLowerCase());
    if (existing) { setSelectedId(existing.id); setDraft(chatTarget.itemId ? chatTarget : null); }
    else { setSelectedId(null); setDraft(chatTarget); }
    // Från en annons: börja meddelandet med plaggets namn. Annonskortet (bild +
    // länk) följer med meddelandet via itemId, så mottagaren ser vilket plagg det gäller.
    if (chatTarget.itemName) setText(prev => prev.trim() ? prev : t('messagesView.interestedPrefill', { item: chatTarget.itemName }));
    onTargetConsumed();
  }, [chatTarget, conversations]);

  const selected = conversations?.find(c => c.id === selectedId) ?? null;

  async function loadMessages(id: string, markRead: boolean) {
    try {
      const msgs = await api.conversationMessages(id);
      setMessages(prev => (prev.length === msgs.length && prev[prev.length - 1]?.id === msgs[msgs.length - 1]?.id ? prev : msgs));
      if (markRead) { await api.markConversationRead(id); onUnreadChanged(); }
    } catch (err: any) {
      setError(err?.message ?? t('messagesView.loadConversationFailed'));
    }
  }
  useEffect(() => {
    if (!selectedId) { setMessages([]); return; }
    setError('');
    loadMessages(selectedId, true).then(() => loadConversations());
    const t = setInterval(() => { if (document.visibilityState === 'visible') loadMessages(selectedId, true); }, 8_000);
    return () => clearInterval(t);
  }, [selectedId]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ block: 'end' }); }, [messages.length, selectedId]);

  async function send() {
    const body = text.trim();
    if (!body || sending) return;
    setSending(true); setError('');
    try {
      const itemId = draft?.itemId;
      const msg = selectedId
        ? await api.sendMessage({ conversationId: selectedId, body, itemId })
        : await api.sendMessage({ toUserId: draft!.userId, body, itemId });
      setText('');
      setDraft(null);
      if (!selectedId) setSelectedId(msg.conversationId);
      else setMessages(prev => [...prev, msg]);
      loadConversations();
    } catch (err: any) {
      setError(err?.message ?? t('messagesView.sendFailed'));
    } finally {
      setSending(false);
    }
  }

  async function toggleBlock(c: Conversation) {
    const block = !c.blockedByMe;
    if (block && !window.confirm(t('messagesView.confirmBlock', { name: c.otherUser.displayName }))) return;
    try { await api.setMessageBlock(c.otherUser.id, block); await loadConversations(); }
    catch (err: any) { setError(err?.message ?? t('messagesView.actionFailed')); }
  }

  const fmtTime = (iso: string) => {
    const d = new Date(iso); const today = new Date().toDateString() === d.toDateString();
    return today ? d.toLocaleTimeString(locale(), { hour: '2-digit', minute: '2-digit' }) : d.toLocaleDateString(locale(), { day: 'numeric', month: 'short' });
  };
  const avatar = (name: string, url?: string | null, size = 36) => url
    ? <img src={url} alt="" style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
    : <div style={{ width: size, height: size, borderRadius: '50%', background: T.surfaceHover, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: size * 0.4, fontWeight: 600, color: T.textSub, flexShrink: 0 }}>{name.charAt(0).toUpperCase()}</div>;

  const threadOpen = !!selectedId || !!draft;
  const headerName = selected?.otherUser.displayName ?? draft?.displayName ?? '';
  const contextItem = draft?.itemName ?? selected?.item?.name ?? null;

  const list = (
    <div style={{ flex: narrow ? '1 1 auto' : '0 0 300px', borderRight: narrow ? 'none' : `1px solid ${T.border}`, overflowY: 'auto', maxHeight: narrow ? 'none' : '70vh' }}>
      {!conversations ? <div style={{ padding: 16, fontSize: 13, color: T.textMuted }}>{t('common.loading')}</div>
        : conversations.length === 0 && !draft ? (
          <div style={{ padding: 16, fontSize: 13, color: T.textMuted, lineHeight: 1.6 }}>
            {tn('messagesView.empty', { action: <strong>{t('messagesView.emptyAction')}</strong> })}
          </div>
        ) : conversations.map(c => (
          <button key={c.id} onClick={() => { setSelectedId(c.id); setDraft(null); }}
            style={{ display: 'flex', gap: 10, width: '100%', textAlign: 'left', padding: '12px 14px', border: 'none', borderBottom: `1px solid ${T.border}`,
              background: c.id === selectedId ? T.surfaceHover : T.surface, cursor: 'pointer', alignItems: 'center' }}>
            {avatar(c.otherUser.displayName, c.otherUser.avatarUrl)}
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                <span style={{ fontSize: 14, fontWeight: c.unreadCount ? 700 : 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.otherUser.displayName}</span>
                <span style={{ fontSize: 11, color: T.textMuted, flexShrink: 0 }}>{fmtTime(c.updatedAt)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginTop: 2 }}>
                <span style={{ fontSize: 12, color: c.unreadCount ? T.text : T.textMuted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {c.lastMessage ? (c.lastMessage.mine ? t('messagesView.youPrefix', { body: c.lastMessage.body }) : c.lastMessage.body) : ''}
                </span>
                {c.unreadCount > 0 && <span style={{ background: T.rose, color: '#fff', borderRadius: 10, fontSize: 10, fontWeight: 700, padding: '0 6px', lineHeight: '16px', flexShrink: 0 }}>{c.unreadCount}</span>}
              </div>
            </div>
          </button>
        ))}
    </div>
  );

  const thread = threadOpen && (
    <div style={{ flex: '1 1 auto', display: 'flex', flexDirection: 'column', minWidth: 0, maxHeight: narrow ? 'none' : '70vh' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px', borderBottom: `1px solid ${T.border}` }}>
        {narrow && <button onClick={() => { setSelectedId(null); setDraft(null); }} style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', padding: '0 6px 0 0' }} aria-label={t('common.back')}>←</button>}
        {avatar(headerName, selected?.otherUser.avatarUrl, 32)}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 600 }}>{headerName}</div>
          {contextItem && <div style={{ fontSize: 11, color: T.textMuted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t('messagesView.regarding', { item: contextItem })}</div>}
        </div>
        {selected && (
          <button onClick={() => toggleBlock(selected)} style={{ ...S.btn('ghost'), padding: '4px 10px', fontSize: 11 }}>
            {selected.blockedByMe ? t('messagesView.unblock') : t('messagesView.block')}
          </button>
        )}
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: 14, display: 'flex', flexDirection: 'column', gap: 8, minHeight: 240 }}>
        <div style={{ fontSize: 11, color: T.textMuted, textAlign: 'center', marginBottom: 6 }}>
          {t('messagesView.keepOnPlatform')}
        </div>
        {messages.map(m => (
          <div key={m.id} style={{ alignSelf: m.mine ? 'flex-end' : 'flex-start', maxWidth: '78%' }}>
            {m.item && <ChatItemCard item={m.item} alignRight={m.mine} />}
            <div style={{ background: m.mine ? T.text : T.surfaceHover, color: m.mine ? '#fff' : T.text, padding: '8px 12px', borderRadius: 14,
              fontSize: 14, lineHeight: 1.45, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {translations[m.id]?.text && !translations[m.id]?.showOriginal ? translations[m.id]!.text : m.body}
            </div>
            {!m.mine && m.flaggedReason === 'off_platform_payment' && (
              <div style={{ marginTop: 4, padding: '6px 10px', borderRadius: 8, background: '#fff8e1', border: '1px solid #f0ad4e', fontSize: 11, lineHeight: 1.45, color: '#7a5200' }}>
                ⚠ {OFF_PLATFORM_WARNING}
              </div>
            )}
            <div style={{ fontSize: 10, color: T.textMuted, marginTop: 2, textAlign: m.mine ? 'right' : 'left' }}>
              {fmtTime(m.createdAt)}
              {!m.mine && (
                <button onClick={() => toggleTranslation(m)} disabled={translations[m.id]?.loading} style={{ background: 'none', border: 'none', color: T.textMuted, fontSize: 10, textDecoration: 'underline', cursor: 'pointer', marginLeft: 8, padding: 0 }}>
                  {translations[m.id]?.loading ? t('messagesView.translating')
                    : translations[m.id]?.text ? (translations[m.id]!.showOriginal ? t('messagesView.showTranslation') : t('messagesView.showOriginal'))
                    : t('messagesView.translate')}
                </button>
              )}
              {!m.mine && (
                <button onClick={() => setReportMessage(m)} style={{ background: 'none', border: 'none', color: T.textMuted, fontSize: 10, textDecoration: 'underline', cursor: 'pointer', marginLeft: 8, padding: 0 }}>
                  {t('messagesView.report')}
                </button>
              )}
              {translations[m.id]?.text && !translations[m.id]?.showOriginal && (
                <span style={{ marginLeft: 8, fontStyle: 'italic' }}>{t('messagesView.translatedNote')}</span>
              )}
              {translations[m.id]?.error && (
                <span style={{ marginLeft: 8, color: T.danger }}>{translations[m.id]!.error}</span>
              )}
            </div>
          </div>
        ))}
        {threadOpen && messages.length === 0 && (
          <div style={{ fontSize: 13, color: T.textMuted, textAlign: 'center', marginTop: 24 }}>{t('messagesView.firstMessage', { name: headerName })}</div>
        )}
        <div ref={bottomRef} />
      </div>

      {error && <div style={{ color: T.danger, fontSize: 12, padding: '0 14px 8px' }}>{error}</div>}
      {selected?.blockedByMe ? (
        <div style={{ padding: 14, fontSize: 12, color: T.textMuted, borderTop: `1px solid ${T.border}` }}>{t('messagesView.youBlocked', { name: headerName })}</div>
      ) : (
        <div style={{ display: 'flex', gap: 8, padding: 12, borderTop: `1px solid ${T.border}`, alignItems: 'flex-end' }}>
          <textarea
            value={text}
            onChange={e => setText(e.target.value.slice(0, 2000))}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder={t('messagesView.inputPlaceholder')}
            rows={2}
            style={{ ...S.input, flex: 1, resize: 'none', fontSize: 14 }}
          />
          <button style={{ ...S.btn('primary'), padding: '10px 16px' }} onClick={send} disabled={sending || !text.trim()}>
            {sending ? '...' : t('common.send')}
          </button>
        </div>
      )}
      {text.length > 1800 && <div style={{ fontSize: 11, color: T.textMuted, padding: '0 14px 8px', textAlign: 'right' }}>{text.length}/2000</div>}
    </div>
  );

  return (
    <div style={{ maxWidth: 1000, margin: '0 auto', padding: '24px 16px' }}>
      <div style={{ fontFamily: 'Georgia, "Times New Roman", serif', fontSize: 26, color: T.text, marginBottom: 16 }}>{t('messagesView.title')}</div>
      <div style={{ ...S.card, padding: 0, display: 'flex', overflow: 'hidden', minHeight: 360 }}>
        {narrow ? (threadOpen ? thread : list) : (<>{list}{thread || (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, color: T.textMuted, padding: 24 }}>{t('messagesView.selectConversation')}</div>
        )}</>)}
      </div>
      {reportMessage && (
        <SupportTicketModal kind="report" user={user} target={{ type: 'message', id: reportMessage.id, name: t('messagesView.reportTargetName', { name: headerName }) }} onClose={() => setReportMessage(null)} />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// ANMÄL / KONTAKTA OSS — öppet även för utloggade (DSA art. 16). Ärendet
// sparas och hanteras i Admin → Ärenden (se api/src/support/tickets.ts).
// ─────────────────────────────────────────────────────────────────

function SupportTicketModal({ kind, user, target, onClose }: {
  kind: 'report' | 'support';
  user: QidUser | null;
  target?: { type: 'wardrobe_item' | 'user' | 'message'; id: string; name: string };
  onClose: () => void;
}) {
  const [categories, setCategories] = useState<string[]>([]);
  const [category, setCategory] = useState('');
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [website, setWebsite] = useState(''); // honungsfälla, dold
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [doneNumber, setDoneNumber] = useState<string | null>(null);

  useEffect(() => {
    api.supportCategories().then(c => setCategories(kind === 'report' ? c.report : c.support)).catch(() => setError(t('supportTicketModal.loadFailed')));
  }, [kind]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    if (!category) { setError(t('supportTicketModal.chooseCategory')); return; }
    setSending(true);
    try {
      const res = await api.submitSupportTicket({
        kind, category, message, website: website || undefined,
        email: user ? undefined : email, name: user ? undefined : (name || undefined),
        targetType: target?.type, targetId: target?.id,
      });
      setDoneNumber(res.number);
      track(kind === 'report' ? 'Anmälan skickad' : 'Kontaktformulär skickat', { kategori: category });
    } catch (err: any) {
      setError(err?.message ?? t('supportTicketModal.submitFailed'));
    } finally {
      setSending(false);
    }
  }

  const title = kind === 'report' ? (target ? t('supportTicketModal.titleReportListing') : t('supportTicketModal.titleReportIllegal')) : t('supportTicketModal.titleContact');
  return (
    <div style={S.modal} onClick={onClose}>
      <div style={{ background: T.surface, borderRadius: 8, width: '100%', maxWidth: 480, border: `1px solid ${T.border}`, padding: 24, maxHeight: '90vh', overflowY: 'auto' }}
        onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <div style={{ fontSize: 18, fontWeight: 700 }}>{title}</div>
          <button style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: T.textMuted }} onClick={onClose} aria-label={t('common.close')}>×</button>
        </div>
        {doneNumber ? (
          <div>
            <div style={{ fontSize: 15, marginBottom: 8 }}>{tn('supportTicketModal.thanks', { number: <strong>{doneNumber}</strong> })}</div>
            <div style={{ fontSize: 13, color: T.textSub, lineHeight: 1.6, marginBottom: 16 }}>
              {kind === 'report'
                ? t('supportTicketModal.confirmationReport', { email: user?.email ?? email })
                : t('supportTicketModal.confirmationSupport', { email: user?.email ?? email })}
            </div>
            <button style={{ ...S.btn('primary'), width: '100%', justifyContent: 'center' }} onClick={onClose}>{t('common.close')}</button>
          </div>
        ) : (
          <form onSubmit={submit}>
            {target && <div style={{ fontSize: 13, color: T.textSub, marginBottom: 12 }}>{tn('supportTicketModal.regarding', { name: <strong>{target.name}</strong> })}</div>}
            {kind === 'report' && (
              <div style={{ fontSize: 12, color: T.textMuted, lineHeight: 1.6, marginBottom: 12 }}>
                {t('supportTicketModal.reportIntro')}
              </div>
            )}
            <label style={{ display: 'block', fontSize: 12, color: T.textSub, marginBottom: 4 }}>{t('common.category')}</label>
            <select style={{ ...S.input, marginBottom: 12 }} value={category} onChange={e => setCategory(e.target.value)} required>
              <option value="">{t('supportTicketModal.choose')}</option>
              {categories.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <label style={{ display: 'block', fontSize: 12, color: T.textSub, marginBottom: 4 }}>{kind === 'report' ? t('supportTicketModal.whatsWrong') : t('supportTicketModal.howCanWeHelp')}</label>
            <textarea style={{ ...S.input, minHeight: 120, resize: 'vertical', marginBottom: 12 }} value={message} onChange={e => setMessage(e.target.value)} maxLength={5000} required />
            {!user && (
              <>
                <label style={{ display: 'block', fontSize: 12, color: T.textSub, marginBottom: 4 }}>{t('supportTicketModal.yourEmail')}</label>
                <input style={{ ...S.input, marginBottom: 12 }} type="email" value={email} onChange={e => setEmail(e.target.value)} required />
                <label style={{ display: 'block', fontSize: 12, color: T.textSub, marginBottom: 4 }}>{t('supportTicketModal.nameOptional')}</label>
                <input style={{ ...S.input, marginBottom: 12 }} value={name} onChange={e => setName(e.target.value)} />
              </>
            )}
            <input type="text" name="website" value={website} onChange={e => setWebsite(e.target.value)} tabIndex={-1} autoComplete="off" aria-hidden="true"
              style={{ position: 'absolute', left: -10000, width: 1, height: 1, opacity: 0 }} />
            {error && <div style={{ color: T.danger, fontSize: 13, marginBottom: 12 }}>{error}</div>}
            <button type="submit" style={{ ...S.btn('primary'), width: '100%', justifyContent: 'center' }} disabled={sending}>
              {sending ? t('common.sending') : kind === 'report' ? t('supportTicketModal.sendReport') : t('common.send')}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// ADMIN: ÄRENDEN — anmälningar och supportärenden. Svar mejlas till
// användaren; interna anteckningar syns bara här. En anmälan stängs med ett
// beslut som mejlas till anmälaren (DSA art. 16.5).
// ─────────────────────────────────────────────────────────────────

const TICKET_STATUS: Record<SupportTicket['status'], { label: string; color: string }> = {
  open: { label: t('ticketStatus.open'), color: '#f0ad4e' },
  in_progress: { label: t('ticketStatus.inProgress'), color: '#5bc0de' },
  closed: { label: t('ticketStatus.closed'), color: T.textMuted },
};

// ─────────────────────────────────────────────────────────────────
// ADMIN → CRM: rekrytering av säljare från andra secondhandsajter
// (api/src/crm/leads.ts). Pipeline som en tavla, personlig inbjudningskod
// per lead och färdigt meddelande att skicka.
// ─────────────────────────────────────────────────────────────────

const CRM_STAGE_COLORS: Record<CrmStage, string> = {
  identified: '#8a8a8a', contacted: '#5bc0de', replied: '#f0ad4e', registered: '#5cb85c', active: '#2e7d32', lost: '#c0392b',
};
const CRM_OWNERS = ['Agnes', 'Lars'];
const crmPlatform = (p: string | null | undefined) =>
  t(`crm.platforms.${((CRM_PLATFORMS as readonly string[]).includes(p ?? '') ? p : 'other') as typeof CRM_PLATFORMS[number]}`);
const crmInviteLink = (code: string) => `https://qvestium.com/?invite=${code}`;
const crmName = (l: Pick<CrmLead, 'displayName' | 'handle'>) => l.displayName || (l.handle ? `@${l.handle}` : t('crm.unnamed'));

function AdminCrmView() {
  const [leads, setLeads] = useState<CrmLead[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [platform, setPlatform] = useState('');
  const [owner, setOwner] = useState('');
  const [priority, setPriority] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);
  const [mode, setMode] = useState<'none' | 'add' | 'import'>('none');
  const [dragId, setDragId] = useState<string | null>(null);

  async function load() {
    try { setLeads(await api.crm.leads()); setError(null); }
    catch (e: any) { setError(e.message ?? t('crm.loadFailed')); }
  }
  useEffect(() => { load(); }, []);

  async function move(id: string, stage: CrmStage) {
    setLeads(prev => prev?.map(l => l.id === id ? { ...l, stage } : l) ?? prev);
    try { await api.crm.update(id, { stage }); } catch (e: any) { setError(e.message); }
    load();
  }

  const today = new Date().toISOString().slice(0, 10);
  const filtered = (leads ?? []).filter(l =>
    (!search || `${l.handle ?? ''} ${l.displayName ?? ''} ${l.location ?? ''} ${l.categories ?? ''}`.toLowerCase().includes(search.toLowerCase()))
    && (!platform || l.platform === platform) && (!owner || l.owner === owner) && (!priority || l.priority === priority));
  const count = (st: CrmStage) => (leads ?? []).filter(l => l.stage === st).length;
  const contactedTotal = (leads ?? []).filter(l => l.stage !== 'identified').length;
  const converted = count('registered') + count('active');
  const select: React.CSSProperties = { ...S.input, width: 'auto', padding: '6px 10px', fontSize: 12 };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', alignItems: 'flex-start', marginBottom: 16 }}>
        <div style={{ fontSize: 13, color: T.textSub, maxWidth: 640 }}>
          {t('crm.intro')}
          {leads && leads.length > 0 && (
            <div style={{ marginTop: 6, color: T.text }}>
              {t('crm.funnel', { total: leads.length, contacted: contactedTotal, converted, active: count('active') })}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button style={{ ...S.btn(mode === 'add' ? 'primary' : 'ghost'), padding: '6px 14px', fontSize: 12 }} onClick={() => setMode(mode === 'add' ? 'none' : 'add')}>{t('crm.add')}</button>
          <button style={{ ...S.btn(mode === 'import' ? 'primary' : 'ghost'), padding: '6px 14px', fontSize: 12 }} onClick={() => setMode(mode === 'import' ? 'none' : 'import')}>{t('crm.import')}</button>
        </div>
      </div>

      {mode === 'add' && <CrmAddForm onDone={id => { setMode('none'); load(); if (id) setOpenId(id); }} />}
      {mode === 'import' && <CrmImport onDone={() => { setMode('none'); load(); }} />}
      {error && <div style={{ color: T.danger, fontSize: 13, marginBottom: 12 }}>{error}</div>}

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
        <input style={{ ...S.input, width: 220, padding: '6px 10px', fontSize: 12 }} placeholder={t('crm.search')} value={search} onChange={e => setSearch(e.target.value)} />
        <select style={select} value={platform} onChange={e => setPlatform(e.target.value)}>
          <option value="">{t('crm.allPlatforms')}</option>
          {CRM_PLATFORMS.map(p => <option key={p} value={p}>{t(`crm.platforms.${p}`)}</option>)}
        </select>
        <select style={select} value={owner} onChange={e => setOwner(e.target.value)}>
          <option value="">{t('crm.allOwners')}</option>
          {Array.from(new Set([...CRM_OWNERS, ...(leads ?? []).map(l => l.owner).filter(Boolean) as string[]])).map(o => <option key={o} value={o}>{o}</option>)}
        </select>
        <select style={select} value={priority} onChange={e => setPriority(e.target.value)}>
          <option value="">{t('crm.allPriorities')}</option>
          {['A', 'B', 'C'].map(p => <option key={p} value={p}>{p}</option>)}
        </select>
      </div>

      {!leads ? <div style={{ fontSize: 13, color: T.textMuted }}>{t('common.loading')}</div> : (
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${CRM_STAGES.length}, minmax(200px, 1fr))`, gap: 10, overflowX: 'auto', paddingBottom: 8 }}>
          {CRM_STAGES.map(stage => (
            <div key={stage}
              onDragOver={e => e.preventDefault()}
              onDrop={() => { if (dragId) move(dragId, stage); setDragId(null); }}
              style={{ background: T.surfaceHover, borderRadius: 8, padding: 8, minHeight: 160 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4, color: CRM_STAGE_COLORS[stage], margin: '2px 4px 8px' }}>
                <span>{t(`crm.stages.${stage}`)}</span><span>{count(stage)}</span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {filtered.filter(l => l.stage === stage).map(l => (
                  <div key={l.id} draggable onDragStart={() => setDragId(l.id)} onClick={() => setOpenId(l.id)}
                    style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 6, padding: 8, cursor: 'pointer', fontSize: 12 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 6 }}>
                      <strong style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{crmName(l)}</strong>
                      {l.priority && <span style={S.badge(l.priority === 'A' ? '#c0392b' : l.priority === 'B' ? '#f0ad4e' : '#8a8a8a')}>{l.priority}</span>}
                    </div>
                    <div style={{ color: T.textMuted, marginTop: 2 }}>
                      {[crmPlatform(l.platform), l.owner, l.noteCount ? t('crm.notesCount', { count: l.noteCount }) : null].filter(Boolean).join(' · ')}
                    </div>
                    {l.nextStepDate && (
                      <div style={{ marginTop: 4, color: l.nextStepDate < today && !['registered', 'active', 'lost'].includes(l.stage) ? T.danger : T.textSub }}>
                        {l.nextStepDate} {l.nextStep ?? ''}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {openId && <CrmLeadModal id={openId} onClose={() => setOpenId(null)} onChanged={load} />}
    </div>
  );
}

function CrmAddForm({ onDone }: { onDone: (id?: string) => void }) {
  const [url, setUrl] = useState('');
  const [platform, setPlatform] = useState('vinted');
  const [handle, setHandle] = useState('');
  const [owner, setOwner] = useState('');
  const [priority, setPriority] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      const lead = await api.crm.create({ ...(url.trim() ? { profileUrl: url.trim() } as any : { platform, handle: handle.trim() }), owner: owner || null, priority: (priority || null) as any });
      onDone(lead.id);
    } catch (err: any) { setError(err.message); } finally { setBusy(false); }
  }
  const field: React.CSSProperties = { ...S.input, padding: '6px 10px', fontSize: 12 };
  return (
    <form onSubmit={submit} style={{ ...S.card, padding: 14, marginBottom: 16, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
      <label style={{ fontSize: 11, color: T.textSub, flex: '2 1 260px' }}>{t('crm.profileUrl')}
        <input style={field} value={url} onChange={e => setUrl(e.target.value)} placeholder="https://www.vinted.se/member/…" autoFocus />
      </label>
      <span style={{ fontSize: 11, color: T.textMuted, alignSelf: 'center' }}>{t('crm.or')}</span>
      <label style={{ fontSize: 11, color: T.textSub }}>{t('crm.platform')}
        <select style={field} value={platform} onChange={e => setPlatform(e.target.value)} disabled={!!url.trim()}>
          {CRM_PLATFORMS.map(p => <option key={p} value={p}>{t(`crm.platforms.${p}`)}</option>)}
        </select>
      </label>
      <label style={{ fontSize: 11, color: T.textSub }}>{t('crm.handle')}
        <input style={field} value={handle} onChange={e => setHandle(e.target.value)} disabled={!!url.trim()} />
      </label>
      <label style={{ fontSize: 11, color: T.textSub }}>{t('crm.owner')}
        <input style={field} value={owner} onChange={e => setOwner(e.target.value)} list="crm-owners" />
      </label>
      <label style={{ fontSize: 11, color: T.textSub }}>{t('crm.priority')}
        <select style={field} value={priority} onChange={e => setPriority(e.target.value)}><option value="">–</option>{['A', 'B', 'C'].map(p => <option key={p}>{p}</option>)}</select>
      </label>
      <datalist id="crm-owners">{CRM_OWNERS.map(o => <option key={o} value={o} />)}</datalist>
      <button type="submit" style={{ ...S.btn('primary'), padding: '7px 14px', fontSize: 12 }} disabled={busy || (!url.trim() && !handle.trim())}>{t('crm.save')}</button>
      {error && <div style={{ color: T.danger, fontSize: 12, flexBasis: '100%' }}>{error}</div>}
    </form>
  );
}

function CrmImport({ onDone }: { onDone: () => void }) {
  const [text, setText] = useState('');
  const [owner, setOwner] = useState('');
  const [rows, setRows] = useState<CrmImportRow[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function preview() {
    setBusy(true); setError(null);
    try { setRows((await api.crm.previewImport(text)).rows); } catch (e: any) { setError(e.message); } finally { setBusy(false); }
  }
  async function run() {
    setBusy(true); setError(null);
    try { await api.crm.import(text, owner || undefined); onDone(); } catch (e: any) { setError(e.message); setBusy(false); }
  }
  const inserts = rows?.filter(r => r.action === 'insert').length ?? 0;
  return (
    <div style={{ ...S.card, padding: 14, marginBottom: 16 }}>
      <div style={{ fontSize: 12, color: T.textSub, marginBottom: 6 }}>{t('crm.importHint')}</div>
      <textarea style={{ ...S.input, minHeight: 110, fontSize: 12, fontFamily: 'monospace' }} value={text} onChange={e => { setText(e.target.value); setRows(null); }} placeholder={'https://www.vinted.se/member/123-namn\nhttps://www.instagram.com/namn'} />
      <div style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <input style={{ ...S.input, width: 160, padding: '6px 10px', fontSize: 12 }} value={owner} onChange={e => setOwner(e.target.value)} list="crm-owners" placeholder={t('crm.owner')} />
        <button style={{ ...S.btn('ghost'), padding: '6px 14px', fontSize: 12 }} disabled={busy || !text.trim()} onClick={preview}>{t('crm.preview')}</button>
        {rows && <button style={{ ...S.btn('primary'), padding: '6px 14px', fontSize: 12 }} disabled={busy || inserts === 0} onClick={run}>{t('crm.importCount', { count: inserts })}</button>}
      </div>
      {error && <div style={{ color: T.danger, fontSize: 12, marginTop: 8 }}>{error}</div>}
      {rows && (
        <table style={{ width: '100%', fontSize: 12, marginTop: 10, borderCollapse: 'collapse' }}>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} style={{ borderTop: `1px solid ${T.border}`, color: r.action === 'skip' ? T.textMuted : T.text }}>
                <td style={{ padding: '4px 6px' }}>{r.platform ? crmPlatform(r.platform) : '–'}</td>
                <td style={{ padding: '4px 6px' }}>{r.handle ?? r.url}</td>
                <td style={{ padding: '4px 6px' }}>{r.action === 'insert' ? t('crm.willImport') : r.reason}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function CrmLeadModal({ id, onClose, onChanged }: { id: string; onClose: () => void; onChanged: () => void }) {
  const [lead, setLead] = useState<CrmLeadDetail | null>(null);
  const [draft, setDraft] = useState<Partial<CrmLead>>({});
  const [noteKind, setNoteKind] = useState<CrmNote['kind']>('dm');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  useEffect(() => { api.crm.lead(id).then(l => { setLead(l); setDraft({}); }).catch(e => setError(e.message)); }, [id]);

  async function act(fn: () => Promise<CrmLeadDetail | void>) {
    setBusy(true); setError(null);
    try { const r = await fn(); if (r) { setLead(r); setDraft({}); } onChanged(); }
    catch (e: any) { setError(e.message); } finally { setBusy(false); }
  }
  if (!lead) return <div style={S.modal} onClick={onClose}><div style={{ ...S.card, padding: 24 }}>{error ?? t('common.loading')}</div></div>;
  const v = <K extends keyof CrmLead>(k: K) => (k in draft ? draft[k] : lead[k]) as CrmLead[K];
  const set = (k: keyof CrmLead, val: string) => setDraft(d => ({ ...d, [k]: val }));
  const dirty = Object.keys(draft).length > 0;
  const message = t('crm.messageTemplate', { name: lead.displayName || lead.handle || '', platform: crmPlatform(lead.platform), code: lead.inviteCode, link: crmInviteLink(lead.inviteCode), owner: lead.owner || 'Qvestium-teamet' });
  const field: React.CSSProperties = { ...S.input, padding: '6px 10px', fontSize: 12 };
  const lbl = (text: string, k: keyof CrmLead, type: string = 'text') => (
    <label style={{ fontSize: 11, color: T.textSub }}>{text}
      <input style={field} type={type} value={(v(k) as string) ?? ''} onChange={e => set(k, e.target.value)} list={k === 'owner' ? 'crm-owners' : undefined} />
    </label>
  );

  return (
    <div style={S.modal} onClick={onClose}>
      <div style={{ background: T.surface, borderRadius: 8, width: '100%', maxWidth: 720, maxHeight: '90vh', overflow: 'auto', border: `1px solid ${T.border}`, padding: 20 }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 700 }}>{crmName(lead)}</div>
            <div style={{ fontSize: 12, color: T.textMuted }}>
              {crmPlatform(lead.platform)}
              {lead.profileUrl && <> · <a href={lead.profileUrl} target="_blank" rel="noreferrer" style={{ color: T.rose }}>{t('crm.openProfile')}</a></>}
              {lead.registeredEmail && <> · {t('crm.registeredAs', { email: lead.registeredEmail })}</>}
            </div>
          </div>
          <button style={{ ...S.btn('ghost'), padding: '4px 8px' }} onClick={onClose}>✕</button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 8, marginTop: 14 }}>
          <label style={{ fontSize: 11, color: T.textSub }}>{t('crm.stage')}
            <select style={field} value={v('stage')} onChange={e => set('stage', e.target.value)}>
              {CRM_STAGES.map(s => <option key={s} value={s}>{t(`crm.stages.${s}`)}</option>)}
            </select>
          </label>
          <label style={{ fontSize: 11, color: T.textSub }}>{t('crm.priority')}
            <select style={field} value={v('priority') ?? ''} onChange={e => set('priority', e.target.value)}><option value="">–</option>{['A', 'B', 'C'].map(p => <option key={p}>{p}</option>)}</select>
          </label>
          {lbl(t('crm.owner'), 'owner')}
          {lbl(t('crm.displayName'), 'displayName')}
          {lbl(t('crm.handle'), 'handle')}
          {lbl(t('crm.profileUrl'), 'profileUrl')}
          {lbl(t('crm.contactEmail'), 'contactEmail', 'email')}
          {lbl(t('crm.contactPhone'), 'contactPhone')}
          {lbl(t('crm.location'), 'location')}
          {lbl(t('crm.categories'), 'categories')}
          {lbl(t('crm.audience'), 'audience')}
          {lbl(t('crm.nextStep'), 'nextStep')}
          {lbl(t('crm.nextStepDate'), 'nextStepDate', 'date')}
        </div>
        <label style={{ fontSize: 11, color: T.textSub, display: 'block', marginTop: 8 }}>{t('crm.description')}
          <textarea style={{ ...field, minHeight: 60 }} value={v('description') ?? ''} onChange={e => set('description', e.target.value)} />
        </label>
        {dirty && <button style={{ ...S.btn('primary'), padding: '6px 14px', fontSize: 12, marginTop: 8 }} disabled={busy} onClick={() => act(() => api.crm.update(id, draft))}>{t('crm.save')}</button>}

        <div style={{ marginTop: 16, padding: 12, background: T.surfaceHover, borderRadius: 6, fontSize: 12 }}>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>{t('crm.inviteTitle', { code: lead.inviteCode })}</div>
          <div style={{ whiteSpace: 'pre-wrap', color: T.textSub }}>{message}</div>
          <button style={{ ...S.btn('ghost'), padding: '5px 12px', fontSize: 12, marginTop: 8 }}
            onClick={() => navigator.clipboard?.writeText(message).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); })}>
            {copied ? t('crm.copied') : t('crm.copyMessage')}
          </button>
        </div>

        <div style={{ marginTop: 16 }}>
          <div style={{ display: 'flex', gap: 6 }}>
            <select style={{ ...field, width: 130 }} value={noteKind} onChange={e => setNoteKind(e.target.value as CrmNote['kind'])}>
              {(['dm', 'email', 'call', 'note'] as const).map(k => <option key={k} value={k}>{t(`crm.noteKinds.${k}`)}</option>)}
            </select>
            <input style={{ ...field, flex: 1 }} value={note} onChange={e => setNote(e.target.value)} placeholder={t('crm.notePlaceholder')} />
            <button style={{ ...S.btn('primary'), padding: '6px 14px', fontSize: 12 }} disabled={busy || !note.trim()} onClick={() => act(async () => { const r = await api.crm.addNote(id, noteKind, note); setNote(''); return r; })}>{t('crm.addNote')}</button>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 10 }}>
            {lead.notes.map(n => (
              <div key={n.id} style={{ fontSize: 12, padding: 8, borderLeft: `3px solid ${T.border}`, background: T.surfaceHover, borderRadius: 4 }}>
                <div style={{ color: T.textMuted, fontSize: 11, display: 'flex', justifyContent: 'space-between' }}>
                  <span>{t(`crm.noteKinds.${n.kind}`)} · {n.author} · {new Date(n.createdAt).toLocaleString(locale(), { dateStyle: 'short', timeStyle: 'short' })}</span>
                  <button style={{ background: 'none', border: 'none', color: T.textMuted, cursor: 'pointer', fontSize: 11 }} onClick={() => act(async () => { await api.crm.deleteNote(id, n.id); return api.crm.lead(id); })}>✕</button>
                </div>
                <div style={{ whiteSpace: 'pre-wrap', marginTop: 2 }}>{n.body}</div>
              </div>
            ))}
          </div>
        </div>

        {error && <div style={{ color: T.danger, fontSize: 12, marginTop: 10 }}>{error}</div>}
        <div style={{ marginTop: 16, paddingTop: 12, borderTop: `1px solid ${T.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 11, color: T.textMuted }}>{t('crm.gdprHint')}</span>
          <button style={{ ...S.btn('danger'), padding: '5px 12px', fontSize: 12 }} disabled={busy}
            onClick={() => { if (window.confirm(t('crm.confirmDelete'))) act(async () => { await api.crm.remove(id); onClose(); }); }}>{t('crm.delete')}</button>
        </div>
      </div>
    </div>
  );
}

// Supportagentens utkast för ett ärende. Bara AGENT_APPROVERS (canDecide) kan
// använda eller avslå det; övriga admins ser det som underlag.
function AgentDraftCard({ task, closed, busy, inUse, onUse, onReject, onRequeue }: {
  task: AgentTask; closed: boolean; busy: boolean; inUse: boolean; onUse: () => void; onReject: () => void; onRequeue: () => void;
}) {
  const box: React.CSSProperties = { marginTop: 16, padding: 12, borderRadius: 8, fontSize: 13, lineHeight: 1.5, border: `1px dashed ${T.border}`, background: T.surfaceHover };
  const small = { padding: '5px 12px', fontSize: 12 };
  const head = (text: string) => <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4, color: T.textMuted, marginBottom: 6 }}>{text}</div>;
  if (task.status === 'queued' || task.status === 'running') {
    return <div style={box}>{head(t('adminTicketsView.agent.title'))}<span style={{ color: T.textMuted }}>{t('adminTicketsView.agent.working')}</span></div>;
  }
  if (task.status === 'failed') {
    return (
      <div style={box}>
        {head(t('adminTicketsView.agent.title'))}
        <div style={{ color: T.danger }}>{t('adminTicketsView.agent.failed', { error: task.error ?? '' })}</div>
        {task.canDecide && !closed && <button style={{ ...S.btn('ghost'), ...small, marginTop: 8 }} disabled={busy} onClick={onRequeue}>{t('adminTicketsView.agent.retry')}</button>}
      </div>
    );
  }
  if (task.status === 'decided') {
    return (
      <div style={{ ...box, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <span style={{ color: T.textMuted, fontSize: 12 }}>{t(`adminTicketsView.agent.decided.${task.decision ?? 'approved'}`, { by: task.decidedBy ?? '' })}</span>
        {task.decision === 'rejected' && task.canDecide && !closed && <button style={{ ...S.btn('ghost'), ...small }} disabled={busy} onClick={onRequeue}>{t('adminTicketsView.agent.retry')}</button>}
      </div>
    );
  }
  const p = task.proposal;
  if (!p) return null;
  return (
    <div style={box}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
        {head(t('adminTicketsView.agent.title'))}
        <span style={{ fontSize: 11, color: T.textMuted }}>{p.language.toUpperCase()} · {t(`adminTicketsView.agent.actions.${p.suggestedAction}`)}</span>
      </div>
      <div style={{ whiteSpace: 'pre-wrap', background: T.surface, padding: 10, borderRadius: 6 }}>{p.reply}</div>
      {p.reasoning && <div style={{ fontSize: 12, color: T.textSub, marginTop: 8 }}>{t('adminTicketsView.agent.reasoning', { text: p.reasoning })}</div>}
      {closed ? null : task.canDecide ? (
        <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <button style={{ ...S.btn('primary'), ...small }} disabled={busy || inUse} onClick={onUse}>{inUse ? t('adminTicketsView.agent.inUse') : t('adminTicketsView.agent.use')}</button>
          <button style={{ ...S.btn('ghost'), ...small }} disabled={busy} onClick={onReject}>{t('adminTicketsView.agent.reject')}</button>
          {inUse && <span style={{ fontSize: 11, color: T.textMuted }}>{t('adminTicketsView.agent.useHint')}</span>}
        </div>
      ) : <div style={{ fontSize: 11, color: T.textMuted, marginTop: 8 }}>{t('adminTicketsView.agent.notApprover')}</div>}
    </div>
  );
}

function AdminTicketsView({ onChanged }: { onChanged: () => void }) {
  const [all, setAll] = useState<SupportTicket[] | null>(null);
  const [filter, setFilter] = useState<'active' | 'closed' | 'all'>('active');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [reply, setReply] = useState('');
  const [internal, setInternal] = useState(false);
  // Satt när svarsrutan fylldes från agentens utkast — svaret räknas då som godkänt eller ändrat.
  const [draftTaskId, setDraftTaskId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<{ text: string; isError: boolean } | null>(null);

  async function load() {
    try { setAll(await api.adminTickets()); }
    catch (err: any) { setNotice({ text: err?.message ?? t('adminTicketsView.loadFailed'), isError: true }); }
  }
  useEffect(() => { load(); }, []);

  const list = (all ?? []).filter(tk => filter === 'all' ? true : filter === 'closed' ? tk.status === 'closed' : tk.status !== 'closed');
  const selected = all?.find(tk => tk.id === selectedId) ?? null;

  async function act(fn: () => Promise<SupportTicket>, okText: string) {
    setBusy(true); setNotice(null);
    try {
      const updated = await fn();
      setAll(prev => prev?.map(tk => tk.id === updated.id ? updated : tk) ?? prev);
      setNotice({ text: okText, isError: false });
      onChanged();
      return true;
    } catch (err: any) {
      setNotice({ text: err?.message ?? t('adminTicketsView.actionFailed'), isError: true });
      return false;
    } finally { setBusy(false); }
  }

  async function sendReply() {
    if (!selected || !reply.trim()) return;
    const taskId = !internal && draftTaskId === selected.agentTask?.id ? draftTaskId ?? undefined : undefined;
    if (await act(() => api.adminReplyTicket(selected.id, reply, internal, taskId), internal ? t('adminTicketsView.noteSaved') : t('adminTicketsView.replySent', { email: selected.reporterEmail }))) {
      setReply(''); setInternal(false); setDraftTaskId(null);
    }
  }

  function applyDraft(tk: SupportTicket) {
    if (!tk.agentTask?.proposal) return;
    setReply(tk.agentTask.proposal.reply); setInternal(false); setDraftTaskId(tk.agentTask.id);
  }

  function close(tk: SupportTicket) {
    const res = window.prompt(tk.kind === 'report'
      ? t('adminTicketsView.promptReportDecision')
      : t('adminTicketsView.promptClosingMessage'));
    if (res === null) return;
    act(() => api.adminSetTicketStatus(tk.id, 'closed', res.trim() || undefined), t('adminTicketsView.ticketClosed'));
  }

  async function removeTarget(tk: SupportTicket) {
    if (!tk.targetId) return;
    const reason = window.prompt(t('adminTicketsView.promptRemoveListing', { name: tk.targetName ?? '' }), tk.category);
    if (!reason?.trim()) return;
    setBusy(true); setNotice(null);
    try {
      await api.adminRemoveListing(tk.targetId, reason.trim());
      await api.adminReplyTicket(tk.id, t('adminTicketsView.removedInternalNote', { reason: reason.trim() }), true);
      await load();
      setNotice({ text: t('adminTicketsView.listingRemovedNotice'), isError: false });
    } catch (err: any) {
      setNotice({ text: err?.message ?? t('adminTicketsView.removeFailed'), isError: true });
    } finally { setBusy(false); }
  }

  const fmtDate = (iso: string) => new Date(iso).toLocaleString(locale(), { dateStyle: 'short', timeStyle: 'short' });
  const filterBtn = (key: typeof filter, label: string) => (
    <button key={key} onClick={() => setFilter(key)} style={{ ...S.btn(filter === key ? 'primary' : 'ghost'), padding: '5px 12px', fontSize: 12 }}>{label}</button>
  );

  return (
    <div>
      {notice && (
        <div style={{ padding: '10px 14px', borderRadius: 8, marginBottom: 16, fontSize: 13,
          background: notice.isError ? 'hsl(var(--destructive) / 0.08)' : '#e8f5e9', color: notice.isError ? T.danger : T.success }}>
          {notice.text}
        </div>
      )}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        {filterBtn('active', t('adminTicketsView.filters.active'))}{filterBtn('closed', t('adminTicketsView.filters.closed'))}{filterBtn('all', t('adminTicketsView.filters.all'))}
      </div>
      {!all ? <div style={{ fontSize: 13, color: T.textMuted }}>{t('common.loading')}</div> : (
        <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <div style={{ flex: '1 1 280px', maxWidth: 380, display: 'flex', flexDirection: 'column', gap: 6 }}>
            {list.length === 0 && <div style={{ fontSize: 13, color: T.textMuted }}>{t('adminTicketsView.empty')}</div>}
            {list.map(tk => (
              <button key={tk.id} onClick={() => { if (tk.id !== selectedId) { setReply(''); setDraftTaskId(null); } setSelectedId(tk.id); setNotice(null); }}
                style={{ textAlign: 'left', background: tk.id === selectedId ? T.surfaceHover : T.surface, border: `1px solid ${tk.id === selectedId ? T.text : T.border}`,
                  borderRadius: 8, padding: 10, cursor: 'pointer' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                  <span style={{ fontSize: 12, fontWeight: 700 }}>{tk.number} · {tk.kind === 'report' ? t('adminTicketsView.kinds.report') : tk.kind === 'dispute' ? t('adminTicketsView.kinds.dispute') : t('adminTicketsView.kinds.support')}</span>
                  <span style={S.badge(TICKET_STATUS[tk.status].color)}>{TICKET_STATUS[tk.status].label}</span>
                </div>
                <div style={{ fontSize: 13, marginTop: 4 }}>{tk.agentTask?.status === 'proposed' && tk.status !== 'closed' && <span title={t('adminTicketsView.agent.hasDraft')}>✎ </span>}{tk.category}{tk.targetName ? ` — ${tk.targetName}` : ''}</div>
                <div style={{ fontSize: 11, color: T.textMuted, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{tk.reporterEmail} · {fmtDate(tk.createdAt)}</div>
              </button>
            ))}
          </div>

          <div style={{ flex: '2 1 360px', minWidth: 0 }}>
            {!selected ? <div style={{ fontSize: 13, color: T.textMuted }}>{t('adminTicketsView.selectTicket')}</div> : (
              <div style={{ ...S.card, padding: 18 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                  <div>
                    <div style={{ fontSize: 17, fontWeight: 700 }}>{selected.number} · {selected.category}</div>
                    <div style={{ fontSize: 12, color: T.textMuted, marginTop: 2, wordBreak: 'break-all' }}>
                      {t(selected.kind === 'report' ? 'adminTicketsView.reportFrom' : 'adminTicketsView.supportFrom', { reporter: `${selected.reporterName ? `${selected.reporterName} ` : ''}${selected.reporterEmail}` })}
                      {' '}{selected.reporterUserId ? t('adminTicketsView.loggedIn') : t('adminTicketsView.notLoggedIn')} · {fmtDate(selected.createdAt)}
                    </div>
                  </div>
                  <span style={{ ...S.badge(TICKET_STATUS[selected.status].color), alignSelf: 'flex-start' }}>{TICKET_STATUS[selected.status].label}</span>
                </div>

                {selected.targetType && (
                  <div style={{ marginTop: 12, padding: 10, background: T.surfaceHover, borderRadius: 6, fontSize: 13, display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                    <div style={{ wordBreak: 'break-all' }}>
                      {tn(selected.targetType === 'wardrobe_item' ? 'adminTicketsView.concernsListing' : 'adminTicketsView.concernsUser', { name: <strong>{selected.targetName ?? selected.targetId}</strong> })}
                      {selected.targetStatus && selected.targetStatus !== 'active' && <span style={{ color: T.textMuted }}> {t('adminTicketsView.targetStatus', { status: selected.targetStatus === 'removed' ? t('adminTicketsView.statusRemoved') : selected.targetStatus })}</span>}
                    </div>
                    {selected.targetType === 'wardrobe_item' && selected.targetStatus === 'active' && (
                      <button style={{ ...S.btn('danger'), padding: '4px 10px', fontSize: 11 }} disabled={busy} onClick={() => removeTarget(selected)}>{t('adminTicketsView.removeListing')}</button>
                    )}
                  </div>
                )}

                <div style={{ marginTop: 14, fontSize: 14, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{selected.message}</div>

                {selected.messages.length > 0 && (
                  <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {selected.messages.map(m => (
                      <div key={m.id} style={{ padding: 10, borderRadius: 6, fontSize: 13, lineHeight: 1.5, whiteSpace: 'pre-wrap',
                        background: m.internal ? '#fff8e1' : T.surfaceHover, borderLeft: `3px solid ${m.internal ? '#f0ad4e' : T.text}` }}>
                        <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 4 }}>
                          {m.internal ? t('adminTicketsView.internalNote') : t('adminTicketsView.replyToUser')} · {m.authorEmail} · {fmtDate(m.createdAt)}
                        </div>
                        {m.body}
                      </div>
                    ))}
                  </div>
                )}

                {selected.agentTask && <AgentDraftCard task={selected.agentTask} closed={selected.status === 'closed'} busy={busy}
                  inUse={draftTaskId === selected.agentTask.id}
                  onUse={() => applyDraft(selected)}
                  onReject={() => act(() => api.adminRejectAgentTask(selected.agentTask!.id), t('adminTicketsView.agent.rejected')).then(ok => { if (ok && draftTaskId) { setDraftTaskId(null); setReply(''); } })}
                  onRequeue={() => act(() => api.adminRequeueAgentTask(selected.agentTask!.id), t('adminTicketsView.agent.requeued'))} />}

                {selected.status !== 'closed' && (
                  <div style={{ marginTop: 16 }}>
                    <textarea style={{ ...S.input, minHeight: 90, resize: 'vertical' }} value={reply} onChange={e => setReply(e.target.value)}
                      placeholder={internal ? t('adminTicketsView.internalNotePlaceholder') : t('adminTicketsView.replyPlaceholder', { email: selected.reporterEmail })} />
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, marginTop: 8, flexWrap: 'wrap' }}>
                      <label style={{ fontSize: 12, color: T.textSub, display: 'flex', gap: 6, alignItems: 'center' }}>
                        <input type="checkbox" checked={internal} onChange={e => setInternal(e.target.checked)} /> {t('adminTicketsView.internalNoteCheckbox')}
                      </label>
                      <button style={{ ...S.btn('primary'), padding: '7px 14px', fontSize: 12 }} disabled={busy || !reply.trim()} onClick={sendReply}>
                        {internal ? t('adminTicketsView.saveNote') : t('adminTicketsView.sendReply')}
                      </button>
                    </div>
                  </div>
                )}

                <div style={{ display: 'flex', gap: 8, marginTop: 16, paddingTop: 12, borderTop: `1px solid ${T.border}`, flexWrap: 'wrap' }}>
                  {selected.status === 'open' && (
                    <button style={{ ...S.btn('ghost'), padding: '5px 12px', fontSize: 12 }} disabled={busy}
                      onClick={() => act(() => api.adminSetTicketStatus(selected.id, 'in_progress'), t('adminTicketsView.markedInProgress'))}>{t('adminTicketsView.markInProgress')}</button>
                  )}
                  {selected.status !== 'closed' ? (
                    <button style={{ ...S.btn('secondary'), padding: '5px 12px', fontSize: 12 }} disabled={busy} onClick={() => close(selected)}>
                      {selected.kind === 'report' ? t('adminTicketsView.closeWithDecision') : t('adminTicketsView.closeTicket')}
                    </button>
                  ) : (
                    <>
                      {selected.resolution && <div style={{ fontSize: 12, color: T.textSub, flex: '1 1 100%' }}>{t('adminTicketsView.decision', { resolution: selected.resolution })}</div>}
                      <button style={{ ...S.btn('ghost'), padding: '5px 12px', fontSize: 12 }} disabled={busy}
                        onClick={() => act(() => api.adminSetTicketStatus(selected.id, 'open'), t('adminTicketsView.reopened'))}>{t('adminTicketsView.reopen')}</button>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// ADMIN: DRIFT — ärenden som kräver åtgärd, moderering, spärrade konton och
// åtgärdslogg. Logiken finns i api/src/admin/operations.ts; alla åtgärder
// loggas och ger användaren ett mejl med skälet.
// ─────────────────────────────────────────────────────────────────

const ISSUE_LABELS: Record<AdminIssue['kind'], string> = {
  stuck_payment: t('issueLabels.stuckPayment'),
  not_shipped: t('issueLabels.notShipped'),
  not_handed_over: t('issueLabels.notHandedOver'),
  cancel_dispute: t('issueLabels.cancelDispute'),
  not_delivered: t('issueLabels.notDelivered'),
  payout_failed: t('issueLabels.payoutFailed'),
  payout_waiting: t('issueLabels.payoutWaiting'),
};

const ADMIN_ACTION_LABELS: Record<string, string> = {
  remove_listing: t('adminActionLabels.removeListing'),
  restore_listing: t('adminActionLabels.restoreListing'),
  block_user: t('adminActionLabels.blockUser'),
  unblock_user: t('adminActionLabels.unblockUser'),
  cancel_transaction: t('adminActionLabels.cancelTransaction'),
  retry_payout: t('adminActionLabels.retryPayout'),
};

function AdminDriftView() {
  const [issues, setIssues] = useState<AdminIssue[] | null>(null);
  const [blocked, setBlocked] = useState<BlockedUser[]>([]);
  const [log, setLog] = useState<AdminAction[]>([]);
  const [query, setQuery] = useState('');
  const [includeRemoved, setIncludeRemoved] = useState(false);
  const [listings, setListings] = useState<WardrobeItem[] | null>(null);
  const [blockEmail, setBlockEmail] = useState('');
  const [blockReason, setBlockReason] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ text: string; isError: boolean } | null>(null);

  async function refresh() {
    try {
      const [i, b, l] = await Promise.all([api.adminIssues(), api.adminBlockedUsers(), api.adminActionLog(30)]);
      setIssues(i); setBlocked(b); setLog(l);
    } catch (err: any) {
      setNotice({ text: err?.message ?? t('adminDriftView.loadFailed'), isError: true });
    }
  }
  useEffect(() => { refresh(); }, []);

  // Kör en admin-åtgärd med gemensam laddnings-/felhantering.
  async function run(key: string, fn: () => Promise<string>) {
    setBusy(key);
    setNotice(null);
    try {
      setNotice({ text: await fn(), isError: false });
      await refresh();
      if (listings) setListings(await api.adminSearchListings(query, includeRemoved));
    } catch (err: any) {
      setNotice({ text: err?.message ?? t('adminDriftView.actionFailed'), isError: true });
    } finally {
      setBusy(null);
    }
  }

  const askReason = (label: string) => {
    const r = window.prompt(t('adminDriftView.reasonPrompt', { question: label }));
    return r && r.trim() ? r.trim() : null;
  };

  function cancelTx(tx: WardrobeTransaction) {
    const paid = tx.status === 'pending';
    const reason = askReason(paid
      ? t('adminDriftView.cancelPaidQuestion', { name: tx.itemName ?? '', amount: fmt(tx.itemPrice + tx.shippingCost) })
      : t('adminDriftView.cancelUnpaidQuestion', { name: tx.itemName ?? '' }));
    if (!reason) return;
    run(`cancel-${tx.id}`, async () => (await api.adminCancelTransaction(tx.id, reason)).message);
  }

  function retryPayout(tx: WardrobeTransaction) {
    run(`payout-${tx.id}`, async () => {
      const r = await api.adminRetryPayout(tx.id);
      if (!r.ok) throw new Error(r.message);
      return r.message;
    });
  }

  function removeListing(item: WardrobeItem) {
    const reason = askReason(t('adminDriftView.removeListingQuestion', { name: item.name, userId: item.userId }));
    if (!reason) return;
    run(`remove-${item.id}`, async () => { await api.adminRemoveListing(item.id, reason); return t('adminDriftView.listingRemoved', { name: item.name }); });
  }

  function blockUser(userId: string, reason: string) {
    if (!window.confirm(t('adminDriftView.blockConfirm', { userId }))) return;
    run(`block-${userId}`, async () => { await api.adminBlockUser(userId, reason); setBlockEmail(''); setBlockReason(''); return t('adminDriftView.userBlocked', { userId }); });
  }

  async function search(e?: React.FormEvent) {
    e?.preventDefault();
    setBusy('search');
    try { setListings(await api.adminSearchListings(query, includeRemoved)); }
    catch (err: any) { setNotice({ text: err?.message ?? t('adminDriftView.searchFailed'), isError: true }); }
    finally { setBusy(null); }
  }

  const sectionTitle = (text: string) => (
    <div style={{ fontSize: 13, fontWeight: 700, textTransform: 'uppercase' as const, letterSpacing: 0.5, color: T.text, margin: '28px 0 12px' }}>{text}</div>
  );
  const small = { ...S.btn('ghost'), padding: '4px 10px', fontSize: 11 };
  const smallDanger = { ...S.btn('danger'), padding: '4px 10px', fontSize: 11 };

  return (
    <div>
      {notice && (
        <div style={{ padding: '10px 14px', borderRadius: 8, marginBottom: 16, fontSize: 13,
          background: notice.isError ? 'hsl(var(--destructive) / 0.08)' : '#e8f5e9', color: notice.isError ? T.danger : T.success }}>
          {notice.text}
        </div>
      )}

      {sectionTitle(issues ? t('adminDriftView.needsActionCount', { count: issues.length }) : t('adminDriftView.needsAction'))}
      {!issues ? (
        <div style={{ fontSize: 13, color: T.textMuted }}>{t('common.loading')}</div>
      ) : issues.length === 0 ? (
        <div style={{ fontSize: 13, color: T.textMuted }}>{t('adminDriftView.noIssues')}</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {issues.map(i => {
            const tx = i.transaction;
            return (
              <div key={`${i.kind}-${tx.id}`} style={{ ...S.card, padding: 14 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                  <div style={{ minWidth: 0 }}>
                    <span style={S.badge(i.kind.startsWith('payout') ? T.danger : '#f0ad4e')}>{ISSUE_LABELS[i.kind]}</span>
                    <div style={{ fontSize: 14, fontWeight: 600, marginTop: 6 }}>{tx.itemName ?? t('adminDriftView.unknownItem')} · {t('common.kr', { amount: fmt(tx.itemPrice) })}</div>
                    <div style={{ fontSize: 12, color: T.textSub, marginTop: 2 }}>{i.message}</div>
                    <div style={{ fontSize: 11, color: T.textMuted, marginTop: 4, wordBreak: 'break-all' }}>
                      {t('adminDriftView.parties', { seller: tx.sellerUserId, buyer: tx.buyerUserId, id: tx.id })}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                    {(tx.status === 'pending' || tx.status === 'awaiting_payment') && (
                      <button style={smallDanger} disabled={busy === `cancel-${tx.id}`} onClick={() => cancelTx(tx)}>
                        {busy === `cancel-${tx.id}` ? '...' : tx.status === 'pending' ? t('adminDriftView.cancelAndRefund') : t('common.cancel')}
                      </button>
                    )}
                    {(i.kind === 'payout_failed' || i.kind === 'payout_waiting' || i.kind === 'cancel_dispute') && (
                      <button style={small} disabled={busy === `payout-${tx.id}`} onClick={() => retryPayout(tx)}>
                        {busy === `payout-${tx.id}` ? '...' : t(i.kind === 'cancel_dispute' ? 'adminDriftView.sellerIsRight' : 'adminDriftView.retryPayout')}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {sectionTitle(t('adminDriftView.moderation'))}
      <form onSubmit={search} style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <input style={{ ...S.input, flex: '1 1 220px', maxWidth: 360 }} placeholder={t('adminDriftView.searchPlaceholder')} value={query} onChange={e => setQuery(e.target.value)} />
        <label style={{ fontSize: 12, color: T.textSub, display: 'flex', gap: 6, alignItems: 'center' }}>
          <input type="checkbox" checked={includeRemoved} onChange={e => setIncludeRemoved(e.target.checked)} /> {t('adminDriftView.showRemoved')}
        </label>
        <button type="submit" style={{ ...S.btn('primary'), padding: '8px 14px', fontSize: 12 }} disabled={busy === 'search'}>{busy === 'search' ? t('adminDriftView.searching') : t('adminDriftView.search')}</button>
      </form>
      {listings && (
        listings.length === 0 ? <div style={{ fontSize: 13, color: T.textMuted, marginTop: 10 }}>{t('adminDriftView.noResults')}</div> : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 12 }}>
            {listings.map(item => (
              <div key={item.id} style={{ display: 'flex', gap: 12, alignItems: 'center', borderBottom: `1px solid ${T.border}`, paddingBottom: 8, flexWrap: 'wrap' }}>
                {item.imageUrl
                  ? <img src={item.imageUrl} alt="" style={{ width: 44, height: 44, objectFit: 'cover', borderRadius: 6 }} />
                  : <div style={{ width: 44, height: 44, borderRadius: 6, background: T.surfaceHover }} />}
                <div style={{ flex: '1 1 200px', minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{item.name}</div>
                  <div style={{ fontSize: 11, color: T.textMuted, wordBreak: 'break-all' }}>{item.userId}</div>
                  {item.status === 'removed' && <div style={{ fontSize: 11, color: T.danger }}>{t('adminDriftView.removedReason', { reason: item.moderationReason ?? '' })}</div>}
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  {item.status === 'active' ? (
                    <button style={smallDanger} disabled={busy === `remove-${item.id}`} onClick={() => removeListing(item)}>{t('common.delete')}</button>
                  ) : (
                    <button style={small} disabled={busy === `restore-${item.id}`}
                      onClick={() => run(`restore-${item.id}`, async () => { await api.adminRestoreListing(item.id); return t('adminDriftView.listingRestored', { name: item.name }); })}>
                      {t('adminDriftView.restore')}
                    </button>
                  )}
                  <button style={small} onClick={() => { setBlockEmail(item.userId); window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' }); }}>{t('adminDriftView.blockOwner')}</button>
                </div>
              </div>
            ))}
          </div>
        )
      )}

      {sectionTitle(t('adminDriftView.blockedAccounts', { count: blocked.length }))}
      <form
        onSubmit={e => { e.preventDefault(); if (blockEmail.trim() && blockReason.trim()) blockUser(blockEmail.trim(), blockReason.trim()); }}
        style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}
      >
        <input style={{ ...S.input, flex: '1 1 200px', maxWidth: 280 }} type="email" placeholder={t('adminDriftView.emailPlaceholder')} value={blockEmail} onChange={e => setBlockEmail(e.target.value)} />
        <input style={{ ...S.input, flex: '2 1 240px', maxWidth: 420 }} placeholder={t('adminDriftView.reasonPlaceholder')} value={blockReason} onChange={e => setBlockReason(e.target.value)} />
        <button type="submit" style={{ ...S.btn('danger'), padding: '8px 14px', fontSize: 12 }} disabled={!blockEmail.trim() || !blockReason.trim() || !!busy}>{t('adminDriftView.block')}</button>
      </form>
      {blocked.map(b => (
        <div key={b.userId} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', borderBottom: `1px solid ${T.border}`, padding: '8px 0', flexWrap: 'wrap' }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 600, wordBreak: 'break-all' }}>{b.userId}</div>
            <div style={{ fontSize: 11, color: T.textMuted }}>{t('adminDriftView.blockedMeta', { reason: b.reason, date: new Date(b.createdAt).toLocaleDateString(locale()), admin: b.blockedBy })}</div>
          </div>
          <button style={small} disabled={busy === `unblock-${b.userId}`}
            onClick={() => window.confirm(t('adminDriftView.unblockConfirm', { userId: b.userId })) && run(`unblock-${b.userId}`, async () => { await api.adminUnblockUser(b.userId); return t('adminDriftView.unblocked', { userId: b.userId }); })}>
            {t('adminDriftView.unblock')}
          </button>
        </div>
      ))}

      {sectionTitle(t('adminDriftView.recentActions'))}
      {log.length === 0 ? <div style={{ fontSize: 13, color: T.textMuted }}>{t('adminDriftView.noActions')}</div> : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <tbody>
              {log.map(a => (
                <tr key={a.id} style={{ borderBottom: `1px solid ${T.border}` }}>
                  <td style={{ padding: '6px', whiteSpace: 'nowrap', color: T.textMuted }}>{new Date(a.createdAt).toLocaleString(locale())}</td>
                  <td style={{ padding: '6px', whiteSpace: 'nowrap' }}>{ADMIN_ACTION_LABELS[a.action] ?? a.action}</td>
                  <td style={{ padding: '6px', color: T.textSub, wordBreak: 'break-all' }}>{a.targetId}</td>
                  <td style={{ padding: '6px', color: T.textSub }}>{a.reason ?? ''}</td>
                  <td style={{ padding: '6px', color: T.textMuted, whiteSpace: 'nowrap' }}>{a.adminUserId}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// EDIT COLLECTION MODAL
// ─────────────────────────────────────────────────────────────────

function EditCollectionModal({ col, onClose, onSaved }: {
  col: FashionCollection;
  onClose: () => void;
  onSaved: (updated: FashionCollection) => void;
}) {
  const [form, setForm] = useState({
    title: col.title,
    subtitle: col.subtitle ?? '',
    description: col.description ?? '',
    location: col.location,
    region: col.region,
    brand: col.brand ?? '',
    designer: col.designer ?? '',
    category: col.category,
    style: col.style,
    season: col.season ?? '',
    price: String(col.price),
    priceUnit: col.priceUnit,
    spotsTotal: String(col.spotsTotal),
    spotsAvailable: String(col.spotsAvailable),
    imageUrl: col.imageUrl ?? '',
    highlights: [...col.highlights, '', '', ''].slice(0, 3),
    includes: [...col.includes, '', '', ''].slice(0, 3),
    featured: col.featured,
    status: col.status,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploadingImage, setUploadingImage] = useState(false);

  async function handleImageUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingImage(true);
    try {
      const result = await api.uploadFile(file);
      setForm(p => ({ ...p, imageUrl: result.url }));
    } catch { setError(t('editCollectionModal.imageUploadFailed')); }
    finally { setUploadingImage(false); }
  }

  async function handleSave() {
    setSaving(true);
    setError('');
    try {
      const updated = await api.updateFashionCollection(col.id, {
        title: form.title,
        subtitle: form.subtitle || undefined,
        description: form.description || undefined,
        location: form.location,
        region: form.region,
        brand: form.brand || undefined,
        designer: form.designer || undefined,
        category: form.category,
        style: form.style,
        season: form.season || undefined,
        price: Number(form.price),
        priceUnit: form.priceUnit,
        spotsTotal: Number(form.spotsTotal),
        spotsAvailable: Number(form.spotsAvailable),
        imageUrl: form.imageUrl || undefined,
        highlights: form.highlights.filter(Boolean),
        includes: form.includes.filter(Boolean),
        featured: form.featured,
        status: form.status,
      });
      onSaved(updated);
    } catch (err: any) {
      setError(err.message ?? t('common.somethingWentWrong'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={S.modal} onClick={onClose}>
      <div style={{ background: T.surface, borderRadius: 8, width: '100%', maxWidth: 600, maxHeight: '90vh', overflow: 'auto', border: `1px solid ${T.border}` }} onClick={e => e.stopPropagation()}>
        <div style={{ padding: '20px 24px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: T.text }}>{t('editCollectionModal.title')}</div>
          <button style={{ ...S.btn('ghost'), padding: '4px 8px' }} onClick={onClose}>✕</button>
        </div>
        <div style={{ padding: '0 24px 24px', display: 'flex', flexDirection: 'column', gap: 10 }}>
          {[
            { label: t('editCollectionModal.fields.title'), key: 'title' }, { label: t('editCollectionModal.fields.subtitle'), key: 'subtitle' },
            { label: t('editCollectionModal.fields.location'), key: 'location' }, { label: t('editCollectionModal.fields.region'), key: 'region' },
            { label: t('editCollectionModal.fields.brand'), key: 'brand' }, { label: t('editCollectionModal.fields.designer'), key: 'designer' },
            { label: t('editCollectionModal.fields.season'), key: 'season' },
          ].map(({ label, key }) => (
            <div key={key}>
              <div style={{ fontSize: 12, color: T.textSub, marginBottom: 3 }}>{label}</div>
              <input style={S.input} value={(form as any)[key]} onChange={e => setForm(p => ({ ...p, [key]: e.target.value }))} />
            </div>
          ))}
          <div>
            <div style={{ fontSize: 12, color: T.textSub, marginBottom: 3 }}>{t('common.description')}</div>
            <textarea style={{ ...S.input, minHeight: 70, resize: 'vertical' }} value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div>
              <div style={{ fontSize: 12, color: T.textSub, marginBottom: 3 }}>{t('common.category')}</div>
              <select style={S.input} value={form.category} onChange={e => setForm(p => ({ ...p, category: e.target.value as any }))}>
                {['Kvinna', 'Man', 'Barn', 'Unisex'].map(c => <option key={c} value={c}>{vl('collectionCategory', c)}</option>)}
              </select>
            </div>
            <div>
              <div style={{ fontSize: 12, color: T.textSub, marginBottom: 3 }}>{t('editCollectionModal.fields.style')}</div>
              <select style={S.input} value={form.style} onChange={e => setForm(p => ({ ...p, style: e.target.value as any }))}>
                {['Klassisk', 'Modern', 'Streetwear', 'Formell', 'Casual', 'Bohemisk'].map(s => <option key={s} value={s}>{vl('style', s)}</option>)}
              </select>
            </div>
            <div>
              <div style={{ fontSize: 12, color: T.textSub, marginBottom: 3 }}>{t('common.price')}</div>
              <input style={S.input} type="number" value={form.price} onChange={e => setForm(p => ({ ...p, price: e.target.value }))} />
            </div>
            <div>
              <div style={{ fontSize: 12, color: T.textSub, marginBottom: 3 }}>{t('editCollectionModal.fields.priceUnit')}</div>
              <select style={S.input} value={form.priceUnit} onChange={e => setForm(p => ({ ...p, priceUnit: e.target.value }))}>
                {['kr/plagg', 'kr/set', 'kr/klänning', 'kr/kostym'].map(u => <option key={u} value={u}>{u === 'kr/plagg' ? t('editCollectionModal.priceUnits.perItem') : u === 'kr/set' ? t('editCollectionModal.priceUnits.perSet') : u === 'kr/klänning' ? t('editCollectionModal.priceUnits.perDress') : u === 'kr/kostym' ? t('editCollectionModal.priceUnits.perSuit') : u}</option>)}
              </select>
            </div>
            <div>
              <div style={{ fontSize: 12, color: T.textSub, marginBottom: 3 }}>{t('editCollectionModal.fields.spotsTotal')}</div>
              <input style={S.input} type="number" value={form.spotsTotal} onChange={e => setForm(p => ({ ...p, spotsTotal: e.target.value }))} />
            </div>
            <div>
              <div style={{ fontSize: 12, color: T.textSub, marginBottom: 3 }}>{t('editCollectionModal.fields.spotsAvailable')}</div>
              <input style={S.input} type="number" value={form.spotsAvailable} onChange={e => setForm(p => ({ ...p, spotsAvailable: e.target.value }))} />
            </div>
            <div>
              <div style={{ fontSize: 12, color: T.textSub, marginBottom: 3 }}>{t('editCollectionModal.fields.status')}</div>
              <select style={S.input} value={form.status} onChange={e => setForm(p => ({ ...p, status: e.target.value }))}>
                {['active', 'sold_out', 'draft'].map(s => <option key={s} value={s}>{s === 'active' ? t('editCollectionModal.statuses.active') : s === 'sold_out' ? t('editCollectionModal.statuses.soldOut') : t('editCollectionModal.statuses.draft')}</option>)}
              </select>
            </div>
          </div>
          <div>
            <div style={{ fontSize: 12, color: T.textSub, marginBottom: 3 }}>{t('editCollectionModal.fields.imageUrl')}</div>
            <div style={{ display: 'flex', gap: 8 }}>
              <input style={{ ...S.input, flex: 1 }} value={form.imageUrl} onChange={e => setForm(p => ({ ...p, imageUrl: e.target.value }))} />
              <button type="button" style={S.btn('ghost')} onClick={() => fileRef.current?.click()} disabled={uploadingImage}>{uploadingImage ? '...' : t('common.upload')}</button>
            </div>
            <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleImageUpload} />
          </div>
          <div>
            <div style={{ fontSize: 12, color: T.textSub, marginBottom: 3 }}>{t('editCollectionModal.fields.highlights')}</div>
            {form.highlights.map((h, i) => (
              <input key={i} style={{ ...S.input, marginBottom: 5 }} value={h} onChange={e => { const arr = [...form.highlights]; arr[i] = e.target.value; setForm(p => ({ ...p, highlights: arr })); }} placeholder={t('editCollectionModal.highlightPlaceholder', { n: i + 1 })} />
            ))}
          </div>
          <div>
            <div style={{ fontSize: 12, color: T.textSub, marginBottom: 3 }}>{t('editCollectionModal.fields.includes')}</div>
            {form.includes.map((inc, i) => (
              <input key={i} style={{ ...S.input, marginBottom: 5 }} value={inc} onChange={e => { const arr = [...form.includes]; arr[i] = e.target.value; setForm(p => ({ ...p, includes: arr })); }} placeholder={t('editCollectionModal.includesPlaceholder', { n: i + 1 })} />
            ))}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input type="checkbox" checked={form.featured} onChange={e => setForm(p => ({ ...p, featured: e.target.checked }))} id="edit-featured" />
            <label htmlFor="edit-featured" style={{ fontSize: 13, color: T.textSub }}>{t('editCollectionModal.featured')}</label>
          </div>
          {error && <div style={{ color: T.danger, fontSize: 13, padding: '8px 12px', background: `${T.danger}15`, borderRadius: 3 }}>{error}</div>}
          <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
            <button style={{ ...S.btn('ghost'), flex: 1, justifyContent: 'center' }} onClick={onClose}>{t('common.cancel')}</button>
            <button style={{ ...S.btn('primary'), flex: 2, justifyContent: 'center' }} onClick={handleSave} disabled={saving}>{saving ? t('common.saving') : t('common.saveChanges')}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// BAKGRUNDSBORTTAGNING (frilägga annonsbilder)
// Körs i säljarens webbläsare (bgRemoval/), gratis och utan serveranrop
// utöver att spara resultatet. Originalet sparas alltid; "Visa original"
// byter tillbaka. Knappen visas bara när flaggan är på och enheten har
// WebGPU (useBackgroundRemoval).
// ─────────────────────────────────────────────────────────────────

function BackgroundRemovalDialog({ imageUrl, state, onUse, onClose }: {
  imageUrl: string;
  state: BackgroundRemovalState;
  onUse: (cutoutUrl: string) => void;
  onClose: () => void;
}) {
  const [progress, setProgress] = useState<RemovalProgress | null>(null);
  const [result, setResult] = useState<{ blob: Blob; previewUrl: string } | null>(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let alive = true;
    let previewUrl = '';
    (async () => {
      try {
        if (!state.remover) throw new Error('unavailable');
        const res = await fetch(imageUrl);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const blob = await state.remover.removeBackground(await res.blob(), p => { if (alive) setProgress(p); });
        previewUrl = URL.createObjectURL(blob);
        if (alive) setResult({ blob, previewUrl });
        else URL.revokeObjectURL(previewUrl);
      } catch (err) {
        if (alive) setError(err instanceof BackgroundRemovalTimeout ? t('bgRemoval.timeout') : t('bgRemoval.error'));
      }
    })();
    return () => { alive = false; if (previewUrl) URL.revokeObjectURL(previewUrl); };
  }, [imageUrl, state.remover]);

  async function handleUse() {
    if (!result) return;
    setSaving(true);
    try {
      const { url } = await api.uploadCutout(imageUrl, result.blob);
      onUse(url);
    } catch {
      setError(t('bgRemoval.saveError'));
      setResult(null);
      setSaving(false);
    }
  }

  const status = error ? null
    : result ? null
    : progress?.phase === 'download' ? (progress.percent > 0 ? t('bgRemoval.downloading', { percent: progress.percent }) : t('bgRemoval.downloadingStart'))
    : progress?.phase === 'inference' ? t('bgRemoval.working')
    : t('bgRemoval.downloadingStart');
  const imgStyle: React.CSSProperties = { width: '100%', aspectRatio: '4/5', objectFit: 'contain', background: state.config.background, display: 'block' };

  return (
    <div style={S.modal} onClick={saving ? undefined : onClose}>
      <div style={{ background: '#fff', borderRadius: 6, padding: 20, maxWidth: 560, width: '100%', maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: T.text }}>{t('bgRemoval.title')}</div>
          <button type="button" aria-label={t('bgRemoval.close')} onClick={onClose} disabled={saving} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: T.textMuted, lineHeight: 1 }}>×</button>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div>
            <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 4 }}>{t('bgRemoval.before')}</div>
            <img src={imageUrl} alt="" style={imgStyle} />
          </div>
          <div>
            <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 4 }}>{t('bgRemoval.after')}</div>
            {result ? (
              <img src={result.previewUrl} alt="" style={imgStyle} />
            ) : (
              <div style={{ ...imgStyle, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 12, textAlign: 'center', fontSize: 12, color: error ? T.danger : T.textSub }}>
                {error || status}
              </div>
            )}
          </div>
        </div>
        {progress?.phase === 'download' && !result && !error && (
          <div style={{ height: 3, background: T.border, marginTop: 10, borderRadius: 2, overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${progress.percent}%`, background: T.rose, transition: 'width 0.2s' }} />
          </div>
        )}
        <div style={{ fontSize: 11, color: T.textMuted, marginTop: 10 }}>{t('bgRemoval.hint')}</div>
        <div style={{ display: 'flex', gap: 8, marginTop: 14, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          <button type="button" style={S.btn('ghost')} onClick={onClose} disabled={saving}>{t('bgRemoval.keepOriginal')}</button>
          {result && (
            <button type="button" style={S.btn('primary')} onClick={handleUse} disabled={saving}>
              {saving ? t('bgRemoval.saving') : t('bgRemoval.useCutout')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// Knapp per bild. "Ta bort bakgrund" öppnar dialogen; finns redan en
// friläggning byts bilden direkt ("Använd frilagd" / "Visa original").
function CutoutButton({ url, images, onChange, compact }: {
  url: string | null | undefined;
  images: ListingImages;
  onChange: (next: ListingImages) => void;
  compact?: boolean;
}) {
  const state = useBackgroundRemoval();
  const [open, setOpen] = useState(false);
  if (!state.available || !url) return null;
  const found = findVariant(images, url);
  if (!found && !canCutout(url)) return null;

  const style: React.CSSProperties = compact
    ? { ...S.btn('ghost'), padding: '3px 6px', fontSize: 9, letterSpacing: 0, whiteSpace: 'normal', justifyContent: 'center', width: '100%', marginTop: 4 }
    : { ...S.btn('ghost'), padding: '6px 12px', fontSize: 11, marginTop: 8 };
  const label = found?.showingCutout ? t('bgRemoval.showOriginal') : found ? t('bgRemoval.useStored') : t('bgRemoval.remove');
  function handleClick() {
    if (found?.showingCutout) onChange(chooseOriginal(images, url!));
    else if (found) onChange(chooseCutout(images, url!, found.variant.cutoutUrl));
    else setOpen(true);
  }

  return (
    <>
      <button type="button" style={style} onClick={handleClick}>{label}</button>
      {open && (
        <BackgroundRemovalDialog
          imageUrl={url}
          state={state}
          onClose={() => setOpen(false)}
          onUse={cutoutUrl => { setOpen(false); onChange(chooseCutout(images, url, cutoutUrl)); }}
        />
      )}
    </>
  );
}

// ─────────────────────────────────────────────────────────────────
// AI-GENERERADE PRODUKTBILDER
// Tar den uppladdade plaggbilden och genererar en bild av plagget på
// modell som kan läggas till i bildgalleriet. (Bakgrundsborttagning görs
// gratis i webbläsaren, se bgRemoval/.)
// ─────────────────────────────────────────────────────────────────

function AiImageGenerator({ imageUrl, name, category, bodyPhotos, onAddToGallery }: {
  imageUrl: string;
  name?: string;
  category?: string;
  bodyPhotos?: string[];
  onAddToGallery: (urls: string[]) => void;
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [variants, setVariants] = useState<{ key: string; label: string; url: string }[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [modelPhotoUrl, setModelPhotoUrl] = useState<string | null>(null);

  async function handleGenerate() {
    setLoading(true);
    setError('');
    setVariants([]);
    setSelected(new Set());
    try {
      const result = await api.generateProductImages(imageUrl, { name, category, modelPhotoUrl: modelPhotoUrl ?? undefined });
      setVariants(result.variants);
      setSelected(new Set(result.variants.map(v => v.url)));
      if (result.errors?.length) setError(t('aiImageGenerator.partialErrors', { errors: result.errors.join(', ') }));
    } catch (err: any) {
      setError(err.message ?? t('aiImageGenerator.failed'));
    } finally {
      setLoading(false);
    }
  }

  function toggle(url: string) {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(url)) next.delete(url); else next.add(url);
      return next;
    });
  }

  return (
    <div style={{ border: `1px dashed ${T.border}`, borderRadius: 4, padding: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
        <div style={{ fontSize: 12, color: T.textSub }}>
          {t('aiImageGenerator.intro')}
        </div>
        <button type="button" style={{ ...S.btn('ghost'), padding: '6px 12px', fontSize: 11, flexShrink: 0 }} onClick={handleGenerate} disabled={loading || !imageUrl}>
          {loading ? t('aiImageGenerator.generating') : t('aiImageGenerator.generate')}
        </button>
      </div>

      {/* Välj vems kropp modellbilden ska visa */}
      {bodyPhotos && bodyPhotos.length > 0 && (
        <div style={{ marginTop: 10 }}>
          <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 6 }}>{t('aiImageGenerator.modelPhotoLabel')}</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <div
              onClick={() => setModelPhotoUrl(null)}
              style={{ width: 44, height: 44, borderRadius: 4, border: `2px solid ${modelPhotoUrl === null ? T.rose : T.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: 9, color: T.textMuted, textAlign: 'center', lineHeight: 1.1 }}
              title={t('aiImageGenerator.aiModelTitle')}
            >
              {t('aiImageGenerator.aiModel')}
            </div>
            {bodyPhotos.map(url => (
              <img key={url} src={url} onClick={() => setModelPhotoUrl(url)} alt=""
                style={{ width: 44, height: 44, objectFit: 'cover', borderRadius: 4, cursor: 'pointer', border: `2px solid ${modelPhotoUrl === url ? T.rose : T.border}` }} />
            ))}
          </div>
        </div>
      )}
      {error && <div style={{ color: T.danger, fontSize: 12, marginTop: 8 }}>{error}</div>}
      {variants.length > 0 && (
        <>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 10 }}>
            {variants.map(v => {
              const isSelected = selected.has(v.url);
              return (
                <div key={v.url} onClick={() => toggle(v.url)} style={{ cursor: 'pointer', textAlign: 'center' }}>
                  <div style={{ position: 'relative', width: 100, height: 100, borderRadius: 4, overflow: 'hidden', border: `2px solid ${isSelected ? T.rose : T.border}` }}>
                    <img src={v.url} alt={v.label} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    <div style={{
                      position: 'absolute', top: 4, right: 4, width: 18, height: 18, borderRadius: '50%',
                      background: isSelected ? T.rose : 'rgba(255,255,255,0.85)', color: isSelected ? '#fff' : T.textMuted,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700,
                    }}>
                      {isSelected ? '✓' : ''}
                    </div>
                  </div>
                  <div style={{ fontSize: 11, color: T.textMuted, marginTop: 4 }}>{v.label}</div>
                </div>
              );
            })}
          </div>
          <button
            type="button"
            style={{ ...S.btn('primary'), padding: '6px 14px', fontSize: 11, marginTop: 10 }}
            disabled={selected.size === 0}
            onClick={() => { onAddToGallery(Array.from(selected)); setVariants([]); }}
          >
            {t('aiImageGenerator.addSelected', { count: selected.size })}
          </button>
        </>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// ADD PRODUCT MODAL
// ─────────────────────────────────────────────────────────────────

function AddProductModal({ defaultCategory, bodyPhotos, onClose, onCreated }: {
  defaultCategory?: string;
  bodyPhotos?: string[];
  onClose: () => void;
  onCreated: (p: Product) => void;
}) {
  const [form, setForm] = useState({
    name: '',
    description: '',
    price: '',
    price1: '',
    price3: '',
    price12: '',
    category: defaultCategory ?? 'Plagg',
    imageUrl: '',
    galleryUrls: [] as string[],
    inStock: true,
    sortOrder: '0',
    published: true,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploadingImage, setUploadingImage] = useState(false);

  // Suggest rental prices from the purchase price — admin can override any of them.
  function handlePriceChange(value: string) {
    setForm(p => {
      const price = Number(value) || 0;
      const suggest = (field: 'price1' | 'price3' | 'price12') => p[field] !== '';
      return {
        ...p,
        price: value,
        price1: suggest('price1') ? p.price1 : String(Math.round(price / 12 * 1.6) || ''),
        price3: suggest('price3') ? p.price3 : String(Math.round(price / 12 * 1.35) || ''),
        price12: suggest('price12') ? p.price12 : String(Math.round(price / 12) || ''),
      };
    });
  }

  async function handleImageUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingImage(true);
    try {
      const result = await api.uploadFile(file);
      setForm(p => ({ ...p, imageUrl: result.url }));
    } catch { setError(t('addProductModal.imageUploadFailed')); }
    finally { setUploadingImage(false); }
  }

  async function handleSave() {
    if (!form.name.trim()) { setError(t('addProductModal.nameRequired')); return; }
    if (!form.price || isNaN(Number(form.price))) { setError(t('addProductModal.invalidPrice')); return; }
    setSaving(true);
    setError('');
    try {
      const price = Number(form.price);
      const created = await api.createProduct({
        name: form.name,
        description: form.description || undefined,
        price,
        monthlyPrice: form.price12 ? Number(form.price12) : Math.round(price / 12),
        price3: form.price3 ? Number(form.price3) : undefined,
        price1: form.price1 ? Number(form.price1) : undefined,
        category: form.category,
        imageUrl: form.imageUrl || undefined,
        galleryUrls: form.galleryUrls,
        inStock: form.inStock,
        sortOrder: Number(form.sortOrder),
        status: form.published ? 'active' : 'draft',
      });
      onCreated(created);
    } catch (err: any) {
      setError(err.message ?? t('common.somethingWentWrong'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={S.modal} onClick={onClose}>
      <div style={{ background: T.surface, borderRadius: 8, width: '100%', maxWidth: 500, maxHeight: '90vh', overflow: 'auto', border: `1px solid ${T.border}` }} onClick={e => e.stopPropagation()}>
        <div style={{ padding: '20px 24px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: T.text }}>{t('addProductModal.title')}</div>
          <button style={{ ...S.btn('ghost'), padding: '4px 8px' }} onClick={onClose}>✕</button>
        </div>
        <div style={{ padding: '0 24px 24px', display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div>
            <div style={{ fontSize: 12, color: T.textSub, marginBottom: 3 }}>{t('addProductModal.nameLabel')}</div>
            <input style={S.input} value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder={t('addProductModal.namePlaceholder')} autoFocus />
          </div>
          <div>
            <div style={{ fontSize: 12, color: T.textSub, marginBottom: 3 }}>{t('common.description')}</div>
            <textarea style={{ ...S.input, minHeight: 60, resize: 'vertical' }} value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} placeholder={t('addProductModal.descriptionPlaceholder')} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div>
              <div style={{ fontSize: 12, color: T.textSub, marginBottom: 3 }}>{t('addProductModal.purchasePriceLabel')}</div>
              <input style={S.input} type="number" value={form.price} onChange={e => handlePriceChange(e.target.value)} placeholder="1200" />
            </div>
            <div>
              <div style={{ fontSize: 12, color: T.textSub, marginBottom: 3 }}>{t('common.category')}</div>
              <select style={S.input} value={form.category} onChange={e => setForm(p => ({ ...p, category: e.target.value }))}>
                {['Plagg', 'Accessoarer'].map(c => <option key={c} value={c}>{vl('category', c)}</option>)}
              </select>
            </div>
          </div>

          {/* Hyrpris per bindningstid */}
          <div>
            <div style={{ fontSize: 12, color: T.textSub, marginBottom: 6 }}>{t('addProductModal.rentalPriceLabel')}</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
              <div>
                <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 3 }}>{t('common.months', { count: 1 })}</div>
                <input style={S.input} type="number" value={form.price1} onChange={e => setForm(p => ({ ...p, price1: e.target.value }))} placeholder={t('addProductModal.perMonthPlaceholder')} />
              </div>
              <div>
                <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 3 }}>{t('common.months', { count: 3 })}</div>
                <input style={S.input} type="number" value={form.price3} onChange={e => setForm(p => ({ ...p, price3: e.target.value }))} placeholder={t('addProductModal.perMonthPlaceholder')} />
              </div>
              <div>
                <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 3 }}>{t('common.months', { count: 12 })}</div>
                <input style={S.input} type="number" value={form.price12} onChange={e => setForm(p => ({ ...p, price12: e.target.value }))} placeholder={t('addProductModal.perMonthPlaceholder')} />
              </div>
            </div>
            <div style={{ fontSize: 11, color: T.textMuted, marginTop: 4 }}>{t('addProductModal.prefillHint')}</div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div>
              <div style={{ fontSize: 12, color: T.textSub, marginBottom: 3 }}>{t('addProductModal.sortOrder')}</div>
              <input style={S.input} type="number" value={form.sortOrder} onChange={e => setForm(p => ({ ...p, sortOrder: e.target.value }))} />
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-end' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <input type="checkbox" checked={form.published} onChange={e => setForm(p => ({ ...p, published: e.target.checked }))} />
                <span style={{ fontSize: 13, color: T.textSub }}>{t('addProductModal.published')} {!form.published && t('addProductModal.draftHint')}</span>
              </label>
            </div>
          </div>
          <div>
            <div style={{ fontSize: 12, color: T.textSub, marginBottom: 3 }}>{t('addProductModal.image')}</div>
            <div style={{ display: 'flex', gap: 8 }}>
              <input style={{ ...S.input, flex: 1 }} value={form.imageUrl} onChange={e => setForm(p => ({ ...p, imageUrl: e.target.value }))} placeholder={t('addProductModal.imagePlaceholder')} />
              <button type="button" style={S.btn('ghost')} onClick={() => fileRef.current?.click()} disabled={uploadingImage}>{uploadingImage ? '...' : t('common.upload')}</button>
            </div>
            <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleImageUpload} />
            {form.imageUrl && (
              <img src={form.imageUrl} alt={t('addProductModal.previewAlt')} style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 4, marginTop: 8, border: `1px solid ${T.border}` }} />
            )}
          </div>

          {/* AI-genererade bilder utifrån uppladdad bild */}
          {uploadPath(form.imageUrl) && (
            <AiImageGenerator
              imageUrl={form.imageUrl}
              name={form.name}
              category={form.category}
              bodyPhotos={bodyPhotos}
              onAddToGallery={urls => setForm(p => ({ ...p, galleryUrls: [...p.galleryUrls, ...urls] }))}
            />
          )}
          {form.galleryUrls.length > 0 && (
            <div>
              <div style={{ fontSize: 12, color: T.textSub, marginBottom: 3 }}>{t('addProductModal.galleryCount', { count: form.galleryUrls.length })}</div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {form.galleryUrls.map((url, i) => (
                  <div key={i} style={{ position: 'relative' }}>
                    <img src={url} alt="" style={{ width: 60, height: 60, objectFit: 'cover', borderRadius: 3, border: `1px solid ${T.border}` }} />
                    {isAiImage(url) && <AiImageBadge small />}
                    <button
                      onClick={() => setForm(p => ({ ...p, galleryUrls: p.galleryUrls.filter((_, j) => j !== i) }))}
                      style={{ position: 'absolute', top: -6, right: -6, width: 18, height: 18, borderRadius: 5, background: T.danger, border: 'none', color: '#fff', fontSize: 11, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    >×</button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input type="checkbox" checked={form.inStock} onChange={e => setForm(p => ({ ...p, inStock: e.target.checked }))} id="add-instock" />
            <label htmlFor="add-instock" style={{ fontSize: 13, color: T.textSub }}>{t('addProductModal.inStock')}</label>
          </div>
          {error && <div style={{ color: T.danger, fontSize: 13, padding: '8px 12px', background: `${T.danger}15`, borderRadius: 3 }}>{error}</div>}
          <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
            <button style={{ ...S.btn('ghost'), flex: 1, justifyContent: 'center' }} onClick={onClose}>{t('common.cancel')}</button>
            <button style={{ ...S.btn('primary'), flex: 2, justifyContent: 'center' }} onClick={handleSave} disabled={saving}>{saving ? t('common.saving') : t('addProductModal.create')}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// EDIT PRODUCT MODAL
// ─────────────────────────────────────────────────────────────────

function EditProductModal({ product, bodyPhotos, onClose, onSaved }: {
  product: Product;
  bodyPhotos?: string[];
  onClose: () => void;
  onSaved: (updated: Product) => void;
}) {
  const [form, setForm] = useState({
    name: product.name,
    description: product.description ?? '',
    price: String(product.price),
    price3: product.price3 != null ? String(product.price3) : '',
    price12: String(product.monthlyPrice ?? ''),
    price1: product.price1 != null ? String(product.price1) : '',
    category: product.category,
    imageUrl: product.imageUrl ?? '',
    galleryUrls: product.galleryUrls ?? [] as string[],
    size: product.size ?? '',
    condition: product.condition ?? 5,
    inStock: product.inStock,
    sortOrder: String(product.sortOrder),
    published: product.status !== 'draft',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);
  const galleryRef = useRef<HTMLInputElement>(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingGallery, setUploadingGallery] = useState(false);

  async function handleImageUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingImage(true);
    try {
      const result = await api.uploadFile(file);
      setForm(p => ({ ...p, imageUrl: result.url }));
    } catch { setError(t('editProductModal.imageUploadFailed')); }
    finally { setUploadingImage(false); }
  }

  async function handleGalleryUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? []);
    if (!files.length) return;
    setUploadingGallery(true);
    try {
      for (const file of files) {
        const result = await api.uploadFile(file);
        setForm(p => ({ ...p, galleryUrls: [...p.galleryUrls, result.url] }));
      }
    } catch { setError(t('editProductModal.galleryUploadFailed')); }
    finally { setUploadingGallery(false); if (galleryRef.current) galleryRef.current.value = ''; }
  }

  async function handleSave() {
    setSaving(true);
    setError('');
    try {
      const price = Number(form.price);
      const updated = await api.updateProduct(product.id, {
        name: form.name,
        description: form.description || undefined,
        price,
        monthlyPrice: form.price12 ? Number(form.price12) : Math.round(price / 12),
        price3: form.price3 ? Number(form.price3) : undefined,
        price1: form.price1 ? Number(form.price1) : undefined,
        category: form.category,
        imageUrl: form.imageUrl || undefined,
        galleryUrls: form.galleryUrls,
        size: form.size || undefined,
        condition: form.condition,
        inStock: form.inStock,
        sortOrder: Number(form.sortOrder),
        status: form.published ? 'active' : 'draft',
      });
      onSaved(updated);
    } catch (err: any) {
      setError(err.message ?? t('common.somethingWentWrong'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={S.modal} onClick={onClose}>
      <div style={{ background: T.surface, borderRadius: 8, width: '100%', maxWidth: 540, maxHeight: '92vh', overflow: 'auto', border: `1px solid ${T.border}` }} onClick={e => e.stopPropagation()}>
        <div style={{ padding: '20px 24px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: T.text }}>{t('editProductModal.title')}</div>
          <button style={{ ...S.btn('ghost'), padding: '4px 8px' }} onClick={onClose}>✕</button>
        </div>
        <div style={{ padding: '0 24px 24px', display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>
            <div style={{ fontSize: 12, color: T.textSub, marginBottom: 3 }}>{t('editProductModal.nameLabel')}</div>
            <input style={S.input} value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} />
          </div>
          <div>
            <div style={{ fontSize: 12, color: T.textSub, marginBottom: 3 }}>{t('common.description')}</div>
            <textarea style={{ ...S.input, minHeight: 60, resize: 'vertical' }} value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div>
              <div style={{ fontSize: 12, color: T.textSub, marginBottom: 3 }}>{t('editProductModal.purchasePriceLabel')}</div>
              <input style={S.input} type="number" value={form.price} onChange={e => setForm(p => ({ ...p, price: e.target.value }))} />
            </div>
            <div>
              <div style={{ fontSize: 12, color: T.textSub, marginBottom: 3 }}>{t('common.category')}</div>
              <select style={S.input} value={form.category} onChange={e => setForm(p => ({ ...p, category: e.target.value }))}>
                {['Plagg', 'Accessoarer'].map(c => <option key={c} value={c}>{vl('category', c)}</option>)}
              </select>
            </div>
            <div>
              <div style={{ fontSize: 12, color: T.textSub, marginBottom: 3 }}>{t('common.size')}</div>
              <input style={S.input} placeholder="XS / S / M / L / XL / 38 / 40..." value={form.size} onChange={e => setForm(p => ({ ...p, size: e.target.value }))} />
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-end' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <input type="checkbox" checked={form.published} onChange={e => setForm(p => ({ ...p, published: e.target.checked }))} />
                <span style={{ fontSize: 13, color: T.textSub }}>{t('editProductModal.published')}</span>
              </label>
            </div>
          </div>

          {/* Hyrpris per bindningstid */}
          <div>
            <div style={{ fontSize: 12, color: T.textSub, marginBottom: 6 }}>{t('editProductModal.rentalPriceLabel')}</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
              <div>
                <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 3 }}>{t('common.months', { count: 1 })}</div>
                <input style={S.input} type="number" value={form.price1} onChange={e => setForm(p => ({ ...p, price1: e.target.value }))} placeholder={t('editProductModal.perMonthPlaceholder')} />
              </div>
              <div>
                <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 3 }}>{t('common.months', { count: 3 })}</div>
                <input style={S.input} type="number" value={form.price3} onChange={e => setForm(p => ({ ...p, price3: e.target.value }))} placeholder={t('editProductModal.perMonthPlaceholder')} />
              </div>
              <div>
                <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 3 }}>{t('common.months', { count: 12 })}</div>
                <input style={S.input} type="number" value={form.price12} onChange={e => setForm(p => ({ ...p, price12: e.target.value }))} placeholder={t('editProductModal.perMonthPlaceholder')} />
              </div>
            </div>
          </div>

          {/* Condition selector */}
          <div>
            <div style={{ fontSize: 12, color: T.textSub, marginBottom: 6 }}>{t('editProductModal.conditionLabel')}</div>
            <div style={{ display: 'flex', gap: 6 }}>
              {[1,2,3,4,5].map(c => {
                const info = CONDITION_LABELS[c];
                const isActive = form.condition === c;
                return (
                  <button key={c} onClick={() => setForm(p => ({ ...p, condition: c }))}
                    style={{ flex: 1, padding: '8px 4px', borderRadius: 4, border: `2px solid ${isActive ? info.color : T.border}`, background: isActive ? `${info.color}22` : 'transparent', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                    <span style={{ fontWeight: 700, fontSize: 15, color: isActive ? info.color : T.textMuted }}>{c}</span>
                    <span style={{ fontSize: 9, color: isActive ? info.color : T.textMuted, textAlign: 'center', lineHeight: 1.1 }}>{info.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Main image */}
          <div>
            <div style={{ fontSize: 12, color: T.textSub, marginBottom: 3 }}>{t('editProductModal.mainImage')}</div>
            <div style={{ display: 'flex', gap: 8 }}>
              <input style={{ ...S.input, flex: 1 }} placeholder={t('editProductModal.imagePlaceholder')} value={form.imageUrl} onChange={e => setForm(p => ({ ...p, imageUrl: e.target.value }))} />
              <button type="button" style={S.btn('ghost')} onClick={() => fileRef.current?.click()} disabled={uploadingImage}>{uploadingImage ? '...' : t('common.upload')}</button>
            </div>
            <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleImageUpload} />
            {form.imageUrl && (
              <img src={form.imageUrl} alt={t('editProductModal.previewAlt')} style={{ width: 80, height: 60, objectFit: 'cover', borderRadius: 3, marginTop: 8, border: `1px solid ${T.border}` }} />
            )}
          </div>

          {/* AI-genererade bilder utifrån uppladdad bild */}
          {uploadPath(form.imageUrl) && (
            <AiImageGenerator
              imageUrl={form.imageUrl}
              name={form.name}
              category={form.category}
              bodyPhotos={bodyPhotos}
              onAddToGallery={urls => setForm(p => ({ ...p, galleryUrls: [...p.galleryUrls, ...urls] }))}
            />
          )}

          {/* Gallery images */}
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <div style={{ fontSize: 12, color: T.textSub }}>{t('editProductModal.galleryCount', { count: form.galleryUrls.length })}</div>
              <button type="button" style={{ ...S.btn('ghost'), padding: '4px 10px', fontSize: 12 }} onClick={() => galleryRef.current?.click()} disabled={uploadingGallery}>
                {uploadingGallery ? t('common.loading') : t('editProductModal.addImages')}
              </button>
            </div>
            <input ref={galleryRef} type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={handleGalleryUpload} />
            {form.galleryUrls.length > 0 && (
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {form.galleryUrls.map((url, i) => (
                  <div key={i} style={{ position: 'relative' }}>
                    <img src={url} alt="" style={{ width: 60, height: 50, objectFit: 'cover', borderRadius: 3, border: `1px solid ${T.border}` }} />
                    {isAiImage(url) && <AiImageBadge small />}
                    <button
                      onClick={() => setForm(p => ({ ...p, galleryUrls: p.galleryUrls.filter((_, j) => j !== i) }))}
                      style={{ position: 'absolute', top: -6, right: -6, width: 18, height: 18, borderRadius: 5, background: T.danger, border: 'none', color: '#fff', fontSize: 11, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    >×</button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input type="checkbox" checked={form.inStock} onChange={e => setForm(p => ({ ...p, inStock: e.target.checked }))} id="edit-instock" />
            <label htmlFor="edit-instock" style={{ fontSize: 13, color: T.textSub }}>{t('editProductModal.inStock')}</label>
          </div>
          {error && <div style={{ color: T.danger, fontSize: 13, padding: '8px 12px', background: `${T.danger}15`, borderRadius: 3 }}>{error}</div>}
          <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
            <button style={{ ...S.btn('ghost'), flex: 1, justifyContent: 'center' }} onClick={onClose}>{t('common.cancel')}</button>
            <button style={{ ...S.btn('primary'), flex: 2, justifyContent: 'center' }} onClick={handleSave} disabled={saving}>{saving ? t('common.saving') : t('common.saveChanges')}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// ANNOUNCEMENT BAR + PROMO BANNER (Vestiaire-style)
// ─────────────────────────────────────────────────────────────────

function AnnouncementBar() {
  return (
    <div style={{ background: '#f0ece4', padding: '8px 16px', textAlign: 'center' }}>
      <span style={{ fontSize: 12, color: T.text }}>
        {t('announcementBar.text')}
      </span>
    </div>
  );
}

function PromoBanner({ onClick }: { onClick?: () => void }) {
  return (
    <div style={{ maxWidth: 1200, margin: '24px auto 0', padding: '0 24px' }}>
      <div
        onClick={onClick}
        style={{
          background: '#eef1e9', borderRadius: 4, padding: '24px 28px',
          display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
          cursor: onClick ? 'pointer' : 'default',
        }}
      >
        <div>
          <div style={{ fontFamily: 'Georgia, "Times New Roman", serif', fontSize: 24, color: T.text, marginBottom: 6 }}>
            {t('promoBanner.title')}
          </div>
          <div style={{ fontSize: 14, color: T.textSub }}>
            {t('promoBanner.body')}
          </div>
        </div>
        <div style={{ width: 22, height: 22, borderRadius: '50%', border: `1px solid ${T.text}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, color: T.text, flexShrink: 0 }}>i</div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// NAV SEARCH INPUT (compact pill, lives in the main nav)
// ─────────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────────
// KARTA (maps.tsx laddas först när en karta visas)
// ─────────────────────────────────────────────────────────────────

const LazyAreaMap = lazy(() => import('./maps').then(m => ({ default: m.AreaMap })));
const LazyAreaPicker = lazy(() => import('./maps').then(m => ({ default: m.AreaPicker })));
const LazyQPanel = lazy(() => import('./q/QPanel').then(m => ({ default: m.QPanel })));

/** Ett kartfel (Leaflet, nätverk) får aldrig ta ner resten av sidan. */
class MapErrorBoundary extends React.Component<{ height: number; children: React.ReactNode }, { failed: boolean }> {
  state = { failed: false };
  static getDerivedStateFromError() { return { failed: true }; }
  componentDidCatch(err: unknown) { console.error('[karta]', err); }
  render() {
    if (this.state.failed) return <div style={{ height: this.props.height, background: T.surfaceHover, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, color: T.textMuted }}>{t('mapErrorBoundary.failed')}</div>;
    return this.props.children;
  }
}

function MapFallback({ height }: { height: number }) {
  return <div style={{ height, background: T.surfaceHover, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, color: T.textMuted }}>{t('mapFallback.loading')}</div>;
}

/** Profilens ungefärliga område — samma cirkel som andra användare ser. */
function AreaMapCard({ area, caption, height = 160 }: { area: GeoArea; caption: string; height?: number }) {
  return (
    <div style={{ marginTop: 12 }}>
      <MapErrorBoundary height={height}><Suspense fallback={<MapFallback height={height} />}><LazyAreaMap area={area} height={height} /></Suspense></MapErrorBoundary>
      <div style={{ fontSize: 11, color: T.textMuted, marginTop: 6 }}>{caption}</div>
    </div>
  );
}

function haversineKm(a: { lat: number; lng: number }, b: { lat: number; lng: number }): number {
  const R = 6371, rad = Math.PI / 180;
  const dLat = (b.lat - a.lat) * rad, dLng = (b.lng - a.lng) * rad;
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(a.lat * rad) * Math.cos(b.lat * rad) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

/** Säljare vars ungefärliga område når in i det valda området. */
function sellersInArea(picked: PickedArea, areas: SellerArea[]): Set<string> {
  return new Set(areas.filter(s => haversineKm(picked, s.area) <= picked.radiusKm + s.area.radiusM / 1000).map(s => s.userId));
}

const AREA_RADII = [2, 5, 10, 25, 50];

function AreaPickerModal({ initial, sellerAreas, onApply, onClear, onClose }: {
  initial: PickedArea | null;
  sellerAreas: SellerArea[];
  onApply: (a: PickedArea) => void;
  onClear: () => void;
  onClose: () => void;
}) {
  const [point, setPoint] = useState<{ lat: number; lng: number } | null>(initial ? { lat: initial.lat, lng: initial.lng } : null);
  const [radiusKm, setRadiusKm] = useState(initial?.radiusKm ?? 10);
  const [locating, setLocating] = useState(false);
  const [error, setError] = useState('');
  const count = point ? sellersInArea({ ...point, radiusKm }, sellerAreas).size : null;

  function useMyPosition() {
    if (!navigator.geolocation) { setError(t('areaPickerModal.geolocationUnsupported')); return; }
    setLocating(true); setError('');
    navigator.geolocation.getCurrentPosition(
      pos => { setPoint({ lat: pos.coords.latitude, lng: pos.coords.longitude }); setLocating(false); },
      () => { setError(t('areaPickerModal.locationFailed')); setLocating(false); },
      { timeout: 10000, maximumAge: 600000 },
    );
  }

  return (
    <div style={S.modal} onClick={onClose}>
      <div style={{ background: T.surface, width: '100%', maxWidth: 640, margin: 16, border: `1px solid ${T.border}`, borderRadius: 8, overflow: 'hidden' }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px' }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 700, color: T.text }}>{t('areaPickerModal.title')}</div>
            <div style={{ fontSize: 12, color: T.textMuted, marginTop: 2 }}>{t('areaPickerModal.hint')}</div>
          </div>
          <button style={{ ...S.btn('ghost'), padding: '4px 8px' }} onClick={onClose} aria-label={t('common.close')}>✕</button>
        </div>
        <MapErrorBoundary height={360}>
          <Suspense fallback={<MapFallback height={360} />}>
            <LazyAreaPicker value={point} radiusKm={radiusKm} onPick={setPoint} sellerAreas={sellerAreas} height={360} />
          </Suspense>
        </MapErrorBoundary>
        <div style={{ padding: '14px 20px 18px', display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={{ fontSize: 12, color: T.textSub, marginRight: 4 }}>{t('areaPickerModal.within')}</span>
            {AREA_RADII.map(r => (
              <button key={r} onClick={() => setRadiusKm(r)} style={{ ...S.btn(r === radiusKm ? 'primary' : 'ghost'), padding: '6px 10px' }}>{t('areaPickerModal.radiusKm', { km: r })}</button>
            ))}
            <button onClick={useMyPosition} disabled={locating} style={{ ...S.btn('ghost'), padding: '6px 10px', marginLeft: 'auto' }}>{locating ? t('areaPickerModal.locating') : t('areaPickerModal.myPosition')}</button>
          </div>
          {error && <div style={{ fontSize: 12, color: T.danger }}>{error}</div>}
          <div style={{ fontSize: 12, color: T.textMuted }}>
            {count == null ? t('areaPickerModal.dotsHint') : t('areaPickerModal.sellersInArea', { count })}
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
            {initial && <button style={S.btn('ghost')} onClick={onClear}>{t('areaPickerModal.showAllSweden')}</button>}
            <button style={S.btn('primary')} disabled={!point} onClick={() => point && onApply({ ...point, radiusKm })}>{t('areaPickerModal.showItemsHere')}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function AreaFilterChip({ area, onEdit, onClear }: { area: PickedArea; onEdit: () => void; onClear: () => void }) {
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: T.roseDim, color: T.text, fontSize: 12, padding: '6px 6px 6px 12px', borderRadius: 16, marginBottom: 16 }}>
      <MapPinIcon size={13} />
      <button onClick={onEdit} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 12, color: T.text }}>{t('areaFilterChip.withinRadius', { radius: area.radiusKm })}</button>
      <button onClick={onClear} aria-label={t('areaFilterChip.clearAreaFilter')} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, lineHeight: 1, color: T.textSub, padding: '0 4px' }}>×</button>
    </div>
  );
}

function MapPinIcon({ size = 16 }: { size?: number }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M12 21s-7-6.2-7-11.5A7 7 0 0 1 19 9.5C19 14.8 12 21 12 21z"/><circle cx="12" cy="9.5" r="2.5"/></svg>;
}

function NavSearchInput({ query, setQuery, onSearch, fullWidth, autoFocus, onOpenMap, mapActive }: {
  query: string; setQuery: (q: string) => void; onSearch?: () => void;
  fullWidth?: boolean; autoFocus?: boolean;
  onOpenMap?: () => void; mapActive?: boolean;
}) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center',
      background: '#f2f0f0', borderRadius: 20, padding: '0 6px 0 14px',
      width: fullWidth ? '100%' : 320, flexShrink: fullWidth ? undefined : 0,
    }}>
      <span style={{ display: 'flex', alignItems: 'center', color: T.textMuted, flexShrink: 0 }}>
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
      </span>
      <input
        value={query}
        onChange={e => setQuery(e.target.value)}
        onKeyDown={e => e.key === 'Enter' && onSearch?.()}
        placeholder={t('navSearchInput.placeholder')}
        autoFocus={autoFocus}
        style={{
          flex: 1, background: 'transparent', border: 'none', outline: 'none',
          color: T.text, fontSize: 13, padding: '9px 8px',
          fontFamily: 'inherit', minWidth: 0, width: '100%',
        }}
      />
      {query && (
        <button onClick={() => setQuery('')} style={{
          background: 'transparent', border: 'none', color: T.textMuted,
          width: 22, height: 22, cursor: 'pointer', fontSize: 15, flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>×</button>
      )}
      {onOpenMap && (
        <button onClick={onOpenMap} aria-label={t('navSearchInput.pickAreaOnMap')} title={t('navSearchInput.pickAreaOnMap')} style={{
          background: mapActive ? T.rose : 'transparent', border: 'none', color: mapActive ? '#fff' : T.textSub,
          width: 28, height: 28, borderRadius: '50%', cursor: 'pointer', flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}><MapPinIcon size={15} /></button>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// RESPONSIVE TABS WITH OVERFLOW -> MER
// ─────────────────────────────────────────────────────────────────

type TabKey = 'drops' | 'plagg' | 'accessoarer' | 'garderob' | 'erbjud' | 'profil' | 'admin' | 'meddelanden';

// Öppnar Meddelanden med en viss motpart (och ev. annons) — skickas som ett
// fönsterevent så att komponenter djupt ner (annonssidan, säljarprofilen)
// inte behöver få en callback genom alla mellanled. App lyssnar, se openChat.
interface ChatTarget { userId: string; displayName: string; itemId?: string; itemName?: string }
/** Öppnar en annons var som helst i appen (hanteras i App, samma som /?item=<id>). */
function openListing(itemId: string) {
  window.dispatchEvent(new CustomEvent<string>('qvestium:open-listing', { detail: itemId }));
}
function openChatWith(target: ChatTarget) {
  window.dispatchEvent(new CustomEvent<ChatTarget>('qvestium:open-chat', { detail: target }));
}

const ALL_TABS: { key: TabKey; label: string }[] = [
  { key: 'plagg', label: t('allTabs.items') },
  { key: 'accessoarer', label: t('allTabs.accessories') },
  { key: 'drops', label: t('allTabs.upcomingDrops') },
  { key: 'garderob', label: t('allTabs.myWardrobe') },
];

const ADMIN_TAB: { key: TabKey; label: string } = { key: 'admin', label: t('adminTab.label') };

function TabBar({ active, setActive, isAdmin }: { active: TabKey; setActive: (t: TabKey) => void; isAdmin?: boolean }) {
  const tabs = isAdmin ? [...ALL_TABS, ADMIN_TAB] : ALL_TABS;
  const containerRef = useRef<HTMLDivElement>(null);
  // Osynlig rad med alla flikar + MER, för att mäta deras verkliga bredd.
  const measureRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState(1);
  const [merOpen, setMerOpen] = useState(false);

  useEffect(() => {
    const container = containerRef.current;
    const measure = measureRef.current;
    if (!container || !measure) return;
    const GAP = 24;
    const fit = () => {
      const avail = container.clientWidth;
      const widths = [...measure.children].map(el => (el as HTMLElement).offsetWidth);
      const merW = widths.pop() ?? 60;
      let used = 0;
      let count = 0;
      for (let i = 0; i < widths.length; i++) {
        const next = used + (i ? GAP : 0) + widths[i];
        const needsMer = i < widths.length - 1;
        if (next + (needsMer ? GAP + merW : 0) > avail) break;
        used = next;
        count = i + 1;
      }
      setVisibleCount(Math.max(1, count));
    };
    fit();
    const observer = new ResizeObserver(fit);
    observer.observe(container);
    document.fonts?.ready.then(fit).catch(() => {});
    return () => observer.disconnect();
  }, [tabs.length]);

  const visible = tabs.slice(0, visibleCount);
  const overflow = tabs.slice(visibleCount);

  return (
    <div ref={containerRef} style={{ display: 'flex', alignItems: 'center', gap: 24, position: 'relative', flex: 1, minWidth: 0 }}>
      <div ref={measureRef} aria-hidden="true" style={{ position: 'absolute', visibility: 'hidden', pointerEvents: 'none', display: 'flex', gap: 24, whiteSpace: 'nowrap', width: 0, height: 0, overflow: 'hidden', left: 0, top: 0 }}>
        {tabs.map(tab => <span key={tab.key} style={{ fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, flexShrink: 0 }}>{tab.label}</span>)}
        <span style={{ fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, flexShrink: 0 }}>{t('tabBar.more')} ▾</span>
      </div>
      {visible.map(tab => (
        <button
          key={tab.key}
          style={{
            background: 'transparent', border: 'none', cursor: 'pointer',
            padding: '14px 0 12px', fontSize: 13, fontWeight: active === tab.key ? 700 : 400,
            textTransform: 'uppercase' as const, letterSpacing: 0.5,
            color: active === tab.key ? T.text : T.textSub,
            borderBottom: active === tab.key ? `2px solid ${T.text}` : '2px solid transparent',
            flexShrink: 0, transition: 'color 0.15s, border-color 0.15s',
          }}
          onClick={() => setActive(tab.key)}
        >
          {tab.label}
        </button>
      ))}
      {overflow.length > 0 && (
        <div style={{ position: 'relative', flexShrink: 0 }}>
          <button
            style={{
              background: 'transparent', border: 'none', cursor: 'pointer',
              padding: '14px 0 12px', fontSize: 13, fontWeight: overflow.some(t => t.key === active) ? 700 : 400,
              textTransform: 'uppercase' as const, letterSpacing: 0.5,
              color: overflow.some(t => t.key === active) ? T.text : T.textSub,
              borderBottom: overflow.some(t => t.key === active) ? `2px solid ${T.text}` : '2px solid transparent',
            }}
            onClick={() => setMerOpen(o => !o)}
          >
            {t('tabBar.more')} ▾
          </button>
          {merOpen && (
            <div style={{
              position: 'absolute', top: '100%', right: 0, background: '#fff',
              border: `1px solid ${T.border}`, overflow: 'hidden', zIndex: 500, minWidth: 160,
            }}>
              {overflow.map(tab => (
                <button
                  key={tab.key}
                  style={{
                    display: 'block', width: '100%', textAlign: 'left',
                    padding: '10px 16px', background: active === tab.key ? '#f7f7f7' : 'transparent',
                    border: 'none', color: T.text, cursor: 'pointer', fontSize: 13,
                    fontWeight: active === tab.key ? 700 : 400,
                  }}
                  onClick={() => { setActive(tab.key); setMerOpen(false); }}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// MIN GARDEROB VIEW
// ─────────────────────────────────────────────────────────────────

const WARDROBE_CATEGORIES = ['Alla', 'Plagg', 'Accessoarer', 'Skor', 'Väskor', 'Övrigt'];
const RETURN_STATUS_LABELS: Record<string, { label: string; color: string }> = {
  ordered: { label: t('returnStatusLabels.ordered'), color: '#f0ad4e' },
  activated: { label: t('returnStatusLabels.activated'), color: '#5bc0de' },
  shipped: { label: t('returnStatusLabels.shipped'), color: '#0275d8' },
  received: { label: t('returnStatusLabels.received'), color: '#5cb85c' },
};
const SWAP_STATUS_LABELS: Record<string, { label: string; color: string }> = {
  pending: { label: t('swapStatusLabels.pending'), color: '#f0ad4e' },
  accepted: { label: t('swapStatusLabels.accepted'), color: '#5cb85c' },
  awaiting_handover: { label: t('swapStatusLabels.awaitingHandover'), color: '#5bc0de' },
  completed: { label: t('swapStatusLabels.completed'), color: '#5cb85c' },
  rejected: { label: t('swapStatusLabels.rejected'), color: T.danger },
  cancelled: { label: t('swapStatusLabels.cancelled'), color: T.textMuted },
  disputed: { label: t('swapStatusLabels.disputed'), color: T.danger },
};
const OFFER_STATUS_LABELS: Record<string, { label: string; color: string }> = {
  pending: { label: t('offerStatusLabels.pending'), color: '#f0ad4e' },
  accepted: { label: t('offerStatusLabels.accepted'), color: '#5cb85c' },
  paid: { label: t('offerStatusLabels.paid'), color: '#5cb85c' },
  rejected: { label: t('offerStatusLabels.rejected'), color: T.danger },
  cancelled: { label: t('offerStatusLabels.cancelled'), color: T.textMuted },
};
const TRANSACTION_STATUS_LABELS: Record<string, { label: string; color: string }> = {
  awaiting_payment: { label: t('transactionStatusLabels.awaitingPayment'), color: '#f0ad4e' },
  pending: { label: t('transactionStatusLabels.pending'), color: '#f0ad4e' },
  shipped: { label: t('transactionStatusLabels.shipped'), color: '#5bc0de' },
  delivered: { label: t('transactionStatusLabels.delivered'), color: '#5cb85c' },
  return_requested: { label: t('transactionStatusLabels.returnRequested'), color: '#f0ad4e' },
  return_shipped: { label: t('transactionStatusLabels.returnShipped'), color: '#5bc0de' },
  completed: { label: t('transactionStatusLabels.completed'), color: '#5cb85c' },
  return_received: { label: t('transactionStatusLabels.returnReceived'), color: '#5cb85c' },
  cancelled: { label: t('transactionStatusLabels.cancelled'), color: T.textMuted },
  refunded: { label: t('transactionStatusLabels.refunded'), color: T.textMuted },
};

// ─────────────────────────────────────────────────────────────────
// GARDEROB-GUIDEN (regelstyrd — ingen extern AI-tjänst)
// Tittar på garderobens tillstånd och visar EN prioriterad kontextuell hint:
// nya förslag/bud > att skicka > tom garderob > inget listat > inget drop > allt bra.
// ─────────────────────────────────────────────────────────────────

type WardrobeGuideMessage = { text: string; actionLabel?: string; onAction?: () => void };

function getWardrobeGuideMessage(params: {
  wardrobeItems: WardrobeItem[];
  ownItems: WardrobeItem[];
  listedForSale: WardrobeItem[];
  listedForRent: WardrobeItem[];
  drops: Drop[];
  pendingReceivedSwaps: SwapProposal[];
  pendingReceivedOffers: PurchaseOffer[];
  pendingSales: WardrobeTransaction[];
  onStartDrop: () => void;
  onGoToTab: (tab: any) => void;
}): WardrobeGuideMessage {
  const {
    wardrobeItems, ownItems, listedForSale, listedForRent, drops,
    pendingReceivedSwaps, pendingReceivedOffers, pendingSales,
    onStartDrop, onGoToTab,
  } = params;

  if (pendingReceivedSwaps.length > 0) {
    return {
      text: t('getWardrobeGuideMessage.newSwaps', { count: pendingReceivedSwaps.length }),
      actionLabel: t('getWardrobeGuideMessage.showSwaps'), onAction: () => onGoToTab('byten'),
    };
  }
  if (pendingReceivedOffers.length > 0) {
    return {
      text: t('getWardrobeGuideMessage.newOffers', { count: pendingReceivedOffers.length }),
      actionLabel: t('getWardrobeGuideMessage.showOffers'), onAction: () => onGoToTab('erbjudanden'),
    };
  }
  if (pendingSales.length > 0) {
    return {
      text: t('getWardrobeGuideMessage.pendingSales', { count: pendingSales.length }),
      actionLabel: t('getWardrobeGuideMessage.showToShip'), onAction: () => onGoToTab('att_skicka'),
    };
  }
  if (wardrobeItems.length === 0) {
    return {
      text: t('getWardrobeGuideMessage.emptyWardrobe'),
    };
  }
  if (listedForSale.length === 0 && listedForRent.length === 0) {
    return {
      text: t('getWardrobeGuideMessage.nothingListed', { count: ownItems.length }),
      actionLabel: t('getWardrobeGuideMessage.showMyItems'), onAction: () => onGoToTab('egna'),
    };
  }
  if (drops.length === 0) {
    return {
      text: t('getWardrobeGuideMessage.suggestDrop'),
      actionLabel: t('getWardrobeGuideMessage.createDrop'), onAction: onStartDrop,
    };
  }
  return { text: t('getWardrobeGuideMessage.allGood') };
}

// ─────────────────────────────────────────────────────────────────
// Delad pipeline: en uppladdad plaggbild -> AI-analys -> skapat
// plaggutkast i garderoben. Används av både Q-chatten och
// bulk-uppladdningen (flera bilder på en gång) så logiken bara finns
// på ett ställe. AI-bilder genereras INTE automatiskt här längre —
// användaren väljer själv om/när de vill generera sådana, via
// AiImageGenerator i EditWardrobeItemModal (se isAiImage/AiImageBadge
// för hur de märks ut när de väl finns).
// ─────────────────────────────────────────────────────────────────

async function createWardrobeItemFromImage(
  file: File,
  onStatus?: (status: string) => void,
): Promise<WardrobeItem> {
  onStatus?.(t('createWardrobeItemFromImage.uploading'));
  const uploaded = await api.uploadFile(file);

  onStatus?.(t('createWardrobeItemFromImage.analyzing'));
  const suggestion = await api.analyzeGarment(uploaded.url);

  onStatus?.(t('createWardrobeItemFromImage.creating'));
  return api.createWardrobeItem({
    name: suggestion.name,
    description: suggestion.description,
    category: suggestion.category,
    size: suggestion.size || undefined,
    condition: suggestion.condition || 5,
    imageUrl: uploaded.url,
    galleryUrls: [],
    forRent: false,
    forSale: false,
    rentPrice: suggestion.rentPrice12 || 0,
    rentPrice3: suggestion.rentPrice3 || undefined,
    rentPrice24: suggestion.rentPrice24 || undefined,
    salePrice: suggestion.salePrice || 0,
  });
}

// ─────────────────────────────────────────────────────────────────
// Q — Qvestiums AI-chatt
// Riktig konversation (OpenAI) + automatisk plagg-skapelse: ladda upp
// en bild så analyserar Q den, genererar AI-bilder (neutral + på modell)
// och skapar ett plaggutkast med föreslagen beskrivning och priser.
// ─────────────────────────────────────────────────────────────────

type QMessage = {
  id: string;
  role: 'q' | 'user';
  text?: string;
  imageUrl?: string;
  pending?: boolean;
  isError?: boolean;
  actionLabel?: string;
  onAction?: () => void;
  createdItem?: { id: string; name: string; imageUrl?: string; salePrice: number; rentPrice: number };
  outfits?: OutfitSuggestion;
};

let qMsgCounter = 0;
function nextQMsgId() { return `qmsg-${++qMsgCounter}`; }

// Enkel **fet text**-tolkning — det är den enda markdown-syntaxen Q:s svar
// använder (se createWardrobeItemFromImage/qChat-prompten på backend).
function renderInlineMarkdown(text: string): React.ReactNode[] {
  return text.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
    part.startsWith('**') && part.endsWith('**')
      ? <strong key={i}>{part.slice(2, -2)}</strong>
      : <React.Fragment key={i}>{part}</React.Fragment>
  );
}

// Skriver ut Q:s svar tecken för tecken när det först dyker upp (som i
// ChatGPT) istället för att bara stå där direkt — annars är det lätt att
// missa välkomsttexten helt. Monteras en gång per meddelande-id (se key=
// på anropsstället), så en omrendering av föräldern startar inte om
// animationen.
function QMessageText({ text }: { text: string }) {
  const [shown, setShown] = useState('');
  useEffect(() => {
    // Tidsbaserad (inte tick-baserad) reveal — hela texten klar på ~0,9s
    // oavsett längd, oberoende av om webbläsaren strular med exakta
    // setInterval-tider (vilket gjorde en enkel char-per-tick-räknare
    // opålitligt långsam för längre hälsningstexter).
    const TARGET_DURATION_MS = 900;
    const start = performance.now();
    let raf = 0;
    function tick(now: number) {
      const elapsed = now - start;
      const count = Math.min(text.length, Math.ceil((elapsed / TARGET_DURATION_MS) * text.length));
      setShown(text.slice(0, count));
      if (count < text.length) raf = requestAnimationFrame(tick);
    }
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return <>{renderInlineMarkdown(shown)}</>;
}

function copyText(text: string) {
  navigator.clipboard?.writeText(text).catch(() => {});
}

function QChat({ greeting, actionLabel, onAction, user, onItemsCreated, onStartDrop, onImportComplete }: {
  greeting: string;
  actionLabel?: string;
  onAction?: () => void;
  user: QidUser | null;
  onItemsCreated: (items: WardrobeItem[]) => void;
  onStartDrop: () => void;
  onImportComplete: () => void;
}) {
  const [messages, setMessages] = useState<QMessage[]>(() => [{
    id: nextQMsgId(), role: 'q',
    text: user?.name
      ? t('qChat.welcomeNamed', { name: user.name.split(' ')[0], greeting })
      : t('qChat.welcome', { greeting }),
    actionLabel, onAction,
  }]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [busyCount, setBusyCount] = useState(0);
  const fileRef = useRef<HTMLInputElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages]);

  function appendMessage(msg: QMessage) {
    setMessages(m => [...m, msg]);
  }
  function updateMessage(id: string, patch: Partial<QMessage>) {
    setMessages(m => m.map(msg => msg.id === id ? { ...msg, ...patch } : msg));
  }

  async function handleSend() {
    const text = input.trim();
    if (!text || sending) return;
    setInput('');
    appendMessage({ id: nextQMsgId(), role: 'user', text });
    setSending(true);
    try {
      const history = messages
        .filter(m => m.text && !m.pending)
        .map(m => ({ role: (m.role === 'q' ? 'assistant' : 'user') as 'assistant' | 'user', content: m.text! }))
        .concat([{ role: 'user', content: text }]);
      const res = await api.qChat(history);
      appendMessage({
        id: nextQMsgId(), role: 'q', text: res.reply,
        ...(res.action === 'outfits' && res.outfits ? { outfits: res.outfits } : {}),
        ...(res.action === 'start_drop' ? { actionLabel: t('qChat.pickItemsForDrop'), onAction: onStartDrop } : {}),
        ...(res.action === 'vinted_import_ready' && res.vintedUserId != null
          ? { actionLabel: t('qChat.importNow'), onAction: () => startVintedImportFlow(res.vintedUserId!, res.itemCount ?? 0) }
          : {}),
      });
    } catch (err: any) {
      appendMessage({ id: nextQMsgId(), role: 'q', text: err.message ?? t('qChat.replyFailed'), isError: true });
    } finally {
      setSending(false);
    }
  }

  async function handleImageUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? []);
    if (!files.length) return;
    if (fileRef.current) fileRef.current.value = '';
    for (const file of files) {
      await processImage(file);
    }
  }

  async function processImage(file: File) {
    setBusyCount(c => c + 1);
    const previewUrl = URL.createObjectURL(file);
    appendMessage({ id: nextQMsgId(), role: 'user', imageUrl: previewUrl });
    const statusId = nextQMsgId();
    appendMessage({ id: statusId, role: 'q', text: t('qChat.uploadingAndAnalyzing'), pending: true });
    try {
      const created = await createWardrobeItemFromImage(file, status => updateMessage(statusId, { text: status }));
      onItemsCreated([created]);

      updateMessage(statusId, {
        pending: false,
        text: t('qChat.itemCreated', { name: created.name, description: created.description, salePrice: fmt(created.salePrice), rentPrice: fmt(created.rentPrice) }),
        createdItem: { id: created.id, name: created.name, imageUrl: created.imageUrl, salePrice: created.salePrice, rentPrice: created.rentPrice },
      });
    } catch (err: any) {
      updateMessage(statusId, { pending: false, isError: true, text: err.message ?? t('qChat.createFailed') });
    } finally {
      setBusyCount(c => c - 1);
    }
  }

  async function startVintedImportFlow(vintedUserId: number, itemCount: number) {
    const statusId = nextQMsgId();
    appendMessage({ id: statusId, role: 'q', text: t('qChat.importProgress', { done: 0, total: itemCount }), pending: true });
    try {
      const { importId } = await api.startVintedImport(vintedUserId, itemCount);
      const poll = setInterval(async () => {
        try {
          const s = await api.getVintedImportStatus(importId);
          if (s.status === 'running') {
            updateMessage(statusId, { text: t('qChat.importProgress', { done: s.done, total: s.total }) });
            return;
          }
          clearInterval(poll);
          if (s.status === 'error') {
            updateMessage(statusId, { pending: false, isError: true, text: s.error ?? t('qChat.importFailed') });
            return;
          }
          updateMessage(statusId, { pending: false, text: s.capped
            ? t('qChat.importDoneCapped', { count: s.done, total: s.total })
            : t('qChat.importDone', { count: s.done }) });
          onImportComplete();
        } catch {
          clearInterval(poll);
          updateMessage(statusId, { pending: false, isError: true, text: t('qChat.importLostContact') });
        }
      }, 2000);
    } catch (err: any) {
      updateMessage(statusId, { pending: false, isError: true, text: err.message ?? t('qChat.importStartFailed') });
    }
  }

  return (
    <div style={{ border: `1px solid ${T.border}`, borderRadius: 8, marginBottom: 20, overflow: 'hidden', background: '#fff' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', borderBottom: `1px solid ${T.border}` }}>
        <img src="/qvestium-logo.png" alt="" style={{ height: 28, width: 'auto', objectFit: 'contain', flexShrink: 0 }} />
        <div style={{ fontSize: 11, color: T.textMuted }}>{t('qChat.subtitle')}</div>
      </div>

      {/* Messages — assistentens text flyter fritt utan bubbla (som ChatGPT),
          användarens egna meddelanden visas som en pratbubbla till höger. */}
      <div ref={scrollRef} style={{ maxHeight: 420, overflowY: 'auto', padding: '18px 16px', display: 'flex', flexDirection: 'column', gap: 16 }}>
        {messages.map(msg => (
          <div key={msg.id} style={{ display: 'flex', flexDirection: 'column', alignItems: msg.role === 'user' ? 'flex-end' : 'flex-start' }}>
            {msg.imageUrl && (
              <img src={msg.imageUrl} alt="" style={{ width: 110, height: 130, objectFit: 'cover', borderRadius: 8, marginBottom: msg.text ? 6 : 0 }} />
            )}
            {msg.text && msg.role === 'user' && (
              <div style={{
                maxWidth: '85%', background: T.rose, color: '#fff',
                borderRadius: 16, padding: '9px 14px', fontSize: 14, lineHeight: 1.5, whiteSpace: 'pre-line',
              }}>
                {msg.text}
              </div>
            )}
            {msg.text && msg.role === 'q' && (
              <div style={{
                width: '100%', color: msg.isError ? T.danger : T.text,
                fontSize: 14, lineHeight: 1.6, whiteSpace: 'pre-line', opacity: msg.pending ? 0.7 : 1,
              }}>
                <QMessageText text={msg.text} />
                {msg.pending && <span style={{ marginLeft: 6, display: 'inline-block', animation: 'spin 1s linear infinite' }}>⟳</span>}
                {!msg.pending && !msg.isError && (
                  <div style={{ display: 'flex', gap: 14, marginTop: 8 }}>
                    <button
                      type="button" onClick={() => copyText(msg.text!)} title={t('qChat.copy')}
                      style={{ background: 'none', border: 'none', padding: 2, cursor: 'pointer', color: T.textMuted, display: 'flex' }}
                    >
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
                    </button>
                  </div>
                )}
              </div>
            )}
            {msg.createdItem && (
              <div style={{ marginTop: 6, display: 'flex', gap: 8, alignItems: 'center', background: '#fff', border: `1px solid ${T.border}`, borderRadius: 8, padding: 8, maxWidth: '85%' }}>
                {msg.createdItem.imageUrl && <img src={msg.createdItem.imageUrl} alt="" style={{ width: 40, height: 50, objectFit: 'cover', borderRadius: 4, flexShrink: 0 }} />}
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: T.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{msg.createdItem.name}</div>
                  <div style={{ fontSize: 11, color: T.textMuted }}>{t('qChat.draftNotPublished')}</div>
                </div>
              </div>
            )}
            {msg.outfits && <div style={{ marginTop: 8, width: '100%' }}><OutfitCards suggestion={msg.outfits} /></div>}
            {msg.actionLabel && msg.onAction && (
              <button style={{ ...S.btn('primary'), marginTop: 6, padding: '6px 14px', fontSize: 11 }} onClick={msg.onAction}>{msg.actionLabel}</button>
            )}
          </div>
        ))}
      </div>

      {/* Input row — pill-formad som ChatGPT:s inputfält */}
      <div style={{ padding: '10px 12px', borderTop: `1px solid ${T.border}` }}>
        <div style={{
          display: 'flex', gap: 8, alignItems: 'center',
          background: '#f4f4f5', border: `1px solid ${T.border}`, borderRadius: 26, padding: '6px 8px 6px 8px',
        }}>
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            title={t('qChat.uploadImages')}
            style={{ background: '#fff', border: `1px solid ${T.border}`, borderRadius: '50%', width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0, color: T.text }}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          </button>
          <input ref={fileRef} type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={handleImageUpload} />
          <input
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSend()}
            placeholder={busyCount > 0 ? t('qChat.placeholderBusy') : t('qChat.placeholder')}
            style={{ flex: 1, border: 'none', background: 'none', outline: 'none', fontSize: 14, color: T.text, minWidth: 0 }}
          />
          <button
            onClick={handleSend}
            disabled={sending || !input.trim()}
            title={t('common.send')}
            style={{
              background: input.trim() ? T.rose : T.border, border: 'none', borderRadius: '50%',
              width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: input.trim() ? 'pointer' : 'default', flexShrink: 0, color: '#fff',
            }}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="19" x2="12" y2="5"/><polyline points="5 12 12 5 19 12"/></svg>
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// BULK-UPPLADDNING — välj många bilder på en gång, Q skapar automatiskt
// ett plaggutkast (AI-bilder + beskrivning + prisförslag) för var och en.
// ─────────────────────────────────────────────────────────────────

type BulkUploadItem = {
  id: string;
  previewUrl: string;
  status: 'pending' | 'processing' | 'done' | 'error';
  statusText?: string;
  created?: WardrobeItem;
  error?: string;
};

function BulkUploadModal({ onClose, onItemsCreated }: {
  onClose: () => void;
  onItemsCreated: (items: WardrobeItem[]) => void;
}) {
  const [items, setItems] = useState<BulkUploadItem[]>([]);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    // Öppna filväljaren direkt så flödet blir: klicka knapp -> välj många bilder -> klart
    fileRef.current?.click();
  }, []);

  function updateItem(id: string, patch: Partial<BulkUploadItem>) {
    setItems(prev => prev.map(i => i.id === id ? { ...i, ...patch } : i));
  }

  async function handleFiles(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? []);
    if (fileRef.current) fileRef.current.value = '';
    if (!files.length) { if (items.length === 0) onClose(); return; }

    const newItems: BulkUploadItem[] = files.map((f, i) => ({
      id: `bulk-${Date.now()}-${i}`,
      previewUrl: URL.createObjectURL(f),
      status: 'pending',
    }));
    setItems(prev => [...prev, ...newItems]);

    // Kör med begränsad parallellitet (2 åt gången) — snabbare än sekventiellt,
    // men skonsammare mot AI-anropen än att köra allt samtidigt.
    const queue = files.map((file, i) => ({ file, item: newItems[i] }));
    const CONCURRENCY = 2;
    let cursor = 0;
    async function worker() {
      while (cursor < queue.length) {
        const { file, item } = queue[cursor++];
        updateItem(item.id, { status: 'processing', statusText: t('bulkUploadModal.starting') });
        try {
          const created = await createWardrobeItemFromImage(file, status => updateItem(item.id, { statusText: status }));
          updateItem(item.id, { status: 'done', created });
          onItemsCreated([created]);
        } catch (err: any) {
          updateItem(item.id, { status: 'error', error: err.message ?? t('common.somethingWentWrong') });
        }
      }
    }
    await Promise.all(Array.from({ length: Math.min(CONCURRENCY, queue.length) }, worker));
  }

  const doneCount = items.filter(i => i.status === 'done' || i.status === 'error').length;
  const allDone = items.length > 0 && doneCount === items.length;

  return (
    <div style={S.modal} onClick={allDone ? onClose : undefined}>
      <div style={{ background: '#fff', borderRadius: 8, padding: 24, width: '100%', maxWidth: 520, maxHeight: '85vh', overflowY: 'auto', border: `1px solid ${T.border}` }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16, gap: 10 }}>
          <div>
            <div style={{ fontFamily: 'Georgia, "Times New Roman", serif', fontSize: 20, color: T.text }}>{t('bulkUploadModal.title')}</div>
            <div style={{ fontSize: 12, color: T.textMuted, marginTop: 2 }}>{t('bulkUploadModal.subtitle')}</div>
          </div>
          <button style={{ ...S.btn('ghost'), padding: '4px 8px', flexShrink: 0 }} onClick={onClose}>✕</button>
        </div>

        <input ref={fileRef} type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={handleFiles} />

        {items.length === 0 ? (
          <button style={{ ...S.btn('primary'), width: '100%', justifyContent: 'center', padding: '12px' }} onClick={() => fileRef.current?.click()}>
            {t('bulkUploadModal.chooseImages')}
          </button>
        ) : (
          <>
            <div style={{ fontSize: 12, color: T.textMuted, marginBottom: 12 }}>{t('bulkUploadModal.progress', { done: doneCount, total: items.length })}</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {items.map(item => (
                <div key={item.id} style={{ display: 'flex', gap: 10, alignItems: 'center', border: `1px solid ${T.border}`, borderRadius: 6, padding: 8 }}>
                  <img src={item.previewUrl} alt="" style={{ width: 44, height: 56, objectFit: 'cover', borderRadius: 4, flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    {item.status === 'done' ? (
                      <div style={{ fontSize: 13, fontWeight: 600, color: T.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.created?.name}</div>
                    ) : item.status === 'error' ? (
                      <div style={{ fontSize: 12, color: T.danger }}>{item.error}</div>
                    ) : (
                      <div style={{ fontSize: 12, color: T.textSub }}>{item.statusText ?? t('bulkUploadModal.waiting')}</div>
                    )}
                  </div>
                  <div style={{ flexShrink: 0, fontSize: 16 }}>
                    {item.status === 'done' && <span style={{ color: '#5cb85c' }}>✓</span>}
                    {item.status === 'error' && <span style={{ color: T.danger }}>✕</span>}
                    {item.status === 'processing' && <span style={{ display: 'inline-block', animation: 'spin 1s linear infinite', fontSize: 14 }}>⟳</span>}
                  </div>
                </div>
              ))}
            </div>
            {allDone ? (
              <button style={{ ...S.btn('primary'), width: '100%', justifyContent: 'center', marginTop: 16, padding: '12px' }} onClick={onClose}>
                {t('bulkUploadModal.doneClose')}
              </button>
            ) : (
              <button style={{ ...S.btn('ghost'), width: '100%', justifyContent: 'center', marginTop: 16, padding: '10px', fontSize: 12 }} onClick={() => fileRef.current?.click()}>
                {t('bulkUploadModal.addMoreImages')}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// ÅTERLÄMNING — när en hyr- eller låneperiod går ut (api/src/returns/returns.ts)
// Låntagaren fotar plagget → ägaren jämför med annonsens bilder och godkänner
// eller föreslår kompensation → båda tar ställning → lämnas/skickas → ägaren
// bekräftar mottaget.
// ─────────────────────────────────────────────────────────────────

const RETURN_VERDICTS: Array<{ key: 'destroyed' | 'heavily_degraded' | 'wear'; label: string; hint: string }> = [
  { key: 'destroyed', label: t('returnVerdicts.destroyed'), hint: t('returnVerdicts.destroyedHint') },
  { key: 'heavily_degraded', label: t('returnVerdicts.heavilyDegraded'), hint: t('returnVerdicts.heavilyDegradedHint') },
  { key: 'wear', label: t('returnVerdicts.wear'), hint: t('returnVerdicts.wearHint') },
];

function previewCompensation(verdict: string, value: number, netRent: number): number {
  const r = (n: number) => Math.round(n * 100) / 100;
  if (verdict === 'destroyed') return r(Math.max(0, value - netRent));
  if (verdict === 'heavily_degraded') return r(value * 0.5);
  if (verdict === 'wear') return r(value * 0.3);
  return 0;
}

const RETURN_STATUS_LABEL: Record<ItemReturnStatus, string> = {
  active: t('returnStatusLabel.active'),
  awaiting_review: t('returnStatusLabel.awaitingReview'),
  approved: t('returnStatusLabel.approved'),
  compensation_proposed: t('returnStatusLabel.compensationProposed'),
  compensation_agreed: t('returnStatusLabel.compensationAgreed'),
  escalated: t('returnStatusLabel.escalated'),
  handed_over: t('returnStatusLabel.handedOver'),
  shipping_booked: t('returnStatusLabel.shippingBooked'),
  completed: t('returnStatusLabel.completed'),
};

function ReturnPhotoRow({ title, urls }: { title: string; urls: string[] }) {
  return (
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: T.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>{title}</div>
      {urls.length === 0 ? <div style={{ fontSize: 12, color: T.textMuted }}>{t('returnPhotoRow.noImages')}</div> : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(90px, 1fr))', gap: 6 }}>
          {urls.map(u => (
            <a key={u} href={u} target="_blank" rel="noreferrer">
              <img src={u} alt="" style={{ width: '100%', aspectRatio: '3 / 4', objectFit: 'cover', display: 'block', background: T.surfaceHover }} />
            </a>
          ))}
        </div>
      )}
    </div>
  );
}

function ItemReturnCard({ r, onChanged }: { r: ItemReturn; onChanged: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [method, setMethod] = useState<'dropoff' | 'shipping'>('dropoff');
  const [photos, setPhotos] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);
  const [reviewMode, setReviewMode] = useState<'none' | 'reject'>('none');
  const [verdict, setVerdict] = useState<string>('wear');
  const [note, setNote] = useState('');
  const [value, setValue] = useState(r.itemValue != null ? String(r.itemValue) : '');

  const isBorrower = r.role === 'borrower';
  const due = new Date(r.dueAt);
  const daysLeft = Math.ceil((due.getTime() - Date.now()) / 86400000);

  async function run(fn: () => Promise<unknown>) {
    setBusy(true); setError(null);
    try { await fn(); onChanged(); } catch (e: any) { setError(e.message ?? t('common.somethingWentWrong')); }
    finally { setBusy(false); }
  }
  const fraktOn = useFraktjaktEnabled();
  const [choosingShipping, setChoosingShipping] = useState(false);
  async function pay(purpose: 'compensation' | 'shipping') {
    if (purpose === 'shipping' && fraktOn) { setChoosingShipping(true); return; }
    setBusy(true); setError(null);
    try { window.location.href = await api.returnPaymentSession(r.id, purpose); }
    catch (e: any) { setError(e.message ?? t('itemReturnCard.paymentStartFailed')); setBusy(false); }
  }
  async function addPhotos(files: FileList | null) {
    if (!files || files.length === 0) return;
    setUploading(true); setError(null);
    try {
      const urls: string[] = [];
      for (const f of Array.from(files).slice(0, 8 - photos.length)) urls.push((await api.uploadFile(f)).url);
      setPhotos(p => [...p, ...urls].slice(0, 8));
    } catch (e: any) { setError(e.message ?? t('itemReturnCard.uploadFailed')); }
    finally { setUploading(false); }
  }

  const numValue = Number(value.replace(',', '.'));
  const verdictLabel = RETURN_VERDICTS.find(v => v.key === r.verdict)?.label;
  const myDecision = isBorrower ? r.borrowerDecision : r.lenderDecision;
  const box = { border: `1px solid ${T.border}`, padding: 16, marginBottom: 12 } as const;
  const p = { fontSize: 13, color: T.textSub, margin: '8px 0', lineHeight: 1.5 } as const;

  return (
    <div style={box}>
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
        {r.itemImageUrl && <img src={r.itemImageUrl} alt="" style={{ width: 56, height: 72, objectFit: 'cover', flexShrink: 0 }} />}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: T.text }}>{r.itemName}</div>
          <div style={{ fontSize: 12, color: T.textMuted, marginTop: 2 }}>
            {isBorrower
              ? t(r.sourceType === 'rental' ? 'itemReturnCard.rentedFrom' : 'itemReturnCard.borrowedFrom', { name: r.counterpartName })
              : t(r.sourceType === 'rental' ? 'itemReturnCard.rentedOutTo' : 'itemReturnCard.lentTo', { name: r.counterpartName })}
            {' · '}{t('itemReturnCard.dueBy', { date: due.toLocaleDateString(locale()) })}
            {r.status === 'active' && daysLeft >= 0 && ` ${t('itemReturnCard.inDays', { count: daysLeft })}`}
            {r.status === 'active' && daysLeft < 0 && ` ${t('itemReturnCard.overdue')}`}
          </div>
          <div style={{ fontSize: 12, fontWeight: 600, color: r.status === 'escalated' ? T.danger : r.status === 'completed' ? T.success : T.text, marginTop: 4 }}>
            {RETURN_STATUS_LABEL[r.status]}
          </div>
          {r.insuranceStatus && ['pending', 'active', 'ended'].includes(r.insuranceStatus) && (
            <div style={{ fontSize: 11, color: T.textSub, marginTop: 4 }}>
              {t('itemReturnCard.insuredViaOmocom')}
              {r.insuranceClaimsFormUrl
                ? <> · <a href={r.insuranceClaimsFormUrl} target="_blank" rel="noreferrer" style={{ color: T.text }}>{t('itemReturnCard.reportDamageOmocom')}</a></>
                : !isBorrower && r.status !== 'completed' ? ` · ${t('itemReturnCard.reportDamageLater')}` : ''}
            </div>
          )}
        </div>
      </div>

      {/* ── Låntagaren: fota och välj sätt ── */}
      {isBorrower && r.status === 'active' && (
        <div style={{ marginTop: 12 }}>
          <p style={p}>{t('itemReturnCard.photoInstructions')}</p>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
            <label style={{ ...S.btn(method === 'dropoff' ? 'secondary' : 'ghost'), cursor: 'pointer' }}>
              <input type="radio" checked={method === 'dropoff'} onChange={() => setMethod('dropoff')} style={{ margin: 0 }} /> {t('itemReturnCard.dropoffAtOwner')}
            </label>
            <label style={{ ...S.btn(method === 'shipping' ? 'secondary' : 'ghost'), cursor: r.shippingAvailable ? 'pointer' : 'not-allowed', opacity: r.shippingAvailable ? 1 : 0.5 }}>
              <input type="radio" disabled={!r.shippingAvailable} checked={method === 'shipping'} onChange={() => setMethod('shipping')} style={{ margin: 0 }} />
              {r.shippingAvailable ? t('itemReturnCard.shipWithQrPrice', { amount: fmtKr(r.shippingCost) }) : t('itemReturnCard.shipWithQrSoon')}
            </label>
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
            {photos.map(u => (
              <div key={u} style={{ position: 'relative' }}>
                <img src={u} alt="" style={{ width: 72, height: 96, objectFit: 'cover', display: 'block' }} />
                <button onClick={() => setPhotos(ps => ps.filter(x => x !== u))} aria-label={t('itemReturnCard.removePhoto')}
                  style={{ position: 'absolute', top: 2, right: 2, width: 20, height: 20, border: 'none', background: 'rgba(0,0,0,0.6)', color: '#fff', cursor: 'pointer', fontSize: 12, lineHeight: '20px', padding: 0 }}>×</button>
              </div>
            ))}
            {photos.length < 8 && (
              <label style={{ width: 72, height: 96, border: `1px dashed ${T.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: T.textMuted, cursor: 'pointer', textAlign: 'center' }}>
                {uploading ? t('itemReturnCard.uploadingShort') : t('itemReturnCard.addPhoto')}
                <input type="file" accept="image/*" capture="environment" multiple hidden onChange={e => { addPhotos(e.target.files); e.target.value = ''; }} />
              </label>
            )}
          </div>
          <button style={S.btn('primary')} disabled={busy || uploading || photos.length < 2}
            onClick={() => run(() => api.startItemReturn(r.id, method, photos))}>
            {busy ? t('itemReturnCard.sendingShort') : t('itemReturnCard.sendPhotosToOwner')}
          </button>
        </div>
      )}
      {!isBorrower && r.status === 'active' && (
        <p style={p}>{t('itemReturnCard.ownerActiveInfo', { name: r.counterpartName })}</p>
      )}

      {/* ── Ägaren: jämför och bedöm ── */}
      {r.status === 'awaiting_review' && (
        <div style={{ marginTop: 12 }}>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            <ReturnPhotoRow title={t('itemReturnCard.listingPhotos')} urls={r.originalImages} />
            <ReturnPhotoRow title={t('itemReturnCard.returnPhotos')} urls={r.photos} />
          </div>
          {isBorrower ? (
            <p style={p}>{t(r.returnMethod === 'shipping' ? 'itemReturnCard.awaitingReviewShip' : 'itemReturnCard.awaitingReviewDropoff', { name: r.counterpartName })}</p>
          ) : reviewMode === 'none' ? (
            <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
              <button style={S.btn('primary')} disabled={busy} onClick={() => run(() => api.reviewItemReturn(r.id, 'approved'))}>{t('itemReturnCard.approveCondition')}</button>
              <button style={S.btn('ghost')} disabled={busy} onClick={() => setReviewMode('reject')}>{t('itemReturnCard.conditionNotOk')}</button>
            </div>
          ) : (
            <div style={{ marginTop: 12 }}>
              {RETURN_VERDICTS.map(v => (
                <label key={v.key} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 13, color: T.text, marginBottom: 6, cursor: 'pointer' }}>
                  <input type="radio" checked={verdict === v.key} onChange={() => setVerdict(v.key)} style={{ marginTop: 3 }} />
                  <span><strong>{v.label}</strong> <span style={{ color: T.textMuted }}>— {v.hint}</span></span>
                </label>
              ))}
              {r.itemValue == null && (
                <input style={{ ...S.input, marginTop: 6 }} inputMode="decimal" placeholder={t('itemReturnCard.replacementValuePlaceholder')} value={value} onChange={e => setValue(e.target.value)} />
              )}
              <textarea style={{ ...S.input, marginTop: 6, minHeight: 70, resize: 'vertical' }} maxLength={1000}
                placeholder={t('itemReturnCard.notePlaceholder')} value={note} onChange={e => setNote(e.target.value)} />
              {numValue > 0 && (
                <p style={p}>{tn('itemReturnCard.proposedCompensation', { amount: <strong>{t('common.kr', { amount: fmtKr(previewCompensation(verdict, numValue, r.netRent)) })}</strong> })}</p>
              )}
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button style={S.btn('primary')} disabled={busy || !note.trim() || !(numValue > 0)}
                  onClick={() => run(() => api.reviewItemReturn(r.id, verdict, note, r.itemValue == null ? numValue : undefined))}>{t('itemReturnCard.proposeCompensation')}</button>
                <button style={S.btn('ghost')} disabled={busy} onClick={() => setReviewMode('none')}>{t('common.cancel')}</button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Kompensation ── */}
      {(r.status === 'compensation_proposed' || r.status === 'compensation_agreed' || (r.status === 'escalated' && r.compensationAmount != null)) && (
        <div style={{ marginTop: 12, background: T.surfaceHover, padding: 12 }}>
          <div style={{ fontSize: 13, color: T.text }}>
            {tn('itemReturnCard.assessment', { verdict: <strong>{verdictLabel ?? r.verdict}</strong>, amount: <strong>{t('common.kr', { amount: fmtKr(r.compensationAmount ?? 0) })}</strong> })}
            {r.itemValue != null && <span style={{ color: T.textMuted }}> {t('itemReturnCard.replacementValue', { amount: fmtKr(r.itemValue) })}</span>}
          </div>
          {r.lenderNote && <p style={{ ...p, fontStyle: 'italic' }}>”{r.lenderNote}”</p>}
          {r.photos.length > 0 && r.status !== 'escalated' && (
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 8 }}>
              <ReturnPhotoRow title={t('itemReturnCard.listingPhotos')} urls={r.originalImages} />
              <ReturnPhotoRow title={t('itemReturnCard.returnPhotos')} urls={r.photos} />
            </div>
          )}
          {r.status === 'compensation_proposed' && (myDecision == null ? (
            <>
              <p style={p}>{isBorrower ? t('itemReturnCard.borrowerDecidePrompt') : t('itemReturnCard.lenderDecidePrompt')}</p>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button style={S.btn('primary')} disabled={busy} onClick={() => run(() => api.decideReturnCompensation(r.id, true))}>{t('itemReturnCard.approve')}</button>
                <button style={S.btn('ghost')} disabled={busy} onClick={() => { if (confirm(t('itemReturnCard.escalateConfirm'))) run(() => api.decideReturnCompensation(r.id, false)); }}>{t('itemReturnCard.disagreeToSupport')}</button>
              </div>
            </>
          ) : <p style={p}>{t('itemReturnCard.approvedWaitingFor', { name: r.counterpartName })}</p>)}
          {r.status === 'compensation_agreed' && (isBorrower ? (
            <>
              <p style={p}>{t('itemReturnCard.payCompensationInfo')}</p>
              <button style={S.btn('primary')} disabled={busy} onClick={() => pay('compensation')}>{t('itemReturnCard.payAmount', { amount: fmtKr(r.compensationAmount ?? 0) })}</button>
            </>
          ) : <p style={p}>{t('itemReturnCard.waitingForPayment', { name: r.counterpartName })}</p>)}
        </div>
      )}
      {r.status === 'escalated' && (
        <p style={p}>{t('itemReturnCard.escalatedInfo')}</p>
      )}

      {/* ── Godkänt: lämna eller skicka ── */}
      {r.status === 'approved' && (isBorrower ? (
        r.returnMethod === 'shipping' ? (
          <div style={{ marginTop: 12 }}>
            <p style={p}>{t('itemReturnCard.payShippingInfo')}</p>
            <button style={S.btn('primary')} disabled={busy} onClick={() => pay('shipping')}>
              {fraktOn ? t('shipping.chooseAndPay') : t('itemReturnCard.payShipping', { amount: fmtKr(r.shippingCost) })}
            </button>
            {choosingShipping && (
              <ShippingChoiceModal title={t('shipping.returnTitle', { name: r.itemName })} loadKey={r.id}
                load={() => api.returnShippingOptions(r.id)}
                onPay={async quoteId => { window.location.href = await api.returnPaymentSession(r.id, 'shipping', quoteId); }}
                onClose={() => setChoosingShipping(false)} />
            )}
          </div>
        ) : (
          <div style={{ marginTop: 12 }}>
            {r.dropoffAddress && <p style={p}>{t('itemReturnCard.dropoffAt')}<br /><strong style={{ whiteSpace: 'pre-line', color: T.text }}>{r.dropoffAddress}</strong></p>}
            <button style={S.btn('primary')} disabled={busy} onClick={() => run(() => api.markItemReturnHandedOver(r.id))}>{t('itemReturnCard.markHandedOver')}</button>
          </div>
        )
      ) : (
        <p style={p}>{t(r.returnMethod === 'shipping' ? 'itemReturnCard.counterpartShips' : 'itemReturnCard.counterpartDropsOff', { name: r.counterpartName })}</p>
      ))}

      {r.status === 'shipping_booked' && r.shipment?.provider === 'fraktjakt' && <ShipmentPanel shipment={r.shipment} sender={isBorrower} />}
      {r.status === 'shipping_booked' && r.shipment?.provider !== 'fraktjakt' && (
        <div style={{ marginTop: 12 }}>
          {isBorrower ? (
            <>
              <p style={p}>{t('itemReturnCard.showQrInfo')}</p>
              {r.qrCodeLink && <img src={r.qrCodeLink} alt={t('itemReturnCard.qrAlt')} style={{ width: 180, height: 180, display: 'block', background: '#fff' }} />}
              {r.labelFreeCode && <div style={{ fontSize: 13, marginTop: 6 }}>{tn('itemReturnCard.code', { code: <strong>{r.labelFreeCode}</strong> })}</div>}
            </>
          ) : <p style={p}>{t('itemReturnCard.shippingBookedInfo')}</p>}
          {r.consignmentNumber && <div style={{ fontSize: 12, color: T.textMuted, marginTop: 4 }}>{t('itemReturnCard.consignmentNumber', { number: r.consignmentNumber })}</div>}
        </div>
      )}
      {r.status === 'handed_over' && isBorrower && <p style={p}>{t('itemReturnCard.waitingForReceipt', { name: r.counterpartName })}</p>}
      {!isBorrower && (r.status === 'handed_over' || r.status === 'shipping_booked') && (
        <button style={{ ...S.btn('primary'), marginTop: 8 }} disabled={busy} onClick={() => run(() => api.confirmItemReturnReceived(r.id))}>{t('itemReturnCard.confirmReceived')}</button>
      )}

      {error && <div style={{ fontSize: 12, color: T.danger, marginTop: 8 }}>{error}</div>}
    </div>
  );
}

function needsMyReturnAction(r: ItemReturn): boolean {
  if (r.role === 'borrower') {
    return (r.status === 'active' && new Date(r.dueAt).getTime() - Date.now() < 7 * 86400000)
      || (r.status === 'compensation_proposed' && r.borrowerDecision == null)
      || r.status === 'compensation_agreed' || r.status === 'approved';
  }
  return r.status === 'awaiting_review' || (r.status === 'compensation_proposed' && r.lenderDecision == null)
    || r.status === 'handed_over' || r.status === 'shipping_booked';
}

function ReturnsSection({ returns, onChanged }: { returns: ItemReturn[]; onChanged: () => void }) {
  const open = returns.filter(r => r.status !== 'completed');
  const done = returns.filter(r => r.status === 'completed');
  if (returns.length === 0) return null;
  return (
    <div style={{ marginBottom: 28 }}>
      <div style={{ fontSize: 16, fontWeight: 700, color: T.text, marginBottom: 12 }}>{t('returnsSection.title')}</div>
      {open.map(r => <ItemReturnCard key={r.id} r={r} onChanged={onChanged} />)}
      {done.length > 0 && (
        <details>
          <summary style={{ fontSize: 12, color: T.textMuted, cursor: 'pointer', marginBottom: 8 }}>{t('returnsSection.completed', { count: done.length })}</summary>
          {done.map(r => <ItemReturnCard key={r.id} r={r} onChanged={onChanged} />)}
        </details>
      )}
    </div>
  );
}

function MinGarderobView({ user, profile, onLogin, onGoToProfile, onAddToCart, initialSubTab }: {
  user: QidUser | null;
  profile: FashionProfile | null;
  onLogin: () => void;
  onGoToProfile: () => void;
  onAddToCart: (item: CartItem) => void;
  initialSubTab?: string | null;
}) {
  const [wardrobeItems, setWardrobeItems] = useState<WardrobeItem[]>([]);
  const [likedItems, setLikedItems] = useState<WardrobeItem[]>([]);
  const [returnBags, setReturnBags] = useState<ReturnBag[]>([]);
  const [itemReturns, setItemReturns] = useState<ItemReturn[]>([]);
  const [transactions, setTransactions] = useState<WardrobeTransaction[]>([]);
  const [sales, setSales] = useState<WardrobeTransaction[]>([]);
  const [drops, setDrops] = useState<Drop[]>([]);
  const [swapsSent, setSwapsSent] = useState<SwapProposal[]>([]);
  const [swapsReceived, setSwapsReceived] = useState<SwapProposal[]>([]);
  const [offersSent, setOffersSent] = useState<PurchaseOffer[]>([]);
  const [offersReceived, setOffersReceived] = useState<PurchaseOffer[]>([]);
  const [pendingReviews, setPendingReviews] = useState<PendingReview[]>([]);
  const [loading, setLoading] = useState(true);
  // Djuplänk från mejl: /?tab=garderob&sub=<flik>
  const GARDEROB_SUBTABS = ['alla', 'egna', 'köpta', 'hyrda', 'uthyrning', 'till_salu', 'gillade', 'outfits', 'att_skicka', 'byten', 'erbjudanden', 'spårning', 'returer', 'försäljning', 'drops', 'betyg'] as const;
  type GarderobSubTab = typeof GARDEROB_SUBTABS[number];
  const [subTab, setSubTab] = useState<GarderobSubTab>((GARDEROB_SUBTABS as readonly string[]).includes(initialSubTab ?? '') ? initialSubTab as GarderobSubTab : 'alla');
  const [showAddItem, setShowAddItem] = useState<string | null>(null);
  const [showReturnModal, setShowReturnModal] = useState<WardrobeItem | null>(null);
  const [editingItem, setEditingItem] = useState<WardrobeItem | null>(null);
  const [selectedItem, setSelectedItem] = useState<WardrobeItem | null>(null);
  const [showShipModal, setShowShipModal] = useState<WardrobeTransaction | null>(null);
  const [dropSelectMode, setDropSelectMode] = useState(false);
  const [selectedForDrop, setSelectedForDrop] = useState<Set<string>>(new Set());
  const [showDropWizard, setShowDropWizard] = useState(false);
  const [deleteSelectMode, setDeleteSelectMode] = useState(false);
  const [selectedForDelete, setSelectedForDelete] = useState<Set<string>>(new Set());
  const [deletingSelected, setDeletingSelected] = useState(false);

  useEffect(() => {
    if (!user) return;
    loadAll();
  }, [user]);

  async function loadAll() {
    setLoading(true);
    try {
      const [items, liked, bags, txs, sl, dr, sentSwaps, receivedSwaps, sentOffers, receivedOffers] = await Promise.all([
        api.myWardrobe(),
        api.myLikedItems(),
        api.myReturnBags(),
        api.myTransactions(),
        api.mySales(),
        api.myDrops(),
        api.mySwapProposalsSent(),
        api.mySwapProposalsReceived(),
        api.myOffersSent(),
        api.myOffersReceived(),
      ]);
      setWardrobeItems(items);
      setLikedItems(liked);
      setReturnBags(bags);
      setTransactions(txs);
      setSales(sl);
      setDrops(dr);
      setSwapsSent(sentSwaps);
      setSwapsReceived(receivedSwaps);
      setOffersSent(sentOffers);
      setOffersReceived(receivedOffers);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
    // Separat så att ett fel här inte tömmer resten av garderoben.
    api.myReturns().then(setItemReturns).catch(e => console.error(e));
    loadPendingReviews();
  }

  function loadPendingReviews() {
    api.myPendingReviews().then(setPendingReviews).catch(e => console.error(e));
  }

  function startDropSelection() {
    setDropSelectMode(true);
    setSelectedForDrop(new Set());
    if (subTab === 'att_skicka' || subTab === 'byten' || subTab === 'erbjudanden' || subTab === 'spårning' || subTab === 'returer' || subTab === 'försäljning' || subTab === 'drops' || subTab === 'betyg' || subTab === 'outfits') {
      setSubTab('egna');
    }
  }

  function cancelDropSelection() {
    setDropSelectMode(false);
    setSelectedForDrop(new Set());
  }

  function toggleDropItem(id: string) {
    setSelectedForDrop(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  function startDeleteSelection() {
    setDeleteSelectMode(true);
    setSelectedForDelete(new Set());
    if (subTab === 'att_skicka' || subTab === 'byten' || subTab === 'erbjudanden' || subTab === 'spårning' || subTab === 'returer' || subTab === 'försäljning' || subTab === 'drops' || subTab === 'betyg' || subTab === 'outfits') {
      setSubTab('egna');
    }
  }

  function cancelDeleteSelection() {
    setDeleteSelectMode(false);
    setSelectedForDelete(new Set());
  }

  function toggleDeleteItem(id: string) {
    setSelectedForDelete(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  async function handleDeleteSelected() {
    const ids = Array.from(selectedForDelete);
    if (ids.length === 0) return;
    if (!confirm(t('minGarderobView.confirmDeleteSelected', { count: ids.length }))) return;
    setDeletingSelected(true);
    try {
      await Promise.all(ids.map(id => api.deleteWardrobeItem(id)));
      setWardrobeItems(prev => prev.filter(i => !selectedForDelete.has(i.id)));
      cancelDeleteSelection();
    } catch (err: any) {
      alert(err.message ?? t('minGarderobView.deleteSelectedFailed'));
    } finally {
      setDeletingSelected(false);
    }
  }

  async function handleCancelDrop(id: string) {
    if (!confirm(t('minGarderobView.confirmCancelDrop'))) return;
    try {
      await api.cancelDrop(id);
      await loadAll();
    } catch (err: any) { alert(err.message ?? t('common.somethingWentWrong')); }
  }

  function updateTransaction(updated: WardrobeTransaction) {
    setTransactions(p => p.map(t => t.id === updated.id ? updated : t));
    setSales(p => p.map(t => t.id === updated.id ? updated : t));
  }

  const [confirmingHandoverId, setConfirmingHandoverId] = useState<string | null>(null);
  async function handleConfirmHandover(transactionId: string) {
    setConfirmingHandoverId(transactionId);
    try {
      const updated = await api.confirmHandover(transactionId);
      updateTransaction(updated);
    } catch (err: any) { alert(err.message ?? t('common.somethingWentWrong')); }
    finally { setConfirmingHandoverId(null); }
  }

  async function handleDeleteItem(id: string) {
    if (!confirm(t('minGarderobView.confirmDeleteItem'))) return;
    try {
      await api.deleteWardrobeItem(id);
      setWardrobeItems(p => p.filter(i => i.id !== id));
      setSelectedItem(null);
    } catch (err: any) { alert(err.message ?? t('common.somethingWentWrong')); }
  }

  const [rejectingSwapId, setRejectingSwapId] = useState<string | null>(null);
  const [rejectSwapMessage, setRejectSwapMessage] = useState('');
  const [swapActionLoading, setSwapActionLoading] = useState<string | null>(null);
  const [swapError, setSwapError] = useState('');
  const [viewingSwap, setViewingSwap] = useState<{ proposal: SwapProposal; role: 'received' | 'sent' } | null>(null);

  async function handleAcceptSwap(id: string, deliveryMethod: 'pickup' | 'shipping') {
    setSwapActionLoading(id); setSwapError('');
    try {
      await api.respondToSwap(id, true, undefined, deliveryMethod);
      setViewingSwap(null);
      await loadAll();
    } catch (err: any) { setSwapError(err.message ?? t('common.somethingWentWrong')); }
    finally { setSwapActionLoading(null); }
  }

  async function handleSwapReceipt(id: string, problem?: string) {
    setSwapActionLoading(id); setSwapError('');
    try {
      const updated = problem ? await api.reportSwapProblem(id, problem) : await api.confirmSwapReceived(id);
      setViewingSwap(p => p && p.proposal.id === id ? { ...p, proposal: updated } : p);
      if (problem) alert(t('swapProposalDetailModal.reportSent'));
      await loadAll();
    } catch (err: any) { setSwapError(err.message ?? t('common.somethingWentWrong')); }
    finally { setSwapActionLoading(null); }
  }

  async function handleConfirmSwapHandover(id: string) {
    setSwapActionLoading(id); setSwapError('');
    try {
      const updated = await api.confirmSwapHandover(id);
      setViewingSwap(p => p && p.proposal.id === id ? { ...p, proposal: updated } : p);
      await loadAll();
    } catch (err: any) { setSwapError(err.message ?? t('common.somethingWentWrong')); }
    finally { setSwapActionLoading(null); }
  }

  const fraktOn = useFraktjaktEnabled();
  const [choosingSwapShipping, setChoosingSwapShipping] = useState<string | null>(null);
  async function handlePaySwapShipping(id: string) {
    if (fraktOn) { setChoosingSwapShipping(id); return; }
    setSwapActionLoading(id); setSwapError('');
    try {
      const { sessionUrl } = await api.paySwapShipping(id);
      window.location.href = sessionUrl;
    } catch (err: any) { setSwapError(err.message ?? t('common.somethingWentWrong')); setSwapActionLoading(null); }
  }

  async function handleRejectSwap(id: string, message?: string) {
    setSwapActionLoading(id); setSwapError('');
    try {
      await api.respondToSwap(id, false, (message ?? rejectSwapMessage).trim() || undefined);
      setRejectingSwapId(null);
      setRejectSwapMessage('');
      setViewingSwap(null);
      await loadAll();
    } catch (err: any) { setSwapError(err.message ?? t('common.somethingWentWrong')); }
    finally { setSwapActionLoading(null); }
  }

  async function handleCancelSwap(id: string) {
    if (!confirm(t('minGarderobView.swaps.confirmCancel'))) return;
    setSwapActionLoading(id); setSwapError('');
    try {
      await api.cancelSwapProposal(id);
      setViewingSwap(null);
      await loadAll();
    } catch (err: any) { setSwapError(err.message ?? t('common.somethingWentWrong')); }
    finally { setSwapActionLoading(null); }
  }

  // Öppnar ett bytesförslag i detaljvy. Om det är ett skickat förslag som redan
  // besvarats men inte visats än, markeras det som sett (nollar "oläst"-notisen).
  async function openSwapProposal(proposal: SwapProposal, role: 'received' | 'sent') {
    setViewingSwap({ proposal, role });
    if (role === 'sent' && proposal.status !== 'pending' && !proposal.proposerSeenAt) {
      try {
        const updated = await api.markSwapProposalSeen(proposal.id);
        setSwapsSent(prev => prev.map(s => s.id === updated.id ? updated : s));
        setViewingSwap({ proposal: updated, role: 'sent' });
      } catch { /* icke-kritiskt om detta misslyckas */ }
    }
  }

  const [rejectingOfferId, setRejectingOfferId] = useState<string | null>(null);
  const [rejectOfferMessage, setRejectOfferMessage] = useState('');
  const [offerActionLoading, setOfferActionLoading] = useState<string | null>(null);
  const [offerError, setOfferError] = useState('');

  async function handleAcceptOffer(id: string) {
    setOfferActionLoading(id); setOfferError('');
    try {
      await api.respondToOffer(id, true);
      await loadAll();
    } catch (err: any) { setOfferError(err.message ?? t('common.somethingWentWrong')); }
    finally { setOfferActionLoading(null); }
  }

  async function handleRejectOffer(id: string) {
    setOfferActionLoading(id); setOfferError('');
    try {
      await api.respondToOffer(id, false, rejectOfferMessage.trim() || undefined);
      setRejectingOfferId(null);
      setRejectOfferMessage('');
      await loadAll();
    } catch (err: any) { setOfferError(err.message ?? t('common.somethingWentWrong')); }
    finally { setOfferActionLoading(null); }
  }

  async function handleCancelOffer(id: string) {
    if (!confirm(t('minGarderobView.offers.confirmCancel'))) return;
    setOfferActionLoading(id); setOfferError('');
    try {
      await api.cancelOffer(id);
      await loadAll();
    } catch (err: any) { setOfferError(err.message ?? t('common.somethingWentWrong')); }
    finally { setOfferActionLoading(null); }
  }

  if (!user) {
    return (
      <div style={{ textAlign: 'center', padding: '48px 24px', color: T.textMuted }}>
        <div style={{ fontSize: 36, marginBottom: 12 }}>👗</div>
        <div style={{ fontSize: 16, fontWeight: 600, color: T.text, marginBottom: 8 }}>{t('minGarderobView.loginTitle')}</div>
        <div style={{ fontSize: 13, marginBottom: 16 }}>{t('minGarderobView.loginSubtitle')}</div>
        <button style={{ ...S.btn('primary'), padding: '10px 24px' }} onClick={onLogin}>{t('common.logIn')}</button>
      </div>
    );
  }

  const ownItems = wardrobeItems.filter(i => i.source === 'own');
  const boughtItems = wardrobeItems.filter(i => i.source === 'bought');
  const rentedItems = wardrobeItems.filter(i => i.source === 'rented');
  const listedForRent = wardrobeItems.filter(i => i.forRent);
  const listedForSale = wardrobeItems.filter(i => i.forSale);
  const pendingSales = sales.filter(s => s.status === 'pending');
  const activeTransactions = [...transactions, ...sales].filter(t => t.status !== 'completed' && t.status !== 'return_received');
  const pendingReceivedSwaps = swapsReceived.filter(s => s.status === 'pending');
  const pendingReceivedOffers = offersReceived.filter(o => o.status === 'pending');
  // Skickade byten som besvarats (accepterat/avböjt) men som föreslagaren inte
  // öppnat/sett ännu — driver "olästa svar"-notisen.
  const unseenRespondedSwaps = swapsSent.filter(s => s.status !== 'pending' && !s.proposerSeenAt);

  // Netto månadskostnad/intäkt: löpande uthyrningar som betalas/tjänas varje månad.
  // Intäkter räknas efter Qvestiums provision (sellerPayout), kostnader är vad man
  // själv betalar (itemPrice, ingen provision dras på köparsidan). Engångsköp och
  // avslutade/avbrutna/obetalda transaktioner räknas inte in.
  const ACTIVE_RENTAL_STATUSES = new Set(['pending', 'shipped', 'delivered', 'return_requested', 'return_shipped']);
  const monthlyIncome = sales
    .filter(t => t.type === 'rent' && ACTIVE_RENTAL_STATUSES.has(t.status))
    .reduce((sum, t) => sum + t.sellerPayout, 0);
  const monthlyCost = transactions
    .filter(t => t.type === 'rent' && ACTIVE_RENTAL_STATUSES.has(t.status))
    .reduce((sum, t) => sum + t.itemPrice, 0);
  const netMonthly = monthlyIncome - monthlyCost;

  const displayItems = subTab === 'egna' ? ownItems
    : subTab === 'köpta' ? boughtItems
    : subTab === 'hyrda' ? rentedItems
    : subTab === 'uthyrning' ? listedForRent
    : subTab === 'till_salu' ? listedForSale
    : subTab === 'alla' ? wardrobeItems
    : [];

  const subTabs: { key: typeof subTab; label: string; count?: number; alert?: boolean }[] = [
    { key: 'alla', label: t('minGarderobView.tabs.all'), count: wardrobeItems.length },
    { key: 'egna', label: t('minGarderobView.tabs.own'), count: ownItems.length },
    { key: 'köpta', label: t('minGarderobView.tabs.bought'), count: boughtItems.length },
    { key: 'hyrda', label: t('minGarderobView.tabs.rented'), count: rentedItems.length },
    { key: 'uthyrning', label: t('minGarderobView.tabs.forRent'), count: listedForRent.length },
    { key: 'till_salu', label: t('minGarderobView.tabs.forSale'), count: listedForSale.length },
    { key: 'gillade', label: t('minGarderobView.tabs.liked'), count: likedItems.length },
    { key: 'outfits', label: t('minGarderobView.tabs.outfits') },
    { key: 'drops', label: t('minGarderobView.tabs.drops'), count: drops.filter(d => d.status !== 'cancelled').length },
    { key: 'att_skicka', label: t('minGarderobView.tabs.toShip'), count: pendingSales.length, alert: pendingSales.length > 0 },
    { key: 'byten', label: t('minGarderobView.tabs.swaps'), count: swapsReceived.length + swapsSent.length, alert: pendingReceivedSwaps.length > 0 || unseenRespondedSwaps.length > 0 },
    { key: 'erbjudanden', label: t('minGarderobView.tabs.offers'), count: offersReceived.length + offersSent.length, alert: pendingReceivedOffers.length > 0 },
    { key: 'spårning', label: t('minGarderobView.tabs.tracking'), count: activeTransactions.length },
    { key: 'returer', label: t('minGarderobView.tabs.returns'), count: returnBags.length + itemReturns.filter(r => r.status !== 'completed').length, alert: itemReturns.some(r => needsMyReturnAction(r)) },
    { key: 'försäljning', label: t('minGarderobView.tabs.sales'), count: sales.length },
    { key: 'betyg', label: t('minGarderobView.tabs.reviews'), count: pendingReviews.length, alert: pendingReviews.length > 0 },
  ];

  const guideMessage = getWardrobeGuideMessage({
    wardrobeItems, ownItems, listedForSale, listedForRent, drops,
    pendingReceivedSwaps, pendingReceivedOffers, pendingSales,
    onStartDrop: startDropSelection,
    onGoToTab: setSubTab,
  });

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: '24px 16px' }}>
      {!selectedItem && !loading && (
        <QChat
          greeting={guideMessage.text}
          actionLabel={guideMessage.actionLabel}
          onAction={guideMessage.onAction}
          user={user}
          onItemsCreated={items => setWardrobeItems(p => [...items, ...p])}
          onStartDrop={startDropSelection}
          onImportComplete={loadAll}
        />
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
            <div style={{ fontFamily: 'Georgia, "Times New Roman", serif', fontSize: 26, color: T.text, marginBottom: 4 }}>{t('minGarderobView.title')}</div>
            {netMonthly !== 0 && (
              <div style={{ fontSize: 14, fontWeight: 600, color: netMonthly > 0 ? T.success : T.danger }}>
                ({netMonthly > 0 ? '+' : '-'}{t('common.krPerMonth', { amount: fmt(Math.abs(netMonthly)) })})
              </div>
            )}
          </div>
          <div style={{ fontSize: 14, color: T.textSub }}>{t('minGarderobView.subtitle')}</div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {dropSelectMode ? (
            <button style={S.btn('ghost')} onClick={cancelDropSelection}>{t('minGarderobView.cancelDrop')}</button>
          ) : deleteSelectMode ? (
            <button style={S.btn('ghost')} onClick={cancelDeleteSelection}>{t('minGarderobView.cancelDelete')}</button>
          ) : (
            <>
              <button style={S.btn('secondary')} onClick={startDropSelection}>{t('minGarderobView.addDrop')}</button>
              <button style={S.btn('secondary')} onClick={startDeleteSelection}>{t('minGarderobView.deleteItems')}</button>
            </>
          )}
          <button style={S.btn('secondary')} onClick={() => setShowAddItem('Accessoarer')}>{t('minGarderobView.addAccessories')}</button>
          <button style={S.btn('primary')} onClick={() => setShowAddItem('Plagg')}>{t('minGarderobView.addItem')}</button>
        </div>
      </div>

      {dropSelectMode && (
        <div style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 5, padding: '10px 14px', marginBottom: 18, fontSize: 13, color: T.textSub }}>
          {tn('minGarderobView.dropSelectHint', { button: <strong>{t('minGarderobView.next')}</strong> })}
        </div>
      )}

      {deleteSelectMode && (
        <div style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 5, padding: '10px 14px', marginBottom: 18, fontSize: 13, color: T.textSub }}>
          {tn('minGarderobView.deleteSelectHint', { button: <strong>{t('minGarderobView.deleteSelected')}</strong> })}
        </div>
      )}

      {/* Sub-tabs */}
      <div style={{ marginBottom: 24 }}>
        <select
          value={subTab}
          onChange={e => setSubTab(e.target.value as typeof subTab)}
          style={{ ...S.input, maxWidth: 280, fontSize: 14, fontWeight: 600 }}
        >
          {subTabs.map(st => (
            <option key={st.key} value={st.key}>
              {st.alert ? '● ' : ''}{st.label}{st.count !== undefined ? ` (${st.count})` : ''}
            </option>
          ))}
        </select>
      </div>

      {selectedItem ? (
        <WardrobeItemDetailPage
          item={selectedItem}
          user={user}
          profile={profile}
          onClose={() => setSelectedItem(null)}
          onEdit={() => { setEditingItem(selectedItem); setSelectedItem(null); }}
          onDelete={() => handleDeleteItem(selectedItem.id)}
          onGoToProfile={onGoToProfile}
          onUpdated={updated => {
            setSelectedItem(updated);
            setWardrobeItems(p => p.map(i => i.id === updated.id ? updated : i));
            setLikedItems(p => updated.likedByMe ? p.map(i => i.id === updated.id ? updated : i) : p.filter(i => i.id !== updated.id));
          }}
        />
      ) : loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: T.textMuted }}>{t('minGarderobView.loadingWardrobe')}</div>
      ) : subTab === 'gillade' ? (
        /* ── GILLADE ── */
        <div>
          {likedItems.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 48, color: T.textMuted }}>
              <div style={{ fontSize: 15, fontWeight: 600, color: T.text, marginBottom: 8 }}>{t('minGarderobView.liked.emptyTitle')}</div>
              <div style={{ fontSize: 13 }}>{t('minGarderobView.liked.emptyText')}</div>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '18px 14px' }}>
              {likedItems.map(item => (
                <SellerWardrobeCard
                  key={item.id}
                  item={item}
                  user={user}
                  onOpen={() => setSelectedItem(item)}
                  onNeedLogin={onLogin}
                  onUpdated={updated => setLikedItems(p => updated.likedByMe ? p.map(i => i.id === updated.id ? updated : i) : p.filter(i => i.id !== updated.id))}
                />
              ))}
            </div>
          )}
        </div>
      ) : subTab === 'att_skicka' ? (
        /* ── ATT SKICKA ── */
        <div>
          <div style={{ fontSize: 16, fontWeight: 700, color: T.text, marginBottom: 16 }}>{t('minGarderobView.toShip.title')}</div>
          {pendingSales.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 32, color: T.textMuted }}>
              <div style={{ fontSize: 13 }}>{t('minGarderobView.toShip.empty')}</div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {pendingSales.map(tx => (
                <div key={tx.id} style={{
                  background: 'linear-gradient(135deg, #f0ad4e22, #f0ad4e08)',
                  border: '1px solid #f0ad4e55',
                  borderRadius: 6, padding: 16,
                }}>
                  <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                    {tx.itemImageUrl && <img src={tx.itemImageUrl} alt="" style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 4, flexShrink: 0 }} />}
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: '#f0ad4e', marginBottom: 4 }}>
                        {tx.type === 'buy' ? t('minGarderobView.toShip.someoneBought') : t('minGarderobView.toShip.someoneRented')}
                      </div>
                      <div style={{ fontSize: 15, fontWeight: 600, color: T.text }}>{tx.itemName ?? t('minGarderobView.itemFallback')}</div>
                      <div style={{ display: 'flex', gap: 8, marginTop: 6, flexWrap: 'wrap', fontSize: 12 }}>
                        <span style={S.badge(tx.type === 'buy' ? '#5cb85c' : '#5bc0de')}>{tx.type === 'buy' ? t('minGarderobView.badgeSold') : t('minGarderobView.badgeRented')}</span>
                        <span style={{ color: T.textMuted }}>{t('minGarderobView.toShip.buyer', { buyer: displayNameFromUserId(tx.buyerUserId, tx.buyerName) })}</span>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginTop: 10, fontSize: 12 }}>
                        <div>
                          <div style={{ color: T.textMuted }}>{t('common.price')}</div>
                          <div style={{ fontWeight: 700, color: T.text }}>{t('common.kr', { amount: fmt(tx.itemPrice) })}</div>
                        </div>
                        <div>
                          <div style={{ color: T.textMuted }}>{tx.type === 'rent' ? t('minGarderobView.commissionRent') : t('minGarderobView.commission')}</div>
                          <div style={{ fontWeight: 600, color: tx.commissionAmount > 0 ? T.danger : T.textSub }}>{tx.commissionAmount > 0 ? t('minGarderobView.commissionDeducted', { amount: fmt(tx.commissionAmount) }) : t('common.kr', { amount: fmt(0) })}</div>
                        </div>
                        <div>
                          <div style={{ color: T.textMuted }}>{t('minGarderobView.yourPayout')}</div>
                          <div style={{ fontWeight: 700, color: '#5cb85c' }}>{t('common.kr', { amount: fmt(tx.sellerPayout) })}</div>
                        </div>
                      </div>
                      {tx.deliveryMethod === 'pickup' ? (
                        <div>
                          <button
                            style={{ ...S.btn('primary'), marginTop: 12, padding: '8px 20px', fontSize: 13, background: '#f0ad4e', color: '#1a1a1a' }}
                            onClick={() => handleConfirmHandover(tx.id)}
                            disabled={confirmingHandoverId === tx.id || Boolean(tx.sellerHandoverConfirmedAt)}
                          >
                            {tx.sellerHandoverConfirmedAt ? t('minGarderobView.toShip.awaitingBuyerConfirmation') : confirmingHandoverId === tx.id ? '...' : t('minGarderobView.toShip.confirmHandover')}
                          </button>
                          <div style={{ fontSize: 11, color: T.textMuted, marginTop: 6 }}>{t('minGarderobView.toShip.pickupHint')}</div>
                        </div>
                      ) : tx.shippingProvider === 'fraktjakt' ? (
                        <ShipmentPanel shipment={tx.shipment} sender />
                      ) : (
                        <button
                          style={{ ...S.btn('primary'), marginTop: 12, padding: '8px 20px', fontSize: 13, background: '#f0ad4e', color: '#1a1a1a' }}
                          onClick={() => setShowShipModal(tx)}
                        >
                          {t('minGarderobView.toShip.shipItem')}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : subTab === 'byten' ? (
        /* ── BYTESFÖRSLAG ── */
        <div>
          {swapError && <div style={{ color: T.danger, fontSize: 13, padding: '8px 12px', background: `${T.danger}15`, borderRadius: 3, marginBottom: 16 }}>{swapError}</div>}

          <div style={{ fontSize: 16, fontWeight: 700, color: T.text, marginBottom: 16 }}>{t('minGarderobView.swaps.receivedTitle')}</div>
          {swapsReceived.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 32, color: T.textMuted, marginBottom: 32 }}>
              <div style={{ fontSize: 13 }}>{t('minGarderobView.swaps.emptyReceived')}</div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 32 }}>
              {swapsReceived.map(sw => {
                const st = SWAP_STATUS_LABELS[sw.status] ?? { label: sw.status, color: T.textMuted };
                const isPending = sw.status === 'pending';
                const busy = swapActionLoading === sw.id;
                return (
                  <div key={sw.id} style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 6, padding: 16, cursor: 'pointer' }} onClick={() => openSwapProposal(sw, 'received')}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                        <span style={S.badge(st.color)}>{st.label}</span>
                        <span style={S.badge(sw.durationMonths ? '#5bc0de' : T.textMuted)}>
                          {sw.durationMonths ? t('common.months', { count: sw.durationMonths }) : t('common.permanent')}
                        </span>
                      </div>
                      <span style={{ fontSize: 11, color: T.textMuted }}>{new Date(sw.createdAt).toLocaleDateString(locale())}</span>
                    </div>
                    <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 10 }}>
                      <div>
                        <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 4 }}>{t('minGarderobView.swaps.yourItem')}</div>
                        <img src={sw.targetItem.imageUrl || undefined} alt={sw.targetItem.name} title={sw.targetItem.name}
                          style={{ width: 56, height: 70, objectFit: 'cover', borderRadius: 3, border: `1px solid ${T.border}`, background: T.bg }} />
                      </div>
                      <div style={{ alignSelf: 'center', fontSize: 18, color: T.textMuted }}>⇄</div>
                      <div>
                        <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 4 }}>{t('minGarderobView.swaps.offeredBy', { user: displayNameFromUserId(sw.proposerUserId, sw.proposerName) })}</div>
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                          {sw.offeredItems.map(it => (
                            <img key={it.id} src={it.imageUrl || undefined} alt={it.name} title={it.name}
                              style={{ width: 56, height: 70, objectFit: 'cover', borderRadius: 3, border: `1px solid ${T.border}`, background: T.bg }} />
                          ))}
                        </div>
                      </div>
                    </div>
                    {sw.message && (
                      <div style={{ fontSize: 12, color: T.textSub, marginBottom: 10, padding: '8px 10px', background: T.bg, borderRadius: 4 }}>
                        "{sw.message}"
                      </div>
                    )}
                    {sw.ownerMessage && (
                      <div style={{ fontSize: 12, color: T.textMuted, marginBottom: 10 }}>{t('minGarderobView.yourReply', { message: sw.ownerMessage })}</div>
                    )}
                    {isPending && (
                      rejectingSwapId === sw.id ? (
                        <div onClick={e => e.stopPropagation()}>
                          <textarea
                            style={{ ...S.input, minHeight: 50, resize: 'vertical' as const, fontFamily: 'inherit', marginBottom: 8 }}
                            value={rejectSwapMessage}
                            onChange={e => setRejectSwapMessage(e.target.value)}
                            placeholder={t('minGarderobView.swaps.rejectPlaceholder')}
                            autoFocus
                          />
                          <div style={{ display: 'flex', gap: 8 }}>
                            <button style={{ ...S.btn('ghost'), flex: 1, justifyContent: 'center' }} onClick={() => { setRejectingSwapId(null); setRejectSwapMessage(''); }}>{t('common.cancel')}</button>
                            <button style={{ ...S.btn('danger'), flex: 1, justifyContent: 'center' }} disabled={busy} onClick={() => handleRejectSwap(sw.id)}>
                              {busy ? '...' : t('minGarderobView.sendRejection')}
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', gap: 8 }} onClick={e => e.stopPropagation()}>
                          <button style={{ ...S.btn('primary'), flex: 1, justifyContent: 'center' }} disabled={busy} onClick={() => openSwapProposal(sw, 'received')}>
                            {t('minGarderobView.swaps.acceptChooseDelivery')}
                          </button>
                          <button style={{ ...S.btn('ghost'), flex: 1, justifyContent: 'center' }} disabled={busy} onClick={() => setRejectingSwapId(sw.id)}>
                            {t('common.reject')}
                          </button>
                        </div>
                      )
                    )}
                  </div>
                );
              })}
            </div>
          )}

          <div style={{ fontSize: 16, fontWeight: 700, color: T.text, marginBottom: 16 }}>{t('minGarderobView.swaps.sentTitle')}</div>
          {swapsSent.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 32, color: T.textMuted }}>
              <div style={{ fontSize: 13 }}>{t('minGarderobView.swaps.emptySent')}</div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {swapsSent.map(sw => {
                const st = SWAP_STATUS_LABELS[sw.status] ?? { label: sw.status, color: T.textMuted };
                const busy = swapActionLoading === sw.id;
                const unseen = sw.status !== 'pending' && !sw.proposerSeenAt;
                return (
                  <div key={sw.id} style={{ background: T.surface, border: `1px solid ${unseen ? T.rose : T.border}`, borderRadius: 6, padding: 16, cursor: 'pointer', position: 'relative' }} onClick={() => openSwapProposal(sw, 'sent')}>
                    {unseen && (
                      <div style={{ position: 'absolute', top: -6, right: -6, width: 12, height: 12, borderRadius: '50%', background: T.rose, border: `2px solid ${T.bg}` }} title={t('minGarderobView.swaps.newReply')} />
                    )}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                        <span style={S.badge(st.color)}>{st.label}</span>
                        <span style={S.badge(sw.durationMonths ? '#5bc0de' : T.textMuted)}>
                          {sw.durationMonths ? t('common.months', { count: sw.durationMonths }) : t('common.permanent')}
                        </span>
                        {unseen && <span style={S.badge(T.rose)}>{t('minGarderobView.swaps.newReply')}</span>}
                      </div>
                      <span style={{ fontSize: 11, color: T.textMuted }}>{new Date(sw.createdAt).toLocaleDateString(locale())}</span>
                    </div>
                    <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 10 }}>
                      <div>
                        <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 4 }}>{t('minGarderobView.swaps.theirItem')}</div>
                        <img src={sw.targetItem.imageUrl || undefined} alt={sw.targetItem.name} title={sw.targetItem.name}
                          style={{ width: 56, height: 70, objectFit: 'cover', borderRadius: 3, border: `1px solid ${T.border}`, background: T.bg }} />
                      </div>
                      <div style={{ alignSelf: 'center', fontSize: 18, color: T.textMuted }}>⇄</div>
                      <div>
                        <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 4 }}>{t('minGarderobView.swaps.youOffered')}</div>
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                          {sw.offeredItems.map(it => (
                            <img key={it.id} src={it.imageUrl || undefined} alt={it.name} title={it.name}
                              style={{ width: 56, height: 70, objectFit: 'cover', borderRadius: 3, border: `1px solid ${T.border}`, background: T.bg }} />
                          ))}
                        </div>
                      </div>
                    </div>
                    {sw.ownerMessage && (
                      <div style={{ fontSize: 12, color: T.textSub, marginBottom: 10, padding: '8px 10px', background: T.bg, borderRadius: 4 }}>
                        {t('minGarderobView.reply', { message: sw.ownerMessage })}
                      </div>
                    )}
                    {sw.status === 'pending' && (
                      <button style={{ ...S.btn('ghost'), padding: '5px 10px', fontSize: 11 }} disabled={busy} onClick={e => { e.stopPropagation(); handleCancelSwap(sw.id); }}>
                        {busy ? '...' : t('minGarderobView.swaps.cancelProposal')}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ) : subTab === 'erbjudanden' ? (
        /* ── BUD (KÖP/HYRESERBJUDANDEN) ── */
        <div>
          {offerError && <div style={{ color: T.danger, fontSize: 13, padding: '8px 12px', background: `${T.danger}15`, borderRadius: 3, marginBottom: 16 }}>{offerError}</div>}

          <div style={{ fontSize: 16, fontWeight: 700, color: T.text, marginBottom: 16 }}>{t('minGarderobView.offers.receivedTitle')}</div>
          {offersReceived.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 32, color: T.textMuted, marginBottom: 32 }}>
              <div style={{ fontSize: 13 }}>{t('minGarderobView.offers.emptyReceived')}</div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 32 }}>
              {offersReceived.map(of => {
                const st = OFFER_STATUS_LABELS[of.status] ?? { label: of.status, color: T.textMuted };
                const isPending = of.status === 'pending';
                const busy = offerActionLoading === of.id;
                return (
                  <div key={of.id} style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 6, padding: 16 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                      <span style={S.badge(st.color)}>{st.label}</span>
                      <span style={{ fontSize: 11, color: T.textMuted }}>{new Date(of.createdAt).toLocaleDateString(locale())}</span>
                    </div>
                    <div style={{ display: 'flex', gap: 12, marginBottom: 10 }}>
                      <img src={of.item.imageUrl || undefined} alt={of.item.name} title={of.item.name}
                        style={{ width: 56, height: 70, objectFit: 'cover', borderRadius: 3, border: `1px solid ${T.border}`, background: T.bg, flexShrink: 0 }} />
                      <div>
                        <div style={{ fontSize: 14, fontWeight: 600, color: T.text }}>{of.item.name}</div>
                        <div style={{ fontSize: 12, color: T.textSub, marginTop: 2 }}>
                          {of.type === 'buy' ? t('common.buy') : t('common.rent')} · <strong style={{ color: T.text }}>{of.type === 'rent' ? t('common.krPerMonth', { amount: fmt(of.offeredPrice) }) : t('common.kr', { amount: fmt(of.offeredPrice) })}</strong>
                        </div>
                        <div style={{ fontSize: 11, color: T.textMuted, marginTop: 2 }}>{t('minGarderobView.offers.offerFrom', { name: displayNameFromUserId(of.buyerUserId, of.buyerName) })}</div>
                      </div>
                    </div>
                    {of.message && (
                      <div style={{ fontSize: 12, color: T.textSub, marginBottom: 10, padding: '8px 10px', background: T.bg, borderRadius: 4 }}>
                        "{of.message}"
                      </div>
                    )}
                    {of.sellerMessage && (
                      <div style={{ fontSize: 12, color: T.textMuted, marginBottom: 10 }}>{t('minGarderobView.yourReply', { message: of.sellerMessage })}</div>
                    )}
                    {isPending && (
                      rejectingOfferId === of.id ? (
                        <div>
                          <textarea
                            style={{ ...S.input, minHeight: 50, resize: 'vertical' as const, fontFamily: 'inherit', marginBottom: 8 }}
                            value={rejectOfferMessage}
                            onChange={e => setRejectOfferMessage(e.target.value)}
                            placeholder={t('minGarderobView.offers.rejectPlaceholder')}
                            autoFocus
                          />
                          <div style={{ display: 'flex', gap: 8 }}>
                            <button style={{ ...S.btn('ghost'), flex: 1, justifyContent: 'center' }} onClick={() => { setRejectingOfferId(null); setRejectOfferMessage(''); }}>{t('common.cancel')}</button>
                            <button style={{ ...S.btn('danger'), flex: 1, justifyContent: 'center' }} disabled={busy} onClick={() => handleRejectOffer(of.id)}>
                              {busy ? '...' : t('minGarderobView.sendRejection')}
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', gap: 8 }}>
                          <button style={{ ...S.btn('primary'), flex: 1, justifyContent: 'center' }} disabled={busy} onClick={() => handleAcceptOffer(of.id)}>
                            {busy ? '...' : t('minGarderobView.offers.accept')}
                          </button>
                          <button style={{ ...S.btn('ghost'), flex: 1, justifyContent: 'center' }} disabled={busy} onClick={() => setRejectingOfferId(of.id)}>
                            {t('common.reject')}
                          </button>
                        </div>
                      )
                    )}
                  </div>
                );
              })}
            </div>
          )}

          <div style={{ fontSize: 16, fontWeight: 700, color: T.text, marginBottom: 16 }}>{t('minGarderobView.offers.sentTitle')}</div>
          {offersSent.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 32, color: T.textMuted }}>
              <div style={{ fontSize: 13 }}>{t('minGarderobView.offers.emptySent')}</div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {offersSent.map(of => {
                const st = OFFER_STATUS_LABELS[of.status] ?? { label: of.status, color: T.textMuted };
                const busy = offerActionLoading === of.id;
                return (
                  <div key={of.id} style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 6, padding: 16 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                      <span style={S.badge(st.color)}>{st.label}</span>
                      <span style={{ fontSize: 11, color: T.textMuted }}>{new Date(of.createdAt).toLocaleDateString(locale())}</span>
                    </div>
                    <div style={{ display: 'flex', gap: 12, marginBottom: 10 }}>
                      <img src={of.item.imageUrl || undefined} alt={of.item.name} title={of.item.name}
                        style={{ width: 56, height: 70, objectFit: 'cover', borderRadius: 3, border: `1px solid ${T.border}`, background: T.bg, flexShrink: 0 }} />
                      <div>
                        <div style={{ fontSize: 14, fontWeight: 600, color: T.text }}>{of.item.name}</div>
                        <div style={{ fontSize: 12, color: T.textSub, marginTop: 2 }}>
                          {of.type === 'buy' ? t('common.buy') : t('common.rent')} · <strong style={{ color: T.text }}>{of.type === 'rent' ? t('common.krPerMonth', { amount: fmt(of.offeredPrice) }) : t('common.kr', { amount: fmt(of.offeredPrice) })}</strong>
                        </div>
                      </div>
                    </div>
                    {of.sellerMessage && (
                      <div style={{ fontSize: 12, color: T.textSub, marginBottom: 10, padding: '8px 10px', background: T.bg, borderRadius: 4 }}>
                        {t('minGarderobView.reply', { message: of.sellerMessage })}
                      </div>
                    )}
                    {of.status === 'pending' && (
                      <button style={{ ...S.btn('ghost'), padding: '5px 10px', fontSize: 11 }} disabled={busy} onClick={() => handleCancelOffer(of.id)}>
                        {busy ? '...' : t('minGarderobView.offers.cancelOffer')}
                      </button>
                    )}
                    {of.status === 'accepted' && (
                      <button style={{ ...S.btn('primary'), padding: '5px 10px', fontSize: 11 }} onClick={() => {
                        const bm = (of.type === 'rent' ? (of.bindingMonths ?? 12) : 'buy') as 1 | 3 | 12 | 'buy';
                        onAddToCart({
                          id: of.item.id, name: of.item.name, price: of.offeredPrice, price1: of.offeredPrice, price3: of.offeredPrice,
                          purchasePrice: of.offeredPrice, priceUnit: of.type === 'rent' ? 'kr/mån' : 'kr', imageUrl: of.item.imageUrl ?? '',
                          qty: 1, category: of.item.category, bindingMonths: bm, kind: 'wardrobe', wardrobeItemId: of.item.id,
                          wardrobeType: of.type === 'rent' ? 'rent' : 'buy', weightTier: of.item.weightTier, offerId: of.id,
                        });
                      }}>
                        {of.type === 'rent' ? t('minGarderobView.offers.payAmountPerMonth', { amount: fmt(of.offeredPrice) }) : t('minGarderobView.offers.payAmount', { amount: fmt(of.offeredPrice) })}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ) : subTab === 'spårning' ? (
        /* ── SPÅRNING ── */
        <div>
          <div style={{ fontSize: 16, fontWeight: 700, color: T.text, marginBottom: 16 }}>{t('minGarderobView.tracking.title')}</div>
          {activeTransactions.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 32, color: T.textMuted }}>
              <div style={{ fontSize: 13 }}>{t('minGarderobView.tracking.empty')}</div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {activeTransactions.map(tx => (
                <TransactionTrackingCard
                  key={tx.id}
                  tx={tx}
                  currentUserEmail={user?.email ?? ''}
                  onUpdated={(updated) => updateTransaction(updated)}
                  onRefresh={loadAll}
                />
              ))}
            </div>
          )}
        </div>
      ) : subTab === 'returer' ? (
        /* ── RETURER ── */
        <div>
          <ReturnsSection returns={itemReturns} onChanged={() => api.myReturns().then(setItemReturns).catch(e => console.error(e))} />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: T.text }}>{t('minGarderobView.returns.bagsTitle')}</div>
            <button style={S.btn('primary')} onClick={async () => {
              try { const b = await api.orderReturnBag(); setReturnBags(p => [b, ...p]); } catch (e: any) { alert(e.message); }
            }}>{t('minGarderobView.returns.orderBag')}</button>
          </div>
          {returnBags.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 32, color: T.textMuted }}>
              <div style={{ fontSize: 13 }}>{t('minGarderobView.returns.emptyBags')}</div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {returnBags.map(bag => {
                const st = RETURN_STATUS_LABELS[bag.status] ?? { label: bag.status, color: T.textMuted };
                return (
                  <div key={bag.id} style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 6, padding: 16 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <span style={{ fontFamily: 'monospace', fontSize: 16, fontWeight: 700, color: T.rose }}>{bag.bagCode}</span>
                        <span style={{ ...S.badge(st.color) }}>{st.label}</span>
                      </div>
                      <div style={{ fontSize: 11, color: T.textMuted }}>{new Date(bag.orderedAt).toLocaleDateString(locale())}</div>
                    </div>
                    {bag.trackingNumber && (
                      <div style={{ fontSize: 12, color: T.textSub, marginBottom: 4 }}>{tn('minGarderobView.trackingNumber', { number: <span style={{ color: T.rose }}>{bag.trackingNumber}</span> })}</div>
                    )}
                    {/* Progress bar */}
                    <div style={{ display: 'flex', gap: 4, marginTop: 8 }}>
                      {['ordered', 'activated', 'shipped', 'received'].map((step, i) => {
                        const steps = ['ordered', 'activated', 'shipped', 'received'];
                        const currentIdx = steps.indexOf(bag.status);
                        const done = i <= currentIdx;
                        return (
                          <div key={step} style={{ flex: 1, height: 4, borderRadius: 1, background: done ? st.color : `${T.border}` }} />
                        );
                      })}
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: T.textMuted, marginTop: 4 }}>
                      <span>{t('minGarderobView.returns.stepOrdered')}</span><span>{t('minGarderobView.returns.stepActivated')}</span><span>{t('minGarderobView.returns.stepShipped')}</span><span>{t('minGarderobView.returns.stepReceived')}</span>
                    </div>
                    {bag.status === 'ordered' && (
                      <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
                        <ScanActivateButton bagId={bag.id} onActivated={(updated) => setReturnBags(p => p.map(b => b.id === updated.id ? updated : b))} />
                      </div>
                    )}
                    {bag.status === 'activated' && (
                      <ShipBagForm bagId={bag.id} onShipped={(updated) => setReturnBags(p => p.map(b => b.id === updated.id ? updated : b))} />
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ) : subTab === 'försäljning' ? (
        /* ── MINA FÖRSÄLJNINGAR ── */
        <div>
          <div style={{ fontSize: 16, fontWeight: 700, color: T.text, marginBottom: 16 }}>{t('minGarderobView.sales.title')}</div>
          {sales.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 32, color: T.textMuted }}>
              <div style={{ fontSize: 13 }}>{t('minGarderobView.sales.empty')}</div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {sales.map(tx => {
                const statusLabels: Record<string, { label: string; color: string }> = {
                  pending: { label: t('minGarderobView.sales.status.pending'), color: '#f0ad4e' },
                  shipped: { label: t('minGarderobView.sales.status.shipped'), color: '#5bc0de' },
                  delivered: { label: t('minGarderobView.sales.status.delivered'), color: '#5cb85c' },
                  return_requested: { label: t('minGarderobView.sales.status.returnRequested'), color: '#f0ad4e' },
                  return_shipped: { label: t('minGarderobView.sales.status.returnShipped'), color: '#5bc0de' },
                  completed: { label: t('minGarderobView.sales.status.completed'), color: '#5cb85c' },
                  return_received: { label: t('minGarderobView.sales.status.returnReceived'), color: '#5cb85c' },
                };
                const stInfo = statusLabels[tx.status] ?? { label: tx.status, color: T.textMuted };
                return (
                  <div key={tx.id} style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 6, padding: 16, display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                    {tx.itemImageUrl && <img src={tx.itemImageUrl} alt="" style={{ width: 60, height: 60, objectFit: 'cover', borderRadius: 4, flexShrink: 0 }} />}
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: T.text }}>{tx.itemName ?? t('minGarderobView.itemFallback')}</div>
                      <div style={{ display: 'flex', gap: 8, marginTop: 4, flexWrap: 'wrap' }}>
                        <span style={S.badge(tx.type === 'buy' ? '#5cb85c' : '#5bc0de')}>{tx.type === 'buy' ? t('minGarderobView.badgeSold') : t('minGarderobView.badgeRented')}</span>
                        <span style={S.badge(stInfo.color)}>{stInfo.label}</span>
                      </div>
                      {tx.sellerTrackingNumber && (
                        <div style={{ fontSize: 11, color: T.textSub, marginTop: 6 }}>{tn('minGarderobView.trackingNumber', { number: <span style={{ color: T.rose }}>{tx.sellerTrackingNumber}</span> })}</div>
                      )}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginTop: 10, fontSize: 12 }}>
                        <div>
                          <div style={{ color: T.textMuted }}>{t('common.price')}</div>
                          <div style={{ fontWeight: 700, color: T.text }}>{tx.type === 'rent' ? t('common.krPerMonth', { amount: fmt(tx.itemPrice) }) : t('common.kr', { amount: fmt(tx.itemPrice) })}</div>
                        </div>
                        <div>
                          <div style={{ color: T.textMuted }}>{tx.type === 'rent' ? t('minGarderobView.commissionRent') : t('minGarderobView.commission')}</div>
                          <div style={{ fontWeight: 600, color: tx.commissionAmount > 0 ? T.danger : T.textSub }}>{tx.commissionAmount > 0 ? t('minGarderobView.commissionDeducted', { amount: fmt(tx.commissionAmount) }) : t('common.kr', { amount: fmt(0) })}</div>
                        </div>
                        <div>
                          <div style={{ color: T.textMuted }}>{t('minGarderobView.yourPayout')}</div>
                          <div style={{ fontWeight: 700, color: '#5cb85c' }}>{t('common.kr', { amount: fmt(tx.sellerPayout) })}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ) : subTab === 'outfits' ? (
        /* ── OUTFITS (Q föreslår, se outfits/Outfits.tsx) ── */
        <OutfitsView profile={profile} onGoToProfile={onGoToProfile} />
      ) : subTab === 'betyg' ? (
        /* ── BETYG ── */
        user ? <ReviewsSection user={user} pending={pendingReviews} onChanged={loadPendingReviews} /> : null
      ) : subTab === 'drops' ? (
        /* ── DROPS ── */
        <div>
          <div style={{ fontSize: 16, fontWeight: 700, color: T.text, marginBottom: 16 }}>{t('minGarderobView.drops.title')}</div>
          {drops.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 32, color: T.textMuted }}>
              <div style={{ fontSize: 13 }}>{t('minGarderobView.drops.empty')}</div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {drops.map(drop => {
                const statusInfo = drop.status === 'live' ? { label: t('minGarderobView.drops.statusLive'), color: '#5cb85c' }
                  : drop.status === 'cancelled' ? { label: t('minGarderobView.drops.statusCancelled'), color: T.textMuted }
                  : { label: t('minGarderobView.drops.statusScheduled'), color: '#f0ad4e' };
                return (
                  <div key={drop.id} style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 6, padding: 16 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 10 }}>
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                          <span style={{ fontSize: 15, fontWeight: 700, color: T.text }}>{drop.name}</span>
                          <span style={S.badge(statusInfo.color)}>{statusInfo.label}</span>
                        </div>
                        <div style={{ fontSize: 12, color: T.textSub }}>
                          {t(drop.status === 'live' ? 'minGarderobView.drops.releasedAt' : 'minGarderobView.drops.releasesAt', { date: new Date(drop.releaseAt).toLocaleString(locale(), { dateStyle: 'long', timeStyle: 'short' }) })}
                        </div>
                      </div>
                      {drop.status === 'scheduled' && (
                        <button style={{ ...S.btn('ghost'), padding: '5px 10px', fontSize: 11 }} onClick={() => handleCancelDrop(drop.id)}>{t('common.cancel')}</button>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      {drop.items.map(it => (
                        <img key={it.id} src={it.imageUrl || undefined} alt={it.name} title={it.name}
                          style={{ width: 52, height: 65, objectFit: 'cover', borderRadius: 3, background: T.bg, border: `1px solid ${T.border}` }} />
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ) : (
        /* ── PLAGG GRID ── */
        <>
          {displayItems.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 48, color: T.textMuted }}>
              <div style={{ fontSize: 15, fontWeight: 600, color: T.text, marginBottom: 8 }}>
                {subTab === 'egna' ? t('minGarderobView.empty.own')
                  : subTab === 'köpta' ? t('minGarderobView.empty.bought')
                  : subTab === 'hyrda' ? t('minGarderobView.empty.rented')
                  : subTab === 'uthyrning' ? t('minGarderobView.empty.forRent')
                  : subTab === 'till_salu' ? t('minGarderobView.empty.forSale')
                  : t('minGarderobView.empty.all')}
              </div>
              <div style={{ fontSize: 13 }}>
                {subTab === 'egna' || subTab === 'uthyrning' || subTab === 'till_salu' ? t('minGarderobView.empty.addClothesHint') : t('minGarderobView.empty.browseHint')}
              </div>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '18px 14px' }}>
              {displayItems.map(item => (
                <WardrobeCard
                  key={item.id}
                  item={item}
                  transactions={transactions}
                  onOpen={() => setSelectedItem(item)}
                  onEdit={() => setEditingItem(item)}
                  onReturn={() => setShowReturnModal(item)}
                  onRefresh={loadAll}
                  onTransactionUpdated={updateTransaction}
                  onUpdated={(updated) => setWardrobeItems(p => p.map(i => i.id === updated.id ? updated : i))}
                  selectMode={dropSelectMode || deleteSelectMode}
                  selected={dropSelectMode ? selectedForDrop.has(item.id) : selectedForDelete.has(item.id)}
                  onToggleSelect={() => dropSelectMode ? toggleDropItem(item.id) : toggleDeleteItem(item.id)}
                />
              ))}
            </div>
          )}
        </>
      )}

      {/* Floating action bar for drop-selection mode */}
      {dropSelectMode && (
        <div style={{
          position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 900,
          background: '#fff', borderTop: `1px solid ${T.border}`,
          boxShadow: '0 -4px 16px rgba(0,0,0,0.08)',
          padding: '14px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
        }}>
          <div style={{ fontSize: 13, color: T.textSub }}>
            {tn('minGarderobView.selectedCount', { count: selectedForDrop.size, n: <strong style={{ color: T.text }}>{selectedForDrop.size}</strong> })}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button style={S.btn('ghost')} onClick={cancelDropSelection}>{t('common.cancel')}</button>
            <button style={S.btn('primary')} disabled={selectedForDrop.size === 0} onClick={() => setShowDropWizard(true)}>{t('minGarderobView.next')}</button>
          </div>
        </div>
      )}

      {/* Floating action bar for delete-selection mode */}
      {deleteSelectMode && (
        <div style={{
          position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 900,
          background: '#fff', borderTop: `1px solid ${T.border}`,
          boxShadow: '0 -4px 16px rgba(0,0,0,0.08)',
          padding: '14px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
        }}>
          <div style={{ fontSize: 13, color: T.textSub }}>
            {tn('minGarderobView.selectedCount', { count: selectedForDelete.size, n: <strong style={{ color: T.text }}>{selectedForDelete.size}</strong> })}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button style={S.btn('ghost')} onClick={cancelDeleteSelection} disabled={deletingSelected}>{t('common.cancel')}</button>
            <button
              style={{ ...S.btn('primary'), background: T.danger }}
              disabled={selectedForDelete.size === 0 || deletingSelected}
              onClick={handleDeleteSelected}
            >
              {deletingSelected ? t('minGarderobView.deleting') : t('minGarderobView.deleteSelected')}
            </button>
          </div>
        </div>
      )}

      {/* Swap proposal detail modal */}
      {viewingSwap && (
        <SwapProposalDetailModal
          proposal={viewingSwap.proposal}
          role={viewingSwap.role}
          onClose={() => setViewingSwap(null)}
          busy={swapActionLoading === viewingSwap.proposal.id}
          onAccept={viewingSwap.role === 'received' ? (deliveryMethod) => handleAcceptSwap(viewingSwap.proposal.id, deliveryMethod) : undefined}
          onReject={viewingSwap.role === 'received' ? (message) => handleRejectSwap(viewingSwap.proposal.id, message) : undefined}
          onCancel={viewingSwap.role === 'sent' ? () => handleCancelSwap(viewingSwap.proposal.id) : undefined}
          onPayShipping={() => handlePaySwapShipping(viewingSwap.proposal.id)}
          onConfirmHandover={() => handleConfirmSwapHandover(viewingSwap.proposal.id)}
          onConfirmReceived={() => handleSwapReceipt(viewingSwap.proposal.id)}
          onReportProblem={message => handleSwapReceipt(viewingSwap.proposal.id, message)}
        />
      )}
      {choosingSwapShipping && (
        <ShippingChoiceModal title={t('shipping.swapTitle')} loadKey={choosingSwapShipping}
          load={() => api.swapShippingOptions(choosingSwapShipping)}
          onPay={async quoteId => { window.location.href = (await api.paySwapShipping(choosingSwapShipping, quoteId)).sessionUrl; }}
          onClose={() => setChoosingSwapShipping(null)} />
      )}

      {/* Add item modal */}
      {showAddItem && (
        <AddWardrobeItemModal
          defaultCategory={showAddItem}
          bodyPhotos={profile?.bodyPhotos}
          onClose={() => setShowAddItem(null)}
          onCreated={(item) => { setWardrobeItems(p => [item, ...p]); setShowAddItem(null); }}
        />
      )}

      {/* Edit item modal */}
      {editingItem && (
        <EditWardrobeItemModal
          item={editingItem}
          bodyPhotos={profile?.bodyPhotos}
          onClose={() => setEditingItem(null)}
          onSaved={(updated) => { setWardrobeItems(p => p.map(i => i.id === updated.id ? updated : i)); setEditingItem(null); }}
          onDeleted={(id) => setWardrobeItems(p => p.filter(i => i.id !== id))}
        />
      )}

      {/* Return modal */}
      {showReturnModal && (
        <ReturnFlowModal
          item={showReturnModal}
          transactions={transactions}
          onClose={() => setShowReturnModal(null)}
          onBagOrdered={(bag) => { setReturnBags(p => [bag, ...p]); setShowReturnModal(null); }}
          onTransactionUpdated={updateTransaction}
        />
      )}

      {/* Ship to customer modal */}
      {showShipModal && (
        <ShipToCustomerModal
          tx={showShipModal}
          onClose={() => setShowShipModal(null)}
          onShipped={(updated) => { updateTransaction(updated); setShowShipModal(null); }}
        />
      )}

      {/* Drop wizard: name + release time → create → animated teaser */}
      {showDropWizard && (
        <DropWizardModal
          items={wardrobeItems.filter(i => selectedForDrop.has(i.id))}
          user={user}
          profile={profile}
          onClose={() => setShowDropWizard(false)}
          onDone={(drop) => {
            setDrops(p => [...p, drop]);
            setShowDropWizard(false);
            cancelDropSelection();
            loadAll();
          }}
        />
      )}
    </div>
  );
}

// ── BETYG & OMDÖME ──
// Betyg (1–5 stjärnor + kommentar) som parterna ger varandra efter en
// genomförd affär — se reviews/reviews.ts i API:t. Omdömet (snitt, antal
// köp/byten/hyror, medlem sedan) visas under användarens namn.

const DEAL_KIND_LABELS: Record<DealKind, string> = { buy: t('common.buy'), rent: t('common.rent'), swap: t('common.swap') };
const STAR_COLOR = '#e0a400';

function Stars({ value, size = 13 }: { value: number; size?: number }) {
  return (
    <span aria-label={t('stars.ariaLabel', { value: value.toLocaleString(locale()) })} style={{ display: 'inline-flex', gap: 1, lineHeight: 1 }}>
      {[1, 2, 3, 4, 5].map(i => {
        const fill = Math.max(0, Math.min(1, value - (i - 1)));
        return (
          <span key={i} aria-hidden style={{ position: 'relative', fontSize: size, color: T.border }}>
            ★
            <span style={{ position: 'absolute', left: 0, top: 0, width: `${fill * 100}%`, overflow: 'hidden', color: STAR_COLOR }}>★</span>
          </span>
        );
      })}
    </span>
  );
}

function StarInput({ value, onChange }: { value: number; onChange: (v: number) => void }) {
  const [hover, setHover] = useState(0);
  const shown = hover || value;
  return (
    <div role="radiogroup" aria-label={t('starInput.ariaLabel')} style={{ display: 'inline-flex', gap: 2 }} onMouseLeave={() => setHover(0)}>
      {[1, 2, 3, 4, 5].map(i => (
        <button key={i} type="button" role="radio" aria-checked={value === i} aria-label={t('starInput.starCount', { count: i })}
          onMouseEnter={() => setHover(i)} onClick={() => onChange(i)}
          style={{ background: 'none', border: 'none', padding: 2, cursor: 'pointer', fontSize: 26, lineHeight: 1, color: i <= shown ? STAR_COLOR : T.border }}>
          ★
        </button>
      ))}
    </div>
  );
}

function useReputation(userId: string | null | undefined) {
  const [rep, setRep] = useState<UserReputation | null>(null);
  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    api.userReputation(userId).then(r => { if (!cancelled) setRep(r); }).catch(() => {});
    return () => { cancelled = true; };
  }, [userId]);
  return rep;
}

function memberSinceLabel(iso: string): string {
  return new Date(iso).toLocaleDateString(locale(), { month: 'long', year: 'numeric' });
}

// Kompakt rad under namnet: stjärnor + snitt (antal) · köp · byten · hyror · medlem sedan.
function ReputationSummary({ rep, compact }: { rep: UserReputation; compact?: boolean }) {
  const stats = [
    t('reputationSummary.buys', { count: rep.buyCount }),
    t('reputationSummary.swaps', { count: rep.swapCount }),
    t('reputationSummary.rents', { count: rep.rentCount }),
  ].join(' · ');
  return (
    <div style={{ fontSize: compact ? 11 : 13, color: T.textSub, marginTop: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        {rep.averageRating != null ? (
          <>
            <Stars value={rep.averageRating} size={compact ? 12 : 15} />
            <span style={{ fontWeight: 700, color: T.text }}>{rep.averageRating.toLocaleString(locale(), { minimumFractionDigits: 1 })}</span>
            <span style={{ color: T.textMuted }}>{t('reputationSummary.reviewCount', { count: rep.reviewCount })}</span>
          </>
        ) : (
          <span style={{ color: T.textMuted }}>{t('reputationSummary.noRatings')}</span>
        )}
      </div>
      <div style={{ color: T.textMuted }}>
        {stats}{rep.memberSince ? ` · ${t('reputationSummary.memberSince', { date: memberSinceLabel(rep.memberSince) })}` : ''}
      </div>
    </div>
  );
}

function ReviewList({ reviews }: { reviews: UserReview[] }) {
  if (reviews.length === 0) return null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {reviews.map(r => (
        <div key={r.id} style={{ border: `1px solid ${T.border}`, borderRadius: 6, padding: '10px 12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <Stars value={r.rating} />
            <span style={{ fontSize: 12, fontWeight: 600, color: T.text }}>{r.reviewerName}</span>
            <span style={S.badge(T.textMuted)}>{DEAL_KIND_LABELS[r.kind] ?? r.kind}</span>
            <span style={{ fontSize: 11, color: T.textMuted, marginLeft: 'auto' }}>{new Date(r.createdAt).toLocaleDateString(locale())}</span>
          </div>
          {r.comment && <div style={{ fontSize: 13, color: T.textSub, marginTop: 6, whiteSpace: 'pre-wrap' }}>{r.comment}</div>}
        </div>
      ))}
    </div>
  );
}

function PendingReviewCard({ p, onDone }: { p: PendingReview; onDone: () => void }) {
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    if (!rating) { setError(t('pendingReviewCard.chooseRating')); return; }
    setBusy(true); setError(null);
    try {
      await api.submitReview(p.sourceType, p.sourceId, rating, comment.trim() || undefined);
      onDone();
    } catch (e: any) { setError(e.message ?? t('common.somethingWentWrong')); }
    finally { setBusy(false); }
  }

  return (
    <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 6, padding: 16 }}>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 10 }}>
        {p.itemImageUrl && <img src={p.itemImageUrl} alt="" style={{ width: 48, height: 60, objectFit: 'cover', borderRadius: 3, flexShrink: 0 }} />}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: T.text }}>{p.itemName}</div>
          <div style={{ fontSize: 12, color: T.textSub, marginTop: 2 }}>
            <span style={S.badge(p.kind === 'buy' ? '#5cb85c' : p.kind === 'rent' ? '#5bc0de' : '#9b59b6')}>{DEAL_KIND_LABELS[p.kind]}</span>
            {' '}{tn('pendingReviewCard.withCounterparty', { name: <strong>{p.counterpartyName}</strong>, date: new Date(p.completedAt).toLocaleDateString(locale()) })}
          </div>
        </div>
      </div>
      <div style={{ fontSize: 12, color: T.textMuted, marginBottom: 4 }}>{t('pendingReviewCard.howDidItGo', { name: p.counterpartyName })}</div>
      <StarInput value={rating} onChange={setRating} />
      <textarea
        style={{ ...S.input, minHeight: 60, resize: 'vertical' as const, fontFamily: 'inherit', marginTop: 8 }}
        value={comment} maxLength={1000} onChange={e => setComment(e.target.value)}
        placeholder={t('pendingReviewCard.commentPlaceholder')}
      />
      {error && <div style={{ color: T.danger, fontSize: 12, marginTop: 6 }}>{error}</div>}
      <button style={{ ...S.btn('primary'), marginTop: 10 }} disabled={busy} onClick={submit}>
        {busy ? '...' : t('pendingReviewCard.submit')}
      </button>
    </div>
  );
}

function ReviewsSection({ user, pending, onChanged }: { user: QidUser; pending: PendingReview[]; onChanged: () => void }) {
  const rep = useReputation(user.email);
  return (
    <div>
      <div style={{ fontSize: 16, fontWeight: 700, color: T.text, marginBottom: 6 }}>{t('reviewsSection.toReviewTitle')}</div>
      <div style={{ fontSize: 13, color: T.textSub, marginBottom: 16 }}>{t('reviewsSection.toReviewText')}</div>
      {pending.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 32, color: T.textMuted, marginBottom: 32 }}>
          <div style={{ fontSize: 13 }}>{t('reviewsSection.emptyPending')}</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 32 }}>
          {pending.map(p => <PendingReviewCard key={`${p.sourceType}-${p.sourceId}`} p={p} onDone={onChanged} />)}
        </div>
      )}
      <div style={{ fontSize: 16, fontWeight: 700, color: T.text, marginBottom: 6 }}>{t('reviewsSection.yourReputationTitle')}</div>
      {rep && <div style={{ marginBottom: 14 }}><ReputationSummary rep={rep} /></div>}
      {rep && rep.reviews.length === 0 && <div style={{ fontSize: 13, color: T.textMuted }}>{t('reviewsSection.noReviewsReceived')}</div>}
      {rep && <ReviewList reviews={rep.reviews} />}
    </div>
  );
}

// ── SELLER BADGE ──
// Shown on WardrobeItemDetailPage when the viewer doesn't own the item. Fetches
// the seller's public profile (avatar + bio only — see sellerProfile in api.ts)
// and shows a derived display name. Clickable into the seller's public wardrobe.

function SellerBadge({ userId, onClick }: { userId: string; onClick?: () => void }) {
  const [sellerProfile, setSellerProfile] = useState<PublicSellerProfile | null>(null);

  useEffect(() => {
    let cancelled = false;
    api.sellerProfile(userId).then(p => { if (!cancelled) setSellerProfile(p); }).catch(() => {});
    return () => { cancelled = true; };
  }, [userId]);

  const displayName = displayNameFromUserId(userId, sellerProfile?.displayName);
  const rep = useReputation(userId);

  return (
    <div
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, padding: '8px 10px',
        border: `1px solid ${T.border}`, borderRadius: 6,
        cursor: onClick ? 'pointer' : 'default', width: 'fit-content',
      }}
    >
      {sellerProfile?.avatarUrl ? (
        <img src={sellerProfile.avatarUrl} alt={displayName} style={{ width: 34, height: 34, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
      ) : (
        <div style={{ width: 34, height: 34, borderRadius: '50%', background: T.bg, border: `1px solid ${T.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, color: T.textMuted, flexShrink: 0 }}>
          {displayName.charAt(0).toUpperCase()}
        </div>
      )}
      <div>
        <div style={{ fontSize: 12, color: T.textMuted }}>{t('sellerBadge.soldBy')}</div>
        <div style={{ fontSize: 13, fontWeight: 700, color: T.text }}>{displayName}</div>
        {rep && <ReputationSummary rep={rep} compact />}
      </div>
    </div>
  );
}

// ── FEATURED ──
// På plaggsidan: ägaren kan lägga en köpt featured-vecka på plagget eller köpa
// ett paket (5 plagg × 1 vecka, se featured/featured.ts i API:t), och admin kan
// featura/ta bort featured utan kostnad. Featured visas överst i marknaden.

function FeaturePanel({ item, isOwner, isAdmin, onUpdated }: {
  item: WardrobeItem;
  isOwner: boolean;
  isAdmin: boolean;
  onUpdated?: (updated: WardrobeItem) => void;
}) {
  const [credits, setCredits] = useState<number | null>(null);
  const [pack, setPack] = useState<FeaturePack | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const listed = item.status === 'active' && item.source !== 'rented' && (item.forSale || item.forRent || item.forSwap);
  const adminFeatured = Boolean(item.isFeatured && !item.featuredUntil);

  useEffect(() => {
    if (!isOwner) return;
    api.myFeatureCredits().then(setCredits).catch(() => setCredits(0));
    api.featurePack().then(setPack).catch(() => {});
  }, [isOwner, item.id]);

  async function run(fn: () => Promise<void>) {
    setBusy(true); setError('');
    try { await fn(); } catch (err: any) { setError(err?.message ?? t('common.somethingWentWrong')); }
    finally { setBusy(false); }
  }

  const useCredit = () => run(async () => {
    const updated = await api.featureMyItem(item.id);
    onUpdated?.(updated);
    setCredits(c => (c == null ? c : Math.max(0, c - 1)));
  });
  const buyPack = () => run(async () => { window.location.href = await api.buyFeaturePack(item.id); });
  const toggleAdmin = () => run(async () => {
    if (item.isFeatured && item.featuredUntil && !window.confirm(t('featured.confirmRemovePaid'))) return;
    onUpdated?.(await api.adminSetItemFeatured(item.id, !item.isFeatured));
  });

  return (
    <div style={{ border: `1px solid ${item.isFeatured ? T.text : T.border}`, borderRadius: 6, padding: 14, marginBottom: 12 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: T.text, marginBottom: 4 }}>
        {item.isFeatured ? `★ ${t('featured.badge')}` : t('featured.title')}
      </div>
      <div style={{ fontSize: 12, color: T.textSub, lineHeight: 1.5, marginBottom: 10 }}>
        {item.isFeatured
          ? (adminFeatured ? t('featured.byQvestium') : t('featured.until', { date: new Date(item.featuredUntil!).toLocaleDateString(locale(), { day: 'numeric', month: 'long' }) }))
          : t('featured.explainer')}
      </div>

      {isOwner && !listed && !item.isFeatured && (
        <div style={{ fontSize: 12, color: T.textMuted }}>{t('featured.notListed')}</div>
      )}

      {isOwner && listed && !adminFeatured && credits != null && (
        credits > 0 ? (
          <>
            <button style={{ ...S.btn('primary'), width: '100%', justifyContent: 'center' }} disabled={busy} onClick={useCredit}>
              {busy ? '...' : item.isFeatured ? t('featured.extendWeek') : t('featured.featureWeek')}
            </button>
            <div style={{ fontSize: 11, color: T.textMuted, marginTop: 6 }}>{t('featured.creditsLeft', { count: credits })}</div>
          </>
        // Paketet är en digital tjänst — i iOS-appen skulle det kräva Apples köp i appen (riktlinje 3.1.1), så det säljs bara på webben.
        ) : pack && !IS_NATIVE_APP && (
          <>
            <button style={{ ...S.btn('primary'), width: '100%', justifyContent: 'center' }} disabled={busy} onClick={buyPack}>
              {busy ? '...' : t('featured.buyPack', { items: pack.items, price: fmt(pack.price) })}
            </button>
            <div style={{ fontSize: 11, color: T.textMuted, marginTop: 6, lineHeight: 1.5 }}>
              {t('featured.packInfo', { items: pack.items, perWeek: fmt(pack.pricePerItemWeek), rest: pack.items - 1 })}
            </div>
          </>
        )
      )}

      {isAdmin && (
        <button style={{ ...S.btn('secondary'), width: '100%', justifyContent: 'center', marginTop: isOwner && listed ? 8 : 0 }} disabled={busy} onClick={toggleAdmin}>
          {item.isFeatured ? t('featured.adminRemove') : t('featured.adminFeature')}
        </button>
      )}

      {error && <div style={{ fontSize: 12, color: T.danger, marginTop: 8 }}>{error}</div>}
    </div>
  );
}

// ── WARDROBE ITEM DETAIL PAGE ──
// Fullsida (inte popup) — samma mönster som ProductDetailPage för butiken.
// Visar annonsen med alla bilder (inklusive AI-genererade) i galleriet.

function WardrobeItemDetailPage({ item, user, profile, onClose, onEdit, onDelete, onGoToProfile, onViewSeller, onUpdated, onAddToCart, onNeedLogin }: {
  item: WardrobeItem;
  user: QidUser | null;
  profile: FashionProfile | null;
  onClose: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  onGoToProfile: () => void;
  onViewSeller?: (sellerId: string) => void;
  onUpdated?: (updated: WardrobeItem) => void;
  onAddToCart?: (cartItem: CartItem) => void;
  onNeedLogin?: () => void;
}) {
  const viewportWidth = useViewportWidth();
  const isNarrow = viewportWidth < MOBILE_BREAKPOINT;
  const condInfo = item.condition ? (CONDITION_LABELS[item.condition] ?? CONDITION_LABELS[5]) : null;
  // isOwner reflects whether the CURRENT VIEWER owns the item — distinct from
  // item.source, which reflects how the *field owner* acquired the item (own upload vs.
  // bought/rented). Gating Edit/Delete etc. on source === 'own' would wrongly show them
  // to any viewer on someone else's self-uploaded item.
  const isOwner = Boolean(user && item.userId === user.email);
  const [showReport, setShowReport] = useState(false);
  const [showProposeSwap, setShowProposeSwap] = useState(false);
  const [showProposeOffer, setShowProposeOffer] = useState(false);
  const canProposeOffer = Boolean(user) && !isOwner && !item.forSale && !item.forRent;

  const galleryFileRef = useRef<HTMLInputElement>(null);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [imageError, setImageError] = useState('');

  const allImages = [
    ...(item.imageUrl ? [item.imageUrl] : []),
    ...(item.galleryUrls ?? []).filter(u => u !== item.imageUrl),
  ];
  const [imgIdx, setImgIdx] = useState(0);
  const currentImg = allImages[imgIdx];

  async function handleAddImages(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? []);
    if (galleryFileRef.current) galleryFileRef.current.value = '';
    if (!files.length) return;
    setUploadingImages(true);
    setImageError('');
    try {
      const newUrls: string[] = [];
      for (const file of files) {
        const uploaded = await api.uploadFile(file);
        newUrls.push(uploaded.url);
      }
      const updated = await api.updateWardrobeItem(item.id, { galleryUrls: [...(item.galleryUrls ?? []), ...newUrls] });
      onUpdated?.(updated);
      setImgIdx(allImages.length); // hoppa till första nya bilden
    } catch (err: any) {
      setImageError(err.message ?? t('wardrobeItemDetailPage.errorUpload'));
    } finally {
      setUploadingImages(false);
    }
  }

  const listingImages: ListingImages = { imageUrl: item.imageUrl ?? '', galleryUrls: item.galleryUrls ?? [], imageVariants: item.imageVariants ?? [] };
  async function handleCutoutChange(next: ListingImages) {
    setImageError('');
    try {
      const updated = await api.updateWardrobeItem(item.id, { imageUrl: next.imageUrl || undefined, galleryUrls: next.galleryUrls, imageVariants: pruneVariants(next) });
      onUpdated?.(updated);
    } catch (err: any) {
      setImageError(err.message ?? t('bgRemoval.saveError'));
    }
  }

  async function handleSetPrimary(url: string) {
    if (url === item.imageUrl) return;
    try {
      const newGallery = [...(item.imageUrl ? [item.imageUrl] : []), ...(item.galleryUrls ?? [])].filter(u => u !== url);
      const updated = await api.updateWardrobeItem(item.id, { imageUrl: url, galleryUrls: newGallery });
      onUpdated?.(updated);
      setImgIdx(0);
    } catch (err: any) {
      setImageError(err.message ?? t('wardrobeItemDetailPage.errorSetPrimary'));
    }
  }

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: '24px 16px 64px' }}>
      <button onClick={onClose} style={{ background: 'none', border: 'none', color: T.textSub, fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, marginBottom: 20, padding: 0 }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>
        {t('common.back')}
      </button>

      <div style={{ display: 'grid', gridTemplateColumns: allImages.length > 0 && !isNarrow ? '1.4fr 1fr' : '1fr', gap: isNarrow ? 28 : 48 }}>
        {/* Image gallery */}
        {allImages.length > 0 ? (
          <div>
            <div style={{ position: 'relative', background: '#f4f4f4', overflow: 'hidden', marginBottom: 10 }}>
              <img src={currentImg} alt={item.name} style={{ width: '100%', aspectRatio: '4/5', objectFit: 'cover', display: 'block' }} />
              {isAiImage(currentImg) && <AiImageBadge />}
              {allImages.length > 1 && (
                <>
                  <button onClick={() => setImgIdx(i => (i - 1 + allImages.length) % allImages.length)}
                    style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', background: 'rgba(255,255,255,0.85)', border: 'none', color: T.text, width: 34, height: 34, borderRadius: '50%', fontSize: 18, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>‹</button>
                  <button onClick={() => setImgIdx(i => (i + 1) % allImages.length)}
                    style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'rgba(255,255,255,0.85)', border: 'none', color: T.text, width: 34, height: 34, borderRadius: '50%', fontSize: 18, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>›</button>
                </>
              )}
              {isOwner && currentImg === item.imageUrl && (
                <div style={{ position: 'absolute', bottom: 10, left: 10, background: 'rgba(0,0,0,0.6)', color: '#fff', fontSize: 11, padding: '3px 8px', borderRadius: 3 }}>{t('wardrobeItemDetailPage.primaryImage')}</div>
              )}
            </div>
            {allImages.length > 1 && (
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: isOwner ? 10 : 0 }}>
                {allImages.map((url, i) => (
                  <div key={i} style={{ position: 'relative', flexShrink: 0 }}>
                    <img src={url} alt="" onClick={() => setImgIdx(i)}
                      style={{ width: 64, height: 80, objectFit: 'cover', cursor: 'pointer', opacity: i === imgIdx ? 1 : 0.5, border: `1px solid ${i === imgIdx ? T.text : 'transparent'}` }} />
                    {isAiImage(url) && <AiImageBadge small />}
                  </div>
                ))}
              </div>
            )}

            {isOwner && (
              <div>
                {currentImg && currentImg !== item.imageUrl && (
                  <button type="button" style={{ ...S.btn('ghost'), width: '100%', justifyContent: 'center', marginBottom: 8, fontSize: 12 }} onClick={() => handleSetPrimary(currentImg)}>
                    {t('wardrobeItemDetailPage.makePrimary')}
                  </button>
                )}
                <div style={{ display: 'flex', gap: 8 }}>
                  <button type="button" style={{ ...S.btn('ghost'), flex: 1, justifyContent: 'center', fontSize: 12 }} onClick={() => galleryFileRef.current?.click()} disabled={uploadingImages}>
                    {uploadingImages ? t('wardrobeItemDetailPage.uploading') : t('wardrobeItemDetailPage.addMoreImages')}
                  </button>
                </div>
                <CutoutButton url={currentImg} images={listingImages} onChange={handleCutoutChange} />
                <input ref={galleryFileRef} type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={handleAddImages} />
                {imageError && <div style={{ fontSize: 12, color: T.danger, marginTop: 6 }}>{imageError}</div>}
              </div>
            )}
          </div>
        ) : (
          <div>
            <div style={{ background: '#f4f4f4', aspectRatio: '4/5', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 56, color: T.textMuted, marginBottom: 10 }}>👗</div>
            {isOwner && (
              <div>
                <button type="button" style={{ ...S.btn('ghost'), width: '100%', justifyContent: 'center', fontSize: 12 }} onClick={() => galleryFileRef.current?.click()} disabled={uploadingImages}>
                  {uploadingImages ? t('wardrobeItemDetailPage.uploading') : t('wardrobeItemDetailPage.addImages')}
                </button>
                <input ref={galleryFileRef} type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={handleAddImages} />
                {imageError && <div style={{ fontSize: 12, color: T.danger, marginTop: 6 }}>{imageError}</div>}
              </div>
            )}
          </div>
        )}

        {/* Info */}
        <div style={{ maxWidth: isNarrow ? '100%' : 420 }}>
          {!isOwner && (
            <SellerBadge userId={item.userId} onClick={onViewSeller ? () => onViewSeller(item.userId) : undefined} />
          )}

          <div style={{ fontSize: 11, color: T.textMuted, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>{vl('category', item.category)}</div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 14 }}>
            <div style={{ fontFamily: 'Georgia, "Times New Roman", serif', fontSize: 26, color: T.text }}>{item.name}</div>
            {isOwner ? <OwnerLikesBadge item={item} /> : (
              <WardrobeLikeButton item={item} user={user} onNeedLogin={onNeedLogin} onUpdated={onUpdated} size="lg" />
            )}
          </div>

          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
            {item.size && (
              <div style={{ fontSize: 12, color: T.text, border: `1px solid ${T.border}`, padding: '4px 10px' }}>{t('wardrobeItemDetailPage.sizeLabel', { size: item.size })}</div>
            )}
            {condInfo && <ConditionBadge condition={item.condition} />}
            <div style={{ fontSize: 12, color: T.textMuted, border: `1px solid ${T.border}`, padding: '4px 10px' }}>
              {item.source === 'own' ? t('wardrobeItemDetailPage.sourceOwn') : item.source === 'bought' ? t('wardrobeItemDetailPage.sourceBought') : item.source === 'rented' ? t('wardrobeItemDetailPage.sourceRented') : item.source}
            </div>
          </div>

          {item.description && (
            <div style={{ fontSize: 14, color: T.textSub, lineHeight: 1.7, marginBottom: 24 }}>{item.description}</div>
          )}

          {/* Listing / price info */}
          {(item.forRent || item.forSale || item.forSwap) && (
            <div style={{ background: '#fafafa', border: `1px solid ${T.border}`, padding: '14px 16px', marginBottom: 20 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: T.text, marginBottom: 10 }}>{t('wardrobeItemDetailPage.listedFor')}</div>
              {item.forSwap && (
                <div style={{ marginBottom: (item.forRent || item.forSale) ? 12 : 0, fontSize: 13, color: T.rose, fontWeight: 600 }}>{t('wardrobeItemDetailPage.openForSwap')}</div>
              )}
              {item.forRent && (
                <div style={{ marginBottom: item.forSale ? 12 : 0 }}>
                  <div style={{ fontSize: 12, color: T.textSub, marginBottom: 6 }}>{t('wardrobeItemDetailPage.rentalChooseCommitment')}</div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    {BINDING_OPTIONS.filter(o => o.months !== 'buy').map(opt => {
                      const price = opt.months === 1 ? (item.rentPrice1 ?? Math.round(item.rentPrice * 4))
                        : opt.months === 3 ? (item.rentPrice3 ?? Math.round(item.rentPrice * 3))
                        : item.rentPrice;
                      return (
                        <button
                          key={opt.months}
                          type="button"
                          onClick={() => {
                            if (!user) { onNeedLogin?.(); return; }
                            onAddToCart?.({
                              id: item.id, name: item.name, price: item.rentPrice, price1: item.rentPrice1, price3: item.rentPrice3,
                              purchasePrice: item.salePrice, priceUnit: 'kr/mån', imageUrl: item.imageUrl ?? '', qty: 1, category: item.category,
                              bindingMonths: opt.months as 1 | 3 | 12, kind: 'wardrobe', wardrobeItemId: item.id, wardrobeType: 'rent', weightTier: item.weightTier,
                            });
                          }}
                          style={{
                            flex: 1, padding: '6px 4px', borderRadius: 4, border: `1.5px solid ${T.border}`,
                            background: 'transparent', color: T.textMuted, cursor: 'pointer', fontSize: 11, fontWeight: 500,
                            textAlign: 'center', transition: 'all 0.15s',
                          }}
                        >
                          <div style={{ fontWeight: 700, fontSize: 12, color: T.text }}>{opt.label}</div>
                          <div style={{ fontSize: 11, marginTop: 2, color: T.textSub }}>{t('common.krPerMonth', { amount: fmt(price) })}</div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              {item.forSale && (
                <div>
                  <div style={{ fontSize: 12, color: T.textSub, marginBottom: 4 }}>{t('common.buy')}</div>
                  <span style={{ fontWeight: 700, fontSize: 16, color: T.text }}>{t('common.kr', { amount: fmt(item.salePrice) })}</span>
                  <div style={{ fontSize: 12, color: T.textSub, marginTop: 4 }}>
                    {tn('wardrobeItemDetailPage.buyerProtectionTotal', { fee: fmtKr(buyerProtectionFee(item.salePrice)), total: <strong>{t('common.kr', { amount: fmtKr(item.salePrice + buyerProtectionFee(item.salePrice)) })}</strong> })}
                  </div>
                </div>
              )}
              <div style={{ fontSize: 11, color: T.textMuted, marginTop: 10 }}>
                {item.forSale ? t('wardrobeItemDetailPage.buyerProtectionInfo') + ' ' : ''}
                {item.forRent ? t('wardrobeItemDetailPage.rentalCommissionInfo') : ''}
                {item.forRent && item.replacementValue ? ' ' + t('wardrobeItemDetailPage.replacementValue', { amount: fmt(item.replacementValue) }) : ''}
              </div>
            </div>
          )}

          {(isOwner || profile?.is_admin) && (
            <FeaturePanel item={item} isOwner={isOwner} isAdmin={Boolean(profile?.is_admin)} onUpdated={onUpdated} />
          )}

          {isOwner && (
            <div style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
              <button style={{ ...S.btn('primary'), flex: 1, justifyContent: 'center' }} onClick={onEdit}>{t('common.edit')}</button>
              <button style={{ ...S.btn('danger'), flex: 1, justifyContent: 'center' }} onClick={onDelete}>{t('common.delete')}</button>
            </div>
          )}

          {!isOwner && (item.forRent || item.forSale) && (
            <div style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
              {item.forRent && (
                <button style={{ ...S.btn('primary'), flex: 1, justifyContent: 'center' }}
                  onClick={() => {
                    if (!user) { onNeedLogin?.(); return; }
                    onAddToCart?.({
                      id: item.id, name: item.name, price: item.rentPrice, price1: item.rentPrice1, price3: item.rentPrice3,
                      purchasePrice: item.salePrice, priceUnit: 'kr/mån', imageUrl: item.imageUrl ?? '', qty: 1, category: item.category,
                      bindingMonths: 12, kind: 'wardrobe', wardrobeItemId: item.id, wardrobeType: 'rent', weightTier: item.weightTier,
                    });
                  }}>
                  {t('wardrobeItemDetailPage.rent')}
                </button>
              )}
              {item.forSale && (
                <button style={{ ...S.btn(item.forRent ? 'ghost' : 'primary'), flex: 1, justifyContent: 'center' }}
                  onClick={() => {
                    if (!user) { onNeedLogin?.(); return; }
                    onAddToCart?.({
                      id: item.id, name: item.name, price: item.rentPrice, price1: item.rentPrice1, price3: item.rentPrice3,
                      purchasePrice: item.salePrice, priceUnit: 'kr', imageUrl: item.imageUrl ?? '', qty: 1, category: item.category,
                      bindingMonths: 'buy', kind: 'wardrobe', wardrobeItemId: item.id, wardrobeType: 'buy', weightTier: item.weightTier,
                    });
                  }}>
                  {t('wardrobeItemDetailPage.buyFor', { amount: fmt(item.salePrice) })}
                </button>
              )}
            </div>
          )}

          {/* Privatperson eller företag — avgör köparens konsumentskydd (account/sellerType.ts) */}
          {!isOwner && item.seller?.type && (
            <div style={{ fontSize: 12, color: T.textSub, lineHeight: 1.5, marginBottom: 10 }}>
              {item.seller.type === 'business'
                ? t('sellerType.buyerBusiness', { name: item.seller.businessName ?? '', org: item.seller.orgNumber ?? '', address: item.seller.address ?? '' })
                : t('sellerType.buyerPrivate')}
            </div>
          )}

          {!isOwner && (
            <button style={{ ...S.btn('secondary'), width: '100%', justifyContent: 'center', marginBottom: 10 }}
              onClick={() => user ? openChatWith({ userId: item.userId, displayName: displayNameFromUserId(item.userId, item.sellerName), itemId: item.id, itemName: item.name }) : onNeedLogin?.()}>
              {t('wardrobeItemDetailPage.messageSeller')}
            </button>
          )}

          {user && !isOwner && (item.forSwap || canProposeOffer) && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 10 }}>
              {item.forSwap && (
                <button style={{ ...S.btn('secondary'), width: '100%', justifyContent: 'center' }} onClick={() => setShowProposeSwap(true)}>
                  {t('wardrobeItemDetailPage.proposeSwap')}
                </button>
              )}
              {canProposeOffer && (
                <button style={{ ...S.btn('secondary'), width: '100%', justifyContent: 'center' }} onClick={() => setShowProposeOffer(true)}>
                  {t('wardrobeItemDetailPage.proposeOffer')}
                </button>
              )}
            </div>
          )}

          {item.imageUrl && uploadPath(item.imageUrl) && (
            <TryOnButton
              garmentImageUrl={item.imageUrl}
              garmentName={item.name}
              garmentCategory={item.category}
              garmentSize={item.size}
              user={user}
              profile={profile}
              onNeedLogin={() => {}}
              onGoToProfile={onGoToProfile}
            />
          )}
        </div>
      </div>

      {showProposeSwap && (
        <ProposeSwapModal
          targetItem={item}
          onClose={() => setShowProposeSwap(false)}
          onProposed={() => alert(t('wardrobeItemDetailPage.swapProposalSent'))}
        />
      )}

      {showProposeOffer && (
        <ProposeOfferModal
          targetItem={item}
          onClose={() => setShowProposeOffer(false)}
          onProposed={() => alert(t('wardrobeItemDetailPage.offerSent'))}
        />
      )}

      {!isOwner && (
        <div style={{ textAlign: 'center', marginTop: 24, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
          <button style={{ background: 'none', border: 'none', color: T.textMuted, fontSize: 12, textDecoration: 'underline', cursor: 'pointer' }} onClick={() => setShowReport(true)}>
            {t('wardrobeItemDetailPage.reportListing')}
          </button>
        </div>
      )}
      {showReport && (
        <SupportTicketModal kind="report" user={user} target={{ type: 'wardrobe_item', id: item.id, name: item.name }} onClose={() => setShowReport(false)} />
      )}
    </div>
  );
}

// ── WARDROBE CARD ──

function WardrobeCard({ item, transactions, onOpen, onEdit, onReturn, onRefresh, onTransactionUpdated, onUpdated, selectMode, selected, onToggleSelect }: {
  item: WardrobeItem;
  transactions: WardrobeTransaction[];
  onOpen: () => void;
  onEdit: () => void;
  onReturn: () => void;
  onRefresh: () => void;
  onTransactionUpdated: (tx: WardrobeTransaction) => void;
  onUpdated: (updated: WardrobeItem) => void;
  selectMode?: boolean;
  selected?: boolean;
  onToggleSelect?: () => void;
}) {
  const [confirmingDelivery, setConfirmingDelivery] = useState(false);
  const [confirmingHandover, setConfirmingHandover] = useState(false);
  const [togglingField, setTogglingField] = useState<'forRent' | 'forSale' | 'forSwap' | null>(null);

  async function handleToggle(field: 'forRent' | 'forSale' | 'forSwap', value: boolean) {
    setTogglingField(field);
    try {
      const updated = await api.updateWardrobeItem(item.id, { [field]: value });
      onUpdated(updated);
    } catch (err: any) { alert(err.message ?? t('common.somethingWentWrong')); }
    finally { setTogglingField(null); }
  }
  const sourceLabel = item.source === 'own' ? t('wardrobeCard.sourceOwn') : item.source === 'bought' ? t('wardrobeCard.sourceBought') : item.source === 'rented' ? t('wardrobeCard.sourceRented') : item.source;
  const sourceColor = item.source === 'own' ? T.rose : item.source === 'bought' ? '#5cb85c' : '#5bc0de';
  const condInfo = item.condition ? (CONDITION_LABELS[item.condition] ?? CONDITION_LABELS[5]) : null;

  // Find the related transaction for bought/rented items
  const relatedTx = (item.source === 'bought' || item.source === 'rented')
    ? transactions.find(t => t.id === item.sourceOrderId)
    : null;

  const deliveryStatusLabels: Record<string, { label: string; color: string }> = {
    pending: { label: t('wardrobeCard.deliveryStatus.pending'), color: '#f0ad4e' },
    shipped: { label: t('wardrobeCard.deliveryStatus.shipped'), color: '#5bc0de' },
    delivered: { label: t('wardrobeCard.deliveryStatus.delivered'), color: '#5cb85c' },
    return_requested: { label: t('wardrobeCard.deliveryStatus.returnRequested'), color: '#f0ad4e' },
    return_shipped: { label: t('wardrobeCard.deliveryStatus.returnShipped'), color: '#5bc0de' },
    completed: { label: t('wardrobeCard.deliveryStatus.completed'), color: '#5cb85c' },
    return_received: { label: t('wardrobeCard.deliveryStatus.returnReceived'), color: '#5cb85c' },
  };

  async function handleConfirmDelivery() {
    if (!relatedTx) return;
    setConfirmingDelivery(true);
    try {
      const updated = await api.confirmDelivery(relatedTx.id);
      onTransactionUpdated(updated);
    } catch (err: any) { alert(err.message ?? t('common.somethingWentWrong')); }
    finally { setConfirmingDelivery(false); }
  }

  async function handleConfirmHandover() {
    if (!relatedTx) return;
    setConfirmingHandover(true);
    try {
      const updated = await api.confirmHandover(relatedTx.id);
      onTransactionUpdated(updated);
    } catch (err: any) { alert(err.message ?? t('common.somethingWentWrong')); }
    finally { setConfirmingHandover(false); }
  }

  const selectable = Boolean(selectMode) && item.source === 'own';

  return (
    <div
      style={{ ...S.card, cursor: selectMode ? (selectable ? 'pointer' : 'default') : 'pointer', opacity: selectMode && !selectable ? 0.4 : 1, position: 'relative' }}
      onClick={selectMode ? (selectable ? onToggleSelect : undefined) : onOpen}
    >
      <div style={{ position: 'relative', paddingTop: '125%', overflow: 'hidden', background: T.bg }}>
        {item.imageUrl ? (
          <img src={item.imageUrl} alt={item.name} style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : (
          <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', fontSize: 40, color: T.textMuted }}>👗</div>
        )}
        {isAiImage(item.imageUrl) && <AiImageBadge small corner="bottom-left" />}
        <div style={{ position: 'absolute', top: 8, left: 8, ...S.badge(sourceColor) }}>{sourceLabel}</div>
        {item.status === 'sold' && <div style={{ position: 'absolute', top: 8, right: 8, ...S.badge(T.danger) }}>{t('wardrobeCard.sold')}</div>}
        {/* Delivery status badge for bought/rented items */}
        {relatedTx && (
          <div style={{ position: 'absolute', top: 8, right: 8, ...S.badge(deliveryStatusLabels[relatedTx.status]?.color ?? T.textMuted) }}>
            {deliveryStatusLabels[relatedTx.status]?.label ?? relatedTx.status}
          </div>
        )}
        {/* Selection checkmark overlay */}
        {selectMode && selectable && (
          <div style={{
            position: 'absolute', top: 8, right: 8, width: 22, height: 22, borderRadius: '50%',
            background: selected ? T.rose : 'rgba(255,255,255,0.85)', color: selected ? '#fff' : T.textMuted,
            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700,
            border: `1px solid ${selected ? T.rose : T.border}`,
          }}>
            {selected ? '✓' : ''}
          </div>
        )}
      </div>
      <div style={{ padding: '9px 10px 10px' }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: T.text, marginBottom: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.name}</div>
        {item.dropStatus === 'scheduled' && (
          <div style={{ fontSize: 9, color: '#f0ad4e', fontWeight: 600, marginBottom: 4 }}>
            {t('wardrobeCard.inDrop', { name: item.dropName ?? '', date: item.dropReleaseAt ? new Date(item.dropReleaseAt).toLocaleDateString(locale()) : '' })}
          </div>
        )}
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 5 }}>
          {item.size && <span style={{ fontSize: 9, padding: '1px 5px', borderRadius: 4, background: `${T.rose}18`, color: T.rose, fontWeight: 600 }}>{t('wardrobeCard.sizeShort', { size: item.size })}</span>}
          {condInfo && <span style={{ fontSize: 9, padding: '1px 5px', borderRadius: 4, background: `${condInfo.color}22`, color: condInfo.color, fontWeight: 600 }}>{item.condition}/5</span>}
        </div>
        {/* Tracking info for bought/rented items */}
        {relatedTx?.sellerTrackingNumber && (relatedTx.status === 'shipped' || relatedTx.status === 'delivered') && (
          <div style={{ fontSize: 10, color: T.textSub, marginBottom: 5 }}>
            {tn('wardrobeCard.tracking', { number: <span style={{ color: T.rose, fontFamily: 'monospace' }}>{relatedTx.sellerTrackingNumber}</span> })}
          </div>
        )}
        {/* Listing info */}
        {(item.forRent || item.forSale || item.forSwap) && (
          <div style={{ fontSize: 11, marginBottom: 6 }}>
            {item.forRent && <div style={{ color: '#5bc0de', fontWeight: 600 }}>{t('common.krPerMonth', { amount: fmt(item.rentPrice) })}</div>}
            {item.forSale && <div style={{ color: '#5cb85c', fontWeight: 600 }}>{t('common.kr', { amount: fmt(item.salePrice) })}</div>}
            {item.forSwap && <div style={{ color: T.rose, fontWeight: 600 }}>{t('wardrobeCard.openForSwap')}</div>}
          </div>
        )}

        {item.source === 'own' && item.likeCount > 0 && (
          <div style={{ marginBottom: 6 }} onClick={e => e.stopPropagation()}>
            <OwnerLikesBadge item={item} />
          </div>
        )}

        {/* Quick toggles — endast egna plagg */}
        {item.source === 'own' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 8 }} onClick={e => e.stopPropagation()}>
            {([['forRent', t('wardrobeCard.toggleRent')], ['forSale', t('wardrobeCard.toggleSale')], ['forSwap', t('wardrobeCard.openForSwap')]] as const).map(([field, label]) => {
              const active = field === 'forRent' ? item.forRent : field === 'forSale' ? item.forSale : item.forSwap;
              const busy = togglingField === field;
              return (
                <label key={field} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6, cursor: busy ? 'default' : 'pointer', fontSize: 10, color: T.textSub, opacity: busy ? 0.6 : 1 }}>
                  <span>{label}</span>
                  <span
                    onClick={() => !busy && handleToggle(field, !active)}
                    style={{
                      width: 28, height: 16, borderRadius: 8, background: active ? T.rose : T.border,
                      position: 'relative', flexShrink: 0, transition: 'background 0.15s',
                    }}
                  >
                    <span style={{
                      position: 'absolute', top: 2, left: active ? 14 : 2, width: 12, height: 12, borderRadius: '50%',
                      background: '#fff', transition: 'left 0.15s',
                    }} />
                  </span>
                </label>
              );
            })}
          </div>
        )}

        {/* Action buttons */}
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {item.source === 'own' && (
            <button style={{ ...S.btn('primary'), padding: '5px 10px', fontSize: 10, fontWeight: 600 }} onClick={e => { e.stopPropagation(); onEdit(); }}>{t('common.edit')}</button>
          )}
          {/* Confirm delivery button when item is shipped */}
          {relatedTx?.status === 'shipped' && (
            <button
              style={{ ...S.btn('primary'), padding: '4px 8px', fontSize: 10, background: '#5cb85c' }}
              onClick={e => { e.stopPropagation(); handleConfirmDelivery(); }}
              disabled={confirmingDelivery}
            >
              {confirmingDelivery ? '...' : t('wardrobeCard.confirmReceived')}
            </button>
          )}
          {/* Hämta (pickup) — köparens bekräftelse att överlämningen skett */}
          {relatedTx?.status === 'pending' && relatedTx?.deliveryMethod === 'pickup' && (
            <button
              style={{ ...S.btn('primary'), padding: '4px 8px', fontSize: 10, background: '#5cb85c' }}
              onClick={e => { e.stopPropagation(); handleConfirmHandover(); }}
              disabled={confirmingHandover || Boolean(relatedTx?.buyerHandoverConfirmedAt)}
            >
              {relatedTx?.buyerHandoverConfirmedAt ? t('wardrobeCard.waitingForSeller') : confirmingHandover ? '...' : t('wardrobeCard.confirmPickedUp')}
            </button>
          )}
          {/* Return button when item is delivered */}
          {relatedTx?.status === 'delivered' && (
            <button style={{ ...S.btn('ghost'), padding: '4px 8px', fontSize: 10, color: '#f0ad4e', borderColor: '#f0ad4e44' }} onClick={e => { e.stopPropagation(); onReturn(); }}>{t('wardrobeCard.returnItem')}</button>
          )}
          {/* Fallback return for items without transaction tracking */}
          {(item.source === 'rented' || item.source === 'bought') && !relatedTx && (
            <button style={{ ...S.btn('ghost'), padding: '4px 8px', fontSize: 10, color: '#f0ad4e', borderColor: '#f0ad4e44' }} onClick={e => { e.stopPropagation(); onReturn(); }}>{t('wardrobeCard.returnItem')}</button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── SELLER WARDROBE CARD ──
// Lightweight, read-only presentational card for the seller-profile grid — no
// quick-toggles/edit/action buttons (those are all owner-only affordances that
// WardrobeCard bakes in via item.source === 'own', which isn't a safe viewer-
// ownership check for someone else's items — see isOwner note on
// WardrobeItemDetailPage). Deliberately not reusing WardrobeCard here.

function SellerWardrobeCard({ item, user, onOpen, onNeedLogin, onUpdated }: {
  item: WardrobeItem;
  user: QidUser | null;
  onOpen: () => void;
  onNeedLogin: () => void;
  onUpdated?: (updated: WardrobeItem) => void;
}) {
  const condInfo = item.condition ? (CONDITION_LABELS[item.condition] ?? CONDITION_LABELS[5]) : null;
  return (
    <div style={S.card} onClick={onOpen}>
      <div style={{ position: 'relative', paddingTop: '125%', overflow: 'hidden', background: T.bg }}>
        {item.imageUrl ? (
          <img src={item.imageUrl} alt={item.name} style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : (
          <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', fontSize: 40, color: T.textMuted }}>👗</div>
        )}
        {isAiImage(item.imageUrl) && <AiImageBadge small />}
        {item.isFeatured && item.status === 'active' && <FeaturedBadge />}
        {item.status === 'sold' && <div style={{ position: 'absolute', top: 8, right: 8, ...S.badge(T.danger) }}>{t('sellerWardrobeCard.sold')}</div>}
        <div style={{ position: 'absolute', top: 8, right: item.status === 'sold' ? 60 : 8, background: 'rgba(255,255,255,0.85)', borderRadius: 14, padding: '4px 8px' }}>
          <WardrobeLikeButton item={item} user={user} onNeedLogin={onNeedLogin} onUpdated={onUpdated} />
        </div>
      </div>
      <div style={{ padding: '9px 10px 10px' }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: T.text, marginBottom: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.name}</div>
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 5 }}>
          {item.size && <span style={{ fontSize: 9, padding: '1px 5px', borderRadius: 4, background: `${T.rose}18`, color: T.rose, fontWeight: 600 }}>{t('sellerWardrobeCard.sizeShort', { size: item.size })}</span>}
          {condInfo && <span style={{ fontSize: 9, padding: '1px 5px', borderRadius: 4, background: `${condInfo.color}22`, color: condInfo.color, fontWeight: 600 }}>{item.condition}/5</span>}
        </div>
        {(item.forRent || item.forSale || item.forSwap) ? (
          <div style={{ fontSize: 11 }}>
            {item.forRent && <div style={{ color: '#5bc0de', fontWeight: 600 }}>{t('common.krPerMonth', { amount: fmt(item.rentPrice) })}</div>}
            {item.forSale && <div style={{ color: '#5cb85c', fontWeight: 600 }}>{t('common.kr', { amount: fmt(item.salePrice) })}</div>}
            {item.forSwap && <div style={{ color: T.rose, fontWeight: 600 }}>{t('sellerWardrobeCard.openForSwap')}</div>}
          </div>
        ) : (
          <div style={{ fontSize: 10, color: T.textMuted }}>{t('sellerWardrobeCard.notListed')}</div>
        )}
      </div>
    </div>
  );
}

// ── SELLER PROFILE VIEW ──
// Public, read-only view of another user's profile + their full active wardrobe
// (not just items listed for sale/rent, per spec). Opened by clicking the seller
// badge on WardrobeItemDetailPage; clicking an item here opens the exact same
// WardrobeItemDetailPage (still correctly gated by isOwner for whoever's viewing).

function SellerProfileView({ sellerId, user, onClose, onOpenItem, onNeedLogin }: {
  sellerId: string;
  user: QidUser | null;
  onClose: () => void;
  onOpenItem: (item: WardrobeItem) => void;
  onNeedLogin: () => void;
}) {
  const [sellerProfile, setSellerProfile] = useState<PublicSellerProfile | null>(null);
  const [items, setItems] = useState<WardrobeItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([api.sellerProfile(sellerId), api.sellerWardrobe(sellerId)])
      .then(([p, its]) => { setSellerProfile(p); setItems(its); })
      .catch(err => console.error(err))
      .finally(() => setLoading(false));
  }, [sellerId]);

  const displayName = displayNameFromUserId(sellerId, sellerProfile?.displayName);
  const rep = useReputation(sellerId);

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: '24px 16px 64px' }}>
      <button onClick={onClose} style={{ background: 'none', border: 'none', color: T.textSub, fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, marginBottom: 20, padding: 0 }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>
        {t('common.back')}
      </button>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: T.textMuted }}>{t('sellerProfileView.loadingProfile')}</div>
      ) : (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 32 }}>
            {sellerProfile?.avatarUrl ? (
              <img src={sellerProfile.avatarUrl} alt={displayName} style={{ width: 72, height: 72, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
            ) : (
              <div style={{ width: 72, height: 72, borderRadius: '50%', background: T.bg, border: `1px solid ${T.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 26, fontWeight: 700, color: T.textMuted, flexShrink: 0 }}>
                {displayName.charAt(0).toUpperCase()}
              </div>
            )}
            <div>
              <div style={{ fontFamily: 'Georgia, "Times New Roman", serif', fontSize: 24, color: T.text }}>{displayName}</div>
              {rep && <ReputationSummary rep={rep} />}
              {sellerProfile?.bio && <div style={{ fontSize: 13, color: T.textSub, marginTop: 4, maxWidth: 480 }}>{sellerProfile.bio}</div>}
              {sellerProfile?.area && (
                <div style={{ width: 240, maxWidth: '100%' }}>
                  <AreaMapCard area={sellerProfile.area} caption={t('sellerProfileView.approximateArea')} height={120} />
                </div>
              )}
              {user?.email !== sellerId && (
                <button style={{ ...S.btn('secondary'), padding: '6px 14px', fontSize: 12, marginTop: 10 }}
                  onClick={() => user ? openChatWith({ userId: sellerId, displayName }) : onNeedLogin?.()}>
                  {t('sellerProfileView.sendMessage')}
                </button>
              )}
            </div>
          </div>

          {rep && rep.reviews.length > 0 && (
            <div style={{ marginBottom: 32, maxWidth: 640 }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: T.text, marginBottom: 12 }}>{t('sellerProfileView.reviewsHeading', { count: rep.reviewCount })}</div>
              <ReviewList reviews={rep.reviews} />
            </div>
          )}

          <div style={{ fontSize: 16, fontWeight: 700, color: T.text, marginBottom: 16 }}>{t('sellerProfileView.wardrobeHeading', { count: items.length })}</div>
          {items.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 32, color: T.textMuted }}>
              <div style={{ fontSize: 13 }}>{t('sellerProfileView.emptyWardrobe')}</div>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '18px 14px' }}>
              {items.map(item => (
                <SellerWardrobeCard
                  key={item.id}
                  item={item}
                  user={user}
                  onOpen={() => onOpenItem(item)}
                  onNeedLogin={onNeedLogin}
                  onUpdated={updated => setItems(p => p.map(i => i.id === updated.id ? updated : i))}
                />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── DROP WIZARD MODAL ──
// Steg 1: namnge dropet och sätt släppdatum/tid → skapar dropet.
// Steg 2: animerad teaser (se DropAnimationPreview) av de valda plaggen.

function nowLocalInputValue(minutesFromNow: number = 5) {
  const d = new Date(Date.now() + minutesFromNow * 60 * 1000);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function DropWizardModal({ items, user, profile, onClose, onDone }: {
  items: WardrobeItem[];
  user: QidUser | null;
  profile: FashionProfile | null;
  onClose: () => void;
  onDone: (drop: Drop) => void;
}) {
  const [step, setStep] = useState<'details' | 'animation'>('details');
  const [name, setName] = useState('');
  const [releaseAt, setReleaseAt] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [createdDrop, setCreatedDrop] = useState<Drop | null>(null);
  const [showShare, setShowShare] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim()) { setError(t('dropWizardModal.errorNameRequired')); return; }
    if (!releaseAt) { setError(t('dropWizardModal.errorReleaseRequired')); return; }
    const releaseTs = new Date(releaseAt).getTime();
    if (!Number.isFinite(releaseTs) || releaseTs <= Date.now()) { setError(t('dropWizardModal.errorReleaseInPast')); return; }
    setSaving(true); setError('');
    try {
      const drop = await api.createDrop({
        name: name.trim(),
        releaseAt: new Date(releaseTs).toISOString(),
        wardrobeItemIds: items.map(i => i.id),
      });
      setCreatedDrop(drop);
      setStep('animation');
    } catch (err: any) { setError(err.message ?? t('common.somethingWentWrong')); }
    finally { setSaving(false); }
  }

  return (
    <div style={S.modal} onClick={step === 'details' ? onClose : undefined}>
      <div
        style={{
          background: T.surface, borderRadius: 8, padding: step === 'animation' ? '0 0 4px' : 28,
          width: '100%', maxWidth: step === 'animation' ? 420 : 480, maxHeight: '90vh',
          overflowY: 'auto', border: `1px solid ${T.border}`,
        }}
        onClick={e => e.stopPropagation()}
      >
        {step === 'details' ? (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <div style={{ fontSize: 18, fontWeight: 700, color: T.text }}>{t('dropWizardModal.createDrop')}</div>
              <button style={{ ...S.btn('ghost'), padding: '4px 8px' }} onClick={onClose}>✕</button>
            </div>

            <div style={{ fontSize: 12, color: T.textSub, marginBottom: 8 }}>{t('dropWizardModal.itemsSelected', { count: items.length })}</div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 20 }}>
              {items.map(it => (
                <img key={it.id} src={it.imageUrl || undefined} alt={it.name} title={it.name}
                  style={{ width: 44, height: 55, objectFit: 'cover', borderRadius: 3, border: `1px solid ${T.border}`, background: T.bg }} />
              ))}
            </div>

            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <div style={{ fontSize: 12, color: T.textSub, marginBottom: 4 }}>{t('dropWizardModal.nameLabel')}</div>
                <input style={S.input} value={name} onChange={e => setName(e.target.value)} placeholder={t('dropWizardModal.namePlaceholder')} required />
              </div>
              <div>
                <div style={{ fontSize: 12, color: T.textSub, marginBottom: 4 }}>{t('dropWizardModal.releaseLabel')}</div>
                <input style={S.input} type="datetime-local" min={nowLocalInputValue()} value={releaseAt} onChange={e => setReleaseAt(e.target.value)} required />
              </div>
              {error && <div style={{ color: T.danger, fontSize: 13, padding: '8px 12px', background: `${T.danger}15`, borderRadius: 3 }}>{error}</div>}
              <button type="submit" style={{ ...S.btn('primary'), justifyContent: 'center', padding: '10px' }} disabled={saving}>
                {saving ? t('dropWizardModal.creatingDrop') : t('dropWizardModal.createDrop')}
              </button>
            </form>
          </>
        ) : (
          createdDrop && (
            <>
              <DropAnimationPreview
                drop={createdDrop}
                items={items}
                user={user}
                profile={profile}
                onDone={() => onDone(createdDrop)}
                onReachedOutro={() => setShowShare(true)}
              />
              {showShare && (
                <>
                  <DropSharePanel drop={createdDrop} items={items} user={user} profile={profile} />
                  <div style={{ padding: '4px 24px 24px' }}>
                    <button type="button" style={{ ...S.btn('ghost'), width: '100%', justifyContent: 'center' }} onClick={() => onDone(createdDrop)}>
                      {t('common.close')}
                    </button>
                  </div>
                </>
              )}
            </>
          )
        )}
      </div>
    </div>
  );
}

// ── DROP ANIMATION PREVIEW ──
// Ren CSS/React-driven teaser (ingen videoexport): intro (logo + användare) →
// plaggen svepande blurrade → outro (namn + släppdatum + qvestium.com).

function DropAnimationPreview({ drop, items, user, profile, onDone, onReachedOutro }: {
  drop: Drop;
  items: WardrobeItem[];
  user: QidUser | null;
  profile: FashionProfile | null;
  onDone: () => void;
  onReachedOutro?: () => void;
}) {
  const [stage, setStage] = useState<'intro' | 'sweep' | 'outro'>('intro');

  useEffect(() => {
    const t1 = setTimeout(() => setStage('sweep'), 2200);
    const t2 = setTimeout(() => setStage('outro'), 2200 + 2600);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []);

  useEffect(() => {
    if (stage === 'outro') onReachedOutro?.();
  }, [stage]);

  const avatarUrl = profile?.avatarUrl ?? user?.picture;
  const displayName = user?.name ?? user?.email ?? 'Qvestium';
  const releaseLabel = new Date(drop.releaseAt).toLocaleString(locale(), { dateStyle: 'long', timeStyle: 'short' });
  const sweepImages = (items.length > 0 ? [...items, ...items] : [])
    .map(i => i.imageUrl)
    .filter((u): u is string => Boolean(u))
    .slice(0, Math.max(items.length, 6));

  return (
    <div style={{ position: 'relative', aspectRatio: '4/5', background: '#111111', overflow: 'hidden', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <style>{`
        @keyframes dropFadeIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes dropSweepMove { from { transform: translateX(30%); } to { transform: translateX(-130%); } }
      `}</style>

      {/* INTRO — logga + användarens namn/profilbild */}
      {stage === 'intro' && (
        <div style={{ textAlign: 'center', animation: 'dropFadeIn 0.6s ease' }}>
          <div style={{ display: 'inline-block', background: '#fff', padding: '10px 18px', borderRadius: 6, marginBottom: 26 }}>
            <img src="/qvestium-logo.png" alt="Qvestium" style={{ height: 22, display: 'block' }} />
          </div>
          <div style={{ width: 72, height: 72, borderRadius: '50%', overflow: 'hidden', margin: '0 auto 12px', border: '2px solid #fff', background: '#333', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {avatarUrl ? (
              <img src={avatarUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : (
              <span style={{ fontSize: 28, color: '#fff' }}>👤</span>
            )}
          </div>
          <div style={{ color: '#fff', fontSize: 15, fontWeight: 600 }}>{displayName}</div>
          <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 11, marginTop: 4, textTransform: 'uppercase', letterSpacing: 1.5 }}>{t('dropAnimationPreview.presents')}</div>
        </div>
      )}

      {/* SWEEP — plaggen blurrade svepande förbi */}
      {stage === 'sweep' && (
        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center' }}>
          <div style={{ display: 'flex', gap: 16, animation: 'dropSweepMove 2.6s linear forwards', whiteSpace: 'nowrap' }}>
            {sweepImages.length > 0 ? sweepImages.map((url, i) => (
              <img key={i} src={url} alt="" style={{ width: 130, height: 165, objectFit: 'cover', borderRadius: 4, filter: 'blur(7px)', flexShrink: 0 }} />
            )) : (
              <div style={{ width: 200, height: 165, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(255,255,255,0.4)', fontSize: 40 }}>👗</div>
            )}
          </div>
          <div style={{ position: 'absolute', bottom: 16, left: 0, right: 0, textAlign: 'center', color: '#fff', fontSize: 13, fontWeight: 600, letterSpacing: 1, textTransform: 'uppercase' }}>
            {drop.name}
          </div>
        </div>
      )}

      {/* OUTRO — namn, släppdatum/tid, qvestium.com */}
      {stage === 'outro' && (
        <div style={{ textAlign: 'center', animation: 'dropFadeIn 0.6s ease', padding: '0 24px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <div style={{ color: '#fff', fontSize: 20, fontWeight: 700, fontFamily: 'Georgia, "Times New Roman", serif' }}>{drop.name}</div>
          <div style={{ color: 'rgba(255,255,255,0.75)', fontSize: 13, marginTop: 14 }}>{t('dropAnimationPreview.releases')}</div>
          <div style={{ color: '#fff', fontSize: 16, fontWeight: 600 }}>{releaseLabel}</div>
          <div style={{ color: 'rgba(255,255,255,0.55)', fontSize: 11, textTransform: 'uppercase', letterSpacing: 1.5, marginTop: 18 }}>qvestium.com</div>
        </div>
      )}

      {/* Hoppa över — tillgänglig i alla steg */}
      <button
        onClick={onDone}
        style={{
          position: 'absolute', top: 10, right: 10, width: 28, height: 28, borderRadius: '50%',
          background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', fontSize: 13, cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >✕</button>
    </div>
  );
}

// ── DROP SHARE BAR ──
// Kopiera-länk + enhetens delningsmeny (Web Share API). Går inte att rikta mot en
// specifik app från webben, men på mobil listar delningsmenyn Instagram/TikTok om de
// är installerade. Används både i skapa-drop-flödet och på den publika dropsidan.

function DropShareBar({ url, title, text }: { url: string; title: string; text: string }) {
  const [copied, setCopied] = useState(false);
  const canNativeShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* clipboard blockerad — länken syns fortfarande i fältet för manuell kopiering */ }
  }

  async function handleShare() {
    try { await navigator.share({ title, text, url }); } catch { /* avbrutet eller ej stött — ignorera */ }
  }

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
        <input
          readOnly value={url} onClick={e => (e.target as HTMLInputElement).select()}
          style={{ ...S.input, flex: 1, fontSize: 12, color: T.textSub }}
        />
        <button type="button" style={S.btn('ghost')} onClick={handleCopy}>{copied ? t('dropShareBar.copied') : t('dropShareBar.copyLink')}</button>
      </div>
      {canNativeShare ? (
        <button type="button" style={{ ...S.btn('primary'), width: '100%', justifyContent: 'center' }} onClick={handleShare}>
          {t('dropShareBar.shareNative')}
        </button>
      ) : (
        <div style={{ fontSize: 11, color: T.textMuted }}>
          {t('dropShareBar.shareHint')}
        </div>
      )}
    </div>
  );
}

// ── DROP SHARE PANEL ── (länk + delning + videoinspelning, visas efter outro)

function DropSharePanel({ drop, items, user, profile }: {
  drop: Drop;
  items: WardrobeItem[];
  user: QidUser | null;
  profile: FashionProfile | null;
}) {
  const shareUrl = `${window.location.origin}/drops/${drop.id}`;
  const shareText = t('dropSharePanel.shareText', { name: drop.name, date: new Date(drop.releaseAt).toLocaleDateString(locale()) });

  return (
    <div style={{ padding: '20px 24px 4px', display: 'flex', flexDirection: 'column', gap: 22 }}>
      <div>
        <div style={{ fontSize: 13, fontWeight: 700, color: T.text, marginBottom: 10 }}>{t('dropSharePanel.shareDrop')}</div>
        <DropShareBar url={shareUrl} title={drop.name} text={shareText} />
      </div>
      <DropVideoRecorder drop={drop} items={items} user={user} profile={profile} shareUrl={shareUrl} shareText={shareText} />
    </div>
  );
}

// ── DROP VIDEO RECORDER ──
// Renderar samma intro/sweep/outro-sekvens på en <canvas> och spelar in den till en
// riktig webm-video via MediaRecorder + canvas.captureStream — helt i webbläsaren,
// ingen backend-infrastruktur. Ger en fil att ladda ner eller dela (Instagram/TikTok
// kräver en riktig bild/video-fil, en länk räcker inte som inlägg).

function loadImageEl(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Kunde inte ladda bild: ${src}`));
    img.src = src;
  });
}

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

function DropVideoRecorder({ drop, items, user, profile, shareUrl, shareText }: {
  drop: Drop;
  items: WardrobeItem[];
  user: QidUser | null;
  profile: FashionProfile | null;
  shareUrl: string;
  shareText: string;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [status, setStatus] = useState<'idle' | 'recording' | 'done' | 'error'>('idle');
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [videoBlob, setVideoBlob] = useState<Blob | null>(null);
  const [errorMsg, setErrorMsg] = useState('');

  const supported = typeof window !== 'undefined'
    && typeof (window as any).MediaRecorder !== 'undefined'
    && typeof (HTMLCanvasElement.prototype as any).captureStream === 'function';

  async function handleRecord() {
    const canvas = canvasRef.current;
    if (!canvas || !supported) return;
    setStatus('recording'); setErrorMsg('');
    try {
      const W = canvas.width, H = canvas.height;
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error(t('dropVideoRecorder.errorCanvasUnsupported'));

      const avatarSrc = profile?.avatarUrl ?? user?.picture ?? null;
      const [logoImg, avatarImg, ...itemImgs] = await Promise.all([
        loadImageEl('/qvestium-logo.png').catch(() => null),
        avatarSrc ? loadImageEl(avatarSrc).catch(() => null) : Promise.resolve(null),
        ...items.map(i => (i.imageUrl ? loadImageEl(i.imageUrl).catch(() => null) : Promise.resolve(null))),
      ]);
      const garmentImages = itemImgs.filter((im): im is HTMLImageElement => Boolean(im));

      // Automatic per-paint capture (30fps) is the standards-track, broadly-supported
      // mode and what actually delivers frames in a real, foregrounded user tab.
      // We additionally call track.requestFrame() after every draw below as a
      // best-effort nudge for engines that otherwise under-deliver frames — it's a
      // no-op/harmless if the browser doesn't need it.
      const stream: MediaStream = (canvas as any).captureStream(30);
      const track = stream.getVideoTracks()[0] as any;
      const candidates = ['video/webm;codecs=vp9', 'video/webm;codecs=vp8', 'video/webm'];
      const mimeType = candidates.find(t => (window as any).MediaRecorder.isTypeSupported?.(t)) ?? 'video/webm';
      const recorder: MediaRecorder = new (window as any).MediaRecorder(stream, { mimeType, videoBitsPerSecond: 2_500_000 });
      const chunks: BlobPart[] = [];
      recorder.ondataavailable = (e: BlobEvent) => { if (e.data.size > 0) chunks.push(e.data); };
      const stopped = new Promise<void>(resolve => { recorder.onstop = () => resolve(); });
      recorder.start(250); // periodic dataavailable so we don't rely solely on a clean stop()
      try { track.requestFrame?.(); } catch { /* not needed on automatic-rate tracks in most engines */ }

      const INTRO = 2200, SWEEP = 2600, OUTRO = 3000;
      const TOTAL = INTRO + SWEEP + OUTRO;
      const displayName = user?.name ?? user?.email ?? 'Qvestium';
      const releaseLabel = new Date(drop.releaseAt).toLocaleString(locale(), { dateStyle: 'long', timeStyle: 'short' });
      // Etiketterna hämtas före frame() — där skuggar lokala `const t` översättningsfunktionen.
      const presentsLabel = t('dropVideoRecorder.presents');
      const releasesLabel = t('dropVideoRecorder.releases');
      const start = performance.now();

      await new Promise<void>(resolve => {
        function frame(now: number) {
          const elapsed = now - start;
          ctx!.fillStyle = '#111111';
          ctx!.fillRect(0, 0, W, H);
          ctx!.textAlign = 'center';

          if (elapsed < INTRO) {
            const t = elapsed / INTRO;
            ctx!.globalAlpha = Math.min(1, t / 0.3);
            const cardW = W * 0.42, cardH = H * 0.075;
            const cardY = H * 0.26;
            ctx!.fillStyle = '#fff';
            roundRect(ctx!, W / 2 - cardW / 2, cardY, cardW, cardH, 8);
            ctx!.fill();
            if (logoImg) {
              const lh = cardH * 0.5, lw = lh * (logoImg.width / logoImg.height);
              ctx!.drawImage(logoImg, W / 2 - lw / 2, cardY + cardH / 2 - lh / 2, lw, lh);
            }
            const r = W * 0.12, cy = cardY + cardH + 50 + r;
            ctx!.save();
            ctx!.beginPath(); ctx!.arc(W / 2, cy, r, 0, Math.PI * 2); ctx!.closePath(); ctx!.clip();
            ctx!.fillStyle = '#333'; ctx!.fillRect(W / 2 - r, cy - r, r * 2, r * 2);
            if (avatarImg) ctx!.drawImage(avatarImg, W / 2 - r, cy - r, r * 2, r * 2);
            ctx!.restore();
            ctx!.strokeStyle = '#fff'; ctx!.lineWidth = 2;
            ctx!.beginPath(); ctx!.arc(W / 2, cy, r, 0, Math.PI * 2); ctx!.stroke();

            ctx!.fillStyle = '#fff'; ctx!.font = `600 ${Math.round(W * 0.045)}px Georgia, serif`;
            ctx!.fillText(displayName, W / 2, cy + r + 34);
            ctx!.fillStyle = 'rgba(255,255,255,0.6)'; ctx!.font = `600 ${Math.round(W * 0.026)}px sans-serif`;
            ctx!.fillText(presentsLabel, W / 2, cy + r + 58);
          } else if (elapsed < INTRO + SWEEP) {
            const t = (elapsed - INTRO) / SWEEP;
            const imgW = W * 0.36, imgH = imgW * 1.27, gap = W * 0.04;
            const totalW = garmentImages.length * (imgW + gap);
            const startX = W * 0.4 - t * (totalW + W * 1.6);
            ctx!.save();
            (ctx as any).filter = 'blur(7px)';
            garmentImages.forEach((img, i) => {
              const x = startX + i * (imgW + gap);
              if (x > -imgW && x < W + imgW) ctx!.drawImage(img, x, H / 2 - imgH / 2, imgW, imgH);
            });
            ctx!.restore();
            ctx!.fillStyle = '#fff'; ctx!.font = `700 ${Math.round(W * 0.032)}px sans-serif`;
            ctx!.fillText(drop.name.toUpperCase(), W / 2, H - 40);
          } else {
            const t = (elapsed - INTRO - SWEEP) / 400;
            ctx!.globalAlpha = Math.min(1, t);
            ctx!.fillStyle = '#fff'; ctx!.font = `700 ${Math.round(W * 0.054)}px Georgia, serif`;
            ctx!.fillText(drop.name, W / 2, H * 0.42);
            ctx!.fillStyle = 'rgba(255,255,255,0.75)'; ctx!.font = `${Math.round(W * 0.028)}px sans-serif`;
            ctx!.fillText(releasesLabel, W / 2, H * 0.42 + 40);
            ctx!.fillStyle = '#fff'; ctx!.font = `600 ${Math.round(W * 0.038)}px sans-serif`;
            ctx!.fillText(releaseLabel, W / 2, H * 0.42 + 68);
            ctx!.fillStyle = 'rgba(255,255,255,0.55)'; ctx!.font = `600 ${Math.round(W * 0.025)}px sans-serif`;
            ctx!.fillText('QVESTIUM.COM', W / 2, H * 0.42 + 110);
          }
          ctx!.globalAlpha = 1;
          try { track.requestFrame?.(); } catch { /* ignore */ }

          if (elapsed < TOTAL) requestAnimationFrame(frame);
          else resolve();
        }
        requestAnimationFrame(frame);
      });

      recorder.stop();
      await stopped;
      const blob = new Blob(chunks, { type: mimeType.split(';')[0] });
      // Some browsers/devices silently fail to deliver canvas frames to the capture
      // stream (throttled/backgrounded tab, disabled hardware encoder, etc.) without
      // throwing — that produces a "valid" but empty/near-empty webm. Catch that here
      // instead of handing the user a broken file that won't play.
      if (blob.size < 20_000) {
        throw new Error(t('dropVideoRecorder.errorEmptyFile'));
      }
      setVideoBlob(blob);
      setVideoUrl(URL.createObjectURL(blob));
      setStatus('done');
    } catch (err: any) {
      setStatus('error');
      setErrorMsg(err?.message ?? t('dropVideoRecorder.errorFailed'));
    }
  }

  async function handleShareVideo() {
    if (!videoBlob) return;
    const file = new File([videoBlob], `${(drop.name || 'drop').replace(/[^a-z0-9]+/gi, '-')}.webm`, { type: videoBlob.type });
    const nav = navigator as any;
    if (nav.canShare?.({ files: [file] })) {
      try { await nav.share({ files: [file], title: drop.name, text: shareText, url: shareUrl }); } catch { /* avbrutet */ }
    }
  }

  const canShareFiles = typeof navigator !== 'undefined' && typeof (navigator as any).canShare === 'function'
    && videoBlob && (navigator as any).canShare({ files: [new File([videoBlob], 'x.webm', { type: videoBlob.type })] });

  return (
    <div>
      <div style={{ fontSize: 13, fontWeight: 700, color: T.text, marginBottom: 8 }}>{t('dropVideoRecorder.title')}</div>
      <canvas
        ref={canvasRef} width={480} height={600}
        style={{ width: '100%', maxWidth: 200, borderRadius: 6, background: '#111', display: 'block', marginBottom: 10 }}
      />
      {!supported ? (
        <div style={{ fontSize: 11, color: T.textMuted }}>
          {t('dropVideoRecorder.unsupported')}
        </div>
      ) : status !== 'done' ? (
        <button type="button" style={S.btn('ghost')} onClick={handleRecord} disabled={status === 'recording'}>
          {status === 'recording' ? t('dropVideoRecorder.recording') : t('dropVideoRecorder.record')}
        </button>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {videoUrl && <video src={videoUrl} controls loop style={{ width: '100%', maxWidth: 200, borderRadius: 6 }} />}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {videoUrl && (
              <a
                href={videoUrl} download={`${(drop.name || 'drop').replace(/[^a-z0-9]+/gi, '-')}.webm`}
                style={{ ...S.btn('primary'), textDecoration: 'none' }}
              >
                {t('dropVideoRecorder.download')}
              </a>
            )}
            {canShareFiles && (
              <button type="button" style={S.btn('ghost')} onClick={handleShareVideo}>{t('dropVideoRecorder.shareVideo')}</button>
            )}
          </div>
        </div>
      )}
      {status === 'error' && <div style={{ color: T.danger, fontSize: 12, marginTop: 6 }}>{errorMsg}</div>}
    </div>
  );
}

// ── TRANSACTION TRACKING CARD ──

function TransactionTrackingCard({ tx, currentUserEmail, onUpdated, onRefresh }: {
  tx: WardrobeTransaction;
  currentUserEmail: string;
  onUpdated: (tx: WardrobeTransaction) => void;
  onRefresh: () => void;
}) {
  const [loading, setLoading] = useState(false);
  const [trackingInput, setTrackingInput] = useState('');
  const [carrierInput, setCarrierInput] = useState('bring');
  const [showTrackingInput, setShowTrackingInput] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const [reportText, setReportText] = useState('');
  const [reportNotice, setReportNotice] = useState('');

  const isSeller = tx.sellerUserId === currentUserEmail;
  const isBuyer = tx.buyerUserId === currentUserEmail;
  // Köparskyddets reklamationsfönster (api/src/payments/claims.ts)
  const paidOut = tx.payoutStatus === 'transferred';
  const inClaimWindow = tx.status === 'delivered' && !paidOut && !!tx.payoutDueAt && !tx.problemReportedAt;
  const canReport = isBuyer && !paidOut && !tx.problemReportedAt && tx.buyerCancelStatus !== 'objected' && ['pending', 'shipped', 'delivered'].includes(tx.status);
  const dueDate = tx.payoutDueAt ? new Date(tx.payoutDueAt).toLocaleString(locale(), { dateStyle: 'medium', timeStyle: 'short' }) : '';

  async function sendReport() {
    setLoading(true);
    try {
      const { ticketNumber } = await api.reportTransactionProblem(tx.id, reportText);
      setReportNotice(t('transactionTrackingCard.reportSent', { number: ticketNumber }));
      setShowReport(false);
      onRefresh();
    } catch (err: any) { alert(err.message ?? t('common.somethingWentWrong')); }
    finally { setLoading(false); }
  }

  const allSteps = [
    { key: 'pending', label: t('transactionTrackingCard.steps.pending') },
    { key: 'shipped', label: t('transactionTrackingCard.steps.shipped') },
    { key: 'delivered', label: t('transactionTrackingCard.steps.delivered') },
    { key: 'return_requested', label: t('transactionTrackingCard.steps.returnRequested') },
    { key: 'return_shipped', label: t('transactionTrackingCard.steps.returnShipped') },
    { key: 'completed', label: t('transactionTrackingCard.steps.completed') },
  ];

  // Show return steps only if return flow has been initiated
  const hasReturn = ['return_requested', 'return_shipped', 'return_received', 'completed'].includes(tx.status) && tx.returnRequestedAt;
  const steps = hasReturn ? allSteps : allSteps.slice(0, 3);

  const currentStepIdx = steps.findIndex(s => s.key === tx.status);

  async function handleAction(action: string) {
    setLoading(true);
    try {
      let updated: WardrobeTransaction;
      switch (action) {
        case 'ship':
          if (!trackingInput.trim()) { alert(t('transactionTrackingCard.enterTrackingNumber')); setLoading(false); return; }
          updated = await api.shipToBuyer(tx.id, trackingInput.trim(), carrierInput);
          break;
        case 'confirm_delivery':
          updated = await api.confirmDelivery(tx.id);
          break;
        case 'confirm_handover':
          updated = await api.confirmHandover(tx.id);
          break;
        case 'request_return':
          updated = await api.requestReturn(tx.id);
          break;
        case 'ship_return':
          if (!trackingInput.trim()) { alert(t('transactionTrackingCard.enterTrackingNumber')); setLoading(false); return; }
          updated = await api.shipReturn(tx.id, trackingInput.trim());
          break;
        case 'confirm_return':
          updated = await api.confirmReturnReceived(tx.id);
          break;
        default:
          setLoading(false); return;
      }
      onUpdated(updated);
      setTrackingInput('');
      setShowTrackingInput(false);
    } catch (err: any) { alert(err.message ?? t('common.somethingWentWrong')); }
    finally { setLoading(false); }
  }

  return (
    <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 6, padding: 16 }}>
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', marginBottom: 12 }}>
        {tx.itemImageUrl && <img src={tx.itemImageUrl} alt="" style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 4, flexShrink: 0 }} />}
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: T.text }}>{tx.itemName ?? t('transactionTrackingCard.itemFallback')}</div>
          <div style={{ display: 'flex', gap: 6, marginTop: 4, flexWrap: 'wrap' }}>
            <span style={S.badge(tx.type === 'buy' ? '#5cb85c' : '#5bc0de')}>{tx.type === 'buy' ? t('common.buy') : t('common.rent')}</span>
            <span style={S.badge(isSeller ? '#f0ad4e' : T.rose)}>{isSeller ? t('common.seller') : t('common.buyer')}</span>
          </div>
        </div>
        <div style={{ textAlign: 'right', fontSize: 12 }}>
          <div style={{ color: T.textMuted }}>{t('common.price')}</div>
          <div style={{ fontWeight: 700, color: T.text }}>{t('common.kr', { amount: fmt(tx.itemPrice) })}</div>
          {isSeller && (
            <>
              <div style={{ color: T.textMuted, marginTop: 4 }}>{t('transactionTrackingCard.yourPayout')}</div>
              <div style={{ fontWeight: 700, color: '#5cb85c' }}>{t('common.kr', { amount: fmt(tx.sellerPayout) })}</div>
            </>
          )}
        </div>
      </div>

      {/* Progress bar */}
      <div style={{ display: 'flex', gap: 3, marginBottom: 4 }}>
        {steps.map((step, i) => {
          const done = i <= currentStepIdx;
          const isCurrent = i === currentStepIdx;
          return (
            <div key={step.key} style={{
              flex: 1, height: 5, borderRadius: 2,
              background: done ? (isCurrent ? T.rose : '#5cb85c') : T.border,
            }} />
          );
        })}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 9, color: T.textMuted, marginBottom: 12 }}>
        {steps.map(step => <span key={step.key}>{step.label}</span>)}
      </div>

      {/* Frakt bokad av Qvestium via Fraktjakt: etikett (säljaren) och spårning */}
      {tx.shippingProvider === 'fraktjakt' && tx.deliveryMethod !== 'pickup' && ['pending', 'shipped', 'delivered'].includes(tx.status) && (
        <div style={{ marginBottom: 8 }}><ShipmentPanel shipment={tx.shipment} sender={isSeller} /></div>
      )}

      {/* Tracking numbers */}
      {tx.sellerTrackingNumber && tx.shippingProvider !== 'fraktjakt' && (
        <div style={{ fontSize: 11, color: T.textSub, marginBottom: 4 }}>
          {tn('transactionTrackingCard.deliveryTracking', { number: <span style={{ color: T.rose, fontFamily: 'monospace' }}>{tx.sellerTrackingNumber}</span> })}
        </div>
      )}
      {tx.buyerReturnTracking && (
        <div style={{ fontSize: 11, color: T.textSub, marginBottom: 4 }}>
          {tn('transactionTrackingCard.returnTracking', { number: <span style={{ color: T.rose, fontFamily: 'monospace' }}>{tx.buyerReturnTracking}</span> })}
        </div>
      )}

      {/* Hyra: betalplan för månad 2–N (api/src/rentals/billing.ts) */}
      {tx.type === 'rent' && (tx.rentalCharges?.length ?? 0) > 0 && (() => {
        const charges = tx.rentalCharges!;
        const total = charges[0].monthsTotal;
        const paid = 1 + charges.filter(c => c.status === 'paid').length;
        const next = charges.find(c => c.status === 'scheduled');
        const unpaid = charges.find(c => c.status === 'action_required');
        const paidOut = 1 + charges.filter(c => c.payoutStatus === 'transferred').length;
        const fmtDate = (iso: string) => new Date(iso).toLocaleDateString(locale(), { day: 'numeric', month: 'long' });
        return (
          <div style={{ fontSize: 12, color: T.textSub, marginTop: 8 }}>
            {isSeller ? t('transactionTrackingCard.rentPaidOut', { paid: paidOut, total }) : t('transactionTrackingCard.rentPlan', { paid, total })}
            {next && !unpaid && <> · {t('transactionTrackingCard.rentNext', { date: fmtDate(next.dueAt) })}</>}
            {unpaid && (
              <div style={{ color: '#7a5200', background: '#fff8e1', padding: '8px 10px', marginTop: 6 }}>
                {isBuyer
                  ? t('transactionTrackingCard.rentActionRequired', { month: unpaid.monthIndex, amount: fmt(unpaid.amount) })
                  : t('transactionTrackingCard.rentSellerWaiting', { month: unpaid.monthIndex })}
                {isBuyer && (
                  <div style={{ marginTop: 6 }}>
                    <button style={{ ...S.btn('primary'), padding: '6px 14px', fontSize: 12 }} disabled={loading} onClick={async () => {
                      setLoading(true);
                      try { window.location.href = await api.rentChargePaymentSession(unpaid.id); }
                      catch (err: any) { alert(err.message ?? t('common.somethingWentWrong')); setLoading(false); }
                    }}>{t('transactionTrackingCard.payRent', { amount: fmt(unpaid.amount) })}</button>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })()}

      {/* Köparskydd: reklamationsfönster och anmälan */}
      {isBuyer && inClaimWindow && (
        <div style={{ fontSize: 12, color: T.textSub, background: T.surfaceHover, padding: '8px 10px', marginTop: 8 }}>{t('transactionTrackingCard.claimWindow', { date: dueDate })}</div>
      )}
      {isSeller && inClaimWindow && (
        <div style={{ fontSize: 12, color: T.textSub, marginTop: 8 }}>{t('transactionTrackingCard.sellerPayoutDue', { date: dueDate })}</div>
      )}
      {/* Hämtning: köparens begäran att avbryta (api/src/payments/buyerCancel.ts) */}
      {tx.status === 'pending' && (tx.buyerCancelStatus === 'requested' || tx.buyerCancelStatus === 'objected') && (
        <div style={{ fontSize: 12, color: '#7a5200', background: '#fff8e1', padding: '8px 10px', marginTop: 8 }}>
          {tx.buyerCancelStatus === 'objected' ? t(isSeller ? 'transactionTrackingCard.cancelObjectedBySeller' : 'transactionTrackingCard.cancelObjected')
            : t(isSeller ? 'transactionTrackingCard.sellerCancelRequest' : 'transactionTrackingCard.cancelRequestPending')}
        </div>
      )}
      {tx.problemReportedAt && !paidOut && (
        <div style={{ fontSize: 12, color: '#7a5200', background: '#fff8e1', padding: '8px 10px', marginTop: 8 }}>
          {reportNotice || t(isSeller ? 'transactionTrackingCard.sellerProblemReported' : 'transactionTrackingCard.problemReported')}
        </div>
      )}
      {showReport && (
        <div style={{ marginTop: 8 }}>
          <textarea style={{ ...S.input, minHeight: 70, resize: 'vertical', fontSize: 13 }} maxLength={2000} value={reportText}
            onChange={e => setReportText(e.target.value)} placeholder={t('transactionTrackingCard.reportPlaceholder')} autoFocus />
          <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
            <button style={{ ...S.btn('primary'), padding: '6px 12px', fontSize: 11 }} onClick={sendReport} disabled={loading || reportText.trim().length < 10}>
              {loading ? '...' : t('transactionTrackingCard.sendReport')}
            </button>
            <button style={{ ...S.btn('ghost'), padding: '6px 8px', fontSize: 11 }} onClick={() => setShowReport(false)}>{t('common.cancel')}</button>
          </div>
        </div>
      )}

      {/* Action buttons based on status and role */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
        {/* Köparen avbryter innan plagget skickats (api/src/payments/buyerCancel.ts) */}
        {isBuyer && tx.buyerCanCancel && (
          <button style={{ ...S.btn('ghost'), padding: '6px 14px', fontSize: 12, color: T.danger }} disabled={loading} onClick={async () => {
            const total = Number(tx.itemPrice) + Number(tx.shippingCost ?? 0) + Number(tx.protectionFee ?? 0);
            const pickup = tx.deliveryMethod === 'pickup';
            if (!confirm(t(pickup ? 'transactionTrackingCard.cancelRequestConfirm' : 'transactionTrackingCard.cancelConfirm', { item: tx.itemName ?? '', amount: fmtKr(total) }))) return;
            setLoading(true);
            try {
              const { refunded, requested } = await api.cancelMyPurchase(tx.id);
              alert(requested ? t('transactionTrackingCard.cancelRequested') : t('transactionTrackingCard.cancelled', { amount: fmtKr(refunded ?? total) }));
              onRefresh();
            } catch (err: any) { alert(err.message ?? t('common.somethingWentWrong')); }
            finally { setLoading(false); }
          }}>{t('transactionTrackingCard.cancelPurchase')}</button>
        )}
        {/* Hämtning: säljaren svarar på köparens begäran att avbryta */}
        {isSeller && tx.status === 'pending' && tx.buyerCancelStatus === 'requested' && (
          <>
            <button style={{ ...S.btn('primary'), padding: '6px 14px', fontSize: 12 }} disabled={loading} onClick={async () => {
              if (!confirm(t('transactionTrackingCard.approveCancelConfirm'))) return;
              setLoading(true);
              try { await api.respondToBuyerCancel(tx.id, true); alert(t('transactionTrackingCard.cancelApproved')); onRefresh(); }
              catch (err: any) { alert(err.message ?? t('common.somethingWentWrong')); }
              finally { setLoading(false); }
            }}>{t('transactionTrackingCard.approveCancel')}</button>
            <button style={{ ...S.btn('ghost'), padding: '6px 14px', fontSize: 12 }} disabled={loading} onClick={async () => {
              const message = prompt(t('transactionTrackingCard.objectCancelPrompt'));
              if (message === null) return;
              setLoading(true);
              try { const { ticketNumber } = await api.respondToBuyerCancel(tx.id, false, message); alert(t('transactionTrackingCard.cancelObjectedSeller', { ticket: ticketNumber ?? '' })); onRefresh(); }
              catch (err: any) { alert(err.message ?? t('common.somethingWentWrong')); }
              finally { setLoading(false); }
            }}>{t('transactionTrackingCard.objectCancel')}</button>
          </>
        )}
        {canReport && !showReport && (
          <button style={{ ...S.btn('ghost'), padding: '6px 14px', fontSize: 12 }} onClick={() => setShowReport(true)} disabled={loading}>
            {t('transactionTrackingCard.reportProblem')}
          </button>
        )}
        {/* Seller: pickup — bekräfta överlämning istället för att skicka */}
        {isSeller && tx.status === 'pending' && tx.deliveryMethod === 'pickup' && tx.buyerCancelStatus !== 'requested' && (
          <button style={{ ...S.btn('primary'), padding: '6px 14px', fontSize: 12 }} onClick={() => handleAction('confirm_handover')} disabled={loading || Boolean(tx.sellerHandoverConfirmedAt)}>
            {tx.sellerHandoverConfirmedAt ? t('transactionTrackingCard.waitingForBuyer') : loading ? '...' : t('transactionTrackingCard.confirmHandedOver')}
          </button>
        )}
        {/* Seller: ship item to buyer */}
        {isSeller && tx.status === 'pending' && tx.deliveryMethod !== 'pickup' && tx.shippingProvider !== 'fraktjakt' && (
          showTrackingInput ? (
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', flex: 1, flexWrap: 'wrap' }}>
              <select style={{ ...S.input, flex: '0 0 120px', fontSize: 12 }} value={carrierInput} onChange={e => setCarrierInput(e.target.value)}>
                <option value="bring">Bring</option>
              </select>
              <input style={{ ...S.input, flex: 1, fontSize: 12 }} value={trackingInput} onChange={e => setTrackingInput(e.target.value)} placeholder={t('transactionTrackingCard.trackingPlaceholder')} autoFocus />
              <button style={{ ...S.btn('primary'), padding: '6px 12px', fontSize: 11 }} onClick={() => handleAction('ship')} disabled={loading}>
                {loading ? '...' : t('common.confirm')}
              </button>
              <button style={{ ...S.btn('ghost'), padding: '6px 8px', fontSize: 11 }} onClick={() => setShowTrackingInput(false)}>{t('common.cancel')}</button>
            </div>
          ) : (
            <button style={{ ...S.btn('primary'), padding: '6px 14px', fontSize: 12 }} onClick={() => setShowTrackingInput(true)}>{t('transactionTrackingCard.shipToBuyer')}</button>
          )
        )}

        {/* Buyer: pickup — bekräfta hämtning */}
        {isBuyer && tx.status === 'pending' && tx.deliveryMethod === 'pickup' && (
          <button style={{ ...S.btn('primary'), padding: '6px 14px', fontSize: 12, background: '#5cb85c' }} onClick={() => handleAction('confirm_handover')} disabled={loading || Boolean(tx.buyerHandoverConfirmedAt)}>
            {tx.buyerHandoverConfirmedAt ? t('transactionTrackingCard.waitingForSeller') : loading ? '...' : t('transactionTrackingCard.confirmPickedUp')}
          </button>
        )}

        {/* Buyer: confirm delivery */}
        {isBuyer && tx.status === 'shipped' && (
          <button style={{ ...S.btn('primary'), padding: '6px 14px', fontSize: 12, background: '#5cb85c' }} onClick={() => handleAction('confirm_delivery')} disabled={loading}>
            {loading ? '...' : t('transactionTrackingCard.confirmReceived')}
          </button>
        )}

        {/* Buyer: request return */}
        {isBuyer && tx.status === 'delivered' && (
          <button style={{ ...S.btn('ghost'), padding: '6px 14px', fontSize: 12, color: '#f0ad4e', borderColor: '#f0ad4e44' }} onClick={() => handleAction('request_return')} disabled={loading}>
            {loading ? '...' : t('transactionTrackingCard.requestReturn')}
          </button>
        )}

        {/* Buyer: ship return */}
        {isBuyer && tx.status === 'return_requested' && (
          showTrackingInput ? (
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', flex: 1 }}>
              <input style={{ ...S.input, flex: 1, fontSize: 12 }} value={trackingInput} onChange={e => setTrackingInput(e.target.value)} placeholder={t('transactionTrackingCard.returnTrackingPlaceholder')} autoFocus />
              <button style={{ ...S.btn('primary'), padding: '6px 12px', fontSize: 11 }} onClick={() => handleAction('ship_return')} disabled={loading}>
                {loading ? '...' : t('common.confirm')}
              </button>
              <button style={{ ...S.btn('ghost'), padding: '6px 8px', fontSize: 11 }} onClick={() => setShowTrackingInput(false)}>{t('common.cancel')}</button>
            </div>
          ) : (
            <button style={{ ...S.btn('primary'), padding: '6px 14px', fontSize: 12 }} onClick={() => setShowTrackingInput(true)}>{t('transactionTrackingCard.shipReturn')}</button>
          )
        )}

        {/* Seller: confirm return received */}
        {isSeller && tx.status === 'return_shipped' && (
          <button style={{ ...S.btn('primary'), padding: '6px 14px', fontSize: 12, background: '#5cb85c' }} onClick={() => handleAction('confirm_return')} disabled={loading}>
            {loading ? '...' : t('transactionTrackingCard.confirmReturnReceived')}
          </button>
        )}
      </div>
    </div>
  );
}

// ── SHIP TO CUSTOMER MODAL ──

function ShipToCustomerModal({ tx, onClose, onShipped }: {
  tx: WardrobeTransaction;
  onClose: () => void;
  onShipped: (tx: WardrobeTransaction) => void;
}) {
  const [step, setStep] = useState<'reference' | 'tracking' | 'done'>('reference');
  const [trackingNumber, setTrackingNumber] = useState('');
  const [carrier, setCarrier] = useState('bring');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Ren referens för säljaren att visa vid inlämning hos ombudet — INTE en
  // riktig fraktetikett/inlämningskod. Att generera en sådan kräver Bring:s
  // Booking-API, vilket kräver ett företagsavtal Qvestium inte har än (bara
  // det fria spårnings-API:et är kopplat, se shipping/bring.ts). Spårnings-
  // numret säljaren faktiskt får av ombudet skrivs in i nästa steg.
  const referenceQrValue = t('shipToCustomerModal.qrReference', { order: tx.id.slice(0, 8), item: tx.itemName ?? t('shipToCustomerModal.itemFallback'), buyer: displayNameFromUserId(tx.buyerUserId, tx.buyerName) });

  async function handleShip() {
    if (!trackingNumber.trim()) { setError(t('shipToCustomerModal.enterTrackingNumber')); return; }
    setLoading(true); setError('');
    try {
      const updated = await api.shipToBuyer(tx.id, trackingNumber.trim(), carrier);
      setStep('done');
      setTimeout(() => onShipped(updated), 1500);
    } catch (err: any) { setError(err.message ?? t('shipToCustomerModal.registerFailed')); }
    finally { setLoading(false); }
  }

  return (
    <div style={S.modal} onClick={onClose}>
      <div style={{ background: T.surface, borderRadius: 8, padding: 28, width: '100%', maxWidth: 480, border: `1px solid ${T.border}` }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: T.text }}>{t('shipToCustomerModal.title')}</div>
          <button style={{ ...S.btn('ghost'), padding: '4px 8px' }} onClick={onClose}>✕</button>
        </div>

        {/* Item info */}
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 20, background: T.bg, borderRadius: 5, padding: 12 }}>
          {tx.itemImageUrl && <img src={tx.itemImageUrl} alt="" style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 4 }} />}
          <div>
            <div style={{ fontSize: 15, fontWeight: 600, color: T.text }}>{tx.itemName ?? t('shipToCustomerModal.itemFallback')}</div>
            <div style={{ fontSize: 12, color: T.textSub }}>{t('shipToCustomerModal.buyer', { buyer: displayNameFromUserId(tx.buyerUserId, tx.buyerName) })}</div>
            <div style={{ fontSize: 12, color: '#5cb85c', fontWeight: 600, marginTop: 2 }}>{t('shipToCustomerModal.payout', { amount: fmt(tx.sellerPayout) })}</div>
          </div>
        </div>

        {step === 'done' ? (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <div style={{ fontSize: 32, marginBottom: 8 }}>✓</div>
            <div style={{ fontSize: 15, fontWeight: 600, color: T.text }}>{t('shipToCustomerModal.shipmentRegistered')}</div>
            <div style={{ fontSize: 13, color: T.textSub, marginTop: 4 }}>{t('shipToCustomerModal.buyerNotified')}</div>
          </div>
        ) : step === 'reference' ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14, alignItems: 'center', textAlign: 'center' }}>
            <div style={{ fontSize: 14, color: T.textSub, lineHeight: 1.6 }}>
              {t('shipToCustomerModal.referenceInfo')}
            </div>
            <div style={{ padding: 16, background: '#fff', border: `1px solid ${T.border}`, borderRadius: 8 }}>
              <QRCodeSVG value={referenceQrValue} size={180} />
            </div>
            <div style={{ fontSize: 12, color: T.textMuted, fontFamily: 'monospace' }}>{t('shipToCustomerModal.orderRef', { order: tx.id.slice(0, 8) })}</div>
            <button
              style={{ ...S.btn('primary'), justifyContent: 'center', padding: '14px', fontSize: 14, width: '100%' }}
              onClick={() => setStep('tracking')}
            >
              {t('shipToCustomerModal.droppedOffContinue')}
            </button>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ fontSize: 14, color: T.textSub, lineHeight: 1.6 }}>
              {t('shipToCustomerModal.trackingIntro')}
            </div>
            <select
              style={{ ...S.input, fontSize: 14 }}
              value={carrier}
              onChange={e => setCarrier(e.target.value)}
            >
              <option value="bring">Bring</option>
            </select>
            <input
              style={{ ...S.input, fontSize: 16, textAlign: 'center', fontFamily: 'monospace', letterSpacing: 1 }}
              value={trackingNumber}
              onChange={e => setTrackingNumber(e.target.value)}
              placeholder={t('shipToCustomerModal.trackingPlaceholder')}
              autoFocus
            />
            <div style={{ fontSize: 11, color: T.textMuted }}>
              {t('shipToCustomerModal.autoPayoutInfo')}
            </div>
            {error && <div style={{ color: T.danger, fontSize: 13 }}>{error}</div>}
            <div style={{ display: 'flex', gap: 10 }}>
              <button style={{ ...S.btn('ghost'), flex: 1, justifyContent: 'center' }} onClick={() => setStep('reference')}>{t('common.back')}</button>
              <button style={{ ...S.btn('primary'), flex: 2, justifyContent: 'center' }} onClick={handleShip} disabled={loading}>
                {loading ? t('shipToCustomerModal.registering') : t('shipToCustomerModal.confirmShipment')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── ADD WARDROBE ITEM MODAL ──

function AddWardrobeItemModal({ defaultCategory, bodyPhotos, onClose, onCreated }: { defaultCategory?: string | null; bodyPhotos?: string[]; onClose: () => void; onCreated: (item: WardrobeItem) => void }) {
  const fixedShipping = useShippingFixedPrice();
  const [form, setForm] = useState({
    name: '', description: '', category: defaultCategory || 'Plagg', size: '', condition: 5,
    weightTier: '0-1',
    imageUrl: '', galleryUrls: [] as string[], imageVariants: [] as ImageVariant[],
    forRent: false, rentPrice1: '', rentPrice3: '', rentPrice12: '',
    forSale: false, salePrice: '', replacementValue: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  // Suggest hyrpriser från säljpriset — samma logik som i Lägg till plagg (butiken)
  function handleSalePriceChange(value: string) {
    setForm(p => {
      const price = Number(value) || 0;
      const suggest = (field: 'rentPrice1' | 'rentPrice3' | 'rentPrice12') => p[field] !== '';
      return {
        ...p,
        salePrice: value,
        rentPrice1: suggest('rentPrice1') ? p.rentPrice1 : String(Math.round(price / 12 * 1.6) || ''),
        rentPrice3: suggest('rentPrice3') ? p.rentPrice3 : String(Math.round(price / 12 * 1.35) || ''),
        rentPrice12: suggest('rentPrice12') ? p.rentPrice12 : String(Math.round(price / 12) || ''),
      };
    });
  }

  async function handleImageUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await api.uploadFile(file);
      setForm(p => ({ ...p, imageUrl: res.url }));
    } catch { setError(t('addWardrobeItemModal.imageUploadFailed')); }
    finally { setUploading(false); }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.name) { setError(t('addWardrobeItemModal.enterName')); return; }
    setSaving(true); setError('');
    try {
      const item = await api.createWardrobeItem({
        name: form.name,
        description: form.description || undefined,
        category: form.category,
        size: form.size || undefined,
        condition: form.condition,
        weightTier: form.weightTier,
        imageUrl: form.imageUrl || undefined,
        galleryUrls: form.galleryUrls,
        imageVariants: pruneVariants(form),
        forRent: form.forRent,
        rentPrice: form.forRent ? (form.rentPrice12 ? Number(form.rentPrice12) : 0) : 0,
        rentPrice3: form.forRent && form.rentPrice3 ? Number(form.rentPrice3) : undefined,
        rentPrice1: form.forRent && form.rentPrice1 ? Number(form.rentPrice1) : undefined,
        forSale: form.forSale,
        salePrice: form.forSale ? Number(form.salePrice) || 0 : 0,
        replacementValue: form.forRent && Number(form.replacementValue) > 0 ? Number(form.replacementValue) : undefined,
      });
      onCreated(item);
    } catch (err: any) { setError(err.message ?? t('common.somethingWentWrong')); }
    finally { setSaving(false); }
  }

  return (
    <div style={S.modal} onClick={onClose}>
      <div style={{ background: T.surface, borderRadius: 8, padding: 28, width: '100%', maxWidth: 520, maxHeight: '90vh', overflowY: 'auto', border: `1px solid ${T.border}` }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: T.text }}>{t('addWardrobeItemModal.title')}</div>
          <button style={{ ...S.btn('ghost'), padding: '4px 8px' }} onClick={onClose}>✕</button>
        </div>
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <div style={{ fontSize: 12, color: T.textSub, marginBottom: 4 }}>{t('addWardrobeItemModal.nameRequired')}</div>
            <input style={S.input} value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder={t('addWardrobeItemModal.namePlaceholder')} required />
          </div>
          <div>
            <div style={{ fontSize: 12, color: T.textSub, marginBottom: 4 }}>{t('common.description')}</div>
            <textarea style={{ ...S.input, minHeight: 60, resize: 'vertical' }} value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} placeholder={t('addWardrobeItemModal.descriptionPlaceholder')} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
            <div>
              <div style={{ fontSize: 12, color: T.textSub, marginBottom: 4 }}>{t('common.category')}</div>
              <select style={S.input} value={form.category} onChange={e => setForm(p => ({ ...p, category: e.target.value }))}>
                {['Plagg', 'Accessoarer', 'Skor', 'Väskor', 'Övrigt'].map(c => <option key={c} value={c}>{vl('category', c)}</option>)}
              </select>
            </div>
            <div>
              <div style={{ fontSize: 12, color: T.textSub, marginBottom: 4 }}>{t('common.size')}</div>
              <input style={S.input} value={form.size} onChange={e => setForm(p => ({ ...p, size: e.target.value }))} placeholder={t('addWardrobeItemModal.sizePlaceholder')} />
            </div>
            <div>
              <div style={{ fontSize: 12, color: T.textSub, marginBottom: 4 }}>{t('addWardrobeItemModal.conditionRange')}</div>
              <select style={S.input} value={form.condition} onChange={e => setForm(p => ({ ...p, condition: Number(e.target.value) }))}>
                {[5,4,3,2,1].map(c => <option key={c} value={c}>{c} — {CONDITION_LABELS[c]?.label}</option>)}
              </select>
            </div>
          </div>
          <div>
            <div style={{ fontSize: 12, color: T.textSub, marginBottom: 4 }}>{t('addWardrobeItemModal.weight')}</div>
            <select style={S.input} value={form.weightTier} onChange={e => setForm(p => ({ ...p, weightTier: e.target.value }))}>
              {weightTierOptions(fixedShipping).map(w => <option key={w.value} value={w.value}>{w.label}</option>)}
            </select>
          </div>
          {/* Image */}
          <div>
            <div style={{ fontSize: 12, color: T.textSub, marginBottom: 4 }}>{t('addWardrobeItemModal.image')}</div>
            <div style={{ display: 'flex', gap: 8 }}>
              <input style={{ ...S.input, flex: 1 }} value={form.imageUrl} onChange={e => setForm(p => ({ ...p, imageUrl: e.target.value }))} placeholder={t('addWardrobeItemModal.imageUrlPlaceholder')} />
              <button type="button" style={S.btn('ghost')} onClick={() => fileRef.current?.click()} disabled={uploading}>{uploading ? '...' : t('common.upload')}</button>
            </div>
            <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleImageUpload} />
            {form.imageUrl && <img src={form.imageUrl} alt="" style={{ marginTop: 8, width: 80, height: 80, objectFit: 'cover', borderRadius: 4, display: 'block' }} />}
            <CutoutButton url={form.imageUrl} images={form} onChange={next => setForm(p => ({ ...p, ...next }))} />
          </div>

          {/* AI-genererade bilder utifrån uppladdad bild */}
          {uploadPath(form.imageUrl) && (
            <AiImageGenerator
              imageUrl={form.imageUrl}
              name={form.name}
              category={form.category}
              bodyPhotos={bodyPhotos}
              onAddToGallery={urls => setForm(p => ({ ...p, galleryUrls: [...p.galleryUrls, ...urls] }))}
            />
          )}
          {form.galleryUrls.length > 0 && (
            <div>
              <div style={{ fontSize: 12, color: T.textSub, marginBottom: 3 }}>{t('addWardrobeItemModal.gallery', { count: form.galleryUrls.length })}</div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {form.galleryUrls.map((url, i) => (
                  <div key={i} style={{ position: 'relative', width: 60 }}>
                    <img src={url} alt="" style={{ width: 60, height: 60, objectFit: 'cover', borderRadius: 3, border: `1px solid ${T.border}`, display: 'block' }} />
                    <CutoutButton compact url={url} images={form} onChange={next => setForm(p => ({ ...p, ...next }))} />
                    {isAiImage(url) && <AiImageBadge small />}
                    <button
                      type="button"
                      onClick={() => setForm(p => ({ ...p, galleryUrls: p.galleryUrls.filter((_, j) => j !== i) }))}
                      style={{ position: 'absolute', top: -6, right: -6, width: 18, height: 18, borderRadius: 5, background: T.danger, border: 'none', color: '#fff', fontSize: 11, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    >×</button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Listing options */}
          <div style={{ background: T.bg, borderRadius: 5, padding: 14, border: `1px solid ${T.border}` }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: T.text, marginBottom: 10 }}>{t('addWardrobeItemModal.listQuestion')}</div>

            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 13, color: T.textSub }}>
                <input type="checkbox" checked={form.forSale} onChange={e => setForm(p => ({ ...p, forSale: e.target.checked }))} />
                {t('addWardrobeItemModal.sell')}
              </label>
              {form.forSale && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <input style={{ ...S.input, width: 80 }} type="number" value={form.salePrice} onChange={e => handleSalePriceChange(e.target.value)} placeholder="0" />
                  <span style={{ fontSize: 12, color: T.textMuted }}>{t('addWardrobeItemModal.currency')}</span>
                </div>
              )}
            </div>

            <div style={{ marginTop: 10 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 13, color: T.textSub, marginBottom: form.forRent ? 8 : 0 }}>
                <input type="checkbox" checked={form.forRent} onChange={e => setForm(p => ({ ...p, forRent: e.target.checked }))} />
                {t('addWardrobeItemModal.rentOut')}
              </label>
              {form.forRent && (
                <div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
                    <div>
                      <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 3 }}>{t('common.months', { count: 1 })}</div>
                      <input style={S.input} type="number" value={form.rentPrice1} onChange={e => setForm(p => ({ ...p, rentPrice1: e.target.value }))} placeholder={t('addWardrobeItemModal.perMonthPlaceholder')} />
                    </div>
                    <div>
                      <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 3 }}>{t('common.months', { count: 3 })}</div>
                      <input style={S.input} type="number" value={form.rentPrice3} onChange={e => setForm(p => ({ ...p, rentPrice3: e.target.value }))} placeholder={t('addWardrobeItemModal.perMonthPlaceholder')} />
                    </div>
                    <div>
                      <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 3 }}>{t('common.months', { count: 12 })}</div>
                      <input style={S.input} type="number" value={form.rentPrice12} onChange={e => setForm(p => ({ ...p, rentPrice12: e.target.value }))} placeholder={t('addWardrobeItemModal.perMonthPlaceholder')} />
                    </div>
                  </div>
                  <div style={{ fontSize: 11, color: T.textMuted, marginTop: 4 }}>{t('addWardrobeItemModal.prefilledHint')}</div>
                </div>
              )}
            </div>

            {form.forRent && (
              <div style={{ marginTop: 12 }}>
                <div style={{ fontSize: 12, color: T.textSub, marginBottom: 4 }}>{t('addWardrobeItemModal.replacementValue')}</div>
                <input style={S.input} type="number" min={0} value={form.replacementValue} onChange={e => setForm(p => ({ ...p, replacementValue: e.target.value }))} placeholder={t('addWardrobeItemModal.replacementValuePlaceholder')} />
                <div style={{ fontSize: 11, color: T.textMuted, marginTop: 4 }}>{t('addWardrobeItemModal.replacementValueHint')}</div>
              </div>
            )}

            {(form.forRent || form.forSale) && (
              <div style={{ marginTop: 10, fontSize: 11, color: T.textMuted }}>
                {t('addWardrobeItemModal.feeInfo')}
              </div>
            )}
          </div>
          {error && <div style={{ color: T.danger, fontSize: 13, padding: '8px 12px', background: `${T.danger}15`, borderRadius: 3 }}>{error}</div>}
          <button type="submit" style={{ ...S.btn('primary'), justifyContent: 'center', padding: '10px' }} disabled={saving}>
            {saving ? t('common.saving') : t('addWardrobeItemModal.submit')}
          </button>
        </form>
      </div>
    </div>
  );
}

// ── SWAP PROPOSAL DETAIL MODAL ──
// Klicka upp ett bytesförslag (mottaget eller skickat) för att se det i
// fullstorlek — bilder, meddelanden, tidsstämplar — med svarsknappar inline.

function SwapProposalDetailModal({ proposal, role, onClose, onAccept, onReject, onCancel, onPayShipping, onConfirmHandover, onConfirmReceived, onReportProblem, busy }: {
  proposal: SwapProposal;
  role: 'received' | 'sent';
  onClose: () => void;
  onAccept?: (deliveryMethod: 'pickup' | 'shipping') => void;
  onReject?: (message: string) => void;
  onCancel?: () => void;
  onPayShipping?: () => void;
  onConfirmHandover?: () => void;
  onConfirmReceived?: () => void;
  onReportProblem?: (message: string) => void;
  busy?: boolean;
}) {
  const fraktOn = useFraktjaktEnabled();
  const [reporting, setReporting] = useState(false);
  const [reportMsg, setReportMsg] = useState('');
  const st = SWAP_STATUS_LABELS[proposal.status] ?? { label: proposal.status, color: T.textMuted };
  const [rejecting, setRejecting] = useState(false);
  const [rejectMsg, setRejectMsg] = useState('');
  const [acceptDeliveryMethod, setAcceptDeliveryMethod] = useState<'pickup' | 'shipping'>('pickup');

  // role==='received' → viewaren är ägaren (svarar på förslaget); 'sent' → viewaren är föreslagaren.
  const myDeliveryMethod = role === 'sent' ? proposal.proposerDeliveryMethod : proposal.ownerDeliveryMethod;
  const myShippingPaidAt = role === 'sent' ? proposal.proposerShippingPaidAt : proposal.ownerShippingPaidAt;
  // Den som föreslog bytet betalar även köparskydd tillsammans med frakten.
  const myShippingCost = role === 'sent' ? proposal.proposerShippingCost + (proposal.proposerProtectionFee ?? 0) : proposal.ownerShippingCost;
  const myHandoverConfirmedAt = role === 'sent' ? proposal.proposerHandoverConfirmedAt : proposal.ownerHandoverConfirmedAt;
  const theirDeliveryMethod = role === 'sent' ? proposal.ownerDeliveryMethod : proposal.proposerDeliveryMethod;
  const theirHandoverConfirmedAt = role === 'sent' ? proposal.ownerHandoverConfirmedAt : proposal.proposerHandoverConfirmedAt;
  // Det jag tar emot: ägaren tar emot föreslagarens plagg (proposer-benet) och tvärtom.
  const myReceivedAt = role === 'sent' ? proposal.ownerLegReceivedAt : proposal.proposerLegReceivedAt;
  const theirShippingPaidAt = role === 'sent' ? proposal.ownerShippingPaidAt : proposal.proposerShippingPaidAt;
  const fixedShipping = useShippingFixedPrice();
  const acceptShippingCost = fixedShipping ?? SHIPPING_COST_BY_WEIGHT[proposal.targetItem.weightTier ?? '0-1'] ?? 0;

  return (
    <div style={S.modal} onClick={onClose}>
      <div style={{ background: '#fff', borderRadius: 8, padding: 24, width: '100%', maxWidth: 560, maxHeight: '90vh', overflowY: 'auto', border: `1px solid ${T.border}` }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div style={{ fontFamily: 'Georgia, "Times New Roman", serif', fontSize: 20, color: T.text }}>{t('swapProposalDetailModal.title')}</div>
          <button style={{ ...S.btn('ghost'), padding: '4px 8px' }} onClick={onClose}>✕</button>
        </div>

        <div style={{ display: 'flex', gap: 8, marginBottom: 18 }}>
          <span style={S.badge(st.color)}>{st.label}</span>
          <span style={S.badge(proposal.durationMonths ? '#5bc0de' : T.textMuted)}>
            {proposal.durationMonths ? t('common.months', { count: proposal.durationMonths }) : t('common.permanent')}
          </span>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: 12, alignItems: 'start', marginBottom: 18 }}>
          <div>
            <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 6 }}>{role === 'received' ? t('swapProposalDetailModal.yourItem') : t('swapProposalDetailModal.theirItem')}</div>
            <img src={proposal.targetItem.imageUrl || undefined} alt={proposal.targetItem.name}
              style={{ width: '100%', aspectRatio: '4/5', objectFit: 'cover', borderRadius: 4, border: `1px solid ${T.border}`, background: T.bg }} />
            <div style={{ fontSize: 12, color: T.text, marginTop: 6 }}>{proposal.targetItem.name}</div>
          </div>
          <div style={{ alignSelf: 'center', fontSize: 22, color: T.textMuted, marginTop: 40 }}>⇄</div>
          <div>
            <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 6 }}>{role === 'received' ? t('swapProposalDetailModal.offeredBy', { user: displayNameFromUserId(proposal.proposerUserId, proposal.proposerName) }) : t('swapProposalDetailModal.youOffered')}</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {proposal.offeredItems.map(it => (
                <div key={it.id}>
                  <img src={it.imageUrl || undefined} alt={it.name}
                    style={{ width: '100%', aspectRatio: '4/5', objectFit: 'cover', borderRadius: 4, border: `1px solid ${T.border}`, background: T.bg }} />
                  <div style={{ fontSize: 12, color: T.text, marginTop: 4 }}>{it.name}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {proposal.message && (
          <div style={{ fontSize: 13, color: T.textSub, marginBottom: 10, padding: '10px 12px', background: T.bg, borderRadius: 4 }}>
            <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 2 }}>{t('swapProposalDetailModal.message')}</div>
            "{proposal.message}"
          </div>
        )}
        {proposal.ownerMessage && (
          <div style={{ fontSize: 13, color: T.textSub, marginBottom: 10, padding: '10px 12px', background: T.bg, borderRadius: 4 }}>
            <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 2 }}>{t('swapProposalDetailModal.reply')}</div>
            "{proposal.ownerMessage}"
          </div>
        )}

        <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 18 }}>
          {t('swapProposalDetailModal.sentAt', { date: new Date(proposal.createdAt).toLocaleString(locale()) })}
          {proposal.updatedAt !== proposal.createdAt && ` · ${t('swapProposalDetailModal.updatedAt', { date: new Date(proposal.updatedAt).toLocaleString(locale()) })}`}
        </div>

        {role === 'received' && proposal.status === 'pending' && (
          rejecting ? (
            <div>
              <textarea
                style={{ ...S.input, minHeight: 50, resize: 'vertical' as const, fontFamily: 'inherit', marginBottom: 8 }}
                value={rejectMsg}
                onChange={e => setRejectMsg(e.target.value)}
                placeholder={t('swapProposalDetailModal.rejectPlaceholder')}
                autoFocus
              />
              <div style={{ display: 'flex', gap: 8 }}>
                <button style={{ ...S.btn('ghost'), flex: 1, justifyContent: 'center' }} onClick={() => setRejecting(false)}>{t('common.cancel')}</button>
                <button style={{ ...S.btn('danger'), flex: 1, justifyContent: 'center' }} disabled={busy} onClick={() => onReject?.(rejectMsg.trim())}>
                  {busy ? '...' : t('swapProposalDetailModal.sendRejection')}
                </button>
              </div>
            </div>
          ) : (
            <div>
              <div style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 12, color: T.textSub, marginBottom: 6 }}>{t('swapProposalDetailModal.handoverQuestion')}</div>
                <div style={{ display: 'flex', gap: 6 }}>
                  {(['pickup', 'shipping'] as const).map(method => {
                    const active = acceptDeliveryMethod === method;
                    return (
                      <button
                        key={method}
                        onClick={() => setAcceptDeliveryMethod(method)}
                        style={{
                          flex: 1, padding: '8px 6px', borderRadius: 4,
                          border: `1.5px solid ${active ? T.rose : T.border}`,
                          background: active ? T.roseDim : 'transparent',
                          color: active ? T.rose : T.textMuted,
                          cursor: 'pointer', fontSize: 12, fontWeight: active ? 700 : 500,
                        }}
                      >
                        {method === 'pickup' ? t('swapProposalDetailModal.pickup') : t('swapProposalDetailModal.shippingWithCost', { amount: fmt(acceptShippingCost) })}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button style={{ ...S.btn('primary'), flex: 1, justifyContent: 'center' }} disabled={busy} onClick={() => onAccept?.(acceptDeliveryMethod)}>
                  {busy ? '...' : t('swapProposalDetailModal.accept')}
                </button>
                <button style={{ ...S.btn('ghost'), flex: 1, justifyContent: 'center' }} disabled={busy} onClick={() => setRejecting(true)}>
                  {t('common.reject')}
                </button>
              </div>
            </div>
          )
        )}

        {role === 'sent' && proposal.status === 'pending' && (
          <button style={{ ...S.btn('ghost'), padding: '8px 16px', fontSize: 12 }} disabled={busy} onClick={onCancel}>
            {busy ? '...' : t('swapProposalDetailModal.cancelProposal')}
          </button>
        )}

        {proposal.status === 'awaiting_handover' && (
          <div>
            {proposal.meetingAddress && (
              <div style={{ fontSize: 13, color: T.textSub, marginBottom: 12, padding: '10px 12px', background: T.bg, borderRadius: 4 }}>
                <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 2 }}>{t('swapProposalDetailModal.meetingPlace')}</div>
                {proposal.meetingAddress}
              </div>
            )}
            <div style={{ fontSize: 12, color: T.textSub, marginBottom: 12 }}>
              <div>{t('swapProposalDetailModal.myItemLabel', { method: myDeliveryMethod === 'shipping' ? t('swapProposalDetailModal.methodShipping') : t('swapProposalDetailModal.methodPickup') })} {
                myDeliveryMethod === 'shipping'
                  ? (myShippingPaidAt ? t('swapProposalDetailModal.shippingPaid') : t(role === 'sent' && proposal.proposerProtectionFee ? 'swapProposalDetailModal.awaitingShippingAndProtectionPayment' : 'swapProposalDetailModal.awaitingShippingPayment', { amount: fmtKr(myShippingCost) }))
                  : (myHandoverConfirmedAt ? t('swapProposalDetailModal.youConfirmedHandover') : t('swapProposalDetailModal.awaitingYourHandover'))
              }</div>
              <div>{t('swapProposalDetailModal.theirItemLabel', { method: theirDeliveryMethod === 'shipping' ? t('swapProposalDetailModal.methodShipping') : t('swapProposalDetailModal.methodPickup') })} {
                theirDeliveryMethod === 'shipping'
                  ? t('swapProposalDetailModal.handledByOther')
                  : (theirHandoverConfirmedAt ? t('swapProposalDetailModal.otherConfirmed') : t('swapProposalDetailModal.waitingForOther'))
              }</div>
            </div>
            {myDeliveryMethod === 'shipping' && !myShippingPaidAt && (
              <button style={{ ...S.btn('primary'), width: '100%', justifyContent: 'center' }} disabled={busy} onClick={onPayShipping}>
                {busy ? '...' : fraktOn ? t('shipping.chooseAndPay') : t(role === 'sent' && proposal.proposerProtectionFee ? 'swapProposalDetailModal.payShippingAndProtection' : 'swapProposalDetailModal.payShipping', { amount: fmtKr(myShippingCost) })}
              </button>
            )}
            {myDeliveryMethod === 'shipping' && myShippingPaidAt && proposal.myShipment && <ShipmentPanel shipment={proposal.myShipment} sender />}
            {theirDeliveryMethod === 'shipping' && proposal.incomingShipment && <ShipmentPanel shipment={proposal.incomingShipment} sender={false} />}
            {myDeliveryMethod === 'pickup' && !myHandoverConfirmedAt && (
              <button style={{ ...S.btn('primary'), width: '100%', justifyContent: 'center' }} disabled={busy} onClick={onConfirmHandover}>
                {busy ? '...' : t('swapProposalDetailModal.confirmHandover')}
              </button>
            )}
            {/* Frakt: bytet slutförs först när plaggen kommit fram (api swaps/receipt.ts) */}
            {theirDeliveryMethod === 'shipping' && theirShippingPaidAt && (myReceivedAt ? (
              <div style={{ fontSize: 12, color: T.textSub, marginTop: 8 }}>{t('swapProposalDetailModal.youConfirmedReceived')}</div>
            ) : (
              <button style={{ ...S.btn('primary'), width: '100%', justifyContent: 'center', marginTop: 8 }} disabled={busy} onClick={onConfirmReceived}>
                {busy ? '...' : t('swapProposalDetailModal.confirmReceived')}
              </button>
            ))}
            {reporting ? (
              <div style={{ marginTop: 8 }}>
                <textarea style={{ ...S.input, minHeight: 50, resize: 'vertical' as const, fontFamily: 'inherit', marginBottom: 8 }}
                  value={reportMsg} onChange={e => setReportMsg(e.target.value)} placeholder={t('swapProposalDetailModal.reportPlaceholder')} autoFocus />
                <div style={{ display: 'flex', gap: 8 }}>
                  <button style={{ ...S.btn('ghost'), flex: 1, justifyContent: 'center' }} onClick={() => setReporting(false)}>{t('common.cancel')}</button>
                  <button style={{ ...S.btn('danger'), flex: 1, justifyContent: 'center' }} disabled={busy || reportMsg.trim().length < 10} onClick={() => onReportProblem?.(reportMsg.trim())}>
                    {busy ? '...' : t('swapProposalDetailModal.sendReport')}
                  </button>
                </div>
              </div>
            ) : (
              <button style={{ ...S.btn('ghost'), padding: '6px 12px', fontSize: 12, marginTop: 8, color: T.danger }} onClick={() => setReporting(true)}>
                {t('swapProposalDetailModal.reportProblem')}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── PROPOSE SWAP MODAL ──
// Viewaren väljer ett eller flera egna plagg att erbjuda mot ett plagg som tillhör
// någon annan. Mirrors the drop-wizard's item-selection grid (checkmark overlay).

function ProposeSwapModal({ targetItem, onClose, onProposed }: {
  targetItem: WardrobeItem;
  onClose: () => void;
  onProposed: () => void;
}) {
  const [items, setItems] = useState<WardrobeItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [duration, setDuration] = useState<1 | 3 | 12 | 'permanent'>('permanent');
  const [message, setMessage] = useState('');
  const [deliveryMethod, setDeliveryMethod] = useState<'pickup' | 'shipping'>('pickup');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Via Fraktjakt skickas plaggen i ett paket, prissatt efter paketets viktklass; annars viktklassernas summa.
  const fixedShipping = useShippingFixedPrice();
  const fraktOn = useFraktjaktEnabled();
  const selectedTiers = Array.from(selected).map(id => items.find(i => i.id === id)?.weightTier ?? '0-1');
  const shippingCost = fixedShipping ?? (selectedTiers.length === 0 ? 0 : fraktOn
    ? SHIPPING_COST_BY_WEIGHT[combinedWeightTier(selectedTiers)]
    : selectedTiers.reduce((sum, tier) => sum + (SHIPPING_COST_BY_WEIGHT[tier] ?? 0), 0));

  useEffect(() => {
    api.myWardrobe()
      .then(all => setItems(all.filter(i => i.status === 'active')))
      .catch(err => setError(err.message ?? t('proposeSwapModal.loadFailed')))
      .finally(() => setLoading(false));
  }, []);

  function toggleSelect(id: string) {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  async function handleSubmit() {
    if (selected.size === 0) { setError(t('proposeSwapModal.selectAtLeastOne')); return; }
    setSaving(true); setError('');
    try {
      await api.proposeSwap({
        targetItemId: targetItem.id,
        offeredItemIds: Array.from(selected),
        message: message.trim() || undefined,
        durationMonths: duration === 'permanent' ? undefined : duration,
        deliveryMethod,
      });
      onProposed();
      onClose();
    } catch (err: any) { setError(err.message ?? t('common.somethingWentWrong')); }
    finally { setSaving(false); }
  }

  return (
    <div style={S.modal} onClick={onClose}>
      <div style={{ background: T.surface, borderRadius: 8, padding: 28, width: '100%', maxWidth: 560, maxHeight: '90vh', overflowY: 'auto', border: `1px solid ${T.border}` }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: T.text }}>{t('proposeSwapModal.title')}</div>
          <button style={{ ...S.btn('ghost'), padding: '4px 8px' }} onClick={onClose}>✕</button>
        </div>
        <div style={{ fontSize: 13, color: T.textSub, marginBottom: 18 }}>
          {tn('proposeSwapModal.intro', { item: <strong style={{ color: T.text }}>{targetItem.name}</strong> })}
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 32, color: T.textMuted }}>{t('proposeSwapModal.loadingWardrobe')}</div>
        ) : items.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 32, color: T.textMuted }}>
            <div style={{ fontSize: 13 }}>{t('proposeSwapModal.noItems')}</div>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))', gap: '12px 10px', marginBottom: 18 }}>
            {items.map(item => {
              const isSelected = selected.has(item.id);
              return (
                <div
                  key={item.id}
                  onClick={() => toggleSelect(item.id)}
                  style={{ cursor: 'pointer', position: 'relative' }}
                >
                  <div style={{ position: 'relative', paddingTop: '125%', overflow: 'hidden', background: T.bg, border: `1px solid ${isSelected ? T.rose : T.border}` }}>
                    {item.imageUrl ? (
                      <img src={item.imageUrl} alt={item.name} style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                      <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', fontSize: 28, color: T.textMuted }}>👗</div>
                    )}
                    <div style={{
                      position: 'absolute', top: 6, right: 6, width: 20, height: 20, borderRadius: '50%',
                      background: isSelected ? T.rose : 'rgba(255,255,255,0.85)', color: isSelected ? '#fff' : T.textMuted,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700,
                      border: `1px solid ${isSelected ? T.rose : T.border}`,
                    }}>
                      {isSelected ? '✓' : ''}
                    </div>
                  </div>
                  <div style={{ fontSize: 11, color: T.text, marginTop: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.name}</div>
                </div>
              );
            })}
          </div>
        )}

        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 12, color: T.textSub, marginBottom: 6 }}>{t('proposeSwapModal.period')}</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
            {([[1, t('common.months', { count: 1 })], [3, t('common.months', { count: 3 })], [12, t('common.months', { count: 12 })], ['permanent', t('common.permanent')]] as const).map(([val, label]) => {
              const isActive = duration === val;
              return (
                <button
                  key={String(val)}
                  type="button"
                  onClick={() => setDuration(val)}
                  style={{
                    padding: '10px 4px', borderRadius: 5, textAlign: 'center',
                    border: `2px solid ${isActive ? T.rose : T.border}`,
                    background: isActive ? T.roseDim : 'transparent',
                    color: isActive ? T.rose : T.textSub,
                    cursor: 'pointer', fontSize: 12, fontWeight: isActive ? 700 : 400,
                  }}
                >
                  {label}
                </button>
              );
            })}
          </div>
          <div style={{ fontSize: 11, color: T.textMuted, marginTop: 6 }}>
            {duration === 'permanent'
              ? t('proposeSwapModal.permanentInfo')
              : t('proposeSwapModal.temporaryInfo', { count: duration })}
          </div>
        </div>

        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 12, color: T.textSub, marginBottom: 6 }}>{t('proposeSwapModal.handoverQuestion')}</div>
          <div style={{ display: 'flex', gap: 6 }}>
            {(['pickup', 'shipping'] as const).map(method => {
              const active = deliveryMethod === method;
              return (
                <button
                  key={method}
                  type="button"
                  onClick={() => setDeliveryMethod(method)}
                  style={{
                    flex: 1, padding: '8px 6px', borderRadius: 4,
                    border: `1.5px solid ${active ? T.rose : T.border}`,
                    background: active ? T.roseDim : 'transparent',
                    color: active ? T.rose : T.textMuted,
                    cursor: 'pointer', fontSize: 12, fontWeight: active ? 700 : 500,
                  }}
                >
                  {method === 'pickup' ? t('proposeSwapModal.pickup') : t('proposeSwapModal.shippingWithCost', { amount: fmt(shippingCost) })}
                </button>
              );
            })}
          </div>
          <div style={{ fontSize: 11, color: T.textMuted, marginTop: 4 }}>{t('proposeSwapModal.shippingInfo')}</div>
          {deliveryMethod === 'shipping' && (
            <div style={{ fontSize: 12, color: T.textSub, marginTop: 6 }}>
              {t('proposeSwapModal.protectionInfo', { amount: fmtKr(buyerProtectionFee(targetItem.salePrice ?? 0)) })}
            </div>
          )}
        </div>

        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 12, color: T.textSub, marginBottom: 4 }}>{t('proposeSwapModal.messageLabel')}</div>
          <textarea
            style={{ ...S.input, minHeight: 70, resize: 'vertical' as const, fontFamily: 'inherit' }}
            value={message}
            onChange={e => setMessage(e.target.value)}
            placeholder={t('proposeSwapModal.messagePlaceholder')}
          />
        </div>

        {error && <div style={{ color: T.danger, fontSize: 13, padding: '8px 12px', background: `${T.danger}15`, borderRadius: 3, marginBottom: 12 }}>{error}</div>}

        <button
          type="button"
          style={{ ...S.btn('primary'), width: '100%', justifyContent: 'center', padding: '10px' }}
          onClick={handleSubmit}
          disabled={saving || loading || items.length === 0}
        >
          {saving ? t('proposeSwapModal.sending') : t('proposeSwapModal.submit', { count: selected.size })}
        </button>
      </div>
    </div>
  );
}

// ── PROPOSE OFFER MODAL ──
// Köp-/hyreserbjudande på ett plagg som inte är listat för försäljning/uthyrning.

function ProposeOfferModal({ targetItem, onClose, onProposed }: {
  targetItem: WardrobeItem;
  onClose: () => void;
  onProposed: () => void;
}) {
  const [type, setType] = useState<'buy' | 'rent'>('buy');
  const [offeredPrice, setOfferedPrice] = useState('');
  const [message, setMessage] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit() {
    const price = Number(offeredPrice);
    if (!(price > 0)) { setError(t('proposeOfferModal.invalidOffer')); return; }
    setSaving(true); setError('');
    try {
      await api.proposeOffer({
        itemId: targetItem.id,
        type,
        offeredPrice: price,
        message: message.trim() || undefined,
      });
      onProposed();
      onClose();
    } catch (err: any) { setError(err.message ?? t('common.somethingWentWrong')); }
    finally { setSaving(false); }
  }

  return (
    <div style={S.modal} onClick={onClose}>
      <div style={{ background: T.surface, borderRadius: 8, padding: 28, width: '100%', maxWidth: 460, maxHeight: '90vh', overflowY: 'auto', border: `1px solid ${T.border}` }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: T.text }}>{t('proposeOfferModal.title')}</div>
          <button style={{ ...S.btn('ghost'), padding: '4px 8px' }} onClick={onClose}>✕</button>
        </div>
        <div style={{ fontSize: 13, color: T.textSub, marginBottom: 18 }}>
          {tn('proposeOfferModal.intro', { item: <strong style={{ color: T.text }}>{targetItem.name}</strong> })}
        </div>

        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          <button style={{ ...S.btn(type === 'buy' ? 'primary' : 'ghost'), flex: 1, justifyContent: 'center' }} onClick={() => setType('buy')}>{t('common.buy')}</button>
          <button style={{ ...S.btn(type === 'rent' ? 'primary' : 'ghost'), flex: 1, justifyContent: 'center' }} onClick={() => setType('rent')}>{t('proposeOfferModal.rent')}</button>
        </div>

        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 12, color: T.textSub, marginBottom: 4 }}>{type === 'rent' ? t('proposeOfferModal.yourOfferRent') : t('proposeOfferModal.yourOfferBuy')}</div>
          <input style={S.input} type="number" min={1} value={offeredPrice} onChange={e => setOfferedPrice(e.target.value)} placeholder={t('proposeOfferModal.pricePlaceholder')} autoFocus />
        </div>

        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 12, color: T.textSub, marginBottom: 4 }}>{t('proposeOfferModal.messageLabel')}</div>
          <textarea
            style={{ ...S.input, minHeight: 70, resize: 'vertical' as const, fontFamily: 'inherit' }}
            value={message}
            onChange={e => setMessage(e.target.value)}
            placeholder={t('proposeOfferModal.messagePlaceholder')}
          />
        </div>

        {error && <div style={{ color: T.danger, fontSize: 13, padding: '8px 12px', background: `${T.danger}15`, borderRadius: 3, marginBottom: 12 }}>{error}</div>}

        <button
          type="button"
          style={{ ...S.btn('primary'), width: '100%', justifyContent: 'center', padding: '10px' }}
          onClick={handleSubmit}
          disabled={saving}
        >
          {saving ? t('proposeOfferModal.sending') : t('proposeOfferModal.submit')}
        </button>
      </div>
    </div>
  );
}

// ── EDIT WARDROBE ITEM MODAL ──

function EditWardrobeItemModal({ item, bodyPhotos, onClose, onSaved, onDeleted }: {
  item: WardrobeItem;
  bodyPhotos?: string[];
  onClose: () => void;
  onSaved: (updated: WardrobeItem) => void;
  onDeleted?: (id: string) => void;
}) {
  const fixedShipping = useShippingFixedPrice();
  const [form, setForm] = useState({
    name: item.name, description: item.description ?? '', category: item.category,
    size: item.size ?? '', condition: item.condition ?? 5,
    weightTier: item.weightTier || '0-1',
    imageUrl: item.imageUrl ?? '', galleryUrls: item.galleryUrls ?? [] as string[],
    imageVariants: item.imageVariants ?? [] as ImageVariant[],
    forRent: item.forRent,
    rentPrice1: item.rentPrice1 != null ? String(item.rentPrice1) : '',
    rentPrice3: item.rentPrice3 != null ? String(item.rentPrice3) : '',
    rentPrice12: String(item.rentPrice || ''),
    forSale: item.forSale, salePrice: String(item.salePrice || ''),
    replacementValue: item.replacementValue != null ? String(item.replacementValue) : '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);
  const galleryRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadingGallery, setUploadingGallery] = useState(false);

  async function handleImageUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try { const res = await api.uploadFile(file); setForm(p => ({ ...p, imageUrl: res.url })); }
    catch { setError(t('editWardrobeItemModal.imageUploadFailed')); }
    finally { setUploading(false); }
  }

  async function handleGalleryUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? []);
    if (!files.length) return;
    setUploadingGallery(true);
    try {
      for (const file of files) {
        const result = await api.uploadFile(file);
        setForm(p => ({ ...p, galleryUrls: [...p.galleryUrls, result.url] }));
      }
    } catch { setError(t('editWardrobeItemModal.galleryUploadFailed')); }
    finally { setUploadingGallery(false); if (galleryRef.current) galleryRef.current.value = ''; }
  }

  async function handleSave() {
    setSaving(true); setError('');
    try {
      const updated = await api.updateWardrobeItem(item.id, {
        name: form.name, description: form.description || undefined,
        category: form.category, size: form.size || undefined,
        condition: form.condition, weightTier: form.weightTier, imageUrl: form.imageUrl || undefined,
        galleryUrls: form.galleryUrls,
        imageVariants: pruneVariants(form),
        forRent: form.forRent,
        rentPrice: form.forRent ? (form.rentPrice12 ? Number(form.rentPrice12) : 0) : 0,
        rentPrice3: form.forRent && form.rentPrice3 ? Number(form.rentPrice3) : undefined,
        rentPrice1: form.forRent && form.rentPrice1 ? Number(form.rentPrice1) : undefined,
        forSale: form.forSale, salePrice: form.forSale ? Number(form.salePrice) || 0 : 0,
        replacementValue: form.forRent ? (Number(form.replacementValue) > 0 ? Number(form.replacementValue) : null) : undefined,
      });
      onSaved(updated);
    } catch (err: any) { setError(err.message ?? t('common.somethingWentWrong')); }
    finally { setSaving(false); }
  }

  async function handleDelete() {
    if (!confirm(t('editWardrobeItemModal.deleteConfirm'))) return;
    try { await api.deleteWardrobeItem(item.id); onDeleted?.(item.id); onClose(); } catch (err: any) { alert(err.message); }
  }

  return (
    <div style={S.modal} onClick={onClose}>
      <div style={{ background: T.surface, borderRadius: 8, padding: 28, width: '100%', maxWidth: 520, maxHeight: '90vh', overflowY: 'auto', border: `1px solid ${T.border}` }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: T.text }}>{t('editWardrobeItemModal.title')}</div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button style={{ ...S.btn('danger'), padding: '4px 10px', fontSize: 11 }} onClick={handleDelete}>{t('common.delete')}</button>
            <button style={{ ...S.btn('ghost'), padding: '4px 8px' }} onClick={onClose}>✕</button>
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <div style={{ fontSize: 12, color: T.textSub, marginBottom: 4 }}>{t('editWardrobeItemModal.name')}</div>
            <input style={S.input} value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} />
          </div>
          <div>
            <div style={{ fontSize: 12, color: T.textSub, marginBottom: 4 }}>{t('common.description')}</div>
            <textarea style={{ ...S.input, minHeight: 60, resize: 'vertical' }} value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
            <div>
              <div style={{ fontSize: 12, color: T.textSub, marginBottom: 4 }}>{t('common.category')}</div>
              <select style={S.input} value={form.category} onChange={e => setForm(p => ({ ...p, category: e.target.value }))}>
                {['Plagg', 'Accessoarer', 'Skor', 'Väskor', 'Övrigt'].map(c => <option key={c} value={c}>{vl('category', c)}</option>)}
              </select>
            </div>
            <div>
              <div style={{ fontSize: 12, color: T.textSub, marginBottom: 4 }}>{t('common.size')}</div>
              <input style={S.input} value={form.size} onChange={e => setForm(p => ({ ...p, size: e.target.value }))} />
            </div>
            <div>
              <div style={{ fontSize: 12, color: T.textSub, marginBottom: 4 }}>{t('editWardrobeItemModal.condition')}</div>
              <select style={S.input} value={form.condition} onChange={e => setForm(p => ({ ...p, condition: Number(e.target.value) }))}>
                {[5,4,3,2,1].map(c => <option key={c} value={c}>{c} — {CONDITION_LABELS[c]?.label}</option>)}
              </select>
            </div>
          </div>
          <div>
            <div style={{ fontSize: 12, color: T.textSub, marginBottom: 4 }}>{t('editWardrobeItemModal.weight')}</div>
            <select style={S.input} value={form.weightTier} onChange={e => setForm(p => ({ ...p, weightTier: e.target.value }))}>
              {weightTierOptions(fixedShipping).map(w => <option key={w.value} value={w.value}>{w.label}</option>)}
            </select>
          </div>
          <div>
            <div style={{ fontSize: 12, color: T.textSub, marginBottom: 4 }}>{t('editWardrobeItemModal.image')}</div>
            <div style={{ display: 'flex', gap: 8 }}>
              <input style={{ ...S.input, flex: 1 }} value={form.imageUrl} onChange={e => setForm(p => ({ ...p, imageUrl: e.target.value }))} />
              <button type="button" style={S.btn('ghost')} onClick={() => fileRef.current?.click()} disabled={uploading}>{uploading ? '...' : t('common.upload')}</button>
            </div>
            <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleImageUpload} />
            {form.imageUrl && <img src={form.imageUrl} alt="" style={{ marginTop: 8, width: 80, height: 80, objectFit: 'cover', borderRadius: 4, display: 'block' }} />}
            <CutoutButton url={form.imageUrl} images={form} onChange={next => setForm(p => ({ ...p, ...next }))} />
          </div>

          {/* AI-genererade bilder utifrån uppladdad bild */}
          {uploadPath(form.imageUrl) && (
            <AiImageGenerator
              imageUrl={form.imageUrl}
              name={form.name}
              category={form.category}
              bodyPhotos={bodyPhotos}
              onAddToGallery={urls => setForm(p => ({ ...p, galleryUrls: [...p.galleryUrls, ...urls] }))}
            />
          )}

          {/* Bildgalleri */}
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <div style={{ fontSize: 12, color: T.textSub }}>{t('editWardrobeItemModal.gallery', { count: form.galleryUrls.length })}</div>
              <button type="button" style={{ ...S.btn('ghost'), padding: '4px 10px', fontSize: 12 }} onClick={() => galleryRef.current?.click()} disabled={uploadingGallery}>
                {uploadingGallery ? t('common.loading') : t('editWardrobeItemModal.addImages')}
              </button>
            </div>
            <input ref={galleryRef} type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={handleGalleryUpload} />
            {form.galleryUrls.length > 0 && (
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {form.galleryUrls.map((url, i) => (
                  <div key={i} style={{ position: 'relative', width: 60 }}>
                    <img src={url} alt="" style={{ width: 60, height: 50, objectFit: 'cover', borderRadius: 3, border: `1px solid ${T.border}`, display: 'block' }} />
                    <CutoutButton compact url={url} images={form} onChange={next => setForm(p => ({ ...p, ...next }))} />
                    {isAiImage(url) && <AiImageBadge small />}
                    <button
                      onClick={() => setForm(p => ({ ...p, galleryUrls: p.galleryUrls.filter((_, j) => j !== i) }))}
                      style={{ position: 'absolute', top: -6, right: -6, width: 18, height: 18, borderRadius: 5, background: T.danger, border: 'none', color: '#fff', fontSize: 11, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    >×</button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div style={{ background: T.bg, borderRadius: 5, padding: 14, border: `1px solid ${T.border}` }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: T.text, marginBottom: 10 }}>{t('editWardrobeItemModal.listing')}</div>

            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 13, color: T.textSub }}>
                <input type="checkbox" checked={form.forSale} onChange={e => setForm(p => ({ ...p, forSale: e.target.checked }))} /> {t('editWardrobeItemModal.sell')}
              </label>
              {form.forSale && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <input style={{ ...S.input, width: 80 }} type="number" value={form.salePrice} onChange={e => setForm(p => ({ ...p, salePrice: e.target.value }))} />
                  <span style={{ fontSize: 12, color: T.textMuted }}>{t('editWardrobeItemModal.currency')}</span>
                </div>
              )}
            </div>

            <div style={{ marginTop: 10 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 13, color: T.textSub, marginBottom: form.forRent ? 8 : 0 }}>
                <input type="checkbox" checked={form.forRent} onChange={e => setForm(p => ({ ...p, forRent: e.target.checked }))} /> {t('editWardrobeItemModal.rentOut')}
              </label>
              {form.forRent && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
                  <div>
                    <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 3 }}>{t('common.months', { count: 1 })}</div>
                    <input style={S.input} type="number" value={form.rentPrice1} onChange={e => setForm(p => ({ ...p, rentPrice1: e.target.value }))} placeholder={t('editWardrobeItemModal.perMonthPlaceholder')} />
                  </div>
                  <div>
                    <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 3 }}>{t('common.months', { count: 3 })}</div>
                    <input style={S.input} type="number" value={form.rentPrice3} onChange={e => setForm(p => ({ ...p, rentPrice3: e.target.value }))} placeholder={t('editWardrobeItemModal.perMonthPlaceholder')} />
                  </div>
                  <div>
                    <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 3 }}>{t('common.months', { count: 12 })}</div>
                    <input style={S.input} type="number" value={form.rentPrice12} onChange={e => setForm(p => ({ ...p, rentPrice12: e.target.value }))} placeholder={t('editWardrobeItemModal.perMonthPlaceholder')} />
                  </div>
                </div>
              )}
            </div>

            {form.forRent && (
              <div style={{ marginTop: 12 }}>
                <div style={{ fontSize: 12, color: T.textSub, marginBottom: 4 }}>{t('editWardrobeItemModal.replacementValue')}</div>
                <input style={S.input} type="number" min={0} value={form.replacementValue} onChange={e => setForm(p => ({ ...p, replacementValue: e.target.value }))} placeholder={t('editWardrobeItemModal.replacementValuePlaceholder')} />
                <div style={{ fontSize: 11, color: T.textMuted, marginTop: 4 }}>{t('editWardrobeItemModal.replacementValueHint')}</div>
              </div>
            )}

            {(form.forRent || form.forSale) && (
              <div style={{ marginTop: 10, fontSize: 11, color: T.textMuted }}>{t('editWardrobeItemModal.feeInfo')}</div>
            )}
          </div>
          {error && <div style={{ color: T.danger, fontSize: 13, padding: '8px 12px', background: `${T.danger}15`, borderRadius: 3 }}>{error}</div>}
          <button style={{ ...S.btn('primary'), justifyContent: 'center', padding: '10px' }} onClick={handleSave} disabled={saving}>
            {saving ? t('common.saving') : t('common.saveChanges')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── RETURN FLOW MODAL ──

function ReturnFlowModal({ item, transactions, onClose, onBagOrdered, onTransactionUpdated }: {
  item: WardrobeItem;
  transactions: WardrobeTransaction[];
  onClose: () => void;
  onBagOrdered: (bag: ReturnBag) => void;
  onTransactionUpdated: (tx: WardrobeTransaction) => void;
}) {
  const [choice, setChoice] = useState<'order' | 'scan' | 'tracking' | null>(null);
  const [scanCode, setScanCode] = useState('');
  const [trackingNumber, setTrackingNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Find the related transaction
  const relatedTx = transactions.find(t => t.id === item.sourceOrderId);

  async function handleRequestReturn() {
    if (!relatedTx) { handleOrderBag(); return; }
    setLoading(true); setError('');
    try {
      const updated = await api.requestReturn(relatedTx.id);
      onTransactionUpdated(updated);
      setSuccess(t('returnFlowModal.returnRequested'));
      setChoice('tracking');
    } catch (err: any) { setError(err.message ?? t('common.somethingWentWrong')); }
    finally { setLoading(false); }
  }

  async function handleOrderBag() {
    setLoading(true); setError('');
    try {
      const bag = await api.orderReturnBag(item.id);
      setSuccess(t('returnFlowModal.bagOrdered', { code: bag.bagCode }));
      onBagOrdered(bag);
    } catch (err: any) { setError(err.message ?? t('common.somethingWentWrong')); }
    finally { setLoading(false); }
  }

  async function handleActivate() {
    if (!scanCode.trim()) { setError(t('returnFlowModal.enterQrCode')); return; }
    setLoading(true); setError('');
    try {
      await api.activateReturnBag(scanCode.trim());
      setSuccess(t('returnFlowModal.bagActivated'));
      setChoice('tracking');
    } catch (err: any) { setError(err.message ?? t('returnFlowModal.codeNotFound')); }
    finally { setLoading(false); }
  }

  async function handleShipReturn() {
    if (!trackingNumber.trim()) { setError(t('returnFlowModal.enterTrackingNumber')); return; }
    if (!relatedTx) { setError(t('returnFlowModal.transactionMissing')); return; }
    setLoading(true); setError('');
    try {
      const updated = await api.shipReturn(relatedTx.id, trackingNumber.trim());
      onTransactionUpdated(updated);
      setSuccess(t('returnFlowModal.returnShipped'));
      setTimeout(onClose, 2000);
    } catch (err: any) { setError(err.message ?? t('common.somethingWentWrong')); }
    finally { setLoading(false); }
  }

  return (
    <div style={S.modal} onClick={onClose}>
      <div style={{ background: T.surface, borderRadius: 8, padding: 28, width: '100%', maxWidth: 440, border: `1px solid ${T.border}` }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: T.text }}>{t('returnFlowModal.title')}</div>
          <button style={{ ...S.btn('ghost'), padding: '4px 8px' }} onClick={onClose}>✕</button>
        </div>
        <div style={{ fontSize: 14, color: T.textSub, marginBottom: 20 }}>
          <strong style={{ color: T.text }}>{item.name}</strong> — {item.source === 'rented' ? t('returnFlowModal.rented') : t('returnFlowModal.bought')}
        </div>

        {success && choice !== 'tracking' ? (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <div style={{ fontSize: 32, marginBottom: 8 }}>✓</div>
            <div style={{ fontSize: 15, fontWeight: 600, color: T.text }}>{success}</div>
          </div>
        ) : choice === 'tracking' ? (
          /* Tracking number input after bag activated or return requested */
          <div>
            {success && <div style={{ fontSize: 13, color: '#5cb85c', marginBottom: 12, padding: '8px 12px', background: '#5cb85c15', borderRadius: 3 }}>{success}</div>}
            <div style={{ fontSize: 14, color: T.textSub, marginBottom: 12, lineHeight: 1.6 }}>
              {t('returnFlowModal.trackingInstructions')}
            </div>
            <input
              style={{ ...S.input, marginBottom: 12, fontSize: 16, textAlign: 'center', fontFamily: 'monospace', letterSpacing: 1 }}
              value={trackingNumber}
              onChange={e => setTrackingNumber(e.target.value)}
              placeholder={t('returnFlowModal.trackingPlaceholder')}
              autoFocus
            />
            {error && <div style={{ color: T.danger, fontSize: 13, marginBottom: 10 }}>{error}</div>}
            <div style={{ display: 'flex', gap: 10 }}>
              <button style={{ ...S.btn('ghost'), flex: 1, justifyContent: 'center' }} onClick={onClose}>{t('returnFlowModal.later')}</button>
              <button style={{ ...S.btn('primary'), flex: 2, justifyContent: 'center' }} onClick={handleShipReturn} disabled={loading}>
                {loading ? t('common.sending') : t('returnFlowModal.confirmReturnShipment')}
              </button>
            </div>
          </div>
        ) : choice === null ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {/* If there's a related transaction in delivered status, use requestReturn flow */}
            {relatedTx?.status === 'delivered' ? (
              <>
                <button style={{ ...S.btn('primary'), justifyContent: 'center', padding: '14px', fontSize: 14 }} onClick={handleRequestReturn} disabled={loading}>
                  {loading ? t('returnFlowModal.requestingReturn') : t('returnFlowModal.requestReturn')}
                </button>
                <div style={{ fontSize: 11, color: T.textMuted, textAlign: 'center', marginTop: 4 }}>
                  {t('returnFlowModal.requestReturnHint')}
                </div>
              </>
            ) : (
              <>
                <button style={{ ...S.btn('primary'), justifyContent: 'center', padding: '14px', fontSize: 14 }} onClick={() => setChoice('order')}>
                  {t('returnFlowModal.orderBag')}
                </button>
                <button style={{ ...S.btn('ghost'), justifyContent: 'center', padding: '14px', fontSize: 14 }} onClick={() => setChoice('scan')}>
                  {t('returnFlowModal.haveBagScan')}
                </button>
                <div style={{ fontSize: 11, color: T.textMuted, textAlign: 'center', marginTop: 4 }}>
                  {t('returnFlowModal.orderBagHint')}
                </div>
              </>
            )}
            {error && <div style={{ color: T.danger, fontSize: 13 }}>{error}</div>}
          </div>
        ) : choice === 'order' ? (
          <div>
            <div style={{ fontSize: 14, color: T.textSub, marginBottom: 16, lineHeight: 1.6 }}>
              {t('returnFlowModal.orderBagInfo')}
            </div>
            {error && <div style={{ color: T.danger, fontSize: 13, marginBottom: 10 }}>{error}</div>}
            <div style={{ display: 'flex', gap: 10 }}>
              <button style={{ ...S.btn('ghost'), flex: 1, justifyContent: 'center' }} onClick={() => setChoice(null)}>{t('common.back')}</button>
              <button style={{ ...S.btn('primary'), flex: 2, justifyContent: 'center' }} onClick={handleOrderBag} disabled={loading}>
                {loading ? t('returnFlowModal.ordering') : t('returnFlowModal.orderBag')}
              </button>
            </div>
          </div>
        ) : (
          <div>
            <div style={{ fontSize: 14, color: T.textSub, marginBottom: 12 }}>{t('returnFlowModal.enterBagCode')}</div>
            <input
              style={{ ...S.input, marginBottom: 12, fontSize: 18, textAlign: 'center', fontFamily: 'monospace', letterSpacing: 2 }}
              value={scanCode} onChange={e => setScanCode(e.target.value.toUpperCase())}
              placeholder="QB-XXXXXX" autoFocus
            />
            {error && <div style={{ color: T.danger, fontSize: 13, marginBottom: 10 }}>{error}</div>}
            <div style={{ display: 'flex', gap: 10 }}>
              <button style={{ ...S.btn('ghost'), flex: 1, justifyContent: 'center' }} onClick={() => setChoice(null)}>{t('common.back')}</button>
              <button style={{ ...S.btn('primary'), flex: 2, justifyContent: 'center' }} onClick={handleActivate} disabled={loading}>
                {loading ? t('returnFlowModal.activating') : t('returnFlowModal.activateBag')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── SCAN / ACTIVATE BUTTON ──

function ScanActivateButton({ bagId, onActivated }: { bagId: string; onActivated: (bag: ReturnBag) => void }) {
  const [showInput, setShowInput] = useState(false);
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);

  async function handleActivate() {
    if (!code.trim()) return;
    setLoading(true);
    try {
      const bag = await api.activateReturnBag(code.trim());
      onActivated(bag);
    } catch (err: any) { alert(err.message ?? t('scanActivateButton.codeNotFound')); }
    finally { setLoading(false); }
  }

  if (!showInput) {
    return <button style={{ ...S.btn('ghost'), padding: '4px 10px', fontSize: 11 }} onClick={() => setShowInput(true)}>{t('scanActivateButton.scanOrEnterCode')}</button>;
  }

  return (
    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
      <input style={{ ...S.input, width: 120, fontSize: 12, fontFamily: 'monospace' }} value={code} onChange={e => setCode(e.target.value.toUpperCase())} placeholder="QB-XXXXXX" autoFocus />
      <button style={{ ...S.btn('primary'), padding: '4px 10px', fontSize: 11 }} onClick={handleActivate} disabled={loading}>{loading ? '...' : t('scanActivateButton.activate')}</button>
    </div>
  );
}

// ── SHIP BAG FORM ──

function ShipBagForm({ bagId, onShipped }: { bagId: string; onShipped: (bag: ReturnBag) => void }) {
  const [tracking, setTracking] = useState('');
  const [loading, setLoading] = useState(false);

  async function handleShip() {
    if (!tracking.trim()) return;
    setLoading(true);
    try {
      const bag = await api.shipReturnBag(bagId, tracking.trim());
      onShipped(bag);
    } catch (err: any) { alert(err.message ?? t('shipBagForm.couldNotRegister')); }
    finally { setLoading(false); }
  }

  return (
    <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 12 }}>
      <input style={{ ...S.input, flex: 1, fontSize: 12 }} value={tracking} onChange={e => setTracking(e.target.value)} placeholder={t('shipBagForm.trackingPlaceholder')} />
      <button style={{ ...S.btn('primary'), padding: '4px 10px', fontSize: 11 }} onClick={handleShip} disabled={loading}>{loading ? '...' : t('shipBagForm.markShipped')}</button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// PUBLIC DROP PAGE — /drops/:id
// Fristående, oautentiserad sida för delningslänkar (Instagram/TikTok-bio etc).
// Renderas direkt av main.tsx utan resten av SPA-skalet/inloggningen.
// ─────────────────────────────────────────────────────────────────

export function PublicDropPage({ id }: { id: string }) {
  const [drop, setDrop] = useState<PublicDrop | null | undefined>(undefined); // undefined = laddar
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    api.dropPublic(id).then(setDrop).catch(() => setDrop(null));
  }, [id]);

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  if (drop === undefined) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: T.textMuted, fontSize: 13 }}>
        {t('publicDropPage.loading')}
      </div>
    );
  }

  if (!drop || drop.status === 'cancelled') {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 14, padding: 24, textAlign: 'center' }}>
        <img src="/qvestium-logo.png" alt="Qvestium" style={{ height: 22 }} />
        <div style={{ fontSize: 14, color: T.textSub }}>{t('publicDropPage.unavailable')}</div>
        <a href="/" style={{ ...S.btn('primary'), textDecoration: 'none' }}>{t('publicDropPage.toQvestium')}</a>
      </div>
    );
  }

  const isLive = drop.status === 'live';
  const releaseDate = new Date(drop.releaseAt);
  const diff = Math.max(0, releaseDate.getTime() - now);
  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  const mins = Math.floor((diff % 3600000) / 60000);
  const secs = Math.floor((diff % 60000) / 1000);
  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';

  return (
    <div style={{ minHeight: '100vh', background: T.bg }}>
      <div style={{ padding: '20px 16px', textAlign: 'center', borderBottom: `1px solid ${T.border}` }}>
        <a href="/"><img src="/qvestium-logo.png" alt="Qvestium" style={{ height: 22 }} /></a>
      </div>

      <div style={{ maxWidth: 560, margin: '0 auto', padding: '40px 20px 64px', textAlign: 'center' }}>
        {!isLive && drop.items.length > 0 && (
          <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginBottom: 8, flexWrap: 'wrap' }}>
            {drop.items.slice(0, 4).map(it => (
              it.imageUrl && <img key={it.id} src={it.imageUrl} alt="" style={{ width: 72, height: 90, objectFit: 'cover', borderRadius: 4, filter: 'blur(6px)' }} />
            ))}
          </div>
        )}

        <div style={{ fontFamily: 'Georgia, "Times New Roman", serif', fontSize: 30, color: T.text, margin: '20px 0 8px' }}>{drop.name}</div>

        {isLive ? (
          <div style={{ color: T.rose, fontWeight: 700, fontSize: 13, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 28 }}>{t('publicDropPage.liveNow')}</div>
        ) : (
          <>
            <div style={{ color: T.textSub, fontSize: 13, marginBottom: 18 }}>
              {t('publicDropPage.releases', { date: releaseDate.toLocaleString(locale(), { dateStyle: 'long', timeStyle: 'short' }) })}
            </div>
            <div style={{ display: 'flex', gap: 18, justifyContent: 'center', marginBottom: 32 }}>
              {([[days, t('publicDropPage.days')], [hours, t('publicDropPage.hours')], [mins, t('publicDropPage.minutes')], [secs, t('publicDropPage.seconds')]] as const).map(([v, l]) => (
                <div key={l}>
                  <div style={{ fontSize: 26, fontWeight: 700, color: T.text }}>{String(v).padStart(2, '0')}</div>
                  <div style={{ fontSize: 10, color: T.textMuted, textTransform: 'uppercase', letterSpacing: 0.5 }}>{l}</div>
                </div>
              ))}
            </div>
          </>
        )}

        {isLive && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 14, marginBottom: 32, textAlign: 'left' }}>
            {drop.items.map(it => (
              <div key={it.id} style={S.card}>
                <div style={{ position: 'relative', paddingTop: '125%', overflow: 'hidden', background: T.bg }}>
                  {it.imageUrl ? (
                    <img src={it.imageUrl} alt={it.name} style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : (
                    <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', fontSize: 32, color: T.textMuted }}>👗</div>
                  )}
                </div>
                <div style={{ padding: '8px 4px' }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: T.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{it.name}</div>
                  {it.forSale && <div style={{ fontSize: 12, color: '#5cb85c', fontWeight: 600 }}>{t('common.kr', { amount: fmt(it.salePrice) })}</div>}
                  {it.forRent && <div style={{ fontSize: 11, color: '#5bc0de' }}>{t('common.krPerMonth', { amount: fmt(it.rentPrice) })}</div>}
                </div>
              </div>
            ))}
          </div>
        )}

        <a href="/" style={{ ...S.btn('primary'), textDecoration: 'none', display: 'inline-flex', marginBottom: 32 }}>
          {isLive ? t('publicDropPage.seeInStore') : t('publicDropPage.toQvestium')}
        </a>

        <div style={{ maxWidth: 340, margin: '0 auto' }}>
          <DropShareBar url={shareUrl} title={drop.name} text={`${drop.name} — Qvestium`} />
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// MAIN APP
// ─────────────────────────────────────────────────────────────────

// ── SPRÅKVÄLJARE ──
// Byter språk för all gränssnittstext (se src/i18n). Sidan laddas om så att
// även etiketter som räknas ut vid modulladdning byter språk.

function LanguageSwitcher({ variant = 'compact' }: { variant?: 'compact' | 'full' }) {
  const current = getLang();
  return (
    <div role="group" aria-label={t('language.label')} style={{ display: 'flex', alignItems: 'center', gap: variant === 'full' ? 10 : 4 }}>
      {LANGS.map((l, i) => (
        <span key={l.code} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          {variant === 'compact' && i > 0 && <span aria-hidden style={{ color: T.border, fontSize: 12 }}>|</span>}
          <button
            onClick={() => setLang(l.code)}
            aria-pressed={l.code === current}
            lang={l.code}
            title={l.label}
            style={{
              background: 'none', border: 'none', padding: 0, cursor: l.code === current ? 'default' : 'pointer',
              fontSize: variant === 'full' ? 13 : 12, letterSpacing: variant === 'compact' ? 0.5 : 0,
              color: l.code === current ? T.text : T.textMuted, fontWeight: l.code === current ? 700 : 400,
              textDecoration: variant === 'full' && l.code !== current ? 'underline' : 'none',
            }}
          >
            {variant === 'compact' ? l.code.toUpperCase() : l.label}
          </button>
        </span>
      ))}
    </div>
  );
}

export default function App() {
  const [tab, setTab] = useState<TabKey>('plagg');
  const viewportWidth = useViewportWidth();
  const isMobile = viewportWidth < MOBILE_BREAKPOINT;
  const [showMobileSearch, setShowMobileSearch] = useState(false);
  const [user, setUser] = useState<QidUser | null>(null);
  // Q-assistenten (src/q): Q i loggan öppnar panelen, ett långt tryck börjar lyssna direkt.
  const [qOpen, setQOpen] = useState(false);
  const [qListen, setQListen] = useState(0);
  useEffect(() => { if (!user) setQOpen(false); }, [user]);
  // Pushnotiser i iOS-appen: lyssnare en gång, registrering när någon är inloggad.
  useEffect(() => { initPush(); }, []);
  useEffect(() => { if (user) syncPush().catch(() => {}); }, [user?.email]);
  // Inloggade som inte godkänt gällande användarvillkor (Legal.tsx).
  const [pendingTermsVersion, setPendingTermsVersion] = useState<string | null>(null);
  useEffect(() => {
    if (!user) { setPendingTermsVersion(null); return; }
    api.legalStatus().then(s => setPendingTermsVersion(s.termsVersion && !s.accepted ? s.termsVersion : null)).catch(() => {});
  }, [user?.email]);
  const [profile, setProfile] = useState<FashionProfile | null>(null);
  const [showLogin, setShowLogin] = useState(false);
  // ?invite=KOD (personlig inbjudan från CRM:et): spara koden och öppna registreringen.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const invite = params.get('invite')?.trim();
    if (!invite) return;
    try { sessionStorage.setItem(INVITE_KEY, invite); } catch { /* privat läge */ }
    params.delete('invite');
    window.history.replaceState(null, '', `${window.location.pathname}${params.toString() ? `?${params}` : ''}${window.location.hash}`);
    setShowLogin(true);
  }, []);
  const [showCart, setShowCart] = useState(false);
  // showMenu removed — profile is inline in nav
  const [cart, setCart] = useState<CartItem[]>([]);
  const [collections, setCollections] = useState<FashionCollection[]>([]);
  const [collectionsLoading, setCollectionsLoading] = useState(false);
  const [selectedCollection, setSelectedCollection] = useState<FashionCollection | null>(null);
  const [editingCollection, setEditingCollection] = useState<FashionCollection | null>(null);
  const [upcomingDrops, setUpcomingDrops] = useState<PublicDrop[]>([]);
  const [upcomingDropsLoading, setUpcomingDropsLoading] = useState(false);
  const [products, setProducts] = useState<Product[]>([]);
  const [productsLoading, setProductsLoading] = useState(true);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [showAddProduct, setShowAddProduct] = useState<string | null>(null); // category: 'Plagg' | 'Accessoarer' | null
  const [marketplaceWardrobe, setMarketplaceWardrobe] = useState<WardrobeItem[]>([]);
  const [selectedMarketplaceItem, setSelectedMarketplaceItem] = useState<WardrobeItem | null>(null);
  const [viewingSellerId, setViewingSellerId] = useState<string | null>(null);

  // Navigera till en flik via toppmenyn/länkar — stänger alltid en öppen
  // produktsida/plaggsida/säljarprofil först, annars fastnar man på den sidan
  // trots att fliken bytt (tab-state och synligt innehåll hamnade annars ur synk).
  function goToTab(t: TabKey) {
    setSelectedProduct(null);
    setSelectedMarketplaceItem(null);
    setViewingSellerId(null);
    setTab(t);
  }
  const [query, setQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('Alla');
  const [filterStyle, setFilterStyle] = useState('Alla');
  const [appReady, setAppReady] = useState(false);
  const [checkoutResult, setCheckoutResult] = useState<'success' | 'cancel' | null>(null);
  const [checkoutOrderId, setCheckoutOrderId] = useState<string | null>(null);

  const isAdmin = profile?.is_admin ?? false;
  const [initialAdminSub, setInitialAdminSub] = useState<string | null>(null);
  // Meddelanden — se MessagesView och openChatWith.
  const [initialConversationId, setInitialConversationId] = useState<string | null>(null);
  const [initialGarderobSub, setInitialGarderobSub] = useState<string | null>(null);
  const [oauthRequestId, setOauthRequestId] = useState<string | null>(null);
  const [chatTarget, setChatTarget] = useState<ChatTarget | null>(null);
  const [unreadMessages, setUnreadMessages] = useState(0);
  useEffect(() => {
    const onOpen = (e: Event) => {
      if (!user) { setShowLogin(true); return; }
      setChatTarget((e as CustomEvent<ChatTarget>).detail);
      goToTab('meddelanden');
      window.scrollTo({ top: 0 });
    };
    window.addEventListener('qvestium:open-chat', onOpen);
    return () => window.removeEventListener('qvestium:open-chat', onOpen);
  }, [user]);
  useEffect(() => {
    if (!user) { setUnreadMessages(0); return; }
    const load = () => api.unreadMessageCount().then(setUnreadMessages).catch(() => {});
    load();
    const t = setInterval(load, 60_000);
    return () => clearInterval(t);
  }, [user, tab]);
  // Anmäl/kontakta-formuläret (sidfoten) — se SupportTicketModal.
  const [supportModal, setSupportModal] = useState<'report' | 'support' | null>(null);
  useEffect(() => {
    if (appReady && tab === 'admin' && !isAdmin) setTab('plagg');
  }, [appReady, isAdmin, tab]);

  // Handle OAuth callback + checkout result
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const state = params.get('state');
    const oidcError = params.get('error');
    if (state && oidcError && !code && window.name === POPUP_WINDOW_NAME) {
      // Qid avbröt (t.ex. account_not_found vid Apple utan konto) — felet
      // vidare till huvudfliken, som visar det i inloggningsrutan.
      localStorage.setItem(POPUP_RESULT_KEY, JSON.stringify({ state, error: oidcError }));
      window.close();
    }
    if (code && state) {
      if (window.name === POPUP_WINDOW_NAME) {
        // Den här sidan laddades inuti popup-fönstret som auth.loginPopup()
        // öppnade (se api.ts) — logga inte in här (det skulle bara skapa en
        // egen session i popup-fliken), skicka istället koden vidare till
        // huvudfliken via localStorage och stäng popupen.
        localStorage.setItem(POPUP_RESULT_KEY, JSON.stringify({ code, state }));
        window.close();
      } else {
        auth.handleCallback(code, state).then(u => {
          setUser(u);
          window.history.replaceState({}, '', '/');
        }).catch(console.error);
      }
    }
    // Stripe checkout result
    const checkout = params.get('checkout');
    const orderId = params.get('order');
    if (checkout === 'success' || checkout === 'cancel') {
      if (checkout === 'success') track('Köp genomfört');
      setCheckoutResult(checkout);
      setCheckoutOrderId(orderId);
      setCart([]); // Clear cart on success
      window.history.replaceState({}, '', '/');
    }
    // Tillbaka från Stripe Connect-onboarding (se routes/connect.ts return_url/refresh_url)
    const connect = params.get('connect');
    if (connect === 'return' || connect === 'refresh') {
      goToTab('profil');
      window.history.replaceState({}, '', '/');
    }
    // Djuplänk från mejlnotiser, t.ex. /?tab=garderob (se api/src/lib/email.ts)
    // Tillbaka från BankID-verifiering (Qid → Idura → Qid → hit), se ProfileView.
    const identityResult = params.get('identity');
    if (identityResult) {
      try { sessionStorage.setItem('qvestium_identity_result', identityResult); } catch { /* ignorera */ }
    }
    // tab=admin visas först när profilen (adminbehörigheten) laddats — se
    // effekten nedan som faller tillbaka till Plagg för icke-admins.
    const linkedTab = params.get('tab');
    if (linkedTab && (ALL_TABS.some(t => t.key === linkedTab) || linkedTab === 'admin' || linkedTab === 'meddelanden')) {
      goToTab(linkedTab as TabKey);
      const sub = params.get('sub');
      if (linkedTab === 'admin' && sub) setInitialAdminSub(sub);
      if (linkedTab === 'garderob' && sub) setInitialGarderobSub(sub);
      const conv = params.get('c');
      if (linkedTab === 'meddelanden' && conv) setInitialConversationId(conv);
      window.history.replaceState({}, '', '/');
    }
    // /?oauth=<id> — en MCP-klient (t.ex. Claude.ai) ber om åtkomst, se OAuthConsentModal.
    const oauthId = params.get('oauth');
    if (oauthId) {
      setOauthRequestId(oauthId);
      window.history.replaceState({}, '', '/');
    }
    // /?item=<id> — länk till en annons (från meddelanden och mejl).
    const linkedItem = params.get('item');
    if (linkedItem) {
      openListingById(linkedItem);
      window.history.replaceState({}, '', '/');
    }
  }, []);

  // Öppna en annons från var som helst (openListing, t.ex. annonskortet i meddelanden).
  async function openListingById(id: string) {
    try {
      const item = await api.wardrobeListing(id);
      if (!item) { alert(t('app.listingGone')); return; }
      goToTab('plagg');
      setSelectedMarketplaceItem(item);
      window.scrollTo({ top: 0 });
    } catch { alert(t('app.openListingFailed')); }
  }
  useEffect(() => {
    const onOpen = (e: Event) => openListingById((e as CustomEvent<string>).detail);
    window.addEventListener('qvestium:open-listing', onOpen);
    return () => window.removeEventListener('qvestium:open-listing', onOpen);
  }, []);

  // Init: restore session
  useEffect(() => {
    async function init() {
      const u = await auth.fetchMe();
      if (u) {
        setUser(u);
        const p = await api.fetchProfile();
        if (p) setProfile(p);
      }
      setAppReady(true);
    }
    init();
    loadProducts();
    loadMarketplaceWardrobe();
  }, []);

  async function loadMarketplaceWardrobe() {
    try { setMarketplaceWardrobe(await api.marketplaceWardrobe()); } catch (e) { console.error(e); }
  }
  // En annons ändrades någonstans i appen (api.ts notifyListingsChanged) —
  // hämta om listan så att Plagg/Accessoarer visar ändringen direkt.
  useEffect(() => {
    let t: ReturnType<typeof setTimeout> | undefined;
    const onChange = () => { clearTimeout(t); t = setTimeout(loadMarketplaceWardrobe, 300); };
    window.addEventListener('qvestium:listings-changed', onChange);
    return () => { clearTimeout(t); window.removeEventListener('qvestium:listings-changed', onChange); };
  }, []);

  // Load upcoming drops when the "Kommande drops" tab is opened
  useEffect(() => {
    if (tab === 'drops') loadUpcomingDrops();
  }, [tab]);

  async function loadUpcomingDrops() {
    setUpcomingDropsLoading(true);
    try {
      setUpcomingDrops(await api.upcomingDrops());
    } catch (e) { console.error(e); }
    finally { setUpcomingDropsLoading(false); }
  }

  async function loadCollections() {
    setCollectionsLoading(true);
    try {
      const data = await api.fashionCollections();
      setCollections(data);
    } catch (e) {
      console.error(e);
    } finally {
      setCollectionsLoading(false);
    }
  }

  async function loadProducts() {
    setProductsLoading(true);
    try {
      const data = await api.products();
      setProducts(data);
    } catch (err) {
      console.error(err);
    } finally {
      setProductsLoading(false);
    }
  }

  function handleLogin(u: QidUser, isNewAccount: boolean) {
    setUser(u);
    setShowLogin(false);
    api.fetchProfile().then(p => { if (p) setProfile(p); });
    // Nytt konto → raka vägen till Min garderob, där Q (QChat) välkomnar och
    // förklarar att det räcker att ladda upp bilder på plaggen för att skapa
    // annonser automatiskt.
    if (isNewAccount) goToTab('garderob');
  }

  function handleLogout() {
    unregisterPush().finally(() => auth.logout());
    setUser(null);
    setProfile(null);
  }

  function addToCart(item: CartItem) {
    setCart(prev => {
      const existing = prev.find(i => i.id === item.id);
      if (existing) {
        // Garderobsplagg är unika (qty ska alltid vara 1) — byt hyra/köp
        // istället för att råka öka antalet om man klickar båda knapparna.
        if (item.kind === 'wardrobe') return prev.map(i => i.id === item.id ? { ...item } : i);
        return prev.map(i => i.id === item.id ? { ...i, qty: i.qty + item.qty } : i);
      }
      return [...prev, { ...item, bindingMonths: item.bindingMonths ?? 12 }];
    });
  }

  function updateCartBinding(id: string, months: 1 | 3 | 12 | 'buy') {
    setCart(prev => prev.map(i => i.id === id ? { ...i, bindingMonths: months } : i));
  }

  function updateCartDeliveryMethod(id: string, method: 'pickup' | 'shipping') {
    setCart(prev => prev.map(i => i.id === id ? { ...i, deliveryMethod: method } : i));
  }

  function removeFromCart(id: string) {
    setCart(prev => prev.filter(i => i.id !== id));
  }

  function updateCartQty(id: string, delta: number) {
    setCart(prev => prev.map(i => i.id === id ? { ...i, qty: Math.max(1, i.qty + delta) } : i).filter(i => i.qty > 0));
  }

  async function handleDeleteCollection(id: string) {
    if (!confirm(t('app.confirmDeleteCollection'))) return;
    try {
      await api.deleteFashionCollection(id);
      setCollections(prev => prev.filter(c => c.id !== id));
    } catch (e: any) {
      alert(e.message ?? t('app.deleteCollectionFailed'));
    }
  }

  async function handleDeleteProduct(id: string) {
    if (!confirm(t('app.confirmDeleteProduct'))) return;
    try {
      await api.deleteProduct(id);
      setProducts(prev => prev.filter(p => p.id !== id));
    } catch (e: any) {
      alert(e.message ?? t('app.deleteProductFailed'));
    }
  }

  // Filtered collections
  const filteredCollections = useMemo(() => {
    const q = query.toLowerCase();
    return collections.filter(c => {
      const matchSearch = !q || c.title.toLowerCase().includes(q) || (c.description ?? '').toLowerCase().includes(q) || (c.brand ?? '').toLowerCase().includes(q) || (c.location ?? '').toLowerCase().includes(q);
      const matchCategory = filterCategory === 'Alla' || c.category === filterCategory;
      const matchStyle = filterStyle === 'Alla' || c.style === filterStyle;
      return matchSearch && matchCategory && matchStyle;
    });
  }, [collections, query, filterCategory, filterStyle]);

  const plaggItems = useMemo(() => products.filter(p => p.category === 'Plagg' && (isAdmin || p.status !== 'draft')), [products, isAdmin]);
  const accessoarItems = useMemo(() => products.filter(p => p.category === 'Accessoarer' && (isAdmin || p.status !== 'draft')), [products, isAdmin]);
  // Plagg/Accessoarer-flikarna visar nu bara riktiga användarplagg (WardrobeItem),
  // inte den gamla adminhanterade produktkatalogen. "Accessoarer" samlar allt som
  // inte är kategoriserat som Plagg, så inget listat plagg blir osynligt.
  // Områdesfilter från kartan i sökfältet — säljarnas ungefärliga områden
  // hämtas första gången kartan öppnas.
  const [areaFilter, setAreaFilter] = useState<PickedArea | null>(null);
  const [showAreaPicker, setShowAreaPicker] = useState(false);
  const [sellerAreas, setSellerAreas] = useState<SellerArea[] | null>(null);
  function openAreaPicker() {
    setShowAreaPicker(true);
    setShowMobileSearch(false);
    if (!sellerAreas) api.sellerAreas().then(setSellerAreas).catch(() => setSellerAreas([]));
  }
  const areaSellers = useMemo(() => areaFilter && sellerAreas ? sellersInArea(areaFilter, sellerAreas) : null, [areaFilter, sellerAreas]);
  const areaWardrobe = useMemo(() => areaSellers ? marketplaceWardrobe.filter(i => areaSellers.has(i.userId)) : marketplaceWardrobe, [marketplaceWardrobe, areaSellers]);
  const marketplaceWardrobePlagg = useMemo(() => areaWardrobe.filter(i => i.category === 'Plagg'), [areaWardrobe]);
  const marketplaceWardrobeAccessoarer = useMemo(() => areaWardrobe.filter(i => i.category !== 'Plagg'), [areaWardrobe]);
  const plaggFilters = useMarketplaceFilters(marketplaceWardrobePlagg, query);
  const accessoarerFilters = useMarketplaceFilters(marketplaceWardrobeAccessoarer, query);

  const cartCount = cart.reduce((s, i) => s + i.qty, 0);

  if (!appReady) {
    return (
      <div style={{ background: T.bg, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center', color: T.textMuted }}>
          <div style={{ fontSize: 32, marginBottom: 12, animation: 'spin 1s linear infinite' }}>⟳</div>
          <div>{t('app.loadingApp')}</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ background: T.bg, minHeight: '100vh', color: T.text }}>
      <AnnouncementBar />

      {/* ── MAIN NAV: search | logo | actions ── */}
      <nav style={{ ...S.nav, position: 'sticky', top: 0 }}>
        {isMobile ? (
          <button
            aria-label={t('app.nav.search')}
            onClick={() => setShowMobileSearch(s => !s)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, display: 'flex', flexShrink: 0, color: showMobileSearch ? T.rose : T.text }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
          </button>
        ) : (
          <NavSearchInput query={query} setQuery={setQuery} onOpenMap={openAreaPicker} mapActive={!!areaFilter} />
        )}

        <div
          style={isMobile
            ? { ...S.logo, gap: 2, flexShrink: 0 }
            : { ...S.logo, position: 'absolute', left: '50%', transform: 'translateX(-50%)', gap: 2 }
          }
          onClick={() => goToTab('plagg')}
        >
          {user ? (
            <span onClick={e => e.stopPropagation()} style={{ display: 'flex' }}>
              <QButton title="Q" onOpen={listen => { setQOpen(true); if (listen) setQListen(n => n + 1); }} style={{ background: 'none', border: 'none', padding: 11, margin: -11, cursor: 'pointer', display: 'flex' }}>
                <img src="/qvestium-logo.png" alt="" style={{ height: isMobile ? 22 : 28, width: 'auto', objectFit: 'contain', flexShrink: 0 }} />
              </QButton>
            </span>
          ) : <img src="/qvestium-logo.png" alt="vestium" style={{ height: isMobile ? 22 : 28, width: 'auto', objectFit: 'contain', flexShrink: 0 }} />}
          <span style={{ fontFamily: 'Georgia, "Times New Roman", serif', fontWeight: 400, fontSize: isMobile ? 16 : 20, color: T.text, letterSpacing: '-0.3px', marginTop: isMobile ? 4 : 6, whiteSpace: 'nowrap' }}>vestium</span>
        </div>

        {/* Mobile search dropdown */}
        {isMobile && showMobileSearch && (
          <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', borderBottom: `1px solid ${T.border}`, padding: '10px 16px', zIndex: 1001, boxShadow: '0 8px 16px rgba(0,0,0,0.06)' }}>
            <NavSearchInput
              query={query}
              setQuery={setQuery}
              fullWidth
              autoFocus
              onSearch={() => setShowMobileSearch(false)}
              onOpenMap={openAreaPicker}
              mapActive={!!areaFilter}
            />
          </div>
        )}

        <div style={{ flex: 1 }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 10 : 18, flexShrink: 0 }}>
          {!isMobile && <LanguageSwitcher />}
          {/* User */}
          {user ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 8 : 16 }}>
              <button
                style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, padding: 0 }}
                onClick={() => goToTab('profil')}
                title={t('app.nav.myProfile')}
              >
                {(profile?.avatarUrl ?? user.picture)
                  ? <img src={profile?.avatarUrl ?? user.picture} alt="" style={{ width: 24, height: 24, borderRadius: '50%', objectFit: 'cover', objectPosition: 'center', verticalAlign: 'middle' }} />
                  : <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={T.text} strokeWidth="1.8"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                }
                {!isMobile && (
                  <span style={{ fontSize: 13, color: T.text, maxWidth: 80, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {(profile?.displayName || user.name)?.split(' ')[0] ?? t('app.nav.profileFallback')}
                  </span>
                )}
              </button>
              {!isMobile && (
                <button style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: T.textMuted }} onClick={handleLogout}>
                  {t('app.nav.logOut')}
                </button>
              )}
            </div>
          ) : (
            <button style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: T.text }} onClick={() => setShowLogin(true)}>
              {t('common.logIn')}
            </button>
          )}

          {/* Meddelanden */}
          {user && (
            <button
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, position: 'relative', display: 'flex', marginRight: 6 }}
              onClick={() => goToTab('meddelanden')}
              title={t('app.nav.messages')}
              aria-label={unreadMessages ? t('app.nav.messagesUnread', { count: unreadMessages }) : t('app.nav.messages')}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={T.text} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
              {unreadMessages > 0 && (
                <span style={{ position: 'absolute', top: -4, right: -6, background: T.rose, color: '#fff', borderRadius: '50%', fontSize: 10, fontWeight: 700, padding: '0 4px', lineHeight: '15px', minWidth: 15, textAlign: 'center' }}>
                  {unreadMessages > 99 ? '99+' : unreadMessages}
                </span>
              )}
            </button>
          )}

          {/* Cart */}
          <button
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, position: 'relative', display: 'flex' }}
            onClick={() => setShowCart(true)}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={T.text} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/></svg>
            {cartCount > 0 && (
              <span style={{ position: 'absolute', top: -4, right: -6, background: T.rose, color: '#fff', borderRadius: '50%', fontSize: 10, fontWeight: 700, padding: '0 4px', lineHeight: '15px', minWidth: 15, textAlign: 'center' }}>
                {cartCount}
              </span>
            )}
          </button>
        </div>
      </nav>

      {/* ── CATEGORY NAV (sticky under main nav) ── */}
      <div style={{
        background: '#ffffff',
        borderBottom: `1px solid ${T.border}`,
        position: 'sticky', top: T.navH, zIndex: 999,
        padding: '0 24px',
      }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <TabBar active={tab} setActive={goToTab} isAdmin={isAdmin} />
        </div>
      </div>

      {/* ── MAIN CONTENT ── */}
      <main style={{ minHeight: '100vh' }}>
        {selectedMarketplaceItem ? (
          <WardrobeItemDetailPage
            item={selectedMarketplaceItem}
            user={user}
            profile={profile}
            onClose={() => setSelectedMarketplaceItem(null)}
            onGoToProfile={() => goToTab('profil')}
            onViewSeller={sellerId => { setSelectedMarketplaceItem(null); setViewingSellerId(sellerId); }}
            onUpdated={updated => { setSelectedMarketplaceItem(updated); loadMarketplaceWardrobe(); }}
            onNeedLogin={() => setShowLogin(true)}
            onAddToCart={cartItem => { addToCart(cartItem); setShowCart(true); }}
          />
        ) : viewingSellerId ? (
          <SellerProfileView
            sellerId={viewingSellerId}
            user={user}
            onClose={() => setViewingSellerId(null)}
            onOpenItem={item => setSelectedMarketplaceItem(item)}
            onNeedLogin={() => setShowLogin(true)}
          />
        ) : selectedProduct ? (
          <ProductDetailPage
            product={selectedProduct}
            user={user}
            profile={profile}
            onClose={() => setSelectedProduct(null)}
            onAddToCart={item => { addToCart(item); setShowCart(true); }}
            onNeedLogin={() => setShowLogin(true)}
            onGoToProfile={() => goToTab('profil')}
          />
        ) : (
        <>
        {user && <PushPrompt />}
        {['drops', 'plagg', 'accessoarer'].includes(tab) && (
          <PromoBanner onClick={() => goToTab('garderob')} />
        )}

        {/* ── KOMMANDE DROPS TAB ── */}
        {tab === 'drops' && (
          <div style={{ maxWidth: 1200, margin: '0 auto', padding: '24px 16px' }}>
            <div style={{ marginBottom: 24 }}>
              <div style={{ fontFamily: 'Georgia, "Times New Roman", serif', fontSize: 26, color: T.text, marginBottom: 4 }}>{t('app.drops.title')}</div>
              <div style={{ fontSize: 14, color: T.textSub }}>{t('app.drops.subtitle')}</div>
            </div>

            {upcomingDropsLoading ? (
              <div style={{ textAlign: 'center', padding: 40, color: T.textMuted }}>{t('app.drops.loading')}</div>
            ) : upcomingDrops.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 48, color: T.textMuted }}>
                <div style={{ fontSize: 15, fontWeight: 600, color: T.text, marginBottom: 8 }}>{t('app.drops.emptyTitle')}</div>
                <div style={{ fontSize: 13 }}>{t('app.drops.emptyHint')}</div>
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '20px 16px' }}>
                {upcomingDrops.map(drop => {
                  const isLive = drop.status === 'live';
                  return (
                    <a
                      key={drop.id}
                      href={`/drops/${drop.id}`}
                      style={{ ...S.card, display: 'block', textDecoration: 'none', border: `1px solid ${T.border}`, borderRadius: 6, overflow: 'hidden' }}
                    >
                      <div style={{ display: 'flex' }}>
                        {drop.items.slice(0, 3).map((it, i) => (
                          <div key={it.id} style={{ flex: 1, aspectRatio: '3/4', overflow: 'hidden', marginLeft: i > 0 ? -1 : 0, background: T.bg }}>
                            {it.imageUrl && (
                              <img src={it.imageUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', filter: isLive ? 'none' : 'blur(5px)' }} />
                            )}
                          </div>
                        ))}
                      </div>
                      <div style={{ padding: '12px 14px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                          <div style={{ fontSize: 14, fontWeight: 700, color: T.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{drop.name}</div>
                          <span style={S.badge(isLive ? '#5cb85c' : '#f0ad4e')}>{isLive ? t('app.drops.badgeLive') : t('app.drops.badgeSoon')}</span>
                        </div>
                        <div style={{ fontSize: 12, color: T.textSub }}>
                          {isLive
                            ? t('app.drops.released', { date: new Date(drop.releaseAt).toLocaleString(locale(), { dateStyle: 'medium', timeStyle: 'short' }) })
                            : t('app.drops.releases', { date: new Date(drop.releaseAt).toLocaleString(locale(), { dateStyle: 'medium', timeStyle: 'short' }) })}
                        </div>
                      </div>
                    </a>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ── PLAGG TAB ── */}
        {tab === 'plagg' && (
          <div style={{ maxWidth: 1200, margin: '0 auto', padding: '32px 16px' }}>
            <div style={{ marginBottom: 24 }}>
              <div style={{ fontSize: 22, fontWeight: 800, color: T.text, marginBottom: 4 }}>{t('app.plagg.title')}</div>
              <div style={{ fontSize: 13, color: T.textMuted }}>{t('app.articleCount', { count: plaggFilters.filtered.length })}</div>
            </div>
            {areaFilter && <AreaFilterChip area={areaFilter} onEdit={openAreaPicker} onClear={() => setAreaFilter(null)} />}
            {marketplaceWardrobePlagg.length > 0 && <MarketplaceFilterBar filters={plaggFilters} />}
            {plaggFilters.filtered.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 48, color: T.textMuted }}>
                <div style={{ fontSize: 15, fontWeight: 600, color: T.text, marginBottom: 8 }}>
                  {areaFilter && marketplaceWardrobePlagg.length === 0 ? t('app.plagg.emptyInArea') : marketplaceWardrobePlagg.length === 0 ? t('app.plagg.emptyNone') : t('app.plagg.emptyNoMatch')}
                </div>
                <div style={{ fontSize: 13 }}>
                  {areaFilter && marketplaceWardrobePlagg.length === 0 ? t('app.plagg.tryLargerArea') : marketplaceWardrobePlagg.length === 0 ? t('app.plagg.beFirst') : t('app.tryClearFilter')}
                </div>
              </div>
            ) : (
              <div style={MARKET_GRID(viewportWidth < MOBILE_BREAKPOINT)}>
                {plaggFilters.filtered.map(item => (
                  <MarketplaceWardrobeCard
                    key={item.id}
                    item={item}
                    user={user}
                    onOpen={() => setSelectedMarketplaceItem(item)}
                    onNeedLogin={() => setShowLogin(true)}
                    onAddToCart={cartItem => { addToCart(cartItem); setShowCart(true); }}
                    onUpdated={updated => setMarketplaceWardrobe(p => p.map(i => i.id === updated.id ? updated : i))}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── ACCESSOARER TAB ── */}
        {tab === 'accessoarer' && (
          <div style={{ maxWidth: 1200, margin: '0 auto', padding: '32px 16px' }}>
            <div style={{ marginBottom: 24 }}>
              <div style={{ fontSize: 22, fontWeight: 800, color: T.text, marginBottom: 4 }}>{t('app.accessoarer.title')}</div>
              <div style={{ fontSize: 14, color: T.textSub }}>{t('app.accessoarer.subtitle')}</div>
              <div style={{ fontSize: 13, color: T.textMuted, marginTop: 4 }}>{t('app.articleCount', { count: accessoarerFilters.filtered.length })}</div>
            </div>
            {areaFilter && <AreaFilterChip area={areaFilter} onEdit={openAreaPicker} onClear={() => setAreaFilter(null)} />}
            {marketplaceWardrobeAccessoarer.length > 0 && <MarketplaceFilterBar filters={accessoarerFilters} />}
            {accessoarerFilters.filtered.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 48, color: T.textMuted }}>
                <div style={{ fontSize: 15, fontWeight: 600, color: T.text, marginBottom: 8 }}>
                  {marketplaceWardrobeAccessoarer.length === 0 ? t('app.accessoarer.emptyNone') : t('app.accessoarer.emptyNoMatch')}
                </div>
                <div style={{ fontSize: 13 }}>
                  {marketplaceWardrobeAccessoarer.length === 0 ? t('app.accessoarer.beFirst') : t('app.tryClearFilter')}
                </div>
              </div>
            ) : (
              <div style={MARKET_GRID(viewportWidth < MOBILE_BREAKPOINT)}>
                {accessoarerFilters.filtered.map(item => (
                  <MarketplaceWardrobeCard
                    key={item.id}
                    item={item}
                    user={user}
                    onOpen={() => setSelectedMarketplaceItem(item)}
                    onNeedLogin={() => setShowLogin(true)}
                    onAddToCart={cartItem => { addToCart(cartItem); setShowCart(true); }}
                    onUpdated={updated => setMarketplaceWardrobe(p => p.map(i => i.id === updated.id ? updated : i))}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── MIN GARDEROB TAB ── */}
        {tab === 'meddelanden' && (
          user ? (
            <MessagesView
              user={user}
              initialConversationId={initialConversationId}
              chatTarget={chatTarget}
              onTargetConsumed={() => { setChatTarget(null); setInitialConversationId(null); }}
              onUnreadChanged={() => api.unreadMessageCount().then(setUnreadMessages).catch(() => {})}
            />
          ) : (
            <div style={{ maxWidth: 600, margin: '48px auto', padding: '0 16px', textAlign: 'center' }}>
              <div style={{ fontSize: 15, marginBottom: 16 }}>{t('app.loginToSeeMessages')}</div>
              <button style={S.btn('primary')} onClick={() => setShowLogin(true)}>{t('common.logIn')}</button>
            </div>
          )
        )}

        {tab === 'garderob' && (
          <MinGarderobView user={user} profile={profile} onLogin={() => setShowLogin(true)} onGoToProfile={() => goToTab('profil')} onAddToCart={item => { addToCart(item); setShowCart(true); }} initialSubTab={initialGarderobSub} />
        )}

        {/* ── ADMIN TAB ── */}
        {tab === 'admin' && isAdmin && (
          <AdminView
            user={user}
            isAdmin={isAdmin}
            initialSubTab={initialAdminSub}
            onProductCreated={() => { goToTab('plagg'); loadCollections(); }}
          />
        )}

        {/* ── PROFIL TAB ── */}
        {tab === 'profil' && (
          <div style={{ maxWidth: 1200, margin: '0 auto', padding: '32px 16px' }}>
            {user ? (
              <ProfileView user={user} profile={profile} onProfileUpdate={setProfile} onLogout={handleLogout} />
            ) : (
              <div style={{ textAlign: 'center', padding: '48px 24px', color: T.textMuted }}>
                <div style={{ fontSize: 36, marginBottom: 12 }}>👤</div>
                <div style={{ fontSize: 16, fontWeight: 600, color: T.text, marginBottom: 8 }}>{t('app.loginToSeeProfile')}</div>
                <button style={{ ...S.btn('primary'), marginTop: 8 }} onClick={() => setShowLogin(true)}>{t('common.logIn')}</button>
              </div>
            )}
          </div>
        )}
        </>
        )}
      </main>

      {/* ── FOOTER ── */}
      <footer style={{ borderTop: `1px solid ${T.border}`, padding: '32px 16px', marginTop: 48 }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 24 }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 16, color: T.rose, marginBottom: 8, letterSpacing: 2, textTransform: 'uppercase' }}>Qvestium</div>
            <div style={{ fontSize: 12, color: T.textMuted, lineHeight: 1.7 }}>{t('app.footer.tagline')}</div>
          </div>
          <div>
            <div style={{ fontWeight: 600, fontSize: 12, color: T.textSub, marginBottom: 10, textTransform: 'uppercase', letterSpacing: 1 }}>{t('app.footer.shop')}</div>
            {ALL_TABS.map(t => (
              <div key={t.key} style={{ fontSize: 13, color: T.textMuted, marginBottom: 6, cursor: 'pointer' }} onClick={() => goToTab(t.key)}>{t.label}</div>
            ))}
          </div>
          <div>
            <div style={{ fontWeight: 600, fontSize: 12, color: T.textSub, marginBottom: 10, textTransform: 'uppercase', letterSpacing: 1 }}>{t('app.footer.contact')}</div>
            <div style={{ fontSize: 13, color: T.textMuted, marginBottom: 6, cursor: 'pointer', textDecoration: 'underline' }} onClick={() => setSupportModal('support')}>{t('app.footer.contactUs')}</div>
            <div style={{ fontSize: 13, color: T.textMuted, marginBottom: 6, cursor: 'pointer', textDecoration: 'underline' }} onClick={() => setSupportModal('report')}>{t('app.footer.reportIllegal')}</div>
            <a href={LEGAL_PATHS.villkor} style={{ display: 'block', fontSize: 13, color: T.textMuted, marginBottom: 6 }}>{t('legal.terms')}</a>
            <a href={LEGAL_PATHS.integritetspolicy} style={{ display: 'block', fontSize: 13, color: T.textMuted, marginBottom: 6 }}>{t('legal.privacy')}</a>
            {['info@qvestium.com', 'Instagram: @qvestium', 'Q Industries'].map(l => (
              <div key={l} style={{ fontSize: 13, color: T.textMuted, marginBottom: 6 }}>{l}</div>
            ))}
          </div>
          <div>
            <div style={{ fontWeight: 600, fontSize: 12, color: T.textSub, marginBottom: 10, textTransform: 'uppercase', letterSpacing: 1 }}>{t('app.footer.sustainability')}</div>
            <div style={{ fontSize: 13, color: T.textMuted, lineHeight: 1.7 }}>{t('app.footer.sustainabilityText')}</div>
          </div>
          <div>
            <div style={{ fontWeight: 600, fontSize: 12, color: T.textSub, marginBottom: 10, textTransform: 'uppercase', letterSpacing: 1 }}>{t('language.label')}</div>
            <LanguageSwitcher variant="full" />
          </div>
        </div>
        <div style={{ textAlign: 'center', marginTop: 24, fontSize: 12, color: T.textMuted }}>© 2025 Qvestium — Qrepublic Qos Platform</div>
      </footer>

      {supportModal && <SupportTicketModal kind={supportModal} user={user} onClose={() => setSupportModal(null)} />}

      {/* ── CHECKOUT RESULT MODAL ── */}
      {checkoutResult && (
        <div style={S.modal} onClick={() => setCheckoutResult(null)}>
          <div style={{ background: T.surface, borderRadius: 8, width: '100%', maxWidth: 440, border: `1px solid ${T.border}`, padding: 32, textAlign: 'center' }} onClick={e => e.stopPropagation()}>
            {checkoutResult === 'success' ? (
              <>
                <div style={{ fontSize: 48, marginBottom: 16 }}>✓</div>
                <div style={{ fontSize: 20, fontWeight: 700, color: T.text, marginBottom: 8 }}>{t('app.checkout.successTitle')}</div>
                <div style={{ fontSize: 14, color: T.textSub, marginBottom: 8 }}>{t('app.checkout.successText')}</div>
                {checkoutOrderId && (
                  <div style={{ fontSize: 12, color: T.textMuted, marginBottom: 20 }}>{t('app.checkout.orderRef', { id: checkoutOrderId.slice(0, 8) })}</div>
                )}
                <button style={{ ...S.btn('primary'), padding: '10px 32px' }} onClick={() => setCheckoutResult(null)}>{t('app.checkout.continueShopping')}</button>
              </>
            ) : (
              <>
                <div style={{ fontSize: 48, marginBottom: 16 }}>✕</div>
                <div style={{ fontSize: 20, fontWeight: 700, color: T.text, marginBottom: 8 }}>{t('app.checkout.cancelTitle')}</div>
                <div style={{ fontSize: 14, color: T.textSub, marginBottom: 20 }}>{t('app.checkout.cancelText')}</div>
                <button style={{ ...S.btn('primary'), padding: '10px 32px' }} onClick={() => setCheckoutResult(null)}>{t('app.checkout.backToStore')}</button>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── MODALS ── */}
      {pendingTermsVersion && <TermsUpdateModal version={pendingTermsVersion} onAccepted={() => setPendingTermsVersion(null)} />}
      {oauthRequestId && !showLogin && (
        <OAuthConsentModal requestId={oauthRequestId} user={user} onNeedLogin={() => setShowLogin(true)} onClose={() => setOauthRequestId(null)} />
      )}
      {showLogin && <LoginModal onClose={() => setShowLogin(false)} onLogin={handleLogin} />}
      {showAreaPicker && (
        <AreaPickerModal
          initial={areaFilter}
          sellerAreas={sellerAreas ?? []}
          onApply={a => { setAreaFilter(a); setShowAreaPicker(false); goToTab(tab === 'accessoarer' ? 'accessoarer' : 'plagg'); }}
          onClear={() => { setAreaFilter(null); setShowAreaPicker(false); }}
          onClose={() => setShowAreaPicker(false)}
        />
      )}
      {showCart && <CartModal cart={cart} onClose={() => setShowCart(false)} onRemove={removeFromCart} onUpdateQty={updateCartQty} onUpdateBinding={updateCartBinding} onUpdateDeliveryMethod={updateCartDeliveryMethod} userEmail={user?.email} onCheckoutSuccess={() => { setCart([]); setShowCart(false); }} />}
      {selectedCollection && (
        <CollectionDetailModal
          col={selectedCollection}
          onClose={() => setSelectedCollection(null)}
          onAddToCart={addToCart}
          user={user}
        />
      )}
      {editingCollection && (
        <EditCollectionModal
          col={editingCollection}
          onClose={() => setEditingCollection(null)}
          onSaved={updated => {
            setCollections(prev => prev.map(c => c.id === updated.id ? updated : c));
            setEditingCollection(null);
          }}
        />
      )}

      {editingProduct && (
        <EditProductModal
          product={editingProduct}
          bodyPhotos={profile?.bodyPhotos}
          onClose={() => setEditingProduct(null)}
          onSaved={updated => {
            setProducts(prev => prev.map(p => p.id === updated.id ? updated : p));
            setEditingProduct(null);
          }}
        />
      )}

      {showAddProduct && (
        <AddProductModal
          defaultCategory={showAddProduct}
          bodyPhotos={profile?.bodyPhotos}
          onClose={() => setShowAddProduct(null)}
          onCreated={created => {
            setProducts(prev => [...prev, created]);
            setShowAddProduct(null);
          }}
        />
      )}

      {/* showMenu removed */}

      {user && (
        <Suspense fallback={null}>
          <LazyQPanel open={qOpen} onClose={() => setQOpen(false)} listenRequest={qListen} user={user} getContext={() => ({ path: selectedMarketplaceItem ? `/${tab}/${selectedMarketplaceItem.id}` : `/${tab}` })} />
        </Suspense>
      )}
    </div>
  );
}
